import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ServiceCalendar from '@/components/ServiceCalendar';
import TimezoneBar from '@/components/TimezoneBar';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import DriverProfileDialog from '@/components/DriverProfileDialog';
import AdminSidebar from '@/components/admin/AdminSidebar';
import StatsCards from '@/components/admin/StatsCards';
import Pagination from '@/components/Pagination';
import OrdersKanban from '@/components/admin/OrdersKanban';
import SubAdminManagement from '@/components/admin/SubAdminManagement';
import GPSLiveTab from '@/components/admin/GPSLiveTab';
import InspectionsTab from '@/components/admin/InspectionsTab';
import TransportServicesTab from '@/components/admin/TransportServicesTab';
import ExpenseReportServiceInfo from '@/components/admin/ExpenseReportServiceInfo';
import ExpenseReportReceipts from '@/components/admin/ExpenseReportReceipts';
import { formatExpenseReportDate } from '@/utils/expenseDisplay';
import { SettlementApprovalChip } from '@/components/admin/SettlementApprovalChip';
import OrderServiceDocumentsField, { uploadOrderServiceDocuments } from '@/components/OrderServiceDocumentsField';
import { fetchAllOrdersPaged } from '@/api/client';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { computeRouteDistanceKm } from '@/utils/distance';
import {
  translateAdditionalServiceName,
  translateAdditionalServiceDescription,
} from '@/utils/additionalServiceDisplayName';
import { buildContractPdfFilename } from '@/utils/contractDownload';
import { formatLooseDate, extractMarginParts, extractMarginDateChunk } from '@/utils/serviceDateDisplay';
import {
  Car,
  LogOut,
  Package,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Eye,
  LayoutDashboard,
  MapPin,
  Calculator,
  Search,
  Filter,
  FileText,
  UserCircle,
  Upload,
  Image,
  AlertTriangle,
  Calendar,
  Menu,
  X,
  CheckCircle as InfoIcon,
  CreditCard,
  User,
  RotateCcw,
  LayoutGrid,
  List,
  Download,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Truck,
  Clock,
  Settings,
  RefreshCw,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOW_ORDER_UPDATE_SUCCESS_TOAST = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_SHOW_ORDER_UPDATE_SUCCESS_TOAST || '').toLowerCase()
);
const SHOW_ORDER_CLASSIFICATION_DEBUG = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_SHOW_ORDER_CLASSIFICATION_DEBUG || '').toLowerCase()
);

/** Normalize driver template names for price map keys (e.g. Básica vs Basica). */
function normalizeDriverTemplateKey(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Floating UI popper (not withPortal): v8's withPortal skips the popper and mis-anchors the calendar in dialogs. */
const DATE_PICKER_DIALOG_PROPS = {
  popperClassName: 'ag-datepicker-popper-admin',
  calendarClassName: 'ag-datepicker-calendar-wide',
  popperProps: { strategy: 'fixed' },
};

// Helper function to get emoji for additional services
const getServiceEmoji = (serviceName) => {
  if (!serviceName) return '➕';
  
  const lowerName = serviceName.toLowerCase();
  
  // Limpieza
  if (lowerName.includes('limpieza')) return '🧽✨';
  
  // ITV
  if (lowerName.includes('itv') || lowerName.includes('inspección técnica')) return '🔍✅';
  
  // PIT STOP
  if (lowerName.includes('pit stop') || lowerName.includes('pit-stop') || lowerName.includes('mantenimiento')) return '🛠️';
  
  // Recuperación
  if (lowerName.includes('recuperación') || lowerName.includes('recuperacion')) {
    if (lowerName.includes('europa') || lowerName.includes('continental')) return '🛡️🗺️';
    if (lowerName.includes('isla') || lowerName.includes('islas') || lowerName.includes('canarias') || lowerName.includes('baleares')) return '🛡️🏝️';
    if (lowerName.includes('ceuta') || lowerName.includes('melilla') || lowerName.includes('áfrica') || lowerName.includes('africa')) return '🛡️🌍';
    return '🛡️';
  }
  
  // Representación
  if (lowerName.includes('representación') || lowerName.includes('representacion') || lowerName.includes('verificación') || lowerName.includes('verificacion')) return '🔍📋';
  
  // Default
  return '➕';
};

/** i18n keys for driver document types (pending list / lookups). */
const DRIVER_DOCUMENT_TYPE_I18N_KEYS = {
  license_front: 'driver_doc_license_front',
  license_back: 'driver_doc_license_back',
  dni_front: 'driver_doc_dni_front',
  dni_back: 'driver_doc_dni_back',
  autonomo_certificate: 'driver_doc_autonomo',
  ss_certificate: 'driver_doc_ss',
  hacienda_certificate: 'driver_doc_hacienda',
};

const getDriverDocumentTypeLabel = (t, documentType) => {
  const key = DRIVER_DOCUMENT_TYPE_I18N_KEYS[documentType];
  if (key) return t(key);
  return t('admin_doc_type_unknown');
};

/** Same labels as in the driver edit document list (emoji + line for id/license, icon keys for certs). */
const getDriverEditDocumentLineLabel = (t, documentType) => {
  if (documentType === 'license_front') return `📄 ${t('driver_doc_license_front')}`;
  if (documentType === 'license_back') return `📄 ${t('driver_doc_license_back')}`;
  if (documentType === 'dni_front') return `📄 ${t('driver_doc_dni_front')}`;
  if (documentType === 'dni_back') return `📄 ${t('driver_doc_dni_back')}`;
  if (documentType === 'autonomo_certificate') return t('admin_doc_type_autonomo_certificate');
  if (documentType === 'ss_certificate') return t('admin_doc_type_ss_certificate');
  if (documentType === 'hacienda_certificate') return t('admin_doc_type_hacienda_certificate');
  return t('admin_doc_type_unknown');
};

function adminOrderLegShortDate(order, leg, dateLocale, naLabel) {
  const formatShortDate = (raw) =>
    formatLooseDate(raw, dateLocale, { day: '2-digit', month: '2-digit' });
  const iso = leg === 'pickup' ? order.pickup_date_from : order.delivery_date_from;
  const isoShort = formatShortDate(iso);
  if (isoShort) return isoShort;
  const margin = leg === 'pickup' ? order.pickup_margin_time : order.delivery_margin_time;
  const marginDate = extractMarginDateChunk(margin);
  if (marginDate) return marginDate.length > 14 ? `${marginDate.slice(0, 14)}…` : marginDate;
  if (leg === 'delivery' && (order.pickup_date_to || order.pickup_date_from)) {
    const fb = order.pickup_date_to || order.pickup_date_from;
    const pickupShort = formatShortDate(fb);
    if (pickupShort) return pickupShort;
  }
  const createdAtShort = formatShortDate(order.created_at);
  if (createdAtShort) return createdAtShort;
  return naLabel;
}

/** Same-day representation: pickup/delivery often share one window — borrow time for display. */
function adminReprSharedTimeWindow(order) {
  const pS = order.pickup_time_start;
  const pE = order.pickup_time_end;
  if (pS && pE) return `${pS} - ${pE}`;
  if (pS || pE) return pS || pE || '';
  const dS = order.delivery_time_start;
  const dE = order.delivery_time_end;
  if (dS && dE) return `${dS} - ${dE}`;
  if (dS || dE) return dS || dE || '';
  const pm = extractMarginParts(order.pickup_margin_time || '');
  if (pm.timeChunk) return pm.timeChunk;
  const dm = extractMarginParts(order.delivery_margin_time || '');
  if (dm.timeChunk) return dm.timeChunk;
  return '';
}

function formatAdminContactLine(name, phone, naLabel) {
  const n = (name || '').trim();
  const p = (phone || '').trim();
  if (!n && !p) return naLabel;
  if (n && p) return `${n} • ${p}`;
  return n || p;
}

function adminOrderLegScheduleLine(order, leg, dateLocale, naLabel) {
  const formatFullDate = (raw) => formatLooseDate(raw, dateLocale);
  const iso = leg === 'pickup' ? order.pickup_date_from : order.delivery_date_from;
  const tStart = leg === 'pickup' ? order.pickup_time_start : order.delivery_time_start;
  const tEnd = leg === 'pickup' ? order.pickup_time_end : order.delivery_time_end;
  const margin = leg === 'pickup' ? order.pickup_margin_time : order.delivery_margin_time;
  const { dateChunk, timeChunk } = extractMarginParts(margin);
  let timePart = tStart && tEnd ? `${tStart} - ${tEnd}` : tStart || tEnd || timeChunk || '';
  if (!timePart && order.service_type === 'representacion') {
    timePart = adminReprSharedTimeWindow(order);
  }
  const isoDate = formatFullDate(iso);
  if (isoDate) return timePart ? `${isoDate} ${timePart}` : isoDate;
  if (dateChunk && !/^\d{1,2}:\d{2}/.test(dateChunk)) {
    return timePart ? `${dateChunk} ${timePart}` : dateChunk;
  }
  if (leg === 'delivery' && timePart && (order.pickup_date_to || order.pickup_date_from)) {
    const fb = order.pickup_date_to || order.pickup_date_from;
    const pickupDate = formatFullDate(fb);
    if (pickupDate) return `${pickupDate} ${timePart}`;
  }
  const createdAtDate = formatFullDate(order.created_at);
  if (createdAtDate) return timePart ? `${createdAtDate} ${timePart}` : createdAtDate;
  return margin || naLabel;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [activeTab, setActiveTab] = useState('overview');
  const [servicesSubTab, setServicesSubTab] = useState('active'); // Sub-tabs for services
  const [ordersViewMode, setOrdersViewMode] = useState('kanban'); // 'kanban' or 'list'
  
  // Client Billing States
  const [clientBillingPeriods, setClientBillingPeriods] = useState([]);
  const [loadingBillingPeriods, setLoadingBillingPeriods] = useState(false);
  const [generatingPeriods, setGeneratingPeriods] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar control
  const [orders, setOrders] = useState([]);

  const refreshOrdersList = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      const ordersData = await fetchAllOrdersPaged({
        headers: { Authorization: `Bearer ${token}` },
        pageSize: 400,
      });
      setOrders(ordersData);
    } catch (err) {
      console.error('Failed to refresh orders:', err);
    }
  }, []);
  const [drivers, setDrivers] = useState([]);
  const [pricingRates, setPricingRates] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [customPricing, setCustomPricing] = useState([]);
  const [clients, setClients] = useState([]);
  const [pricingTemplates, setPricingTemplates] = useState([]);
  const [clientAssignments, setClientAssignments] = useState([]);
  
  // Admin permissions state (null until /api/admin/current loads — avoids showing super_admin UI to other roles)
  const [adminType, setAdminType] = useState(null);
  const [adminSessionEmail, setAdminSessionEmail] = useState('');
  const [canDelete, setCanDelete] = useState(true);
  const [canSeeClientPrices, setCanSeeClientPrices] = useState(true);
  const [canSeeUnassigned, setCanSeeUnassigned] = useState(true);
  const [assignedDriverIds, setAssignedDriverIds] = useState([]);  // For sub-admins: list of driver IDs they can see
  
  // Billing states
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [expenseReports, setExpenseReports] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [periodDetails, setPeriodDetails] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  
  // Liquidation generation state
  const [showGenerateLiquidationDialog, setShowGenerateLiquidationDialog] = useState(false);
  const [generatingLiquidations, setGeneratingLiquidations] = useState(false);
  const [liquidationForm, setLiquidationForm] = useState({
    period_type: 'quincena_1',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [reconcilingApprovals, setReconcilingApprovals] = useState(false);
  const [lastApprovalReconcile, setLastApprovalReconcile] = useState(null);

  // Incidents state
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [incidentStatusFilter, setIncidentStatusFilter] = useState('all');
  const [incidentCount, setIncidentCount] = useState(0);
  const [incidentTab, setIncidentTab] = useState('active'); // 'active' or 'history'
  const [sendingIncidentToClient, setSendingIncidentToClient] = useState(false);
  const [finalizingServiceFromIncident, setFinalizingServiceFromIncident] = useState(false);
  const [showFinalizeFromIncidentForm, setShowFinalizeFromIncidentForm] = useState(false);
  const [finalizeIncidentForm, setFinalizeIncidentForm] = useState({
    final_status: 'delivered', // 'delivered' or 'cancelled_at_pickup'
    admin_notes: '',
    expenses: 0,
    toll_expenses: 0,
    fuel_expenses: 0,
    other_expenses: 0,
    photos: []
  });

  const contractPdfDownloadLockRef = useRef(false);
  const [downloadingContractOrderId, setDownloadingContractOrderId] = useState(null);
  
  // Liquidation tabs and filter state
  const [activeLiquidationPeriod, setActiveLiquidationPeriod] = useState(null);
  const [liquidationSearchTerm, setLiquidationSearchTerm] = useState('');
  const [currentPeriodData, setCurrentPeriodData] = useState(null);
  const [pendingInvoicingData, setPendingInvoicingData] = useState(null);
  const [loadingCurrentPeriod, setLoadingCurrentPeriod] = useState(false);
  const [expandedLiquidationRows, setExpandedLiquidationRows] = useState({});

  // Driver billing info edit state
  const [showDriverBillingDialog, setShowDriverBillingDialog] = useState(false);
  const [editingDriverBilling, setEditingDriverBilling] = useState(null);
  const [driverBillingForm, setDriverBillingForm] = useState({
    full_name: '',
    dni_nie: '',
    fiscal_address: '',
    bank_name: '',
    bank_account: '',
    irpf_retention: 0
  });

  // Client liquidation state (new - like driver liquidations)
  const [clientCurrentPeriodData, setClientCurrentPeriodData] = useState(null);
  const [loadingClientCurrentPeriod, setLoadingClientCurrentPeriod] = useState(false);
  const [activeClientLiquidationPeriod, setActiveClientLiquidationPeriod] = useState('current');
  const [clientLiquidationSearchTerm, setClientLiquidationSearchTerm] = useState('');
  const [expandedClientRows, setExpandedClientRows] = useState({});
  const [showGenerateClientLiquidationDialog, setShowGenerateClientLiquidationDialog] = useState(false);
  const [generatingClientLiquidations, setGeneratingClientLiquidations] = useState(false);
  const [clientLiquidationForm, setClientLiquidationForm] = useState({
    period_type: 'quincena_1',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  // Dialog states
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showOrderUpdateDialog, setShowOrderUpdateDialog] = useState(false);
  const [showCompleteServiceDialog, setShowCompleteServiceDialog] = useState(false);
  const [showServiceHistoryDialog, setShowServiceHistoryDialog] = useState(false);
  const [showCustomPricingDialog, setShowCustomPricingDialog] = useState(false);
  const [showAdditionalServiceDialog, setShowAdditionalServiceDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showTemplateRateDialog, setShowTemplateRateDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showClientEditDialog, setShowClientEditDialog] = useState(false);
  const [showDriverEditDialog, setShowDriverEditDialog] = useState(false);
  const [showCreateServiceDialog, setShowCreateServiceDialog] = useState(false);
  
  // Penalty dialog state (for driver reassignment)
  const [showPenaltyDialog, setShowPenaltyDialog] = useState(false);
  const [penaltyData, setPenaltyData] = useState({
    driver_id: '',
    driver_name: '',
    order_id: '',
    order_info: null,
    hours_until_pickup: 0,
    suggested_amount: 0,
    amount: 0,
    reason: 'Cancelación tardía',
    apply_penalty: false
  });
  const [pendingOrderUpdate, setPendingOrderUpdate] = useState(null);
  const [useFixedPickupDate, setUseFixedPickupDate] = useState(false);
  const [useFixedDeliveryDate, setUseFixedDeliveryDate] = useState(false);
  const [useFixedPointCDate, setUseFixedPointCDate] = useState(false);
  const [searchClient, setSearchClient] = useState('');
  const [searchDriver, setSearchDriver] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateRates, setTemplateRates] = useState([]);
  const [loadingCreateService, setLoadingCreateService] = useState(false);
  const [uploadingDriverDoc, setUploadingDriverDoc] = useState(false);
  const [licenseExpiryDate, setLicenseExpiryDate] = useState('');
  const [dniExpiryDate, setDniExpiryDate] = useState('');
  const [autonomoExpiryDate, setAutonomoExpiryDate] = useState('');
  const [ssExpiryDate, setSsExpiryDate] = useState('');
  const [haciendaExpiryDate, setHaciendaExpiryDate] = useState('');
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  
  // Fleet Management states
  const [fleetData, setFleetData] = useState({ fleets: [], available_drivers: [], potential_owners: [] });
  const [showFleetDialog, setShowFleetDialog] = useState(false);
  const [selectedFleetOwner, setSelectedFleetOwner] = useState(null);
  const [selectedDriversForFleet, setSelectedDriversForFleet] = useState([]);
  const [fleetSearchTerm, setFleetSearchTerm] = useState('');
  
  // Complete service states
  const [completeServiceForm, setCompleteServiceForm] = useState({
    fuel: '',
    tolls: '',
    parking: '',
    taxi: '',
    other: '',
    other_description: '',
    completion_notes: ''
  });
  const [uploadingCompletionPhoto, setUploadingCompletionPhoto] = useState(false);
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [expensePhotos, setExpensePhotos] = useState([]);
  const [contractPhotos, setContractPhotos] = useState([]);
  const [serviceHistoryDetails, setServiceHistoryDetails] = useState(null);
  
  // Admin additional charges state (for client, not visible to driver)
  const [adminCharges, setAdminCharges] = useState([]);
  const [newCharge, setNewCharge] = useState({ type: '', description: '', amount: '' });
  const CHARGE_TYPES = useMemo(
    () => [
      { value: 'espera', label: t('admin_charge_espera') },
      { value: 'pit_stop', label: t('admin_charge_pit_stop') },
      { value: 'itv', label: t('admin_charge_itv') },
      { value: 'express', label: t('admin_charge_express') },
      { value: 'recogida_extra', label: t('admin_charge_recogida_extra') },
      { value: 'cambio_ruta', label: t('admin_charge_cambio_ruta') },
      { value: 'otros', label: t('admin_charge_otros') },
    ],
    [t]
  );

  // Driver extras state (additional payments for driver: Pit Stop, Espera, ITV, etc.)
  const [driverExtraCategories, setDriverExtraCategories] = useState([]);
  const [driverExtras, setDriverExtras] = useState([]); // Current extras being edited
  const [newDriverExtra, setNewDriverExtra] = useState({ 
    category_id: '', 
    category_name: '', 
    unit_price: 0, 
    quantity: 1, 
    total: 0,
    description: '' 
  });

  // Pagination states
  const ITEMS_PER_PAGE = 10;
  const [servicesPage, setServicesPage] = useState(1);
  const [historicalPage, setHistoricalPage] = useState(1);
  const [driversPage, setDriversPage] = useState(1);
  const [clientsPage, setClientsPage] = useState(1);


  // Form states
  const [driverForm, setDriverForm] = useState({
    name: '',
    phone: '',
    email: '',
    license_number: '',
    status: 'available'
  });

  const [pricingForm, setPricingForm] = useState({
    min_distance: '',
    max_distance: '',
    delivery_price: '',
    bc_service_price: '',
    tradein_price: '',
    representacion_price: '',
    delivery_price_per_km: '',
    bc_service_price_per_km: '',
    tradein_price_per_km: ''
  });

  const [customPricingForm, setCustomPricingForm] = useState({
    min_distance: '',
    max_distance: '',
    delivery_price: '',
    bc_service_price: '',
    tradein_price: '',
    representacion_price: ''
  });
  
  // Vehicle photos and expenses states for admin
  const [showAdminPhotosDialog, setShowAdminPhotosDialog] = useState(false);
  const [showAdminExpensesDialog, setShowAdminExpensesDialog] = useState(false);
  const [selectedOrderForView, setSelectedOrderForView] = useState(null);
  const [adminVehiclePhotos, setAdminVehiclePhotos] = useState(null);
  const [adminOrderExpenses, setAdminOrderExpenses] = useState(null);
  const [loadingAdminPhotos, setLoadingAdminPhotos] = useState(false);
  const [loadingAdminExpenses, setLoadingAdminExpenses] = useState(false);

  const [additionalServiceForm, setAdditionalServiceForm] = useState({
    name: '',
    price: '',
    unit: 'per_service',
    description: ''
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: ''
  });

  const [templateRateForm, setTemplateRateForm] = useState({
    min_distance: '',
    max_distance: '',
    delivery_price: '',
    bc_service_price: '',
    tradein_price: '',
    representacion_price: '45',
    delivery_price_per_km: '',
    bc_service_price_per_km: '',
    tradein_price_per_km: ''
  });

  const [assignmentForm, setAssignmentForm] = useState({
    client_id: '',
    template_id: 'general'
  });

  const [clientEditForm, setClientEditForm] = useState({
    id: '',
    name: '',
    email: '',
    company_name: '',
    cif_dni: '',
    phone: '',
    address: '',
    country: '',
    payment_type: 'biweekly' // biweekly or instant
  });

  const [driverEditForm, setDriverEditForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    license_number: '',
    vehicle_info: '',
    is_available: true,
    is_fleet_driver: false,
    fleet_driver_id: '',
    full_name: '',
    dni_nie: '',
    fiscal_address: '',
    irpf_retention: 0,
    bank_name: '',
    bank_account: ''
  });

  const [createServiceForm, setCreateServiceForm] = useState({
    client_id: '',
    service_type: 'delivery',
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    pickup_contact_name: '',
    pickup_contact_phone: '',
    pickup_margin_time: 'flexible',
    delivery_address: '',
    delivery_lat: null,
    delivery_lng: null,
    delivery_contact_name: '',
    delivery_contact_phone: '',
    delivery_margin_time: 'flexible',
    point_c_address: '',
    point_c_lat: null,
    point_c_lng: null,
    point_c_contact_name: '',
    point_c_contact_phone: '',
    point_c_date_start: null,
    point_c_date_end: null,
    point_c_time_start: '',
    point_c_time_end: '',
    car_type: 'Turismo',
    car_brand: '',
    car_model: '',
    car_plate: '',
    fuel_type: 'gasoline',
    second_car_type: 'Turismo',
    second_car_brand: '',
    second_car_model: '',
    second_car_plate: '',
    second_fuel_type: 'gasoline',
    car_color: '',
    car_year: '',
    distance_km: 0,
    calculated_price: 0,
    additional_services: [],
    pickup_date_start: null,
    pickup_date_end: null,
    pickup_time_start: '',
    pickup_time_end: '',
    delivery_date_start: null,
    delivery_date_end: null,
    delivery_time_start: '',
    delivery_time_end: '',
    pickup_fixed_date: false,
    delivery_fixed_date: false,
    driver_id: '',
    driver_price: 0,
    is_paid: false,
    notes: '',
    first_vehicle_notes: '',
    second_vehicle_notes: ''
  });
  const createServiceFormRef = useRef(createServiceForm);
  useEffect(() => {
    createServiceFormRef.current = createServiceForm;
  }, [createServiceForm]);

  const [showClientCreateDialog, setShowClientCreateDialog] = useState(false);
  const [showDriverCreateDialog, setShowDriverCreateDialog] = useState(false);
  const [clientCreateForm, setClientCreateForm] = useState({
    name: '',
    email: '',
    company_name: '',
    cif_dni: '',
    phone: '',
    country: 'ES',
    address: ''
  });
  const [driverCreateForm, setDriverCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    is_fleet_driver: false,
    fleet_driver_id: ''
  });
  const [driverDocuments, setDriverDocuments] = useState({
    license_front: null,
    license_back: null,
    dni_front: null,
    dni_back: null,
    autonomo_certificate: null,
    hacienda_certificate: null,
    hacienda_expiry: '',
    ss_certificate: null,
    ss_expiry: ''
  });
  const [fleetDrivers, setFleetDrivers] = useState([]);
  const [logoUploadFile, setLogoUploadFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showDriverDocumentsDialog, setShowDriverDocumentsDialog] = useState(false);
  const [showDriverProfileDialog, setShowDriverProfileDialog] = useState(false);
  const [driverProfileData, setDriverProfileData] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showPeriodTripsDialog, setShowPeriodTripsDialog] = useState(false);
  const [periodTrips, setPeriodTrips] = useState([]);
  const [loadingPeriodTrips, setLoadingPeriodTrips] = useState(false);

  // Driver pricing selection
  const [showDriverPricingDialog, setShowDriverPricingDialog] = useState(false);
  const [driverPricingTemplates, setDriverPricingTemplates] = useState([]);
  const [driverPricingRates, setDriverPricingRates] = useState({}); // Store rates by template_id
  const [calculatedPrices, setCalculatedPrices] = useState({});
  const [selectedDriverForPricing, setSelectedDriverForPricing] = useState(null);
  const [customDriverPrice, setCustomDriverPrice] = useState('');
  const [showCustomPriceInput, setShowCustomPriceInput] = useState(false);
  
  // Driver pricing rates view
  const [showDriverRatesDialog, setShowDriverRatesDialog] = useState(false);
  const [selectedTemplateRates, setSelectedTemplateRates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Edit rate
  const [showEditRateDialog, setShowEditRateDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  
  // Create new template
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
  const [showUploadInvoiceDialog, setShowUploadInvoiceDialog] = useState(false);
  const [showPeriodDetailsDialog, setShowPeriodDetailsDialog] = useState(false);
  const [uploadInvoiceFile, setUploadInvoiceFile] = useState(null);
  const [uploadInvoiceNumber, setUploadInvoiceNumber] = useState('');
  const [uploadInvoiceDate, setUploadInvoiceDate] = useState('');
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const [orderUpdateForm, setOrderUpdateForm] = useState({
    status: '',
    payment_status: '',
    driver_id: '',
    client_id: '',
    notes: '',
    car_plate: '',
    car_brand: '',
    car_model: '',
    pickup_address: '',
    delivery_address: '',
    pickup_contact_name: '',
    pickup_contact_phone: '',
    delivery_contact_name: '',
    delivery_contact_phone: '',
    point_c_address: '',
    point_c_lat: null,
    point_c_lng: null,
    point_c_contact_name: '',
    point_c_contact_phone: '',
    second_car_type: 'Turismo',
    second_car_brand: '',
    second_car_model: '',
    second_car_plate: '',
    second_fuel_type: 'gasoline',
    first_vehicle_notes: '',
    second_vehicle_notes: '',
    selected_additional_services: [],
    distance_km: 0,
    service_type: '',
    driver_pricing_template_id: null,
    driver_price: 0,
    is_driver_price_custom: false,
    driver_extras: []  // Driver extras for liquidation
  });
  const [orderUpdateAttachmentFiles, setOrderUpdateAttachmentFiles] = useState([]);

  // Driver search state
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [showDriverOptions, setShowDriverOptions] = useState(false);

  // Filters state
  const [filters, setFilters] = useState({
    searchClient: '',
    searchPlate: '',
    orderStatus: 'all',
    paymentStatus: 'all',
    driverFilter: 'all',
    city: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [listDriverFilterOpen, setListDriverFilterOpen] = useState(false);

  useEffect(() => {
    loadGoogleMaps().catch((err) => {
      console.warn('Google Maps failed to load:', err?.message || err);
    });
  }, []);

  useEffect(() => {
    if (showOrderUpdateDialog) {
      setOrderUpdateAttachmentFiles([]);
    }
  }, [showOrderUpdateDialog]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    // Load admin permissions from current admin endpoint
    const loadAdminPermissions = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          // Clone response to prevent "body already used" errors
          const responseClone = response.clone();
          let adminData;
          try {
            adminData = await response.json();
          } catch (e) {
            adminData = await responseClone.json();
          }
          const resolvedType = adminData.admin_type || 'super_admin';
          setAdminType(resolvedType);
          setAdminSessionEmail(adminData.email || '');
          setCanDelete(adminData.can_delete !== false);
          setCanSeeClientPrices(adminData.can_see_client_prices !== false);
          setCanSeeUnassigned(adminData.can_see_unassigned !== false);
          setAssignedDriverIds(adminData.assigned_drivers || []);
          if (resolvedType !== 'super_admin') {
            setActiveTab((tab) => (tab === 'sub-admins' ? 'overview' : tab));
          }
        }
      } catch (error) {
        console.error('Error loading admin permissions:', error);
        setAdminType('sub_admin');
      }
    };
    
    loadAdminPermissions();
    loadData();
    loadIncidents();
  }, [navigate]);


  // Client Billing Functions
  const loadClientBillingPeriods = async () => {
    try {
      setLoadingBillingPeriods(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/clients/billing-periods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const responseClone = response.clone();
        let data;
        try {
          data = await response.json();
        } catch (e) {
          data = await responseClone.json();
        }
        setClientBillingPeriods(data.billing_periods || []);
      }
    } catch (error) {
      console.error('Error loading client billing periods:', error);
      toast.error(t('admin_toast_error_load_periods'));
    } finally {
      setLoadingBillingPeriods(false);
    }
  };

  const generateClientBillingPeriods = async () => {
    try {
      setGeneratingPeriods(true);
      const token = localStorage.getItem('admin_token');
      
      // Generate periods for the last 3 months
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/clients/billing-periods/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || t('admin_toast_periods_generated'));
        loadClientBillingPeriods();
      } else {
        throw new Error('Failed to generate periods');
      }
    } catch (error) {
      console.error('Error generating billing periods:', error);
      toast.error(t('admin_toast_error_generate_periods'));
    } finally {
      setGeneratingPeriods(false);
    }
  };

  const createInvoiceForPeriod = async (periodId) => {
    try {
      setCreatingInvoice(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/clients/billing-periods/${periodId}/create-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t('admin_toast_invoice_created', { number: data.invoice_number }));
        loadClientBillingPeriods();
      } else {
        throw new Error(data.detail || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.message || t('admin_toast_error_create_invoice'));
    } finally {
      setCreatingInvoice(false);
    }
  };

  const markServiceAsPaid = async (serviceId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/services/${serviceId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('admin_toast_service_marked_paid'));
        loadData(); // Reload services
      } else {
        throw new Error('Failed to mark service as paid');
      }
    } catch (error) {
      console.error('Error marking service as paid:', error);
      toast.error(t('admin_toast_error_mark_paid'));
    }
  };

  const markInvoiceAsPaid = async (periodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/client-billing-periods/${periodId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('admin_toast_invoice_marked_paid'));
        loadClientBillingPeriods(); // Reload billing periods
      } else {
        throw new Error('Failed to mark invoice as paid');
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error(t('admin_toast_error_invoice_paid'));
    }
  };

  // Load billing data when switching to specific tabs
  useEffect(() => {
    const loadData = async () => {
      if (activeTab === 'driver-invoices' && billingPeriods.length === 0 && !loadingBilling) {
        await loadBillingData();
      }
      if (activeTab === 'client-invoices' && !clientCurrentPeriodData && !loadingClientCurrentPeriod) {
        await loadBillingData();
      }
    };
    loadData();
  }, [activeTab]); // Only depend on activeTab to avoid loops

  // Auto-refresh orders every 30 seconds when on orders tab (for collaborators to see admin changes)
  useEffect(() => {
    if (activeTab !== 'orders') return;

    const interval = setInterval(refreshOrdersList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [activeTab, refreshOrdersList]);

  // When the admin opens the Approvals tab, reconcile orders that may have
  // been completed by the mobile app (driver app writes photos / expenses
  // straight to MongoDB without triggering the web's completion hook).
  useEffect(() => {
    if (activeTab !== 'approvals') return;
    reconcileApprovals({ silent: true });
    const interval = setInterval(() => {
      reconcileApprovals({ silent: true });
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Close driver dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDriverOptions && !e.target.closest('.relative')) {
        setShowDriverOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDriverOptions]);

  // Load vehicle photos for admin view
  const loadAdminVehiclePhotos = async (orderId) => {
    setLoadingAdminPhotos(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/vehicle-photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminVehiclePhotos(data);
      } else {
        // No error toast - the dialog will show "No hay fotos disponibles"
        setAdminVehiclePhotos(null);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error(t('admin_toast_error_load_photos'));
      setAdminVehiclePhotos(null);
    } finally {
      setLoadingAdminPhotos(false);
    }
  };

  // Load order expenses for admin view
  const loadAdminOrderExpenses = async (orderId) => {
    setLoadingAdminExpenses(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/expenses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminOrderExpenses(data);
      } else {
        // No error toast - the dialog will show "No hay gastos reportados"
        setAdminOrderExpenses(null);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error(t('admin_toast_error_load_expenses'));
      setAdminOrderExpenses(null);
    } finally {
      setLoadingAdminExpenses(false);
    }
  };

  /**
   * Re-run the server-side completion check for orders that may have been
   * uploaded by the mobile driver app (which writes photos/expenses straight
   * to MongoDB without triggering the web's completion hook).
   */
  const reconcileApprovals = async ({ silent = false } = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) return null;
    setReconcilingApprovals(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/orders/reconcile-approvals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (!silent) toast.error(t('admin_approvals_reconcile_failed', { defaultValue: 'No se pudo recargar la lista de aprobaciones.' }));
        return null;
      }
      const data = await res.json();
      setLastApprovalReconcile(new Date());
      if (!silent) {
        const count = Number(data?.pending_approval ?? 0);
        if (count > 0) {
          toast.success(t('admin_approvals_reconcile_found', { count, defaultValue: '{{count}} servicio(s) pendientes de aprobación.' }));
        } else {
          toast.success(t('admin_approvals_reconcile_clean', { defaultValue: 'Lista actualizada. Nada nuevo que aprobar.' }));
        }
      }
      // Refresh order data so the approvals tab reflects new statuses.
      await loadData();
      return data;
    } catch (err) {
      if (!silent) toast.error(t('admin_approvals_reconcile_failed', { defaultValue: 'No se pudo recargar la lista de aprobaciones.' }));
      return null;
    } finally {
      setReconcilingApprovals(false);
    }
  };

  const loadData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchWithTimeout = (url, options, timeout = 15000) => {
      return Promise.race([
        fetch(url, options),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), timeout)
        )
      ]);
    };

    const safeJson = async (response) => {
      if (!response || !response.ok) return null;
      const clone = response.clone();
      try {
        return await response.json();
      } catch (e) {
        return await clone.json();
      }
    };

    const catchFailed = (p) =>
      p.catch((e) => {
        console.warn('API call failed:', e);
        return { ok: false, error: e };
      });

    const ordersPromise = fetchAllOrdersPaged({ headers, pageSize: 400 }).catch((e) => {
      console.warn('Orders load failed:', e);
      return null;
    });

    // Phase 1: data needed for overview, sidebar counts, and orders/calendar — end full-page loading after this.
    try {
      const [statsRes, driversRes, expiringDocsRes] = await Promise.all([
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/stats`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/drivers`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/admin/documents/expiring-soon`, { headers }))
      ]);

      if (statsRes.ok) {
        const statsData = await safeJson(statsRes);
        if (statsData) setStats(statsData);
      }
      const ordersData = await ordersPromise;
      if (ordersData != null) setOrders(ordersData);
      if (driversRes.ok) {
        const driversData = await safeJson(driversRes);
        if (driversData) setDrivers(driversData);
      }
      if (expiringDocsRes.ok) {
        const expiringData = await safeJson(expiringDocsRes);
        if (expiringData) setExpiringDocuments(expiringData.expiring_documents || []);
      }
    } catch (error) {
      console.error('Error loading admin core data:', error);
      toast.error(t('admin_toast_failed_load_data'));
    } finally {
      setLoading(false);
    }

    // Phase 2: pricing, assignments, documents queue, fleet, driver templates (non-blocking for initial UI).
    try {
      const [
        pricingRes,
        servicesRes,
        customPricingRes,
        usersRes,
        templatesRes,
        assignmentsRes,
        docsRes,
        fleetRes,
        driverPricingRes
      ] = await Promise.all([
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/pricing`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/additional-services`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/custom-pricing`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/users`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/pricing-templates`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/client-pricing-assignments`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/admin/documents/pending`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/admin/fleet-drivers`, { headers })),
        catchFailed(fetchWithTimeout(`${BACKEND_URL}/api/driver-pricing-templates`, { headers }))
      ]);

      if (pricingRes.ok) {
        const pricingData = await safeJson(pricingRes);
        if (pricingData) setPricingRates(pricingData);
      }
      if (servicesRes.ok) {
        const servicesData = await safeJson(servicesRes);
        if (servicesData) setAdditionalServices(servicesData);
      }
      if (customPricingRes.ok) {
        const customPricingData = await safeJson(customPricingRes);
        if (customPricingData) setCustomPricing(customPricingData);
      }
      if (usersRes.ok) {
        const usersData = await safeJson(usersRes);
        if (usersData) setClients(usersData.filter(u => u.role === 'client'));
      }
      if (templatesRes.ok) {
        const templatesData = await safeJson(templatesRes);
        if (templatesData) setPricingTemplates(templatesData);
      }
      if (assignmentsRes.ok) {
        const assignmentsData = await safeJson(assignmentsRes);
        if (assignmentsData) setClientAssignments(assignmentsData);
      }
      if (docsRes.ok) {
        const docsData = await safeJson(docsRes);
        if (docsData) setPendingDocuments(docsData.documents || []);
      }
      if (fleetRes.ok) {
        const fleetData = await safeJson(fleetRes);
        if (fleetData) setFleetDrivers(fleetData.fleet_drivers || []);
      }

      if (driverPricingRes.ok) {
        const driverPricingData = await safeJson(driverPricingRes);
        if (driverPricingData) {
          setDriverPricingTemplates(driverPricingData);
          const rateResults = await Promise.all(
            driverPricingData.map(async (template) => {
              try {
                const ratesRes = await fetchWithTimeout(
                  `${BACKEND_URL}/api/driver-pricing-templates/${template.id}/rates`,
                  { headers }
                );
                if (!ratesRes.ok) return [template.id, null];
                const rates = await safeJson(ratesRes);
                return [template.id, rates];
              } catch (e) {
                console.warn(`Failed to load rates for template ${template.id}:`, e);
                return [template.id, null];
              }
            })
          );
          const ratesData = {};
          for (const [id, rates] of rateResults) {
            if (rates) ratesData[id] = rates;
          }
          setDriverPricingRates(ratesData);
        }
      }

      try {
        const extraCategoriesRes = await fetchWithTimeout(`${BACKEND_URL}/api/driver-extra-categories`, { headers });
        if (extraCategoriesRes.ok) {
          const extraCategoriesData = await safeJson(extraCategoriesRes);
          if (extraCategoriesData) setDriverExtraCategories(extraCategoriesData || []);
        }
      } catch (e) {
        console.warn('Failed to load driver extra categories:', e);
      }
    } catch (error) {
      console.warn('Secondary admin data load failed:', error);
    }
  };

  // Safe JSON parser helper for billing data
  const safeJsonParse = async (response) => {
    if (!response || !response.ok) return null;
    const clone = response.clone();
    try {
      return await response.json();
    } catch (e) {
      return await clone.json();
    }
  };

  // Load billing periods and expense reports
  const loadBillingData = async () => {
    try {
      setLoadingBilling(true);
      setLoadingCurrentPeriod(true);
      setLoadingClientCurrentPeriod(true);
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load basic billing data first (fast endpoints)
      const [periodsRes, expensesRes, pendingRes, clientCurrentPeriodRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/billing-periods`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/expense-reports`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/driver-liquidations/pending-services`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/client-liquidations/current-period`, { headers })
      ]);

      if (periodsRes.ok) {
        const periodsData = await safeJsonParse(periodsRes);
        if (periodsData) {
          setBillingPeriods(periodsData || []);
          if (!activeLiquidationPeriod) {
            setActiveLiquidationPeriod('current');
          }
        }
      }

      if (expensesRes.ok) {
        const expensesData = await safeJsonParse(expensesRes);
        if (expensesData) setExpenseReports(expensesData || []);
      }
      
      if (pendingRes.ok) {
        const pendingData = await safeJsonParse(pendingRes);
        if (pendingData) setPendingApprovalCount(pendingData.count || 0);
      }
      
      if (clientCurrentPeriodRes.ok) {
        const clientCurrentData = await safeJsonParse(clientCurrentPeriodRes);
        if (clientCurrentData) setClientCurrentPeriodData(clientCurrentData);
      }
      
      setLoadingBilling(false);
      setLoadingClientCurrentPeriod(false);
      
      // Load driver current period and pending invoicing period in parallel
      try {
        const [currentPeriodRes, pendingInvoicingRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/driver-liquidations/current-period`, { headers }),
          fetch(`${BACKEND_URL}/api/admin/driver-liquidations/pending-invoicing`, { headers })
        ]);
        
        if (currentPeriodRes.ok) {
          const currentData = await safeJsonParse(currentPeriodRes);
          if (currentData) setCurrentPeriodData(currentData);
        }
        
        if (pendingInvoicingRes.ok) {
          const pendingData = await safeJsonParse(pendingInvoicingRes);
          if (pendingData) {
            setPendingInvoicingData(pendingData);
            // If there's a pending period with services, default to showing it
            const pendingHasWork = pendingData.has_pending_period && (
              (pendingData.total_services || 0) > 0
              || (pendingData.unapproved_service_count || 0) > 0
              || (pendingData.billing_preview_service_count || 0) > 0
            );
            if (pendingHasWork && !activeLiquidationPeriod) {
              setActiveLiquidationPeriod('pending');
            } else if (!activeLiquidationPeriod) {
              setActiveLiquidationPeriod('current');
            }
          }
        }
      } catch (e) {
        console.error('Error loading driver periods:', e);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error(t('admin_toast_error_load_billing'));
    } finally {
      setLoadingBilling(false);
      setLoadingCurrentPeriod(false);
      setLoadingClientCurrentPeriod(false);
    }
  };
  
  // Load incidents
  const loadIncidents = async () => {
    try {
      setLoadingIncidents(true);
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await fetch(`${BACKEND_URL}/api/admin/incidents`, { headers });
      if (response.ok) {
        const responseClone = response.clone();
        let data;
        try {
          data = await response.json();
        } catch (e) {
          data = await responseClone.json();
        }
        if (data) {
          setIncidents(data.incidents || []);
          // Count only 'reported' or 'in_progress' incidents
          const activeCount = (data.incidents || []).filter(i => i.status === 'reported' || i.status === 'in_progress').length;
          setIncidentCount(activeCount);
        }
      }
    } catch (error) {
      console.error('Error loading incidents:', error);
    } finally {
      setLoadingIncidents(false);
    }
  };
  
  // Update incident status
  const updateIncidentStatus = async (incidentId, status, adminNotes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/incidents/${incidentId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, admin_notes: adminNotes })
      });
      
      if (response.ok) {
        toast.success(t('admin_toast_incident_updated'));
        loadIncidents();
        setShowIncidentDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_toast_error_update_incident'));
      }
    } catch (error) {
      console.error('Error updating incident:', error);
      toast.error(t('admin_toast_error_update_incident'));
    }
  };
  
  // Send incident report to client
  const sendIncidentToClient = async (incident) => {
    try {
      setSendingIncidentToClient(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/incidents/${incident.id}/send-to-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success(t('admin_toast_incident_report_sent_client'));
        // Mark as sent in the incident
        await updateIncidentStatus(incident.id, incident.status, (incident.admin_notes || '') + '\n' + t('admin_incident_sent_to_client_log', { datetime: new Date().toLocaleString(dateLocale) }));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_toast_error_send_incident_report_client'));
      }
    } catch (error) {
      console.error('Error sending incident to client:', error);
      toast.error(t('admin_toast_error_send_incident_report_client'));
    } finally {
      setSendingIncidentToClient(false);
    }
  };
  
  // Finalize service from incident (admin only) - with expenses
  const finalizeServiceFromIncident = async (incident) => {
    try {
      setFinalizingServiceFromIncident(true);
      const token = localStorage.getItem('admin_token');
      
      // Calculate total expenses
      const totalExpenses = (parseFloat(finalizeIncidentForm.toll_expenses) || 0) + 
                           (parseFloat(finalizeIncidentForm.fuel_expenses) || 0) + 
                           (parseFloat(finalizeIncidentForm.other_expenses) || 0);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${incident.order_id}/finalize-from-incident`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          incident_id: incident.id,
          final_status: finalizeIncidentForm.final_status, // 'delivered' or 'cancelled_at_pickup'
          reason: incident.cancellation_reason || incident.description,
          admin_notes: finalizeIncidentForm.admin_notes,
          expenses: {
            toll: parseFloat(finalizeIncidentForm.toll_expenses) || 0,
            fuel: parseFloat(finalizeIncidentForm.fuel_expenses) || 0,
            other: parseFloat(finalizeIncidentForm.other_expenses) || 0,
            total: totalExpenses
          },
          photos: finalizeIncidentForm.photos
        })
      });
      
      if (response.ok) {
        toast.success(
          finalizeIncidentForm.final_status === 'delivered'
            ? t('admin_toast_incident_finalize_delivered_ok')
            : t('admin_toast_incident_finalize_cancel_ok')
        );
        // Reset form
        setFinalizeIncidentForm({
          final_status: 'delivered',
          admin_notes: '',
          expenses: 0,
          toll_expenses: 0,
          fuel_expenses: 0,
          other_expenses: 0,
          photos: []
        });
        setShowFinalizeFromIncidentForm(false);
        setShowIncidentDialog(false);
        loadIncidents();
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_toast_error_finalize_service'));
      }
    } catch (error) {
      console.error('Error finalizing service:', error);
      toast.error(t('admin_toast_error_finalize_service'));
    } finally {
      setFinalizingServiceFromIncident(false);
    }
  };
  
  // Helper function to group billing periods by date range
  const groupPeriodsByDate = (periods) => {
    const groups = {};
    const now = new Date();
    
    periods.forEach(period => {
      // Create a key from period type and date range
      const periodStart = new Date(period.period_start);
      const periodEnd = new Date(period.period_end);
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const periodLabel = period.period_type === 'quincena_1' ? 'Q1' : 'Q2';
      const monthName = monthNames[periodEnd.getMonth()];
      const year = periodEnd.getFullYear();
      const key = `${periodLabel}_${year}_${periodEnd.getMonth()}`;
      const label = `${periodLabel} ${monthName} ${year}`;
      
      // Check if this is the current period (we're within the payment window)
      const paymentStart = period.payment_start ? new Date(period.payment_start) : null;
      const paymentEnd = period.payment_end ? new Date(period.payment_end) : null;
      const isCurrentPeriod = paymentStart && paymentEnd && now >= paymentStart && now <= paymentEnd;
      
      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          periodType: period.period_type,
          periodStart: period.period_start,
          periodEnd: period.period_end,
          paymentStart: period.payment_start,
          paymentEnd: period.payment_end,
          isCurrentPeriod,
          drivers: []
        };
      }
      
      groups[key].drivers.push(period);
      // Update isCurrentPeriod if any driver in the group has it
      if (isCurrentPeriod) {
        groups[key].isCurrentPeriod = true;
      }
    });
    
    // Convert to array and sort by date (most recent first)
    return Object.values(groups).sort((a, b) => {
      const dateA = new Date(a.periodEnd);
      const dateB = new Date(b.periodEnd);
      return dateB - dateA;
    });
  };
  
  // Get grouped periods
  const groupedPeriods = groupPeriodsByDate(billingPeriods);
  
  // Get drivers for active period, filtered by search term
  const getFilteredDriversForPeriod = () => {
    // Handle "current" period specially
    if (activeLiquidationPeriod === 'current') {
      if (!currentPeriodData || !currentPeriodData.drivers) return [];
      
      let drivers = currentPeriodData.drivers;
      
      if (liquidationSearchTerm.trim()) {
        const searchLower = liquidationSearchTerm.toLowerCase();
        drivers = drivers.filter(d => 
          d.driver_name?.toLowerCase().includes(searchLower) ||
          d.driver_id?.toLowerCase().includes(searchLower)
        );
      }
      
      return drivers;
    }
    
    // Handle "pending" invoicing period
    if (activeLiquidationPeriod === 'pending') {
      if (!pendingInvoicingData || !pendingInvoicingData.drivers) return [];
      
      let drivers = pendingInvoicingData.drivers;
      
      if (liquidationSearchTerm.trim()) {
        const searchLower = liquidationSearchTerm.toLowerCase();
        drivers = drivers.filter(d => 
          d.driver_name?.toLowerCase().includes(searchLower) ||
          d.driver_id?.toLowerCase().includes(searchLower)
        );
      }
      
      return drivers;
    }
    
    // Handle historical periods
    const activeGroup = groupedPeriods.find(g => g.key === activeLiquidationPeriod);
    if (!activeGroup) return [];
    
    let drivers = activeGroup.drivers;
    
    if (liquidationSearchTerm.trim()) {
      const searchLower = liquidationSearchTerm.toLowerCase();
      drivers = drivers.filter(d => 
        d.driver_name?.toLowerCase().includes(searchLower) ||
        d.driver_id?.toLowerCase().includes(searchLower)
      );
    }
    
    return drivers;
  };
  
  // Get current period info for display
  const getCurrentPeriodInfo = () => {
    if (!currentPeriodData) return null;
    
    const periodEnd = new Date(currentPeriodData.period_end);
    const periodLabel = currentPeriodData.period_type === 'quincena_1' ? 'Q1' : 'Q2';
    const monthName = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1).toLocaleDateString(dateLocale, { month: 'short' });
    const year = periodEnd.getFullYear();
    
    return {
      label: `${periodLabel} ${monthName} ${year}`,
      ...currentPeriodData
    };
  };
  
  // Get pending period info for display
  const getPendingPeriodInfo = () => {
    if (!pendingInvoicingData || !pendingInvoicingData.has_pending_period) return null;
    
    const periodEnd = new Date(pendingInvoicingData.period_end);
    const periodLabel = pendingInvoicingData.period_type === 'quincena_1' ? 'Q1' : 'Q2';
    const monthName = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1).toLocaleDateString(dateLocale, { month: 'short' });
    const year = periodEnd.getFullYear();
    
    return {
      label: `${periodLabel} ${monthName} ${year}`,
      ...pendingInvoicingData
    };
  };
  
  // Auto-populate liquidation form with pending period data
  const handleGeneratePendingLiquidations = async () => {
    if (!pendingInvoicingData || !pendingInvoicingData.has_pending_period) {
      toast.error(t('admin_toast_no_pending_invoice_period'));
      return;
    }
    
    // Set the liquidation form with pending period info
    const formData = {
      period_type: pendingInvoicingData.period_type,
      year: pendingInvoicingData.generate_year,
      month: pendingInvoicingData.generate_month
    };
    
    setLiquidationForm(formData);
    
    // Generate liquidations directly
    try {
      setGeneratingLiquidations(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/driver-liquidations/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        if (data.liquidations_created > 0) {
          let message = t('admin_toast_liquidations_created', { count: data.liquidations_created });
          if (data.carried_over_services > 0) {
            message += t('admin_toast_liquidations_created_carried_suffix', { carried: data.carried_over_services });
          }
          toast.success(message);
          // Reload billing data
          loadBillingData();
        } else {
          toast.info(data.message || t('admin_toast_no_services_pending_liquidation'));
        }
      } else {
        toast.error(data.detail || data.message || t('admin_toast_error_generate_liquidations'));
      }
    } catch (error) {
      console.error('Error generating liquidations:', error);
      toast.error(t('admin_toast_error_generate_liquidations'));
    } finally {
      setGeneratingLiquidations(false);
    }
  };

  const generateDriverLiquidations = async () => {
    try {
      setGeneratingLiquidations(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/driver-liquidations/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(liquidationForm)
      });

      const data = await response.json();

      if (response.ok) {
        if (data.liquidations_created > 0) {
          let message = t('admin_toast_liquidations_created', { count: data.liquidations_created });
          if (data.carried_over_services > 0) {
            message += t('admin_toast_liquidations_created_carried_suffix', { carried: data.carried_over_services });
          }
          toast.success(message);
        } else {
          toast.info(data.message || t('admin_toast_no_approved_services_period'));
        }
        setShowGenerateLiquidationDialog(false);
        loadBillingData();
      } else {
        toast.error(data.detail || t('admin_toast_error_generate_liquidations'));
      }
    } catch (error) {
      console.error('Error generating liquidations:', error);
      toast.error(t('admin_toast_error_generate_liquidations'));
    } finally {
      setGeneratingLiquidations(false);
    }
  };

  // Open driver billing edit dialog
  const openDriverBillingEdit = (driver) => {
    setEditingDriverBilling(driver);
    setDriverBillingForm({
      full_name: driver.full_name || driver.name || '',
      dni_nie: driver.dni_nie || '',
      fiscal_address: driver.fiscal_address || '',
      bank_name: driver.bank_name || '',
      bank_account: driver.bank_account || '',
      irpf_retention: driver.irpf_retention || 0
    });
    setShowDriverBillingDialog(true);
  };

  // Fleet Management Functions
  const loadFleetData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/fleet-management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFleetData(data);
      }
    } catch (error) {
      console.error('Error loading fleet data:', error);
    }
  };

  const assignDriversToFleet = async () => {
    if (!selectedFleetOwner || selectedDriversForFleet.length === 0) {
      toast.error(t('admin_toast_select_fleet_owner_and_driver'));
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/fleet-management/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fleet_owner_id: selectedFleetOwner,
          driver_ids: selectedDriversForFleet
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setSelectedDriversForFleet([]);
        loadFleetData();
      } else {
        toast.error(data.detail || t('admin_toast_error_assign_drivers'));
      }
    } catch (error) {
      console.error('Error assigning drivers:', error);
      toast.error(t('admin_toast_error_assign_drivers'));
    }
  };

  const removeDriverFromFleet = async (driverId, driverName) => {
    if (!window.confirm(t('admin_confirm_remove_driver_from_fleet', { name: driverName }))) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/fleet-management/remove`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ driver_id: driverId })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        loadFleetData();
      } else {
        toast.error(data.detail || t('admin_toast_error_remove_driver_assignment'));
      }
    } catch (error) {
      console.error('Error removing driver:', error);
      toast.error(t('admin_toast_error_remove_driver_assignment'));
    }
  };

  const createNewFleet = async (ownerId, ownerName) => {
    if (!window.confirm(t('admin_confirm_promote_fleet_owner', { name: ownerName }))) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/fleet-management/create-fleet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ owner_id: ownerId })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        loadFleetData();
      } else {
        toast.error(data.detail || t('admin_toast_error_create_fleet'));
      }
    } catch (error) {
      console.error('Error creating fleet:', error);
      toast.error(t('admin_toast_error_create_fleet'));
    }
  };

  // Apply penalty to driver (after confirming in modal)
  const applyDriverPenalty = async () => {
    if (!penaltyData.apply_penalty || !penaltyData.driver_id) {
      // No penalty to apply, just proceed with the order update
      await executeOrderUpdate(pendingOrderUpdate);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/driver-penalties`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driver_id: penaltyData.driver_id,
          order_id: penaltyData.order_id,
          amount: penaltyData.amount,
          reason: penaltyData.reason,
          description: penaltyData.order_info ? 
            `${penaltyData.order_info.car_plate} - ${penaltyData.order_info.route}` : '',
          hours_until_pickup: penaltyData.hours_until_pickup
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(t('admin_toast_penalty_applied', { amount: penaltyData.amount, name: penaltyData.driver_name }));
      } else {
        toast.error(data.detail || t('admin_toast_error_apply_penalty'));
      }
    } catch (error) {
      console.error('Error applying penalty:', error);
      toast.error(t('admin_toast_error_apply_penalty'));
    }

    // Now proceed with the order update
    await executeOrderUpdate(pendingOrderUpdate);
  };

  // Execute the actual order update (called after penalty decision)
  const executeOrderUpdate = async (dataToSend) => {
    if (!dataToSend || !selectedOrder) return;
    const orderId = selectedOrder.id;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        let errorMessage = t('error_updating_order');
        try {
          const errorData = await response.json();
          errorMessage = errorData?.detail || errorData?.message || errorMessage;
          if (
            typeof errorMessage === 'string' &&
            errorMessage.toLowerCase().includes('is not defined')
          ) {
            errorMessage = t('error_updating_order');
          }
        } catch (_e) {
          // Ignore JSON parsing failures and keep the default message
        }
        throw new Error(errorMessage);
      }

      if (orderUpdateAttachmentFiles.length > 0) {
        try {
          await uploadOrderServiceDocuments(orderId, orderUpdateAttachmentFiles, BACKEND_URL, token, true);
        } catch (uploadErr) {
          console.error(uploadErr);
          toast.warning(t('service_docs_upload_warn'), {
            description: uploadErr?.message || t('service_docs_upload_warn_generic'),
          });
        }
      }

      if (SHOW_ORDER_UPDATE_SUCCESS_TOAST) {
        toast.success(t('order_updated'));
      }
      setShowOrderUpdateDialog(false);
      setShowPenaltyDialog(false);
      setSelectedOrder(null);
      setPendingOrderUpdate(null);
      setDriverSearchTerm('');
      setDriverExtras([]);
      setOrderUpdateAttachmentFiles([]);
      setNewDriverExtra({ category_id: '', category_name: '', unit_price: 0, quantity: 1, total: 0, description: '' });
      setOrderUpdateForm({
        status: '',
        payment_status: '',
        driver_id: '',
        car_plate: '',
        car_brand: '',
        car_model: '',
        pickup_address: '',
        delivery_address: '',
        pickup_contact_name: '',
        pickup_contact_phone: '',
        delivery_contact_name: '',
        delivery_contact_phone: '',
        point_c_address: '',
        point_c_lat: null,
        point_c_lng: null,
        point_c_contact_name: '',
        point_c_contact_phone: '',
        second_car_type: 'Turismo',
        second_car_brand: '',
        second_car_model: '',
        second_car_plate: '',
        second_fuel_type: 'gasoline',
        first_vehicle_notes: '',
        second_vehicle_notes: '',
        selected_additional_services: [],
        pickup_date_from: '',
        pickup_date_to: '',
        pickup_time_start: '',
        pickup_time_end: '',
        delivery_date_from: '',
        delivery_date_to: '',
        delivery_time_start: '',
        delivery_time_end: '',
        service_date: '',
        pickup_fixed_date: false,
        delivery_fixed_date: false,
        driver_price: null,
        driver_pricing_template_id: null,
        is_driver_price_custom: false
      });
      // Refresh orders
      await refreshOrdersList();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error?.message || t('error_updating_order'));
      // If backend updated DB but failed while building response, this keeps
      // kanban in sync without manual page reload.
      await refreshOrdersList();
    }
  };

  // Save driver billing info
  const saveDriverBillingInfo = async () => {
    if (!editingDriverBilling) return;
    
    if (!editingDriverBilling.id) {
      toast.error(t('admin_toast_driver_id_not_found'));
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/drivers/${editingDriverBilling.id}/billing-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(driverBillingForm)
      });

      if (response.ok) {
        toast.success(t('admin_toast_billing_data_updated'));
        setShowDriverBillingDialog(false);
        setEditingDriverBilling(null);
        // Reload billing data to reflect changes
        loadBillingData();
      } else {
        const data = await response.json();
        console.error('Server error:', data);
        toast.error(
          data.detail ||
            t('admin_toast_server_error_generic', {
              status: response.status,
              message: data.message || t('admin_toast_billing_update_failed_fallback'),
            })
        );
      }
    } catch (error) {
      console.error('Error updating driver billing info:', error);
      toast.error(t('admin_toast_connection_error_with_message', { message: error.message }));
    }
  };

  const generateClientLiquidations = async () => {
    try {
      setGeneratingClientLiquidations(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/client-liquidations/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientLiquidationForm)
      });

      const data = await response.json();

      if (response.ok) {
        if (data.invoices_created > 0) {
          toast.success(t('admin_toast_client_invoices_created', { count: data.invoices_created }));
        } else {
          toast.info(data.message || t('admin_toast_no_services_pending_invoice_period'));
        }
        setShowGenerateClientLiquidationDialog(false);
        loadBillingData();
        loadClientBillingPeriods();
      } else {
        toast.error(data.detail || t('admin_toast_error_generate_client_invoices'));
      }
    } catch (error) {
      console.error('Error generating client liquidations:', error);
      toast.error(t('admin_toast_error_generate_client_invoices'));
    } finally {
      setGeneratingClientLiquidations(false);
    }
  };

  const filenameFromContentDisposition = (response, fallback) => {
    const cd = response.headers.get('Content-Disposition');
    if (!cd) return fallback;
    const utf = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
    if (utf) {
      try {
        return decodeURIComponent(utf[1].trim());
      } catch (_) {
        /* ignore */
      }
    }
    const quoted = cd.match(/filename="([^"]+)"/i);
    if (quoted) return quoted[1];
    const bare = cd.match(/filename=([^;\n]+)/i);
    if (bare) return bare[1].replace(/^['"]|['"]$/g, '').trim();
    return fallback;
  };

  const downloadClientExcel = async (periodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/client-liquidations/${periodId}/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filenameFromContentDisposition(response, `factura_cliente_${periodId}.xlsx`);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('admin_toast_excel_downloaded'));
      } else {
        toast.error(t('admin_toast_error_download_excel'));
      }
    } catch (error) {
      console.error('Error downloading client excel:', error);
      toast.error(t('admin_toast_error_download_excel'));
    }
  };

  const downloadDriverLiquidationExcel = async (periodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/driver-liquidations/${periodId}/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filenameFromContentDisposition(response, `liquidacion_${periodId}.xlsx`);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('admin_toast_excel_downloaded'));
      } else {
        toast.error(t('admin_toast_error_download_excel'));
      }
    } catch (error) {
      console.error('Error downloading driver liquidation excel:', error);
      toast.error(t('admin_toast_error_download_excel'));
    }
  };

  const downloadProvisionalDriverLiquidationExcel = async (period, periodStart, periodEnd) => {
    const orderIds = period.service_ids;
    if (!orderIds || !orderIds.length) {
      toast.error(t('admin_toast_provisional_excel_no_orders'));
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error(t('admin_toast_error_download_excel'));
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/driver-liquidations/export-provisional-excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: period.driver_id,
          order_ids: orderIds,
          period_start: periodStart,
          period_end: periodEnd,
          driver_name: period.driver_name || ''
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filenameFromContentDisposition(response, `liquidacion_en_curso_${period.driver_id}.xlsx`);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('admin_toast_excel_downloaded'));
      } else {
        toast.error(t('admin_toast_error_download_excel'));
      }
    } catch (error) {
      console.error('Error downloading provisional liquidation excel:', error);
      toast.error(t('admin_toast_error_download_excel'));
    }
  };

  const loadPeriodTrips = async (period) => {
    try {
      setLoadingPeriodTrips(true);
      setSelectedPeriod(period);
      setShowPeriodTripsDialog(true);
      
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/billing-periods/${period.id}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPeriodTrips(data.orders || []);
      } else {
        toast.error(t('admin_toast_error_load_period_details'));
      }
    } catch (error) {
      console.error('Error loading period trips:', error);
      toast.error(t('admin_toast_error_load_period_trips'));
    } finally {
      setLoadingPeriodTrips(false);
    }
  };

  const generateInvoiceForPeriod = async (periodId, forceRegenerate = false) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = forceRegenerate 
        ? `${BACKEND_URL}/api/admin/billing-periods/${periodId}/generate-invoice?regenerate=true`
        : `${BACKEND_URL}/api/admin/billing-periods/${periodId}/generate-invoice`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          forceRegenerate
            ? t('admin_toast_invoice_regenerated_success', { number: data.invoice_number })
            : t('admin_toast_invoice_generated_success', { number: data.invoice_number })
        );
        loadBillingData(); // Reload to show new invoice
        
        // Open PDF in new tab
        if (data.pdf_url) {
          window.open(`${BACKEND_URL}${data.pdf_url}`, '_blank');
        }
      } else {
        // Show more specific error message
        if (data.detail && data.detail.includes('incomplete')) {
          toast.error(t('admin_toast_driver_profile_incomplete'));
        } else {
          toast.error(data.detail || t('admin_toast_error_create_invoice'));
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(t('admin_toast_error_generate_invoice'));
    }
  };

  // State for expense approval dialog
  const [showApproveExpenseDialog, setShowApproveExpenseDialog] = useState(false);
  const [selectedExpenseReport, setSelectedExpenseReport] = useState(null);
  const [approvalCharges, setApprovalCharges] = useState([]);
  const [newApprovalCharge, setNewApprovalCharge] = useState({ type: '', description: '', amount: '' });

  const openApproveExpenseDialog = (report) => {
    setSelectedExpenseReport(report);
    setApprovalCharges([]);
    setNewApprovalCharge({ type: '', description: '', amount: '' });
    setShowApproveExpenseDialog(true);
  };

  const handleAddApprovalCharge = () => {
    if (!newApprovalCharge.type || !newApprovalCharge.amount) {
      toast.error(t('admin_toast_select_charge_and_amount'));
      return;
    }
    const chargeType = CHARGE_TYPES.find(t => t.value === newApprovalCharge.type);
    setApprovalCharges([...approvalCharges, {
      type: newApprovalCharge.type,
      label: chargeType?.label || newApprovalCharge.type,
      description: newApprovalCharge.description,
      amount: parseFloat(newApprovalCharge.amount)
    }]);
    setNewApprovalCharge({ type: '', description: '', amount: '' });
  };

  const handleRemoveApprovalCharge = (index) => {
    setApprovalCharges(approvalCharges.filter((_, i) => i !== index));
  };

  const approveExpenseReport = async (reportId, status, reason = null, additionalCharges = []) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/expense-reports/${reportId}/approval`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status, 
          rejection_reason: reason,
          admin_additional_charges: additionalCharges 
        })
      });

      if (response.ok) {
        toast.success(status === 'approved' ? t('admin_toast_expense_report_approved_ok') : t('admin_toast_expense_report_rejected_ok'));
        setShowApproveExpenseDialog(false);
        setSelectedExpenseReport(null);
        setApprovalCharges([]);
        loadBillingData();
        loadData();
      }
    } catch (error) {
      console.error('Error updating expense report:', error);
      toast.error(t('admin_toast_error_update_report'));
    }
  };

  const markPeriodAsPaid = async (periodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/billing-periods/${periodId}/status?status=paid`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(t('admin_toast_period_marked_paid'));
        loadBillingData();
      }
    } catch (error) {
      console.error('Error marking period as paid:', error);
      toast.error(t('admin_toast_error_mark_period_paid'));
    }
  };

  const handleLogout = () => {
    ['admin_token', 'admin_user', 'admin_id', 'session_token', 'user', 'driver_token', 'driver_data'].forEach(
      (key) => localStorage.removeItem(key)
    );
    toast.success(t('logout_successful'));
    navigate('/admin/login');
  };

  const downloadOrderContractPdf = async (order) => {
    if (!order?.id || contractPdfDownloadLockRef.current) return;
    contractPdfDownloadLockRef.current = true;
    setDownloadingContractOrderId(order.id);
    try {
      const token = localStorage.getItem('admin_token');
      const contractUrl = `${BACKEND_URL}/api/admin/orders/${order.id}/generate-contract?token=${encodeURIComponent(token || '')}&_=${Date.now()}`;
      const response = await fetch(contractUrl, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });

      if (response.ok) {
        const renderer = response.headers.get('X-AutoGo-Contract-Renderer');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildContractPdfFilename(order.id);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('admin_toast_contract_downloaded'));
        if (renderer === 'reportlab') {
          toast.warning(t('admin_toast_contract_fallback_simple_pdf'));
        }
      } else {
        toast.error(t('admin_toast_error_generate_contract'));
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      const token = localStorage.getItem('admin_token');
      const contractUrl = `${BACKEND_URL}/api/admin/orders/${order.id}/generate-contract?token=${encodeURIComponent(token || '')}&_=${Date.now()}`;
      const link = document.createElement('a');
      link.href = contractUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.info(t('client_toast_preparing_contract'));
    } finally {
      contractPdfDownloadLockRef.current = false;
      setDownloadingContractOrderId(null);
    }
  };

  // Driver Management
  const handleSaveDriverForm = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingItem
        ? `${BACKEND_URL}/api/drivers/${editingItem.id}`
        : `${BACKEND_URL}/api/drivers`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(driverForm)
      });

      if (!response.ok) throw new Error('Failed to save driver');

      toast.success(editingItem ? t('admin_toast_driver_updated_ok') : t('admin_toast_driver_created_ok'));
      setShowDriverDialog(false);
      setEditingItem(null);
      setDriverForm({
        name: '',
        phone: '',
        email: '',
        license_number: '',
        status: 'available'
      });
      loadData();
    } catch (error) {
      console.error('Error saving driver:', error);
      toast.error(t('admin_toast_failed_save_driver'));
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm(t('admin_confirm_delete_driver'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/${driverId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete driver');

      toast.success(t('admin_toast_driver_deleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error(t('admin_toast_failed_delete_driver'));
    }
  };

  // Pricing Management
  const handleCreatePricing = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingItem
        ? `${BACKEND_URL}/api/pricing/${editingItem.id}`
        : `${BACKEND_URL}/api/pricing`;

      // Prepare data, only include per-km prices if they have values
      const data = {
        min_distance: parseFloat(pricingForm.min_distance),
        max_distance: parseFloat(pricingForm.max_distance),
        delivery_price: parseFloat(pricingForm.delivery_price),
        bc_service_price: parseFloat(pricingForm.bc_service_price),
        tradein_price: parseFloat(pricingForm.tradein_price),
        representacion_price: parseFloat(pricingForm.representacion_price)
      };

      // Add per-km prices if provided
      if (pricingForm.delivery_price_per_km) {
        data.delivery_price_per_km = parseFloat(pricingForm.delivery_price_per_km);
      }
      if (pricingForm.bc_service_price_per_km) {
        data.bc_service_price_per_km = parseFloat(pricingForm.bc_service_price_per_km);
      }
      if (pricingForm.tradein_price_per_km) {
        data.tradein_price_per_km = parseFloat(pricingForm.tradein_price_per_km);
      }

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save pricing');

      toast.success(editingItem ? t('admin_toast_pricing_updated_ok') : t('admin_toast_pricing_created_ok'));
      setShowPricingDialog(false);
      setEditingItem(null);
      setPricingForm({
        min_distance: '',
        max_distance: '',
        delivery_price: '',
        bc_service_price: '',
        tradein_price: '',
        representacion_price: '',
        delivery_price_per_km: '',
        bc_service_price_per_km: '',
        tradein_price_per_km: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error(t('admin_toast_error_save_rate'));
    }
  };

  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm('Are you sure you want to delete this pricing rate?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing/${pricingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete pricing');

      toast.success(t('admin_toast_pricing_deleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting pricing:', error);
      toast.error(t('admin_toast_failed_delete_pricing'));
    }
  };

  // Additional Services Management
  const handleSaveAdditionalService = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const url = editingItem
        ? `${BACKEND_URL}/api/additional-services/${editingItem.id}`
        : `${BACKEND_URL}/api/additional-services`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: additionalServiceForm.name,
          price: parseFloat(additionalServiceForm.price),
          unit: additionalServiceForm.unit,
          description: additionalServiceForm.description
        })
      });

      if (!response.ok) throw new Error('Failed to save additional service');

      toast.success(editingItem ? t('service_toast_updated') : t('service_toast_created_ok'));
      setShowAdditionalServiceDialog(false);
      setEditingItem(null);
      setAdditionalServiceForm({
        name: '',
        price: '',
        unit: 'per_service',
        description: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving additional service:', error);
      toast.error(t('admin_toast_error_save_service'));
    }
  };

  const handleDeleteAdditionalService = async (serviceId) => {
    if (!window.confirm(t('admin_confirm_delete_additional_service'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/additional-services/${serviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete service');

      toast.success(t('admin_toast_service_deleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(t('admin_toast_error_delete_service'));
    }
  };

  // Pricing Templates Management
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateForm)
      });

      if (!response.ok) throw new Error('Failed to create template');

      const newTemplate = await response.json();
      toast.success(t('admin_toast_template_created'));
      setShowTemplateDialog(false);
      setTemplateForm({ name: '', description: '' });
      loadData();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(t('admin_toast_error_create_template'));
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('admin_confirm_delete_template'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete template');

      toast.success(t('admin_toast_template_deleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('admin_toast_error_delete_template'));
    }
  };

  const loadTemplateRates = async (templateId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing-templates/${templateId}/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const rates = await response.json();
        setTemplateRates(rates);
      }
    } catch (error) {
      console.error('Error loading template rates:', error);
    }
  };

  const handleSaveTemplateRate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      const data = {
        template_id: selectedTemplate.id,
        min_distance: parseFloat(templateRateForm.min_distance),
        max_distance: parseFloat(templateRateForm.max_distance),
        delivery_price: parseFloat(templateRateForm.delivery_price),
        bc_service_price: parseFloat(templateRateForm.bc_service_price),
        tradein_price: parseFloat(templateRateForm.tradein_price),
        representacion_price: parseFloat(templateRateForm.representacion_price)
      };

      // Add per-km rates if provided
      if (templateRateForm.delivery_price_per_km) {
        data.delivery_price_per_km = parseFloat(templateRateForm.delivery_price_per_km);
      }
      if (templateRateForm.bc_service_price_per_km) {
        data.bc_service_price_per_km = parseFloat(templateRateForm.bc_service_price_per_km);
      }
      if (templateRateForm.tradein_price_per_km) {
        data.tradein_price_per_km = parseFloat(templateRateForm.tradein_price_per_km);
      }

      const response = await fetch(`${BACKEND_URL}/api/pricing-templates/${selectedTemplate.id}/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save rate');

      toast.success(t('admin_toast_rate_added'));
      setShowTemplateRateDialog(false);
      setTemplateRateForm({
        min_distance: '',
        max_distance: '',
        delivery_price: '',
        bc_service_price: '',
        tradein_price: '',
        representacion_price: '45',
        delivery_price_per_km: '',
        bc_service_price_per_km: '',
        tradein_price_per_km: ''
      });
      loadTemplateRates(selectedTemplate.id);
    } catch (error) {
      console.error('Error saving rate:', error);
      toast.error(t('admin_toast_error_save_rate'));
    }
  };

  const handleDeleteTemplateRate = async (rateId) => {
    if (!window.confirm(t('admin_confirm_delete_rate'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing-template-rates/${rateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete rate');

      toast.success(t('admin_toast_rate_deleted'));
      if (selectedTemplate) {
        loadTemplateRates(selectedTemplate.id);
      }
    } catch (error) {
      console.error('Error deleting rate:', error);
      toast.error(t('admin_toast_error_delete_rate'));
    }
  };

  const handleAssignPricing = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/client-pricing-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(assignmentForm)
      });

      if (!response.ok) throw new Error('Failed to assign pricing');

      toast.success(t('admin_toast_rate_assigned'));
      setShowAssignmentDialog(false);
      setAssignmentForm({ client_id: '', template_id: 'general' });
      loadData();
    } catch (error) {
      console.error('Error assigning pricing:', error);
      toast.error(t('admin_toast_error_assign_rate'));
    }
  };

  const handleRemoveAssignment = async (clientId) => {
    if (!window.confirm(t('admin_confirm_remove_custom_rate'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/client-pricing-assignments/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to remove assignment');

      toast.success(t('admin_toast_custom_rate_removed'));
      loadData();
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast.error(t('admin_toast_error_remove_assignment'));
    }
  };

  // Client and Driver Profile Management
  const handleEditClient = (client) => {
    setClientEditForm({
      id: client.id,
      name: client.name || '',
      email: client.email || '',
      company_name: client.company_name || '',
      cif_dni: client.cif_dni || '',
      phone: client.phone || '',
      address: client.address || '',
      country: client.country || '',
      payment_type: client.payment_type || 'biweekly'
    });
    setShowClientEditDialog(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      
      console.log('💾 Saving client with payment_type:', clientEditForm.payment_type);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${clientEditForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: clientEditForm.name,
          email: clientEditForm.email,
          company_name: clientEditForm.company_name,
          cif_dni: clientEditForm.cif_dni,
          phone: clientEditForm.phone,
          address: clientEditForm.address,
          country: clientEditForm.country,
          payment_type: clientEditForm.payment_type
        })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Client updated, received payment_type:', data.payment_type);
        toast.success(t('admin_toast_client_updated'));
        setShowClientEditDialog(false);
        loadData();
      } else {
        console.error('❌ Error updating client:', data);
        toast.error(t('admin_toast_error_update_client'));
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(t('admin_toast_error_update_client'));
    }
  };

  const handleEditDriver = (driver) => {
    setDriverEditForm({
      id: driver.id,
      name: driver.name || '',
      email: driver.email || '',
      phone: driver.phone || '',
      license_number: driver.license_number || '',
      vehicle_info: driver.vehicle_info || '',
      is_available: driver.is_available ?? true,
      is_fleet_driver: driver.is_fleet_driver || false,
      fleet_driver_id: driver.fleet_driver_id || driver.fleet_owner_id || '',
      documents_validated: driver.documents_validated || false,
      full_name: driver.full_name || '',
      dni_nie: driver.dni_nie || '',
      fiscal_address: driver.fiscal_address || '',
      irpf_retention: driver.irpf_retention || 0,
      bank_name: driver.bank_name || '',
      bank_account: driver.bank_account || ''
    });
    
    // Load existing documents for this driver
    loadDriverDocuments(driver.id);
    
    setShowDriverEditDialog(true);
  };

  const handleSaveDriver = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/${driverEditForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: driverEditForm.name,
          email: driverEditForm.email,
          phone: driverEditForm.phone,
          license_number: driverEditForm.license_number,
          vehicle_info: driverEditForm.vehicle_info,
          is_available: driverEditForm.is_available,
          documents_validated: driverEditForm.documents_validated,
          // Fleet: must be sent on every save or assignment never persists (PUT uses partial updates)
          is_fleet_driver: !!driverEditForm.is_fleet_driver,
          fleet_driver_id: driverEditForm.is_fleet_driver
            ? null
            : (driverEditForm.fleet_driver_id && String(driverEditForm.fleet_driver_id).trim()
                ? String(driverEditForm.fleet_driver_id).trim()
                : null),
          // Billing/payment fields
          full_name: driverEditForm.full_name,
          dni_nie: driverEditForm.dni_nie,
          fiscal_address: driverEditForm.fiscal_address,
          irpf_retention: driverEditForm.irpf_retention,
          bank_name: driverEditForm.bank_name,
          bank_account: driverEditForm.bank_account
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update driver');
      }

      toast.success(t('admin_toast_driver_updated'));
      setShowDriverEditDialog(false);
      loadData();
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error(t('admin_toast_error_update_driver', { message: error.message }));
    }
  };


  // Load expiring documents (within 7 days)
  const loadExpiringDocuments = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/documents/expiring-soon`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExpiringDocuments(data.expiring_documents || []);
      }
    } catch (error) {
      console.error('Error loading expiring documents:', error);
    }
  };

  // Load existing documents for a driver
  const loadDriverDocuments = async (driverId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers/${driverId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const docs = await response.json();
        setExistingDocuments(docs);
      }
    } catch (error) {
      console.error('Error loading driver documents:', error);
    }
  };

  // Download/View document
  const handleViewDocument = async (documentId, documentType) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        toast.error(t('admin_toast_error_open_document'));
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error(t('admin_toast_error_view_document'));
    }
  };

  // Upload driver documents
  const handleDriverDocumentUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('admin_toast_file_too_large'));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('admin_toast_invalid_format'));
      return;
    }

    // Get expiry date based on document type
    let expiryDate = null;
    if (documentType === 'license_front' || documentType === 'license_back') {
      expiryDate = licenseExpiryDate;
      if (!expiryDate && (documentType === 'license_front')) {
        toast.error(t('admin_toast_license_expiry_required'));
        return;
      }
    } else if (documentType === 'dni_front' || documentType === 'dni_back') {
      expiryDate = dniExpiryDate;
      if (!expiryDate && (documentType === 'dni_front')) {
        toast.error(t('admin_toast_dni_expiry_required'));
        return;
      }
    } else if (documentType === 'autonomo_certificate') {
      expiryDate = autonomoExpiryDate;
    } else if (documentType === 'ss_certificate') {
      expiryDate = ssExpiryDate;
    } else if (documentType === 'hacienda_certificate') {
      expiryDate = haciendaExpiryDate;
    }

    try {
      setUploadingDriverDoc(true);
      const token = localStorage.getItem('admin_token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      formData.append('driver_id', driverEditForm.id);
      formData.append('upload_date', new Date().toISOString());
      if (expiryDate) {
        formData.append('expiry_date', expiryDate);
      }

      const response = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir documento');
      }

      const result = await response.json();
      
      // Show success message
      let message = t('admin_toast_driver_document_uploaded');
      if (result.document?.driver_validated) {
        message = t('admin_toast_driver_document_validated_auto');
      } else if (result.document?.message_extra) {
        message = result.document.message_extra;
      }
      
      toast.success(message);
      
      // Reset file input
      e.target.value = '';
      
      // Reload driver data to update validation status
      await loadData();
      
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || t('admin_toast_error_upload_driver_document'));
    } finally {
      setUploadingDriverDoc(false);
    }
  };

  // Create new client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...clientCreateForm,
          send_welcome_email: true
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create client';
        try {
          const error = await response.json();
          if (Array.isArray(error?.detail)) {
            errorMessage = error.detail.map((item) => item?.msg).filter(Boolean).join(', ') || errorMessage;
          } else if (typeof error?.detail === 'string') {
            errorMessage = error.detail;
          } else if (typeof error?.message === 'string') {
            errorMessage = error.message;
          }
        } catch (_err) {
          // Keep generic fallback when backend returns non-JSON body.
        }
        throw new Error(errorMessage);
      }

      toast.success(t('admin_toast_client_created'));
      setShowClientCreateDialog(false);
      setClientCreateForm({
        name: '',
        email: '',
        company_name: '',
        cif_dni: '',
        phone: '',
        country: 'ES',
        address: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.message || t('admin_toast_error_create_client'));
    }
  };

  // Create new driver
  const handleCreateDriver = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/drivers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...driverCreateForm,
          send_welcome_email: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.detail || t('admin_toast_error_create_driver'));
        return;
      }

      const newDriver = await response.json();
      const driverId = newDriver.id;

      // Upload documents if provided
      const documentTypes = [
        { key: 'license_front', expiry: null },
        { key: 'license_back', expiry: null },
        { key: 'dni_front', expiry: null },
        { key: 'dni_back', expiry: null },
        { key: 'autonomo_certificate', expiry: null },
        { key: 'hacienda_certificate', expiry: 'hacienda_expiry' },
        { key: 'ss_certificate', expiry: 'ss_expiry' }
      ];

      let uploadedCount = 0;
      for (const docType of documentTypes) {
        const file = driverDocuments[docType.key];
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('document_type', docType.key);
          formData.append('upload_date', new Date().toISOString());
          formData.append('driver_id', driverId);
          
          if (docType.expiry && driverDocuments[docType.expiry]) {
            formData.append('expiry_date', new Date(driverDocuments[docType.expiry]).toISOString());
          }

          const docResponse = await fetch(`${BACKEND_URL}/api/drivers/documents/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (docResponse.ok) {
            uploadedCount++;
          }
        }
      }

      toast.success(
        t('admin_toast_driver_created_with_docs_welcome', {
          docsPart: uploadedCount > 0 ? t('admin_toast_driver_docs_uploaded', { count: uploadedCount }) + ' ' : '',
        })
      );
      setShowDriverCreateDialog(false);
      setDriverCreateForm({
        name: '',
        email: '',
        phone: '',
        license_number: '',
        is_fleet_driver: false,
        fleet_driver_id: ''
      });
      setDriverDocuments({
        license_front: null,
        license_back: null,
        dni_front: null,
        dni_back: null,
        autonomo_certificate: null,
        hacienda_certificate: null,
        hacienda_expiry: '',
        ss_certificate: null,
        ss_expiry: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating driver:', error);
      toast.error(error.message || t('admin_toast_error_create_driver'));
    }
  };


  // Calculate price based on distance and service type
  const calculateServicePrice = async (distanceKm, serviceType) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pricing/calculate?distance_km=${distanceKm}&service_type=${serviceType}`
      );
      
      if (response.ok) {
        const data = await response.json();
        return data[`${serviceType}_price`] || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error calculating price:', error);
      return 0;
    }
  };

  const hasCoordinateValue = (value) =>
    value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

  const updateCreateServiceDistanceAndPrice = async (partialForm = {}) => {
    // Use a ref to avoid stale state snapshots when pickup/delivery are selected quickly.
    const nextForm = { ...createServiceFormRef.current, ...partialForm };
    const hasMainRoute =
      hasCoordinateValue(nextForm.pickup_lat) &&
      hasCoordinateValue(nextForm.pickup_lng) &&
      hasCoordinateValue(nextForm.delivery_lat) &&
      hasCoordinateValue(nextForm.delivery_lng);

    if (!hasMainRoute) {
      setCreateServiceForm(prev => ({ ...prev, ...partialForm, distance_km: 0, calculated_price: 0 }));
      return;
    }

    const normalizedDistance = await computeRouteDistanceKm(nextForm);
    if (normalizedDistance <= 0) {
      setCreateServiceForm(prev => ({ ...prev, ...partialForm, distance_km: 0, calculated_price: 0 }));
      return;
    }
    const price = await calculateServicePrice(normalizedDistance, nextForm.service_type);
    setCreateServiceForm(prev => ({
      ...prev,
      ...partialForm,
      distance_km: normalizedDistance,
      calculated_price: price
    }));
  };

  // Handle pickup address selection
  const handlePickupAddressSelect = async (address, lat, lng) => {
    await updateCreateServiceDistanceAndPrice({
      pickup_address: address,
      pickup_lat: lat,
      pickup_lng: lng
    });
  };

  // Handle delivery address selection
  const handleDeliveryAddressSelect = async (address, lat, lng) => {
    await updateCreateServiceDistanceAndPrice({
      delivery_address: address,
      delivery_lat: lat,
      delivery_lng: lng
    });
  };

  // Handle service type change
  const handleServiceTypeChange = async (serviceType) => {
    await updateCreateServiceDistanceAndPrice({ service_type: serviceType });
  };



  // Helper function to update form fields efficiently


  // Handle delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(t('admin_confirm_delete_service'))) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(t('admin_toast_service_deleted'));
        await loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar el servicio');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(t('admin_toast_error_delete_service'));
    }
  };

  // Handle opening complete service dialog
  const handleOpenCompleteService = async (order) => {
    setSelectedOrder(order);
    setCompleteServiceForm({
      fuel: '',
      tolls: '',
      parking: '',
      taxi: '',
      other: '',
      other_description: '',
      completion_notes: ''
    });
    setPickupPhotos([]);
    setDeliveryPhotos([]);
    setExpensePhotos([]);
    setContractPhotos([]);
    setAdminCharges([]);  // Clear admin charges
    setNewCharge({ type: '', description: '', amount: '' });
    
    // Clear any previously uploaded photos for this order (to avoid duplicates)
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/completion-photos`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Error clearing previous photos:', error);
      // Continue anyway - not critical
    }
    
    setShowCompleteServiceDialog(true);
  };
  
  // Handle adding an admin charge
  const handleAddCharge = () => {
    if (!newCharge.type || !newCharge.amount) {
      toast.error(t('admin_toast_select_charge_and_amount'));
      return;
    }
    const chargeType = CHARGE_TYPES.find(t => t.value === newCharge.type);
    setAdminCharges([...adminCharges, {
      type: newCharge.type,
      label: chargeType?.label || newCharge.type,
      description: newCharge.description,
      amount: parseFloat(newCharge.amount)
    }]);
    setNewCharge({ type: '', description: '', amount: '' });
  };
  
  // Handle removing an admin charge
  const handleRemoveCharge = (index) => {
    setAdminCharges(adminCharges.filter((_, i) => i !== index));
  };

  // Handle uploading completion photo with category - stores in MongoDB
  const handleUploadCompletionPhoto = async (event, category) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingCompletionPhoto(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const uploadedUrls = [];
      
      // Upload each file
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('photo_category', category);

        const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/upload-completion-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          // Store photo ID (photo is stored in MongoDB)
          const photoUrl = `/api/photos/${data.photo_id}`;
          uploadedUrls.push(photoUrl);
        } else {
          const error = await response.json();
          toast.error(error.detail || `Error al subir ${file.name}`);
        }
      }
      
      // Add all uploaded photos to the corresponding array
      if (uploadedUrls.length > 0) {
        if (category === 'pickup') {
          setPickupPhotos(prev => [...prev, ...uploadedUrls]);
        } else if (category === 'delivery') {
          setDeliveryPhotos(prev => [...prev, ...uploadedUrls]);
        } else if (category === 'expense') {
          setExpensePhotos(prev => [...prev, ...uploadedUrls]);
        } else if (category === 'contract') {
          setContractPhotos(prev => [...prev, ...uploadedUrls]);
        }
        toast.success(t('admin_toast_complete_photos_uploaded', { count: uploadedUrls.length }));
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error(t('admin_toast_error_upload_photos'));
    } finally {
      setUploadingCompletionPhoto(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle completing service
  const handleCompleteService = async () => {
    if (!selectedOrder) return;

    // Extract photo IDs from URLs (format: /api/photos/{id})
    const extractPhotoId = (url) => url.split('/').pop();

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expenses: {
            fuel: parseFloat(completeServiceForm.fuel) || 0,
            tolls: parseFloat(completeServiceForm.tolls) || 0,
            parking: parseFloat(completeServiceForm.parking) || 0,
            taxi: parseFloat(completeServiceForm.taxi) || 0,
            other: parseFloat(completeServiceForm.other) || 0,
            other_description: completeServiceForm.other_description
          },
          pickup_photos: pickupPhotos.map(extractPhotoId),
          delivery_photos: deliveryPhotos.map(extractPhotoId),
          expense_photos: expensePhotos.map(extractPhotoId),
          contract_photos: contractPhotos.map(extractPhotoId),
          completion_notes: completeServiceForm.completion_notes,
          admin_additional_charges: adminCharges  // Cargos adicionales al cliente
        })
      });

      const bodyText = await response.text();

      if (response.ok) {
        toast.success(t('admin_toast_service_completed_historic'));
        setShowCompleteServiceDialog(false);
        setSelectedOrder(null);
        setAdminCharges([]);
        await loadData();
      } else {
        try {
          const error = bodyText ? JSON.parse(bodyText) : {};
          toast.error(error.detail || t('admin_toast_complete_service_failed'));
        } catch {
          toast.error(t('admin_toast_complete_service_failed_code', { code: response.status }));
        }
      }
    } catch (error) {
      console.error('Error completing service:', error);
      toast.error(t('admin_toast_error_connection_complete'));
    }
  };

  // Handle reverting a completed service back to pending
  const handleRevertToPending = async (order) => {
    if (!window.confirm(t('admin_confirm_revert_service'))) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/revert-to-pending`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          t('admin_toast_service_reverted_ok', {
            status: t(data.new_status === 'assigned' ? 'assigned' : 'service_status_pending'),
          })
        );
        await loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_toast_error_revert_service'));
      }
    } catch (error) {
      console.error('Error reverting service:', error);
      toast.error(t('admin_toast_error_revert_service'));
    }
  };

  // Handle viewing service history/details
  const handleViewServiceHistory = async (order) => {
    setSelectedOrder(order);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/completion-details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServiceHistoryDetails(data);
        setShowServiceHistoryDialog(true);
      } else {
        // If no completion details, show basic info
        setServiceHistoryDetails({
          order_id: order.id,
          status: order.status,
          is_fully_completed: order.is_fully_completed,
          completion_photos: null,
          expenses: null,
          completion_notes: order.completion_notes
        });
        setShowServiceHistoryDialog(true);
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
      toast.error(t('admin_toast_error_load_service_details'));
    }
  };

  // Filter clients by search term
  const filteredClients = clients.filter(client => {
    if (!searchClient) return true;
    const searchLower = searchClient.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchLower) ||
      client.cif_dni?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower)
    );
  });

  // Filter drivers by search term
  const filteredDrivers = drivers.filter(driver => {
    // For sub-admins (collaborators), only show assigned drivers or drivers they created
    if (!canSeeClientPrices && assignedDriverIds.length > 0) {
      const isAssigned = assignedDriverIds.includes(driver.id);
      const isCreatedByMe = driver.created_by_admin_id === localStorage.getItem('admin_id');
      if (!isAssigned && !isCreatedByMe) return false;
    }
    
    if (!searchDriver) return true;
    const searchLower = searchDriver.toLowerCase();
    return (
      driver.name?.toLowerCase().includes(searchLower) ||
      driver.dni_nie?.toLowerCase().includes(searchLower) ||
      driver.license_number?.toLowerCase().includes(searchLower) ||
      driver.email?.toLowerCase().includes(searchLower)
    );
  });

  // Filter fleet drivers for collaborators - only show assigned fleet drivers
  const filteredFleetDrivers = fleetDrivers.filter(fleet => {
    // Super admins can see all fleet drivers
    if (canSeeClientPrices) return true;
    // Sub-admins can only see fleet drivers they have assigned
    if (assignedDriverIds.length > 0) {
      return assignedDriverIds.includes(fleet.id);
    }
    return false;
  });

  // Helper function to format date for input type="date" (YYYY-MM-DD)
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    // If it's an ISO string with time, extract just the date part
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    // Try to parse as date and format
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      console.error('Error formatting date:', e);
    }
    return dateStr;
  };

  // Helper function to extract time from ISO string (HH:MM)
  const formatTimeForInput = (dateStr, timeStr) => {
    // If we have a separate time string, use it
    if (timeStr) return timeStr;
    // If date string has time component, extract it
    if (dateStr && typeof dateStr === 'string' && dateStr.includes('T')) {
      const timePart = dateStr.split('T')[1];
      if (timePart) {
        return timePart.substring(0, 5); // Get HH:MM
      }
    }
    return '';
  };

  const updateFormField = (field, value) => {
    setCreateServiceForm(prev => ({ ...prev, [field]: value }));
  };

  // Create service from admin
  const handleCreateAdminService = async (e) => {
    e.preventDefault();
    setLoadingCreateService(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Validation
      if (!createServiceForm.pickup_address || !createServiceForm.delivery_address) {
        toast.error(t('admin_toast_complete_pickup_delivery'));
        setLoadingCreateService(false);
        return;
      }

      if (!createServiceForm.car_brand || !createServiceForm.car_model || !createServiceForm.car_plate) {
        toast.error(t('admin_toast_complete_vehicle_data'));
        setLoadingCreateService(false);
        return;
      }

      if (['bc_service', 'tradein'].includes(createServiceForm.service_type)) {
        if (!createServiceForm.second_car_brand || !createServiceForm.second_car_plate) {
          toast.error(t('client_validation_tradein_second_vehicle'));
          setLoadingCreateService(false);
          return;
        }
      }

      if (createServiceForm.service_type === 'bc_service') {
        if (!createServiceForm.point_c_address || !createServiceForm.point_c_lat || !createServiceForm.point_c_lng) {
          toast.error(t('client_validation_bc_point_c'));
          setLoadingCreateService(false);
          return;
        }
        if (!createServiceForm.point_c_contact_name || !createServiceForm.point_c_contact_phone) {
          toast.error(t('client_validation_bc_point_c_contact'));
          setLoadingCreateService(false);
          return;
        }
      }

      // Validate driver price if driver is assigned (allow 0€)
      if (createServiceForm.driver_id && createServiceForm.driver_id !== "__none__" && createServiceForm.driver_price === undefined) {
        toast.error(t('admin_toast_set_driver_price'));
        setLoadingCreateService(false);
        return;
      }

      // Prepare request data
      const requestData = {
        client_id: createServiceForm.client_id || null, // null if no client (admin owner)
        service_type: createServiceForm.service_type,
        pickup_address: createServiceForm.pickup_address,
        pickup_lat: createServiceForm.pickup_lat,
        pickup_lng: createServiceForm.pickup_lng,
        pickup_contact_name: createServiceForm.pickup_contact_name,
        pickup_contact_phone: createServiceForm.pickup_contact_phone,
        pickup_margin_time: createServiceForm.pickup_margin_time,
        delivery_address: createServiceForm.delivery_address,
        delivery_lat: createServiceForm.delivery_lat,
        delivery_lng: createServiceForm.delivery_lng,
        delivery_contact_name: createServiceForm.delivery_contact_name,
        delivery_contact_phone: createServiceForm.delivery_contact_phone,
        delivery_margin_time: createServiceForm.delivery_margin_time,
        car_type: createServiceForm.car_type,
        car_brand: createServiceForm.car_brand,
        car_model: createServiceForm.car_model,
        car_plate: createServiceForm.car_plate,
        fuel_type: createServiceForm.fuel_type,
        second_vehicle: ['bc_service', 'tradein'].includes(createServiceForm.service_type)
          ? {
              car_type: createServiceForm.second_car_type || 'Turismo',
              car_brand: createServiceForm.second_car_brand,
              car_model: createServiceForm.second_car_model,
              car_plate: createServiceForm.second_car_plate,
              fuel_type: createServiceForm.second_fuel_type || 'gasoline'
            }
          : null,
        car_color: createServiceForm.car_color,
        car_year: createServiceForm.car_year ? parseInt(createServiceForm.car_year) : null,
        distance_km: createServiceForm.distance_km,
        additional_services: createServiceForm.additional_services,
        pickup_date_from: createServiceForm.pickup_date_start && createServiceForm.pickup_time_start ? 
          (() => {
            const date = new Date(createServiceForm.pickup_date_start);
            const [hours, minutes] = createServiceForm.pickup_time_start.split(':');
            date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
            return date.toISOString();
          })() : 
          (createServiceForm.pickup_date_start ? createServiceForm.pickup_date_start.toISOString() : null),
        pickup_date_to: createServiceForm.pickup_date_end && createServiceForm.pickup_time_end ? 
          (() => {
            const date = new Date(createServiceForm.pickup_date_end);
            const [hours, minutes] = createServiceForm.pickup_time_end.split(':');
            date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
            return date.toISOString();
          })() : 
          (createServiceForm.pickup_date_end ? createServiceForm.pickup_date_end.toISOString() : null),
        pickup_time_start: createServiceForm.pickup_time_start || null,
        pickup_time_end: createServiceForm.pickup_time_end || null,
        delivery_date_from: createServiceForm.delivery_date_start && createServiceForm.delivery_time_start ? 
          (() => {
            const date = new Date(createServiceForm.delivery_date_start);
            const [hours, minutes] = createServiceForm.delivery_time_start.split(':');
            date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
            return date.toISOString();
          })() : 
          (createServiceForm.delivery_date_start ? createServiceForm.delivery_date_start.toISOString() : null),
        delivery_date_to: createServiceForm.delivery_date_end && createServiceForm.delivery_time_end ? 
          (() => {
            const date = new Date(createServiceForm.delivery_date_end);
            const [hours, minutes] = createServiceForm.delivery_time_end.split(':');
            date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
            return date.toISOString();
          })() : 
          (createServiceForm.delivery_date_end ? createServiceForm.delivery_date_end.toISOString() : null),
        delivery_time_start: createServiceForm.delivery_time_start || null,
        delivery_time_end: createServiceForm.delivery_time_end || null,
        point_c_address: createServiceForm.point_c_address || null,
        point_c_lat: createServiceForm.point_c_lat,
        point_c_lng: createServiceForm.point_c_lng,
        point_c_contact_name: createServiceForm.point_c_contact_name || null,
        point_c_contact_phone: createServiceForm.point_c_contact_phone || null,
        point_c_date_from: createServiceForm.point_c_date_start && createServiceForm.point_c_time_start
          ? (() => {
              const date = new Date(createServiceForm.point_c_date_start);
              const [hours, minutes] = createServiceForm.point_c_time_start.split(':');
              date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
              return date.toISOString();
            })()
          : (createServiceForm.point_c_date_start ? createServiceForm.point_c_date_start.toISOString() : null),
        point_c_date_to: createServiceForm.point_c_date_end && createServiceForm.point_c_time_end
          ? (() => {
              const date = new Date(createServiceForm.point_c_date_end);
              const [hours, minutes] = createServiceForm.point_c_time_end.split(':');
              date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
              return date.toISOString();
            })()
          : (createServiceForm.point_c_date_end ? createServiceForm.point_c_date_end.toISOString() : null),
        point_c_time_start: createServiceForm.point_c_time_start || null,
        point_c_time_end: createServiceForm.point_c_time_end || null,
        service_date: null,
        pickup_fixed_date: createServiceForm.pickup_fixed_date,
        delivery_fixed_date: createServiceForm.delivery_fixed_date,
        driver_id: createServiceForm.driver_id || null,
        driver_price: createServiceForm.driver_price || null,
        is_paid: createServiceForm.is_paid,
        notes: createServiceForm.notes,
        first_vehicle_notes: createServiceForm.first_vehicle_notes || null,
        second_vehicle_notes: createServiceForm.second_vehicle_notes || null
      };

      console.log('Creating service:', requestData);

      const response = await fetch(`${BACKEND_URL}/api/admin/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear el servicio');
      }

      const newService = await response.json();
      
      toast.success(
        createServiceForm.client_id 
          ? t('admin_toast_service_created_with_client') 
          : t('admin_toast_service_created_admin_only')
      );
      
      // Reset form
      setCreateServiceForm({
        client_id: '',
        service_type: 'delivery',
        pickup_address: '',
        pickup_lat: null,
        pickup_lng: null,
        pickup_contact_name: '',
        pickup_contact_phone: '',
        pickup_margin_time: 'flexible',
        delivery_address: '',
        delivery_lat: null,
        delivery_lng: null,
        delivery_contact_name: '',
        delivery_contact_phone: '',
        delivery_margin_time: 'flexible',
        point_c_address: '',
        point_c_lat: null,
        point_c_lng: null,
        point_c_contact_name: '',
        point_c_contact_phone: '',
        point_c_date_start: null,
        point_c_date_end: null,
        point_c_time_start: '',
        point_c_time_end: '',
        car_type: 'Turismo',
        car_brand: '',
        car_model: '',
        car_plate: '',
        fuel_type: 'gasoline',
        second_car_type: 'Turismo',
        second_car_brand: '',
        second_car_model: '',
        second_car_plate: '',
        second_fuel_type: 'gasoline',
        car_color: '',
        car_year: '',
        distance_km: 0,
        calculated_price: 0,
        additional_services: [],
        pickup_date_start: null,
        pickup_date_end: null,
        pickup_time_start: '',
        pickup_time_end: '',
        delivery_date_start: null,
        delivery_date_end: null,
        delivery_time_start: '',
        delivery_time_end: '',
        pickup_fixed_date: false,
        delivery_fixed_date: false,
        driver_id: '',
        driver_price: 0,
        is_paid: false,
        notes: '',
        first_vehicle_notes: '',
        second_vehicle_notes: ''
      });
      
      setUseFixedPickupDate(false);
      setUseFixedDeliveryDate(false);
      setUseFixedPointCDate(false);
      setShowCreateServiceDialog(false);
      loadData(); // Reload orders
      
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(error.message || t('admin_toast_error_create_service'));
    } finally {
      setLoadingCreateService(false);
    }
  };


  // Upload logo for client (admin)
  const handleAdminLogoUpload = async (clientId, file) => {
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/upload/logo/${clientId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload logo');
      }

      toast.success(t('admin_toast_logo_uploaded'));
      loadData();  // Reload to show new logo
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.message || t('admin_toast_error_upload_logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Document Management
  const handleApproveDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/documents/${documentId}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'approved'
        })
      });

      if (!response.ok) throw new Error('Failed to approve document');

      toast.success(t('admin_toast_document_approved'));
      loadData();
    } catch (error) {
      console.error('Error approving document:', error);
      toast.error(t('admin_toast_error_approve_document'));
    }
  };

  const handleRejectDocument = async (documentId) => {
    const reason = window.prompt(t('admin_prompt_rejection_reason'));
    if (!reason) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/documents/${documentId}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'rejected',
          rejection_reason: reason
        })
      });

      if (!response.ok) throw new Error('Failed to reject document');

      toast.success(t('admin_toast_document_rejected'));
      loadData();
    } catch (error) {
      console.error('Error rejecting document:', error);
      toast.error(t('admin_toast_error_reject_document'));
    }
  };

  // Order Management
  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    // VALIDACIÓN: Si se está asignando un conductor, debe tener tarifa
    const isAssigningDriver = orderUpdateForm.driver_id && orderUpdateForm.driver_id !== 'no-driver' && orderUpdateForm.driver_id !== '';
    const hadDriverBefore = selectedOrder.driver_id && selectedOrder.driver_id !== '';
    const isNewDriverAssignment = isAssigningDriver && (!hadDriverBefore || orderUpdateForm.driver_id !== selectedOrder.driver_id);
    
    // Verificar si tiene tarifa asignada (del orden actual o nueva) - permitir 0€
    const hasDriverPrice = selectedOrder.driver_price !== undefined || orderUpdateForm.driver_price !== undefined;
    
    if (isAssigningDriver && !hasDriverPrice) {
      toast.error(t('admin_toast_select_rate_before_save'));
      return;
    }

    // Check if driver is being removed/changed (potential penalty scenario)
    const isRemovingDriver = hadDriverBefore && 
      (orderUpdateForm.driver_id === 'no-driver' || orderUpdateForm.driver_id === '' || 
       (orderUpdateForm.driver_id && orderUpdateForm.driver_id !== selectedOrder.driver_id));
    
    // Preparar datos para enviar, convertir "no-driver" a cadena vacía
    const dataToSend = {
      status: orderUpdateForm.status,
      payment_status: orderUpdateForm.payment_status,
      driver_id: orderUpdateForm.driver_id === 'no-driver' ? '' : orderUpdateForm.driver_id,
      client_id: orderUpdateForm.client_id || null,
      car_plate: orderUpdateForm.car_plate,
      car_brand: orderUpdateForm.car_brand,
      car_model: orderUpdateForm.car_model,
      pickup_address: orderUpdateForm.pickup_address,
      delivery_address: orderUpdateForm.delivery_address,
      pickup_contact_name: orderUpdateForm.pickup_contact_name,
      pickup_contact_phone: orderUpdateForm.pickup_contact_phone,
      delivery_contact_name: orderUpdateForm.delivery_contact_name,
      delivery_contact_phone: orderUpdateForm.delivery_contact_phone,
      point_c_address: orderUpdateForm.point_c_address || null,
      point_c_lat: orderUpdateForm.point_c_lat,
      point_c_lng: orderUpdateForm.point_c_lng,
      point_c_contact_name: orderUpdateForm.point_c_contact_name || null,
      point_c_contact_phone: orderUpdateForm.point_c_contact_phone || null,
      second_vehicle: ['bc_service', 'tradein'].includes(orderUpdateForm.service_type)
        ? {
            car_type: orderUpdateForm.second_car_type || 'Turismo',
            car_brand: orderUpdateForm.second_car_brand || '',
            car_model: orderUpdateForm.second_car_model || '',
            car_plate: orderUpdateForm.second_car_plate || '',
            fuel_type: orderUpdateForm.second_fuel_type || 'gasoline'
          }
        : null,
      first_vehicle_notes: orderUpdateForm.first_vehicle_notes || null,
      second_vehicle_notes: orderUpdateForm.second_vehicle_notes || null,
      selected_additional_services: orderUpdateForm.selected_additional_services || [],
      // Dates and times
      pickup_date_from: orderUpdateForm.pickup_date_from,
      pickup_date_to: orderUpdateForm.pickup_date_to,
      pickup_time_start: orderUpdateForm.pickup_time_start,
      pickup_time_end: orderUpdateForm.pickup_time_end,
      delivery_date_from: orderUpdateForm.delivery_date_from,
      delivery_date_to: orderUpdateForm.delivery_date_to,
      delivery_time_start: orderUpdateForm.delivery_time_start,
      delivery_time_end: orderUpdateForm.delivery_time_end,
      service_date: orderUpdateForm.service_date,
      pickup_fixed_date: orderUpdateForm.pickup_fixed_date,
      delivery_fixed_date: orderUpdateForm.delivery_fixed_date,
      // Service details for pricing
      distance_km: orderUpdateForm.distance_km,
      service_type: orderUpdateForm.service_type,
      // Base price - recalculated when distance changes
      base_price: orderUpdateForm.base_price || null,
      total_price: orderUpdateForm.total_price || null,
      // Driver pricing - IMPORTANT: These fields must be sent to save the driver tariff
      driver_price: orderUpdateForm.driver_price || null,
      driver_pricing_template_id: orderUpdateForm.driver_pricing_template_id || null,
      is_driver_price_custom: orderUpdateForm.is_driver_price_custom || false,
      // Driver extras - additional payments for driver (Pit Stop, Espera, ITV, etc.)
      driver_extras: driverExtras
    };
    
    // If removing/changing driver, show penalty dialog
    if (isRemovingDriver) {
      // Calculate hours until pickup
      const pickupDate = selectedOrder.pickup_date_from || selectedOrder.service_date;
      let hoursUntilPickup = 0;
      if (pickupDate) {
        const pickup = new Date(pickupDate);
        const now = new Date();
        hoursUntilPickup = Math.max(0, (pickup - now) / (1000 * 60 * 60));
      }
      
      // Find the driver being removed
      const removedDriver = drivers.find(d => d.id === selectedOrder.driver_id);
      
      // Calculate suggested penalty amount
      const suggestedAmount = hoursUntilPickup < 48 ? -50 : -25;
      
      setPenaltyData({
        driver_id: selectedOrder.driver_id,
        driver_name: removedDriver?.name || 'Conductor',
        order_id: selectedOrder.id,
        order_info: {
          car_plate: selectedOrder.car_plate || '',
          route: `${selectedOrder.pickup_city || selectedOrder.pickup_address?.split(',')[0] || ''} → ${selectedOrder.delivery_city || selectedOrder.delivery_address?.split(',')[0] || ''}`
        },
        hours_until_pickup: hoursUntilPickup,
        suggested_amount: suggestedAmount,
        amount: suggestedAmount,
        reason: 'Cancelación tardía',
        apply_penalty: false
      });
      setPendingOrderUpdate(dataToSend);
      setShowPenaltyDialog(true);
      return;
    }

    // No driver change, proceed directly
    await executeOrderUpdate(dataToSend);
  };

  const handleCancelOrder = async (order) => {
    const confirmed = window.confirm(
      t('admin_confirm_cancel_order', {
        serviceId: order.id.substring(0, 8).toUpperCase(),
        clientName: order.client_name,
        driverName: order.driver_name || t('admin_unassigned'),
      })
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/orders/${order.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('admin_toast_cancel_service_failed'));
      }

      const data = await response.json();
      
      toast.success(
        t('admin_toast_order_cancelled_success', {
          status: data.status,
          clientFee: data.cancellation_fee?.toFixed(2) || '0',
          driverPayment: data.driver_payment?.toFixed(2) || '0',
          reasonExtra: data.driver_payment_reason ? `\n${data.driver_payment_reason}` : '',
        })
      );

      // Reload data to show updated status
      loadData();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(t('admin_toast_cancel_order_error', { message: error.message }));
    }
  };

  const handleCancelAtPickup = async (order) => {
    const confirmed = window.confirm(
      t('admin_confirm_cancel_at_pickup', {
        serviceId: order.id.substring(0, 8).toUpperCase(),
        clientName: order.client_name,
        driverName: order.driver_name || t('admin_unassigned'),
      })
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/cancel-at-pickup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('admin_toast_error_cancel_at_pickup_detail'));
      }

      const data = await response.json();
      
      toast.success(
        t('admin_toast_order_cancelled_at_pickup_success', {
          status: data.status,
          clientFee: data.cancellation_fee?.toFixed(2) || '0',
          driverPayment: data.driver_payment?.toFixed(2) || '0',
        })
      );

      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
    }
  };

  const handleCancelInTransit = async (order) => {
    const confirmed = window.confirm(
      t('admin_confirm_cancel_in_transit', {
        serviceId: order.id.substring(0, 8).toUpperCase(),
        clientName: order.client_name,
        driverName: order.driver_name || t('admin_unassigned'),
      })
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/cancel-in-transit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('admin_toast_error_cancel_in_transit_detail'));
      }

      const data = await response.json();
      
      toast.success(
        t('admin_toast_order_marked_cancel_in_transit_success', {
          status: data.status,
        })
      );

      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
    }
  };

  const handleApproveTransitCancellation = async (order) => {
    const confirmed = window.confirm(
      t('admin_confirm_approve_transit', {
        serviceId: order.id.substring(0, 8).toUpperCase(),
        driverName: order.driver_name || t('admin_unassigned'),
      })
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/approve-transit-cancellation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('admin_toast_error_approve_transit_detail'));
      }

      const data = await response.json();
      
      toast.success(
        t('admin_toast_transit_cancellation_approved_success', {
          status: data.status,
          clientFee: data.cancellation_fee?.toFixed(2) ?? '0',
          driverPayment: data.driver_payment?.toFixed(2) ?? '0',
        })
      );

      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
    }
  };

  const handleUploadClientInvoice = async () => {
    if (!uploadInvoiceFile || !uploadInvoiceNumber || !uploadInvoiceDate) {
      toast.error(t('admin_toast_complete_all_fields'));
      return;
    }

    try {
      setUploadingInvoice(true);
      const token = localStorage.getItem('admin_token');
      
      const formData = new FormData();
      formData.append('file', uploadInvoiceFile);
      formData.append('invoice_number', uploadInvoiceNumber);
      formData.append('invoice_date', uploadInvoiceDate);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/client-billing-periods/${selectedPeriod.id}/upload-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t('admin_toast_invoice_uploaded'));
        setShowUploadInvoiceDialog(false);
        setUploadInvoiceFile(null);
        setUploadInvoiceNumber('');
        setUploadInvoiceDate('');
        loadClientBillingPeriods();
      } else {
        throw new Error(data.detail || t('admin_toast_error_upload_client_invoice_detail'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || t('admin_toast_error_upload_client_invoice_detail'));
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleMarkClientPeriodPaid = async (periodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/client-billing-periods/${periodId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('admin_toast_period_marked_paid_short'));
        loadClientBillingPeriods();
      } else {
        throw new Error(t('admin_toast_error_mark_period_paid'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('admin_toast_error_mark_period_paid'));
    }
  };

  const handleDeleteClientInvoice = async (periodId) => {
    if (!confirm(t('admin_confirm_delete_invoice'))) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/client-billing-periods/${periodId}/invoice`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(t('admin_toast_invoice_deleted'));
        loadClientBillingPeriods();
      } else {
        throw new Error(t('admin_toast_error_delete_invoice'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('admin_toast_error_delete_invoice'));
    }
  };

  const handleViewPeriodDetails = async (period) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      console.log('Period object:', period);
      console.log('Service IDs:', period.service_ids);
      
      // Validate period has service_ids
      if (!period.service_ids || !Array.isArray(period.service_ids)) {
        toast.error(t('admin_toast_period_no_services'));
        console.error('Period missing service_ids:', period);
        return;
      }
      
      if (period.service_ids.length === 0) {
        toast.warning(t('admin_toast_warning_period_no_services'));
        setPeriodDetails({
          ...period,
          services: []
        });
        setShowPeriodDetailsDialog(true);
        return;
      }
      
      console.log('Loading details for period:', period.id);
      console.log('Number of services:', period.service_ids.length);
      
      const allOrders = await fetchAllOrdersPaged({
        headers: { 'Authorization': `Bearer ${token}` },
        pageSize: 400,
      });
      if (!Array.isArray(allOrders)) {
        throw new Error(t('admin_toast_error_load_services'));
      }
      console.log('Total orders loaded:', allOrders.length);
      
      // Filter services that are in this period
      const validServices = allOrders.filter(order => 
        period.service_ids.includes(order.id)
      );
      
      console.log('Services found in period:', validServices.length);
      console.log('Service IDs found:', validServices.map(s => s.id));
      
      if (validServices.length === 0) {
        toast.warning(t('admin_toast_warning_services_not_found'));
      }
      
      setPeriodDetails({
        ...period,
        services: validServices
      });
      setShowPeriodDetailsDialog(true);
    } catch (error) {
      console.error('Error loading period details:', error);
      toast.error(t('admin_toast_period_details_load_error', { message: error.message }));
    }
  };

  // Filter drivers based on search term (includes fleet drivers)
  const getFilteredDrivers = () => {
    // Show ALL drivers for assignment
    // Admin should have full control to assign any driver regardless of status
    // Only exclude drivers that are explicitly offline
    const availableDrivers = drivers.filter(d => 
      d.status !== 'offline'
    );
    
    if (!driverSearchTerm) return availableDrivers;
    
    const term = driverSearchTerm.toLowerCase();
    return availableDrivers.filter(driver => 
      driver.name?.toLowerCase().includes(term) ||
      driver.email?.toLowerCase().includes(term) ||
      driver.license_number?.toLowerCase().includes(term)
    );
  };

  // Generate Google Calendar link for a service
  const generateGoogleCalendarLink = (order) => {
    // Get driver name
    const driver = drivers.find(d => d.id === order.driver_id);
    const driverName = driver ? driver.name : t('admin_unassigned');
    
    // Extract city from addresses (take the last part before comma)
    const pickupCity = order.pickup_address ? order.pickup_address.split(',').pop().trim() : t('gps_live_na');
    const deliveryCity = order.delivery_address ? order.delivery_address.split(',').pop().trim() : t('gps_live_na');
    
    // Event title: Matrícula - Conductor - Ciudad Origen → Ciudad Destino
    const title = `${order.car_plate || t('service_no_plate')} - ${driverName} - ${pickupCity} → ${deliveryCity}`;
    
    // Parse dates and times correctly
    // Pickup date with time
    let pickupDate = order.pickup_date_from ? new Date(order.pickup_date_from) : new Date();
    if (order.pickup_time_start) {
      // Parse time (format: "09:00" or "09:00:00")
      const [hours, minutes] = order.pickup_time_start.split(':');
      pickupDate.setHours(parseInt(hours) || 9, parseInt(minutes) || 0, 0, 0);
    }
    
    // Delivery date with time
    let deliveryDate = order.delivery_date_to ? new Date(order.delivery_date_to) : new Date(pickupDate.getTime() + 2 * 60 * 60 * 1000);
    if (order.delivery_time_end) {
      // Parse time (format: "18:00" or "18:00:00")
      const [hours, minutes] = order.delivery_time_end.split(':');
      deliveryDate.setHours(parseInt(hours) || 18, parseInt(minutes) || 0, 0, 0);
    }
    
    // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ)
    const formatDateForGoogleCal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = '00';
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };
    
    const startDate = formatDateForGoogleCal(pickupDate);
    const endDate = formatDateForGoogleCal(deliveryDate);
    
    // Event details (without price)
    const details = `${t('admin_google_cal_service_title')}
${t('admin_incident_vehicle_label')} ${order.car_brand} ${order.car_model} (${order.car_plate})
${t('admin_driver_label')} ${driverName}
${t('admin_status_label')} ${t(order.status) || order.status}

📍 ${t('client_pickup_label')} ${order.pickup_address}
   ${t('admin_calendar_schedule_prefix')} ${order.pickup_time_start || t('admin_calendar_time_tbd')} - ${order.pickup_time_end || ''}
   ${t('admin_calendar_contact_prefix')} ${order.pickup_contact_name || ''} ${order.pickup_contact_phone || ''}

📍 ${t('client_delivery_label')} ${order.delivery_address}
   ${t('admin_calendar_schedule_prefix')} ${order.delivery_time_start || t('admin_calendar_time_tbd')} - ${order.delivery_time_end || ''}
   ${t('admin_calendar_contact_prefix')} ${order.delivery_contact_name || ''} ${order.delivery_contact_phone || ''}`;
    
    // Location
    const location = `${order.pickup_address} → ${order.delivery_address}`;
    
    // Build Google Calendar URL
    const baseUrl = 'https://calendar.google.com/calendar/render';
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${startDate}/${endDate}`,
      details: details,
      location: location,
      trp: 'false'
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Calculate driver prices for all 3 templates
  const calculateDriverPrices = async (orderId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const order = orders.find(o => o.id === orderId);
      
      if (!order) {
        console.error('Order not found:', orderId);
        toast.error(t('admin_toast_service_not_found'));
        return {};
      }

      if (!order.distance_km) {
        console.error('Order missing distance_km:', order);
        toast.error(t('admin_toast_service_no_distance'));
        return {};
      }

      if (!order.service_type) {
        console.error('Order missing service_type:', order);
        toast.error(t('admin_toast_service_no_type'));
        return {};
      }

      const prices = {};
      
      for (const template of driverPricingTemplates) {
        const response = await fetch(
          `${BACKEND_URL}/api/admin/calculate-driver-price?distance_km=${order.distance_km}&service_type=${order.service_type}&template_id=${template.id}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          prices[template.id] = {
            name: template.name,
            price: data.price
          };
        } else {
          console.error('Failed to calculate price for template:', template.name);
        }
      }
      
      console.log('Calculated prices:', prices);
      return prices;
    } catch (error) {
      console.error('Error calculating driver prices:', error);
      toast.error(t('admin_toast_error_calculate_prices'));
      return {};
    }
  };

  // Handle driver selection with pricing
  const handleDriverSelection = async (driver) => {
    console.log('=== handleDriverSelection called ===');
    console.log('Driver:', driver);
    console.log('orderUpdateForm:', orderUpdateForm);
    console.log('selectedOrder:', selectedOrder);
    console.log('driverPricingTemplates:', driverPricingTemplates);
    
    if (!driver.documents_validated) {
      toast.error(t('admin_toast_driver_docs_not_validated'));
      return;
    }
    
    // Get distance and service type from form
    const distance = orderUpdateForm.distance_km;
    const serviceType = orderUpdateForm.service_type;
    const totalPrice = selectedOrder?.total_price || 0;
    
    console.log('Using values - distance:', distance, 'serviceType:', serviceType, 'totalPrice:', totalPrice);
    
    // Validate
    if (!distance || distance === 0) {
      console.error('Missing distance_km');
      toast.error(t('admin_toast_enter_distance_before_assign'));
      return;
    }
    
    if (!serviceType) {
      console.error('Missing service_type');
      toast.error(t('admin_toast_select_service_type_before_assign'));
      return;
    }
    
    if (driverPricingTemplates.length === 0) {
      console.error('No driver pricing templates loaded');
      toast.error(t('admin_toast_templates_not_loaded'));
      return;
    }
    
    // Set selected driver for pricing
    setSelectedDriverForPricing(driver);
    
    // Create a temporary order object with current values for the modal
    const currentOrderForModal = {
      ...selectedOrder,
      distance_km: distance,
      service_type: serviceType,
      total_price: totalPrice
    };
    setSelectedOrder(currentOrderForModal);
    
    console.log('Calculating prices...');
    
    // Calculate prices for all templates
    const prices = {};
    const token = localStorage.getItem('admin_token');
    
    try {
      for (const template of driverPricingTemplates) {
        console.log(`Calculating for template: ${template.name}`);
        const response = await fetch(
          `${BACKEND_URL}/api/admin/calculate-driver-price?distance_km=${distance}&service_type=${serviceType}&template_id=${template.id}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log(`Price for ${template.name}:`, data.price);
          prices[template.id] = {
            name: template.name,
            price: data.price
          };
        } else {
          console.error(`Failed to calculate price for ${template.name}`, await response.text());
        }
      }
      
      console.log('All calculated prices:', prices);
      
      if (Object.keys(prices).length > 0) {
        setCalculatedPrices(prices);
        console.log('Opening driver pricing dialog...');
        setShowDriverPricingDialog(true);
      } else {
        console.error('No prices calculated');
        toast.error(t('admin_toast_could_not_calculate_rates'));
      }
    } catch (error) {
      console.error('Error calculating prices:', error);
      toast.error(t('admin_toast_error_calculate_rates'));
    }
  };

  // Confirm driver assignment with selected pricing
  const confirmDriverAssignment = (templateId, isCustom = false, customPrice = null) => {
    const priceToUse = isCustom ? parseFloat(customPrice) : calculatedPrices[templateId]?.price;
    
    setOrderUpdateForm({ 
      ...orderUpdateForm, 
      driver_id: selectedDriverForPricing.id,
      driver_pricing_template_id: isCustom ? null : templateId,
      driver_price: priceToUse,
      is_driver_price_custom: isCustom
    });
    
    setShowDriverPricingDialog(false);
    setShowCustomPriceInput(false);
    setCustomDriverPrice('');
    toast.success(
      isCustom
        ? t('admin_toast_driver_tariff_assigned_custom', {
            amount: typeof priceToUse === 'number' ? priceToUse.toFixed(2) : String(priceToUse),
          })
        : t('admin_toast_driver_tariff_assigned_named', {
            label: calculatedPrices[templateId]?.name || '',
            amount: typeof priceToUse === 'number' ? priceToUse.toFixed(2) : String(priceToUse),
          })
    );
  };

  // Geocoding function using Nominatim
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'AutoGo/1.0'
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const refreshOrderUpdateDrivingDistance = (partialForm) => {
    setOrderUpdateForm((prev) => {
      const merged = { ...prev, ...partialForm };
      computeRouteDistanceKm(merged).then((dk) => {
        if (dk > 0) {
          setOrderUpdateForm((p) => ({ ...p, distance_km: dk }));
        }
      });
      return merged;
    });
  };

  /** Manual distance: prefer Google coords on the form; otherwise Nominatim (legacy orders). */
  const recalculateOrderUpdateDistanceFromGeocode = async () => {
    const pickupAddr = orderUpdateForm.pickup_address?.trim();
    const deliveryAddr = orderUpdateForm.delivery_address?.trim();
    if (!pickupAddr || !deliveryAddr) {
      toast.error(t('admin_toast_complete_pickup_delivery'));
      return;
    }
    try {
      let pickupCoords =
        orderUpdateForm.pickup_lat != null && orderUpdateForm.pickup_lng != null
          ? { lat: Number(orderUpdateForm.pickup_lat), lng: Number(orderUpdateForm.pickup_lng) }
          : await geocodeAddress(pickupAddr);
      let deliveryCoords =
        orderUpdateForm.delivery_lat != null && orderUpdateForm.delivery_lng != null
          ? { lat: Number(orderUpdateForm.delivery_lat), lng: Number(orderUpdateForm.delivery_lng) }
          : await geocodeAddress(deliveryAddr);
      if (!pickupCoords || !deliveryCoords) {
        toast.error(t('admin_toast_complete_distance_and_type'));
        return;
      }
      let merged = {
        ...orderUpdateForm,
        pickup_lat: pickupCoords.lat,
        pickup_lng: pickupCoords.lng,
        delivery_lat: deliveryCoords.lat,
        delivery_lng: deliveryCoords.lng,
      };
      const st = orderUpdateForm.service_type || 'delivery';
      if (st === 'bc_service' && orderUpdateForm.point_c_address?.trim()) {
        const cCoords =
          orderUpdateForm.point_c_lat != null && orderUpdateForm.point_c_lng != null
            ? { lat: Number(orderUpdateForm.point_c_lat), lng: Number(orderUpdateForm.point_c_lng) }
            : await geocodeAddress(orderUpdateForm.point_c_address);
        if (!cCoords) {
          toast.error(t('client_validation_bc_point_c'));
          return;
        }
        merged = { ...merged, point_c_lat: cCoords.lat, point_c_lng: cCoords.lng };
      }
      const dk = await computeRouteDistanceKm(merged);
      if (dk <= 0) {
        toast.error(t('admin_toast_complete_distance_and_type'));
        return;
      }
      setOrderUpdateForm((prev) => ({
        ...prev,
        pickup_lat: merged.pickup_lat,
        pickup_lng: merged.pickup_lng,
        delivery_lat: merged.delivery_lat,
        delivery_lng: merged.delivery_lng,
        point_c_lat: merged.point_c_lat ?? prev.point_c_lat,
        point_c_lng: merged.point_c_lng ?? prev.point_c_lng,
        distance_km: dk,
      }));
      toast.success(t('admin_toast_distance_calculated', { km: dk }));
    } catch (error) {
      console.error('Error calculating distance:', error);
    }
  };

  // Calculate driver price for a specific template (LOCAL CALCULATION)
  const calculateDriverPriceForTemplate = async (templateName, distance, serviceType) => {
    try {
      console.log('=== calculateDriverPriceForTemplate ===');
      console.log('Template:', templateName, 'Distance:', distance, 'Service Type:', serviceType);
      console.log('Available templates:', driverPricingTemplates);
      console.log('Loaded rates:', driverPricingRates);
      
      // Find template by name (accent-insensitive)
      const template = driverPricingTemplates.find(
        (t) => normalizeDriverTemplateKey(t.name) === normalizeDriverTemplateKey(templateName)
      );
      if (!template) {
        console.error('Template not found:', templateName);
        toast.error(t('admin_toast_template_not_found', { name: templateName }));
        return 0;
      }

      console.log('Found template:', template);

      // Get rates for this template
      const rates = driverPricingRates[template.id];
      if (!rates || rates.length === 0) {
        console.error('No rates found for template:', templateName, 'template.id:', template.id);
        toast.error(t('admin_toast_no_rates_for_template', { name: templateName }));
        return 0;
      }

      console.log('Found rates:', rates);

      // Find the rate that matches the distance
      let matchingRate = null;
      for (const rate of rates) {
        if (rate.max_distance === null) {
          // This is the "1001+ km" rate
          if (distance >= rate.min_distance) {
            matchingRate = rate;
            break;
          }
        } else {
          // Regular range
          if (distance >= rate.min_distance && distance <= rate.max_distance) {
            matchingRate = rate;
            break;
          }
        }
      }

      if (!matchingRate) {
        console.error('No matching rate found for distance:', distance);
        toast.error(t('admin_toast_no_rate_for_km', { distance }));
        return 0;
      }

      console.log('Matching rate:', matchingRate);

      // Get price based on service type
      let basePrice = 0;
      let pricePerKm = null;
      
      switch(serviceType) {
        case 'delivery':
          basePrice = matchingRate.delivery_price;
          pricePerKm = matchingRate.delivery_price_per_km;
          break;
        case 'bc_service':
          basePrice = matchingRate.bc_service_price;
          pricePerKm = matchingRate.bc_service_price_per_km;
          break;
        case 'tradein':
          basePrice = matchingRate.tradein_price;
          pricePerKm = matchingRate.tradein_price_per_km;
          break;
        case 'representacion':
          basePrice = matchingRate.representacion_price;
          break;
        default:
          console.error('Unknown service type:', serviceType);
          toast.error(t('admin_toast_unknown_service_type', { type: serviceType }));
          return 0;
      }

      // If there's a price per km (for distances > 1000 km), calculate
      let finalPrice = basePrice;
      if (pricePerKm && matchingRate.max_distance === null && distance > 1000) {
        const extraKm = distance - 1000;
        finalPrice = basePrice + (extraKm * pricePerKm);
      }

      console.log('Final calculated price:', finalPrice);
      return parseFloat(finalPrice.toFixed(2));
    } catch (error) {
      console.error('Error calculating price:', error);
      toast.error(t('admin_toast_error_calculate_price_message', { message: error.message }));
      return 0;
    }
  };

  // Handle pricing template selection
  const handleSelectPricingTemplate = async (templateName) => {
    try {
      console.log('=== handleSelectPricingTemplate called ===');
      console.log('Template:', templateName);
      console.log('Distance:', orderUpdateForm.distance_km);
      console.log('Service Type:', orderUpdateForm.service_type);
      
      // Validate inputs
      if (!orderUpdateForm.distance_km || orderUpdateForm.distance_km === 0) {
        toast.error(t('admin_toast_enter_distance_before_rate'));
        return;
      }
      
      if (!orderUpdateForm.service_type) {
        toast.error(t('admin_toast_select_type_before_rate'));
        return;
      }
      
      const price = await calculateDriverPriceForTemplate(templateName, orderUpdateForm.distance_km, orderUpdateForm.service_type);
      console.log('Calculated price:', price);
      
      if (price === 0) {
        toast.error(t('admin_toast_could_not_calculate_price'));
        return;
      }
      
      setOrderUpdateForm({ 
        ...orderUpdateForm, 
        driver_pricing_template_id: templateName,
        driver_price: price,
        is_driver_price_custom: false
      });
      
      toast.success(t('admin_toast_driver_tariff_selected', { label: templateName, price: price.toFixed(2) }));
    } catch (error) {
      console.error('Error in handleSelectPricingTemplate:', error);
      toast.error(t('admin_toast_error_calculate_rate_message', { message: error.message }));
    }
  };
  
  // Auto-calculate all pricing options when distance/service_type changes
  React.useEffect(() => {
    console.log('=== Auto-calculating prices ===');
    console.log('Distance:', orderUpdateForm.distance_km);
    console.log('Service Type:', orderUpdateForm.service_type);
    console.log('driverPricingRates keys:', Object.keys(driverPricingRates));
    console.log('driverPricingTemplates:', driverPricingTemplates);
    
    if (orderUpdateForm.distance_km > 0 && orderUpdateForm.service_type && Object.keys(driverPricingRates).length > 0) {
      const newPrices = {};
      const distance = orderUpdateForm.distance_km;
      const serviceType = orderUpdateForm.service_type;
      
      // Para cada plantilla, buscar el precio
      Object.keys(driverPricingRates).forEach(templateId => {
        const rates = driverPricingRates[templateId];
        const template = driverPricingTemplates.find(t => t.id === templateId);
        
        console.log(`Processing template ID: ${templateId}`);
        console.log('Template:', template);
        console.log('Rates count:', rates?.length);
        
        if (template && rates && rates.length > 0) {
          // Encontrar el rango correcto de distancia
          const matchingRate = rates.find(r => {
            if (r.max_distance === null) {
              return distance >= r.min_distance;
            } else {
              return distance >= r.min_distance && distance <= r.max_distance;
            }
          });
          
          console.log('Matching rate for distance', distance, ':', matchingRate);
          
          if (matchingRate) {
            // Obtener precio según tipo de servicio
            let price = 0;
            if (serviceType === 'delivery') price = matchingRate.delivery_price;
            else if (serviceType === 'bc_service') price = matchingRate.bc_service_price;
            else if (serviceType === 'tradein') price = matchingRate.tradein_price;
            else if (serviceType === 'representacion') price = matchingRate.representacion_price;
            
            console.log(`Price for ${template.name}: ${price}`);
            
            // Guardar con el nombre de la plantilla en minúsculas Y con el ID del template
            const nk = normalizeDriverTemplateKey(template.name);
            newPrices[nk] = price;
            newPrices[templateId] = { name: template.name, price: price };
          }
        }
      });
      
      console.log('Final calculated prices:', newPrices);
      setCalculatedPrices(newPrices);
    } else {
      console.log('Conditions not met for calculation');
    }
  }, [orderUpdateForm.distance_km, orderUpdateForm.service_type, driverPricingTemplates, driverPricingRates]);

  // Calculate suggested driver prices for an order
  const calculateSuggestedPrices = (order) => {
    if (!order.distance_km || !order.service_type || Object.keys(driverPricingRates).length === 0) {
      return null;
    }

    const prices = {};
    const distance = order.distance_km;
    const serviceType = order.service_type;

    Object.keys(driverPricingRates).forEach(templateId => {
      const rates = driverPricingRates[templateId];
      const template = driverPricingTemplates.find(t => t.id === templateId);

      if (template && rates && rates.length > 0) {
        const matchingRate = rates.find(r => {
          if (r.max_distance === null) {
            return distance >= r.min_distance;
          } else {
            return distance >= r.min_distance && distance <= r.max_distance;
          }
        });

        if (matchingRate) {
          let price = 0;
          if (serviceType === 'delivery') price = matchingRate.delivery_price;
          else if (serviceType === 'bc_service') price = matchingRate.bc_service_price;
          else if (serviceType === 'tradein') price = matchingRate.tradein_price;
          else if (serviceType === 'representacion') price = matchingRate.representacion_price;

          prices[normalizeDriverTemplateKey(template.name)] = price;
        }
      }
    });

    return prices;
  };

  // Expose to window for debugging
  React.useEffect(() => {
    window.handleSelectPricingTemplate = handleSelectPricingTemplate;
  }, [orderUpdateForm]);


  // Handle edit rate
  const handleEditRate = async (rate) => {
    setEditingRate({...rate});
    setShowEditRateDialog(true);
  };

  // Save edited rate
  const handleSaveEditedRate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/driver-pricing-rates/${editingRate.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            template_id: editingRate.template_id,
            min_distance: editingRate.min_distance,
            max_distance: editingRate.max_distance,
            delivery_price: parseFloat(editingRate.delivery_price),
            bc_service_price: parseFloat(editingRate.bc_service_price),
            tradein_price: parseFloat(editingRate.tradein_price),
            representacion_price: parseFloat(editingRate.representacion_price),
            delivery_price_per_km: editingRate.delivery_price_per_km ? parseFloat(editingRate.delivery_price_per_km) : null,
            bc_service_price_per_km: editingRate.bc_service_price_per_km ? parseFloat(editingRate.bc_service_price_per_km) : null,
            tradein_price_per_km: editingRate.tradein_price_per_km ? parseFloat(editingRate.tradein_price_per_km) : null
          })
        }
      );

      if (response.ok) {
        toast.success(t('admin_toast_rate_updated'));
        setShowEditRateDialog(false);
        
        // Reload rates for the current template
        const ratesResponse = await fetch(
          `${BACKEND_URL}/api/driver-pricing-templates/${selectedTemplateId}/rates`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (ratesResponse.ok) {
          const rates = await ratesResponse.json();
          setSelectedTemplateRates(rates);
        }
      } else {
        toast.error(t('admin_toast_error_update_rate'));
      }
    } catch (error) {
      console.error('Error updating rate:', error);
      toast.error(t('admin_toast_error_update_rate'));
    }
  };

  // Create new driver pricing template
  const handleCreateDriverPricingTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error(t('admin_toast_template_name_required'));
      return;
    }

    setCreatingTemplate(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Create template
      const templateResponse = await fetch(
        `${BACKEND_URL}/api/driver-pricing-templates`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: newTemplateName,
            description: newTemplateDescription
          })
        }
      );

      if (templateResponse.ok) {
        const newTemplate = await templateResponse.json();
        
        // Create default 17 rates for the new template (copy from Básica as base)
        const basicaTemplate = driverPricingTemplates.find(t => t.name === 'Básica');
        if (basicaTemplate) {
          const basicaRatesResponse = await fetch(
            `${BACKEND_URL}/api/driver-pricing-templates/${basicaTemplate.id}/rates`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          if (basicaRatesResponse.ok) {
            const basicaRates = await basicaRatesResponse.json();
            
            // Create rates for new template
            for (const rate of basicaRates) {
              await fetch(
                `${BACKEND_URL}/api/driver-pricing-rates`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    template_id: newTemplate.id,
                    min_distance: rate.min_distance,
                    max_distance: rate.max_distance,
                    delivery_price: rate.delivery_price,
                    bc_service_price: rate.bc_service_price,
                    tradein_price: rate.tradein_price,
                    representacion_price: rate.representacion_price,
                    delivery_price_per_km: rate.delivery_price_per_km,
                    bc_service_price_per_km: rate.bc_service_price_per_km,
                    tradein_price_per_km: rate.tradein_price_per_km
                  })
                }
              );
            }
          }
        }
        
        toast.success(t('admin_toast_template_created_short'));
        setShowCreateTemplateDialog(false);
        setNewTemplateName('');
        setNewTemplateDescription('');
        
        // Reload templates
        await loadData();
      } else {
        toast.error(t('admin_toast_error_create_template'));
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(t('admin_toast_error_create_template'));
    } finally {
      setCreatingTemplate(false);
    }
  };

  // Handle create new driver from order modal
  const handleCreateNewDriver = () => {
    setShowOrderUpdateDialog(false);
    setActiveTab('drivers');
    setShowDriverDialog(true);
  };

  // Filter orders function
  const getFilteredOrders = () => {
    return orders.filter(order => {
      const normalizedStatus = (order.status || '').toLowerCase();
      const isHistoricalByStatus =
        normalizedStatus === 'delivered' ||
        normalizedStatus === 'completed' ||
        normalizedStatus === 'cancelled' ||
        normalizedStatus.includes('cancelado');
      const isHistoricalOrder = order.approval_status === 'approved' || isHistoricalByStatus;

      // Filter by sub-tab: active or historical
      if (servicesSubTab === 'active') {
        // En Proceso: only non-finalized services.
        if (isHistoricalOrder) return false;
      } else if (servicesSubTab === 'historical') {
        // Histórico: approved services OR completed/cancelled by status.
        if (!isHistoricalOrder) return false;
      }

      // Search by client name or email
      if (filters.searchClient && 
          !order.client_name?.toLowerCase().includes(filters.searchClient.toLowerCase()) &&
          !order.client_email?.toLowerCase().includes(filters.searchClient.toLowerCase())) {
        return false;
      }

      // Search by plate
      if (filters.searchPlate && !order.car_plate?.toLowerCase().includes(filters.searchPlate.toLowerCase())) {
        return false;
      }

      // Filter by order status
      if (filters.orderStatus !== 'all' && order.status !== filters.orderStatus) {
        return false;
      }

      // Filter by payment status
      if (filters.paymentStatus !== 'all' && order.payment_status !== filters.paymentStatus) {
        return false;
      }

      // Filter by driver
      if (filters.driverFilter !== 'all') {
        if (filters.driverFilter === 'no-driver' && order.driver_id) {
          return false;
        }
        if (filters.driverFilter !== 'no-driver' && order.driver_id !== filters.driverFilter) {
          return false;
        }
      }

      // Filter by city
      if (filters.city !== 'all' && 
          !order.pickup_address?.toLowerCase().includes(filters.city.toLowerCase()) &&
          !order.delivery_address?.toLowerCase().includes(filters.city.toLowerCase())) {
        return false;
      }

      // Filter by date range
      if (filters.dateFrom || filters.dateTo) {
        const orderDate = new Date(order.created_at);
        if (filters.dateFrom && orderDate < new Date(filters.dateFrom)) return false;
        if (filters.dateTo) {
          const endDate = new Date(filters.dateTo);
          endDate.setHours(23, 59, 59, 999); // Include the entire end date
          if (orderDate > endDate) return false;
        }
      }

      return true;
    });
  };

  // Pagination helpers
  const getPaginatedItems = (items, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getTotalPages = (totalItems) => {
    return Math.ceil(totalItems / ITEMS_PER_PAGE);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setServicesPage(1);
    setHistoricalPage(1);
  }, [filters, servicesSubTab]);

  const getDocumentProgress = () => {
    const uploaded = requiredDocuments.filter(docType =>
      documents.some(d => d.document_type === docType)
    ).length;
    return { uploaded, total: requiredDocuments.length };
  };

  // Get driver profile status badge
  const getDriverStatusBadge = (driver) => {
    if (driver.documents_validated) {
      return {
        text: t('admin_driver_badge_validated'),
        color: 'bg-green-100 text-green-800 border-green-200',
      };
    }

    if (driver.has_pending_documents) {
      return {
        text: t('admin_driver_badge_pending'),
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      };
    }

    if (driver.has_expiring_documents) {
      return {
        text: t('admin_driver_badge_expires_soon'),
        color: 'bg-orange-100 text-orange-800 border-orange-200',
      };
    }

    return {
      text: t('admin_driver_badge_no_docs'),
      color: 'bg-red-100 text-red-800 border-red-200',
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AdminSidebar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        pendingApprovalCount={orders.filter(o => o.approval_status === 'pending_approval').length}
        incidentCount={incidentCount}
        adminType={adminType}
        canSeeClientPrices={canSeeClientPrices}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm flex-shrink-0 border-b border-gray-100">
          <div className="flex w-full min-w-0 items-center justify-between gap-4 px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-4 min-w-0">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-800 truncate">{t('dashboard')}</h2>
                {adminSessionEmail && (
                  <p className="text-xs text-gray-500 truncate" title={adminSessionEmail}>
                    {adminSessionEmail}
                    {adminType ? ` · ${t(adminType === 'super_admin' ? 'admin_super_admin' : adminType === 'admin_full' ? 'admin_full' : 'admin_sub_admin')}` : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0">
              <LanguageSwitcher />
              <Button onClick={handleLogout} variant="outline" size="sm" className="hidden sm:flex">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" className="sm:hidden p-2">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main content area — full width of column; same horizontal padding as header (no nested max-width) */}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          <div className="w-full px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{t('overview')}</h2>
                <p className="text-gray-600 mt-1">{t('monitor_performance')}</p>
              </div>

              {/* Stats Cards - Clicables */}
              <StatsCards 
                orders={orders}
                setActiveTab={setActiveTab}
                setServicesSubTab={setServicesSubTab}
                canSeeClientPrices={canSeeClientPrices}
              />


              {/* Expiring Documents Alert Widget */}
              {expiringDocuments && expiringDocuments.length > 0 && (
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 rounded-lg shadow-lg p-6 animate-pulse-slow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        ⚠️ {t('admin_overview_expiring_docs_title')}
                      </h3>
                      <p className="text-sm text-gray-700 mb-4">
                        {t('admin_overview_expiring_docs_intro', { count: expiringDocuments.length })}
                      </p>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {expiringDocuments.slice(0, 5).map((doc) => {
                          const daysUntilExpiry = Math.ceil(
                            (new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)
                          );
                          const isExpired = daysUntilExpiry < 0;
                          const docTypeKey =
                            doc.document_type === 'license_front' ? 'admin_doc_type_license_front'
                            : doc.document_type === 'license_back' ? 'admin_doc_type_license_back'
                            : doc.document_type === 'dni_front' ? 'admin_doc_type_dni_front'
                            : doc.document_type === 'dni_back' ? 'admin_doc_type_dni_back'
                            : doc.document_type === 'autonomo_certificate' ? 'admin_doc_type_autonomo_certificate'
                            : doc.document_type === 'ss_certificate' ? 'admin_doc_type_ss_certificate'
                            : doc.document_type === 'hacienda_certificate' ? 'admin_doc_type_hacienda_certificate'
                            : 'admin_doc_type_unknown';
                          
                          return (
                            <div key={doc.id} className="bg-white rounded-lg p-4 border border-orange-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">
                                    {doc.driver_name}
                                  </p>
                                  <p className="text-sm text-gray-600">{doc.driver_email}</p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                                      {t(docTypeKey)}
                                    </span>
                                    <span className={`text-xs font-bold ${
                                      isExpired ? 'text-red-600' : 'text-orange-600'
                                    }`}>
                                      {isExpired
                                        ? `❌ ${t('admin_overview_doc_status_expired', { days: Math.abs(daysUntilExpiry) })}`
                                        : `⏰ ${t('admin_overview_doc_status_expires', { days: daysUntilExpiry })}`
                                      }
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Find driver and open edit modal
                                    const driver = drivers.find(d => d.email === doc.driver_email);
                                    if (driver) {
                                      handleEditDriver(driver);
                                    }
                                  }}
                                  className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm font-medium"
                                >
                                  {t('admin_overview_view_driver')}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {expiringDocuments.length > 5 && (
                        <p className="text-sm text-gray-600 mt-3">
                          {t('admin_overview_more_documents', { count: expiringDocuments.length - 5 })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Orders */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{t('recent_orders')}</h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <div className="inline-block min-w-full align-middle">
                      <table className="data-table min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-2 sm:px-4">{t('admin_table_service_id')}</th>
                          <th className="px-2 sm:px-4">{t('admin_table_client')}</th>
                          <th className="hidden md:table-cell px-2 sm:px-4">{t('admin_table_plate')}</th>
                          <th className="hidden lg:table-cell px-2 sm:px-4">{t('admin_table_pickup')}</th>
                          <th className="hidden lg:table-cell px-2 sm:px-4">{t('admin_table_delivery')}</th>
                          <th className="px-2 sm:px-4">{t('admin_table_dates')}</th>
                          <th className="hidden md:table-cell px-2 sm:px-4">{t('admin_table_driver')}</th>
                          <th className="px-2 sm:px-4">{t('admin_table_status')}</th>
                          {canSeeClientPrices && <th className="hidden sm:table-cell px-2 sm:px-4">{t('admin_table_price')}</th>}
                          <th className="px-2 sm:px-4">{t('admin_table_action')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.slice(0, 5).map((order) => (
                          <tr 
                            key={order.id}
                            onClick={() => {
                              setActiveTab('orders');
                              setOrdersViewMode('list');
                              setServicesSubTab('active');
                              setTimeout(() => {
                                const element = document.querySelector(`[data-order-id="${order.id}"]`);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  element.style.boxShadow = '0 0 0 3px #36e961';
                                  setTimeout(() => {
                                    element.style.boxShadow = '';
                                  }, 2000);
                                }
                              }, 150);
                            }}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                            title={t('admin_recent_orders_row_title')}
                          >
                            <td className="font-mono text-xs sm:text-sm px-2 sm:px-4">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <span className="truncate max-w-[60px] sm:max-w-none">#{order.id.substring(0, 6)}</span>
                                <span className="text-xs font-medium px-1 sm:px-1.5 py-0.5 rounded whitespace-nowrap" style={{backgroundColor: '#36e961', color: 'white'}}>
                                  {t(order.service_type)}
                                </span>
                              </div>
                            </td>
                            <td className="text-xs sm:text-sm px-2 sm:px-4 max-w-[100px] sm:max-w-none truncate">{order.client_name}</td>
                            <td className="hidden md:table-cell text-xs sm:text-sm font-mono px-2 sm:px-4">{order.car_plate}</td>
                            <td className="hidden lg:table-cell text-xs max-w-[150px] truncate px-2 sm:px-4" title={order.pickup_address}>
                              {order.pickup_address.substring(0, 30)}...
                            </td>
                            <td className="hidden lg:table-cell text-xs max-w-[150px] truncate px-2 sm:px-4" title={order.delivery_address}>
                              {order.delivery_address.substring(0, 30)}...
                            </td>
                            <td className="text-xs px-2 sm:px-4">
                              <div className="whitespace-nowrap">📅 {adminOrderLegShortDate(order, 'pickup', dateLocale, t('gps_live_na'))}</div>
                              <div className="whitespace-nowrap">📦 {adminOrderLegShortDate(order, 'delivery', dateLocale, t('gps_live_na'))}</div>
                            </td>
                            <td className="hidden md:table-cell text-xs px-2 sm:px-4">
                              {order.driver_name ? (
                                <span className="text-green-700 font-medium truncate max-w-[80px] block">✓ {order.driver_name}</span>
                              ) : (
                                <span className="text-gray-400 text-xs">{t('no_driver_assigned')}</span>
                              )}
                            </td>
                            <td className="px-2 sm:px-4">
                              <div className="flex items-center gap-1">
                                <span className={`status-badge status-${order.status} text-xs`}>{t(order.status)}</span>
                                {order.has_incident && (
                                  <span className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" title={t('admin_dash_incident_tooltip')}>
                                    <AlertTriangle className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                            </td>
                            {canSeeClientPrices && (
                              <td className="hidden sm:table-cell font-semibold px-2 sm:px-4">
                                <div className="whitespace-nowrap">€{(order.price || order.total_price || 0).toFixed(2)}</div>
                                {order.distance_km && (
                                  <div className="text-xs text-gray-500 font-normal">{order.distance_km} km</div>
                                )}
                              </td>
                            )}
                            <td className="px-2 sm:px-4">
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs px-2 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click
                                    setSelectedOrder(order);
                                    const validStatuses = [
                                      'pending', 'confirmed', 'assigned', 'in_transit', 'delivered', 'cancelled',
                                      'Cancelado en Garantía', 'Cancelado con -24h antelación', 'Cancelado con +24h de antelación'
                                    ];
                                    const currentStatus = validStatuses.includes(order.status) ? order.status : 'pending';
                                    
                                    const assignedDriver = drivers.find(d => d.id === order.driver_id);
                                    setDriverSearchTerm(assignedDriver ? assignedDriver.name : '');
                                    setShowDriverOptions(false);
                                    
                                    setOrderUpdateForm({
                                      status: currentStatus,
                                      payment_status: order.payment_status || 'pending',
                                      driver_id: order.driver_id || '',
                                      client_id: order.client_id || '',
                                      notes: order.notes || '',
                                      car_plate: order.car_plate || '',
                                      car_brand: order.car_brand || '',
                                      car_model: order.car_model || '',
                                      pickup_address: order.pickup_address || '',
                                      delivery_address: order.delivery_address || '',
                                      pickup_contact_name: order.pickup_contact_name || '',
                                      pickup_contact_phone: order.pickup_contact_phone || '',
                                      delivery_contact_name: order.delivery_contact_name || '',
                                      delivery_contact_phone: order.delivery_contact_phone || '',
                                    point_c_address: order.point_c_address || '',
                                    point_c_lat: order.point_c_lat || null,
                                    point_c_lng: order.point_c_lng || null,
                                    point_c_contact_name: order.point_c_contact_name || '',
                                    point_c_contact_phone: order.point_c_contact_phone || '',
                                    second_car_type: order.second_vehicle?.car_type || 'Turismo',
                                    second_car_brand: order.second_vehicle?.car_brand || '',
                                    second_car_model: order.second_vehicle?.car_model || '',
                                    second_car_plate: order.second_vehicle?.car_plate || '',
                                    second_fuel_type: order.second_vehicle?.fuel_type || 'gasoline',
                                    first_vehicle_notes: order.first_vehicle_notes || '',
                                    second_vehicle_notes: order.second_vehicle_notes || '',
                                      selected_additional_services: order.additional_services ? order.additional_services.map(s => s.id || s) : [],
                                      // Dates and times - format for input fields
                                      pickup_date_from: formatDateForInput(order.pickup_date_from),
                                      pickup_date_to: formatDateForInput(order.pickup_date_to),
                                      pickup_time_start: formatTimeForInput(order.pickup_date_from, order.pickup_time_start),
                                      pickup_time_end: formatTimeForInput(order.pickup_date_to, order.pickup_time_end),
                                      delivery_date_from: formatDateForInput(order.delivery_date_from),
                                      delivery_date_to: formatDateForInput(order.delivery_date_to),
                                      delivery_time_start: formatTimeForInput(order.delivery_date_from, order.delivery_time_start),
                                      delivery_time_end: formatTimeForInput(order.delivery_date_to, order.delivery_time_end),
                                      service_date: formatDateForInput(order.service_date),
                                      pickup_fixed_date: order.pickup_fixed_date || false,
                                      delivery_fixed_date: order.delivery_fixed_date || false,
                                      // Service details for pricing
                                      distance_km: order.distance_km || 0,
                                      service_type: order.service_type || '',
                                      // Client pricing - IMPORTANT: preserve base_price and total_price
                                      base_price: order.base_price || order.price || 0,
                                      total_price: order.total_price || order.price || 0,
                                      // Driver pricing - preserve existing driver tariff
                                      driver_price: order.driver_price || 0,
                                      driver_pricing_template_id: order.driver_pricing_template_id || null,
                                      is_driver_price_custom: order.is_driver_price_custom || false,
                                      // Driver extras - additional payments for driver
                                      driver_extras: order.driver_extras || []
                                    });
                                    // Set driver extras state for editing
                                    setDriverExtras(order.driver_extras || []);
                                    // Force recalculate prices after setting form
                                    setTimeout(() => {
                                      const dist = order.distance_km || 0;
                                      const svcType = order.service_type || '';
                                      if (dist > 0 && svcType && Object.keys(driverPricingRates).length > 0) {
                                        const newPrices = {};
                                        Object.keys(driverPricingRates).forEach(templateId => {
                                          const rates = driverPricingRates[templateId];
                                          const template = driverPricingTemplates.find(t => t.id === templateId);
                                          if (template && rates && rates.length > 0) {
                                            const matchingRate = rates.find(r => {
                                              if (r.max_distance === null) return dist >= r.min_distance;
                                              return dist >= r.min_distance && dist <= r.max_distance;
                                            });
                                            if (matchingRate) {
                                              let price = 0;
                                              if (svcType === 'delivery') price = matchingRate.delivery_price;
                                              else if (svcType === 'bc_service') price = matchingRate.bc_service_price;
                                              else if (svcType === 'tradein') price = matchingRate.tradein_price;
                                              else if (svcType === 'representacion') price = matchingRate.representacion_price;
                                              newPrices[template.name.toLowerCase()] = price;
                                              newPrices[templateId] = { name: template.name, price: price };
                                            }
                                          }
                                        });
                                        if (Object.keys(newPrices).length > 0) {
                                          setCalculatedPrices(newPrices);
                                        }
                                      }
                                    }, 100);
                                    setShowOrderUpdateDialog(true);
                                  }}
                                  className="text-xs"
                                  title={t('edit_order')}
                                  aria-label={t('edit_order')}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                {order.payment_status === 'pending' && (
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markServiceAsPaid(order.id);
                                    }}
                                    className="text-xs bg-green-600 hover:bg-green-700 text-white"
                                    title={t('admin_mark_as_paid')}
                                    aria-label={t('admin_mark_as_paid')}
                                  >
                                    <CreditCard className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{t('calendar_title')}</h2>
                <p className="text-gray-600 mt-1">{t('calendar_subtitle')}</p>
              </div>

              <Card>
                <CardContent className="p-6 space-y-4">
                  <TimezoneBar />
                  <ServiceCalendar 
                    key={`calendar-${orders.length}-${orders.filter(o => o.service_date || o.pickup_date_from).length}`}
                    orders={orders}
                    onServiceClick={(order) => {
                      // Navigate to orders tab
                      setActiveTab('orders');
                      // Scroll to the specific order after a brief delay
                      setTimeout(() => {
                        const orderElement = document.querySelector(`[data-order-id="${order.id}"]`);
                        if (orderElement) {
                          orderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          // Highlight the order briefly
                          orderElement.style.backgroundColor = '#36e96120';
                          setTimeout(() => {
                            orderElement.style.backgroundColor = '';
                          }, 2000);
                        }
                      }, 100);
                    }}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6 sm:space-y-7 animate-fade-in pb-4 sm:pb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{t('orders_management')}</h2>
                  <p className="text-gray-600 mt-1">{t('view_manage_orders')}</p>
                </div>
                <div className="flex items-center gap-3">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setOrdersViewMode('kanban')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        ordersViewMode === 'kanban'
                          ? 'bg-white text-[#36e961] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid="view-mode-kanban"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      {t('admin_view_mode_kanban')}
                    </button>
                    <button
                      onClick={() => setOrdersViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        ordersViewMode === 'list'
                          ? 'bg-white text-[#36e961] shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid="view-mode-list"
                    >
                      <List className="w-4 h-4" />
                      {t('admin_list')}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowCreateServiceDialog(true)}
                    className="px-6 py-3 bg-[#36e961] text-white rounded-lg hover:bg-[#2dd351] transition-colors flex items-center gap-2 font-medium shadow-md"
                  >
                    <Plus className="w-5 h-5" />
                    {t('create_service')}
                  </button>
                </div>
              </div>

              {/* Sub-tabs for Active / Historical - Only show in list view or when in historical */}
              {(ordersViewMode === 'list' || servicesSubTab === 'historical') && (
              <div className="flex gap-2 border-b bg-white rounded-t-lg px-6 pt-4">
                <button
                  onClick={() => setServicesSubTab('active')}
                  className={`px-4 py-2 font-medium transition-colors relative ${
                    servicesSubTab === 'active'
                      ? 'text-[#36e961] border-b-2 border-[#36e961]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('admin_services_in_progress_tab')}
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                    {orders.filter(o => {
                      const s = (o.status || '').toLowerCase();
                      const isHistByStatus = s === 'delivered' || s === 'completed' || s === 'cancelled' || s.includes('cancelado');
                      return !(o.approval_status === 'approved' || isHistByStatus);
                    }).length}
                  </span>
                </button>
                <button
                  onClick={() => setServicesSubTab('historical')}
                  className={`px-4 py-2 font-medium transition-colors relative ${
                    servicesSubTab === 'historical'
                      ? 'text-[#36e961] border-b-2 border-[#36e961]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('admin_historical')}
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                    {orders.filter(o => {
                      const s = (o.status || '').toLowerCase();
                      const isHistByStatus = s === 'delivered' || s === 'completed' || s === 'cancelled' || s.includes('cancelado');
                      return o.approval_status === 'approved' || isHistByStatus;
                    }).length}
                  </span>
                </button>
              </div>
              )}

              {/* KANBAN VIEW - Only for active services */}
              {ordersViewMode === 'kanban' && servicesSubTab === 'active' && (
                <OrdersKanban
                  orders={orders}
                  drivers={drivers}
                  clients={clients}
                  downloadingContractOrderId={downloadingContractOrderId}
                  filters={filters}
                  setFilters={setFilters}
                  onEditOrder={(order) => {
                    setSelectedOrder(order);
                    const validStatuses = [
                      'pending', 'confirmed', 'assigned', 'in_transit', 'delivered', 'cancelled',
                      'Cancelado en Garantía', 'Cancelado con -24h antelación', 'Cancelado con +24h de antelación'
                    ];
                    const currentStatus = validStatuses.includes(order.status) ? order.status : 'pending';
                    
                    const assignedDriver = drivers.find(d => d.id === order.driver_id);
                    setDriverSearchTerm(assignedDriver ? assignedDriver.name : '');
                    setShowDriverOptions(false);
                    
                    setOrderUpdateForm({
                      status: currentStatus,
                      payment_status: order.payment_status || 'pending',
                      driver_id: order.driver_id || '',
                      car_plate: order.car_plate || '',
                      car_brand: order.car_brand || '',
                      car_model: order.car_model || '',
                      pickup_address: order.pickup_address || '',
                      delivery_address: order.delivery_address || '',
                      pickup_contact_name: order.pickup_contact_name || '',
                      pickup_contact_phone: order.pickup_contact_phone || '',
                      delivery_contact_name: order.delivery_contact_name || '',
                      delivery_contact_phone: order.delivery_contact_phone || '',
                      point_c_address: order.point_c_address || '',
                      point_c_lat: order.point_c_lat || null,
                      point_c_lng: order.point_c_lng || null,
                      point_c_contact_name: order.point_c_contact_name || '',
                      point_c_contact_phone: order.point_c_contact_phone || '',
                      second_car_type: order.second_vehicle?.car_type || 'Turismo',
                      second_car_brand: order.second_vehicle?.car_brand || '',
                      second_car_model: order.second_vehicle?.car_model || '',
                      second_car_plate: order.second_vehicle?.car_plate || '',
                      second_fuel_type: order.second_vehicle?.fuel_type || 'gasoline',
                      first_vehicle_notes: order.first_vehicle_notes || '',
                      second_vehicle_notes: order.second_vehicle_notes || '',
                      selected_additional_services: order.additional_services ? order.additional_services.map(s => s.id || s) : [],
                      pickup_date_from: formatDateForInput(order.pickup_date_from),
                      pickup_date_to: formatDateForInput(order.pickup_date_to),
                      pickup_time_start: formatTimeForInput(order.pickup_date_from, order.pickup_time_start),
                      pickup_time_end: formatTimeForInput(order.pickup_date_to, order.pickup_time_end),
                      delivery_date_from: formatDateForInput(order.delivery_date_from),
                      delivery_date_to: formatDateForInput(order.delivery_date_to),
                      delivery_time_start: formatTimeForInput(order.delivery_date_from, order.delivery_time_start),
                      delivery_time_end: formatTimeForInput(order.delivery_date_to, order.delivery_time_end),
                      service_date: formatDateForInput(order.service_date),
                      pickup_fixed_date: order.pickup_fixed_date || false,
                      delivery_fixed_date: order.delivery_fixed_date || false,
                      distance_km: order.distance_km || 0,
                      service_type: order.service_type || '',
                      // Client pricing - IMPORTANT: preserve base_price and total_price
                      base_price: order.base_price || order.price || 0,
                      total_price: order.total_price || order.price || 0,
                      // Driver pricing - preserve existing driver tariff
                      driver_price: order.driver_price || 0,
                      driver_pricing_template_id: order.driver_pricing_template_id || null,
                      is_driver_price_custom: order.is_driver_price_custom || false,
                      driver_extras: order.driver_extras || []
                    });
                    setDriverExtras(order.driver_extras || []);
                    setShowOrderUpdateDialog(true);
                  }}
                  onCompleteService={handleOpenCompleteService}
                  onViewHistory={handleViewServiceHistory}
                  onRevertToPending={handleRevertToPending}
                  onDeleteOrder={handleDeleteOrder}
                  onCancelOrder={handleCancelOrder}
                  onCancelAtPickup={handleCancelAtPickup}
                  onCancelInTransit={handleCancelInTransit}
                  onApproveTransitCancellation={handleApproveTransitCancellation}
                  onViewPhotos={(orderId) => {
                    setSelectedOrderForView(orderId);
                    loadAdminVehiclePhotos(orderId);
                    setShowAdminPhotosDialog(true);
                  }}
                  onViewExpenses={(orderId) => {
                    setSelectedOrderForView(orderId);
                    loadAdminOrderExpenses(orderId);
                    setShowAdminExpensesDialog(true);
                  }}
                  onDownloadContract={downloadOrderContractPdf}
                  onAddToCalendar={(order) => {
                    const calendarLink = generateGoogleCalendarLink(order);
                    window.open(calendarLink, '_blank');
                    toast.success(t('admin_toast_opening_calendar'));
                  }}
                  onAssignDriver={(order) => {
                    // Open edit dialog focused on driver assignment
                    setSelectedOrder(order);
                    const validStatuses = [
                      'pending', 'confirmed', 'assigned', 'in_transit', 'delivered', 'cancelled',
                      'Cancelado en Garantía', 'Cancelado con -24h antelación', 'Cancelado con +24h de antelación'
                    ];
                    const currentStatus = validStatuses.includes(order.status) ? order.status : 'pending';
                    
                    setDriverSearchTerm('');
                    setShowDriverOptions(true);
                    
                    setOrderUpdateForm({
                      status: currentStatus,
                      payment_status: order.payment_status || 'pending',
                      driver_id: order.driver_id || '',
                      car_plate: order.car_plate || '',
                      car_brand: order.car_brand || '',
                      car_model: order.car_model || '',
                      pickup_address: order.pickup_address || '',
                      delivery_address: order.delivery_address || '',
                      pickup_contact_name: order.pickup_contact_name || '',
                      pickup_contact_phone: order.pickup_contact_phone || '',
                      delivery_contact_name: order.delivery_contact_name || '',
                      delivery_contact_phone: order.delivery_contact_phone || '',
                      point_c_address: order.point_c_address || '',
                      point_c_lat: order.point_c_lat || null,
                      point_c_lng: order.point_c_lng || null,
                      point_c_contact_name: order.point_c_contact_name || '',
                      point_c_contact_phone: order.point_c_contact_phone || '',
                      second_car_type: order.second_vehicle?.car_type || 'Turismo',
                      second_car_brand: order.second_vehicle?.car_brand || '',
                      second_car_model: order.second_vehicle?.car_model || '',
                      second_car_plate: order.second_vehicle?.car_plate || '',
                      second_fuel_type: order.second_vehicle?.fuel_type || 'gasoline',
                      first_vehicle_notes: order.first_vehicle_notes || '',
                      second_vehicle_notes: order.second_vehicle_notes || '',
                      selected_additional_services: order.additional_services ? order.additional_services.map(s => s.id || s) : [],
                      pickup_date_from: formatDateForInput(order.pickup_date_from),
                      pickup_date_to: formatDateForInput(order.pickup_date_to),
                      pickup_time_start: formatTimeForInput(order.pickup_date_from, order.pickup_time_start),
                      pickup_time_end: formatTimeForInput(order.pickup_date_to, order.pickup_time_end),
                      delivery_date_from: formatDateForInput(order.delivery_date_from),
                      delivery_date_to: formatDateForInput(order.delivery_date_to),
                      delivery_time_start: formatTimeForInput(order.delivery_date_from, order.delivery_time_start),
                      delivery_time_end: formatTimeForInput(order.delivery_date_to, order.delivery_time_end),
                      service_date: formatDateForInput(order.service_date),
                      pickup_fixed_date: order.pickup_fixed_date || false,
                      delivery_fixed_date: order.delivery_fixed_date || false,
                      distance_km: order.distance_km || 0,
                      service_type: order.service_type || '',
                      // Client pricing - IMPORTANT: preserve base_price and total_price
                      base_price: order.base_price || order.price || 0,
                      total_price: order.total_price || order.price || 0,
                      // Driver pricing - preserve existing driver tariff
                      driver_price: order.driver_price || 0,
                      driver_pricing_template_id: order.driver_pricing_template_id || null,
                      is_driver_price_custom: order.is_driver_price_custom || false,
                      driver_extras: order.driver_extras || []
                    });
                    setDriverExtras(order.driver_extras || []);
                    setShowOrderUpdateDialog(true);
                    toast.info(t('admin_toast_select_driver_for_service'));
                  }}
                  onApproveService={async (order) => {
                    try {
                      const token = localStorage.getItem('admin_token');
                      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/approve`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (response.ok) {
                        toast.success(t('admin_toast_service_approved_completed'));
                        await loadData();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || t('admin_toast_error_approve_service'));
                      }
                    } catch (error) {
                      console.error('Error approving service:', error);
                      toast.error(t('admin_toast_error_approve_service'));
                    }
                  }}
                  canSeeClientPrices={canSeeClientPrices}
                  canDelete={canDelete}
                />
              )}

              {/* LIST VIEW - For both active and historical */}
              {(ordersViewMode === 'list' || servicesSubTab === 'historical') && (
              <>
              {/* Filters Panel */}
              <Card className="border-gray-200/80 shadow-sm">
                <CardHeader className="pb-3 sm:pb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold">{t('filters')}</h3>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-8 sm:pb-9">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
                    {/* Search by Client */}
                    <div>
                      <Label>{t('search_by_client')}</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder={t('search_by_client')}
                          value={filters.searchClient}
                          onChange={(e) => setFilters({ ...filters, searchClient: e.target.value })}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Search by Plate */}
                    <div>
                      <Label>{t('search_by_plate')}</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder={t('search_by_plate')}
                          value={filters.searchPlate}
                          onChange={(e) => setFilters({ ...filters, searchPlate: e.target.value })}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Filter by City */}
                    <div>
                      <Label>{t('filter_by_city')}</Label>
                      <Input
                        placeholder={t('filter_by_city')}
                        value={filters.city === 'all' ? '' : filters.city}
                        onChange={(e) => setFilters({ ...filters, city: e.target.value || 'all' })}
                      />
                    </div>

                    {/* Filter by Driver — searchable combobox (same pattern as Kanban) */}
                    <div>
                      <Label>{t('filter_by_driver')}</Label>
                      <Popover open={listDriverFilterOpen} onOpenChange={setListDriverFilterOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={listDriverFilterOpen}
                            className="h-10 w-full justify-between font-normal text-sm px-3"
                          >
                            {filters.driverFilter === 'all'
                              ? t('all_drivers')
                              : filters.driverFilter === 'no-driver'
                                ? t('no_driver_assigned')
                                : drivers.find((d) => d.id === filters.driverFilter)?.name ?? filters.driverFilter}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                          <Command
                            filter={(value, search) => {
                              if (value === 'all' || value === 'no-driver') return 1;
                              const driver = drivers.find((d) => d.id === value);
                              if (!driver) return 0;
                              if (!search) return 1;
                              return driver.name?.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                            }}
                          >
                            <CommandInput placeholder={t('search_driver')} />
                            <CommandList>
                              <CommandEmpty>{t('type_to_search')}</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  value="all"
                                  onSelect={() => {
                                    setFilters({ ...filters, driverFilter: 'all' });
                                    setListDriverFilterOpen(false);
                                  }}
                                >
                                  {t('all_drivers')}
                                </CommandItem>
                                <CommandItem
                                  value="no-driver"
                                  onSelect={() => {
                                    setFilters({ ...filters, driverFilter: 'no-driver' });
                                    setListDriverFilterOpen(false);
                                  }}
                                >
                                  {t('no_driver_assigned')}
                                </CommandItem>
                                {drivers.map((driver) => (
                                  <CommandItem
                                    key={driver.id}
                                    value={driver.id}
                                    onSelect={() => {
                                      setFilters({ ...filters, driverFilter: driver.id });
                                      setListDriverFilterOpen(false);
                                    }}
                                  >
                                    {driver.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Filter by Order Status */}
                    <div>
                      <Label>{t('filter_by_status')}</Label>
                      <Select value={filters.orderStatus} onValueChange={(value) => setFilters({ ...filters, orderStatus: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('all_statuses')}</SelectItem>
                          <SelectItem value="pending">{t('pending')}</SelectItem>
                          <SelectItem value="confirmed">{t('confirmed')}</SelectItem>
                          <SelectItem value="assigned">{t('assigned')}</SelectItem>
                          <SelectItem value="in_transit">{t('in_transit')}</SelectItem>
                          <SelectItem value="delivered">{t('delivered')}</SelectItem>
                          <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Filter by Payment Status */}
                    <div>
                      <Label>{t('filter_by_payment')}</Label>
                      <Select value={filters.paymentStatus} onValueChange={(value) => setFilters({ ...filters, paymentStatus: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('all_statuses')}</SelectItem>
                          <SelectItem value="pending">{t('payment_status_pending')}</SelectItem>
                          <SelectItem value="paid">{t('paid')}</SelectItem>
                          <SelectItem value="failed">{t('failed')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date From */}
                    <div>
                      <Label>{t('from_date')}</Label>
                      <Input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                      />
                    </div>

                    {/* Date To */}
                    <div>
                      <Label>{t('to_date')}</Label>
                      <Input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                      />
                    </div>

                    {/* Clear Filters Button */}
                    <div className="lg:col-span-4 flex justify-end">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setListDriverFilterOpen(false);
                          setFilters({
                          searchClient: '',
                          searchPlate: '',
                          orderStatus: 'all',
                          paymentStatus: 'all',
                          driverFilter: 'all',
                          city: 'all',
                          dateFrom: '',
                          dateTo: ''
                          });
                        }}
                      >
                        {t('clear_filters')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results Count */}
              <div className="text-sm text-gray-600 mb-4 sm:mb-5">
                {getFilteredOrders().length} {t('results')}
              </div>

              {/* Orders List */}
              <div className="space-y-4 sm:space-y-5" data-testid="admin-orders-list">
                {getFilteredOrders().length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-8 text-gray-500">
                      {t('no_results')}
                    </CardContent>
                  </Card>
                ) : (
                  getPaginatedItems(
                    getFilteredOrders(),
                    servicesSubTab === 'active' ? servicesPage : historicalPage
                  ).map((order) => (
                    (() => {
                      const normalizedStatus = (order.status || '').toLowerCase();
                      const isHistoricalByStatus =
                        normalizedStatus === 'delivered' ||
                        normalizedStatus === 'completed' ||
                        normalizedStatus === 'cancelled' ||
                        normalizedStatus.includes('cancelado');
                      const isHistoricalByApproval = order.approval_status === 'approved';
                      const classificationReason = isHistoricalByApproval
                        ? 'approval'
                        : (isHistoricalByStatus ? 'status' : 'active');
                      return (
                    <Card key={order.id} className="card-hover border-gray-200/80 shadow-sm overflow-hidden" data-order-id={order.id}>
                      <CardHeader className="pb-0 space-y-0 p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-2 pb-2 sm:pb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-base">{t('service')} #{order.id.substring(0, 8).toUpperCase()}</h3>
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{backgroundColor: '#36e961', color: 'white'}}>
                                  {t(order.service_type)}
                                </span>
                              </div>
                              {/* Driver info if assigned */}
                              {order.driver_name && (
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-600">👤 {order.driver_name}</span>
                                  {order.driver_phone && <span className="text-gray-600">📞 {order.driver_phone}</span>}
                                  {order.driver_dni && <span className="text-gray-600">• {order.driver_dni}</span>}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span>{t('calendar_client')} {order.client_name}</span>
                              <span>{t('admin_creation')} {new Date(order.created_at).toLocaleString(dateLocale)}</span>
                              {SHOW_ORDER_CLASSIFICATION_DEBUG && (
                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                                  {`debug:${classificationReason}`}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right space-y-1">
                            <div className="flex gap-2 justify-end items-center flex-wrap">
                              <span className={`status-badge status-${order.status} text-xs`}>{t(order.status)}</span>
                              <span className={`status-badge status-${order.payment_status} text-xs`}>{t(order.payment_status)}</span>
                              <SettlementApprovalChip order={order} t={t} />
                              {order.has_incident && (
                                <span className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" title={t('admin_order_incident_tooltip')}>
                                  <AlertTriangle className="w-3 h-3" /> {t('admin_order_incident_label')}
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1.5 justify-end">
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setSelectedOrder(order);
                                  const validStatuses = [
                                    'pending', 'confirmed', 'assigned', 'in_transit', 'delivered', 'cancelled',
                                    'Cancelado en Garantía', 'Cancelado con -24h antelación', 'Cancelado con +24h de antelación'
                                  ];
                                  const currentStatus = validStatuses.includes(order.status) ? order.status : 'pending';
                                  
                                  const assignedDriver = drivers.find(d => d.id === order.driver_id);
                                  setDriverSearchTerm(assignedDriver ? assignedDriver.name : '');
                                  setShowDriverOptions(false);
                                  
                                  setOrderUpdateForm({
                                    status: currentStatus,
                                    payment_status: order.payment_status || 'pending',
                                    driver_id: order.driver_id || '',
                                    car_plate: order.car_plate || '',
                                    car_brand: order.car_brand || '',
                                    car_model: order.car_model || '',
                                    pickup_address: order.pickup_address || '',
                                    delivery_address: order.delivery_address || '',
                                    pickup_contact_name: order.pickup_contact_name || '',
                                    pickup_contact_phone: order.pickup_contact_phone || '',
                                    delivery_contact_name: order.delivery_contact_name || '',
                                    delivery_contact_phone: order.delivery_contact_phone || '',
                                    point_c_address: order.point_c_address || '',
                                    point_c_lat: order.point_c_lat || null,
                                    point_c_lng: order.point_c_lng || null,
                                    point_c_contact_name: order.point_c_contact_name || '',
                                    point_c_contact_phone: order.point_c_contact_phone || '',
                                    second_car_type: order.second_vehicle?.car_type || 'Turismo',
                                    second_car_brand: order.second_vehicle?.car_brand || '',
                                    second_car_model: order.second_vehicle?.car_model || '',
                                    second_car_plate: order.second_vehicle?.car_plate || '',
                                    second_fuel_type: order.second_vehicle?.fuel_type || 'gasoline',
                                    first_vehicle_notes: order.first_vehicle_notes || '',
                                    second_vehicle_notes: order.second_vehicle_notes || '',
                                    selected_additional_services: order.additional_services ? order.additional_services.map(s => s.id || s) : [],
                                    // Dates and times - format for input fields
                                    pickup_date_from: formatDateForInput(order.pickup_date_from),
                                    pickup_date_to: formatDateForInput(order.pickup_date_to),
                                    pickup_time_start: formatTimeForInput(order.pickup_date_from, order.pickup_time_start),
                                    pickup_time_end: formatTimeForInput(order.pickup_date_to, order.pickup_time_end),
                                    delivery_date_from: formatDateForInput(order.delivery_date_from),
                                    delivery_date_to: formatDateForInput(order.delivery_date_to),
                                    delivery_time_start: formatTimeForInput(order.delivery_date_from, order.delivery_time_start),
                                    delivery_time_end: formatTimeForInput(order.delivery_date_to, order.delivery_time_end),
                                    service_date: formatDateForInput(order.service_date),
                                    pickup_fixed_date: order.pickup_fixed_date || false,
                                    delivery_fixed_date: order.delivery_fixed_date || false,
                                    // Service details for pricing
                                    distance_km: order.distance_km || 0,
                                    service_type: order.service_type || '',
                                    // Client pricing - IMPORTANT: preserve base_price and total_price
                                    base_price: order.base_price || order.price || 0,
                                    total_price: order.total_price || order.price || 0,
                                    // Driver pricing - preserve existing driver tariff
                                    driver_price: order.driver_price || 0,
                                    driver_pricing_template_id: order.driver_pricing_template_id || null,
                                    is_driver_price_custom: order.is_driver_price_custom || false,
                                    driver_extras: order.driver_extras || []
                                  });
                                  setDriverExtras(order.driver_extras || []);
                                  setShowOrderUpdateDialog(true);
                                }}
                                data-testid={`edit-order-${order.id}`}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                {t('edit')}
                              </Button>
                              
                              {/* Complete Service button - only for non-delivered orders */}
                              {order.status !== 'delivered' && order.status !== 'cancelled' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-green-500 text-green-700 hover:bg-green-50"
                                  onClick={() => handleOpenCompleteService(order)}
                                  data-testid={`complete-order-${order.id}`}
                                  title={t('admin_complete_service_title')}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {t('admin_complete')}
                                </Button>
                              )}
                              
                              {/* View History button - only for delivered orders */}
                              {order.status === 'delivered' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-blue-500 text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleViewServiceHistory(order)}
                                  data-testid={`history-order-${order.id}`}
                                  title={t('admin_view_history_title')}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  {t('admin_history')}
                                </Button>
                              )}
                              
                              {/* Revert to Pending button - for delivered/completed orders */}
                              {(order.status === 'delivered' || order.status === 'completed') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-orange-500 text-orange-700 hover:bg-orange-50"
                                  onClick={() => handleRevertToPending(order)}
                                  data-testid={`revert-order-${order.id}`}
                                  title={t('admin_revert_title')}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  {t('admin_revert')}
                                </Button>
                              )}
                              
                              {/* Delete button - only if admin can delete */}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-red-300 hover:bg-red-50"
                                  onClick={() => handleDeleteOrder(order.id)}
                                  data-testid={`delete-order-${order.id}`}
                                  title={t('admin_delete_service_title')}
                                >
                                  <Trash2 className="w-3 h-3 mr-1 text-red-600" />
                                  {t('delete')}
                                </Button>
                              )}
                              
                              {/* Cancel buttons - different types */}
                              {!order.status?.includes('Cancelado') && 
                               order.status !== 'cancelled' && 
                               order.status !== 'delivered' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => handleCancelOrder(order)}
                                    data-testid={`cancel-order-${order.id}`}
                                    title={t('admin_title_cancellation_normal')}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    {t('cancel')}
                                  </Button>
                                  
                                  {/* Only show special cancel options for assigned/in_transit orders */}
                                  {(order.status === 'assigned' || order.status === 'in_transit') && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => handleCancelAtPickup(order)}
                                        title={t('admin_title_cancellation_vehicle_unavailable_pickup')}
                                      >
                                        🚫 {t('admin_cancel_at_pickup')}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => handleCancelInTransit(order)}
                                        title={t('admin_title_cancellation_during_transit')}
                                      >
                                        🛑 {t('admin_cancel_in_transit')}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              )}
                              
                              {/* Approve button for transit cancellations */}
                              {order.status === 'Cancelado en Transporte - Pendiente' && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleApproveTransitCancellation(order)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Aprobar
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3 border-t border-gray-100 pt-3 sm:pt-4">
                        {/* Current Driver Pricing Display */}
                        {order.driver_price && (
                          <div className="p-3 sm:p-4 bg-green-50/90 rounded-xl border border-green-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">✓</span>
                                <p className="text-xs font-semibold text-gray-700">
                                  {t('admin_rate_assigned_driver')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {order.is_driver_price_custom ? (
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{t('admin_customized')}</span>
                                ) : order.driver_pricing_template_id && (
                                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                    {driverPricingTemplates.find(t => t.id === order.driver_pricing_template_id)?.name || t('admin_pricing_template_fallback')}
                                  </span>
                                )}
                                <span className="text-sm font-bold text-green-700">€{order.driver_price.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Suggested Driver Pricing - ALWAYS SHOW - Inline calculation */}
                        {(() => {
                          // Always show pricing buttons, even if data is incomplete
                          const hasCompleteData = order.distance_km && order.service_type && Object.keys(driverPricingRates).length > 0;
                          
                          const suggestedPrices = {};
                          
                          // Debug logging
                          if (order.id.startsWith('3b1bc016')) {
                            console.log('=== DEBUG PRICING CALCULATION ===');
                            console.log('Order ID:', order.id);
                            console.log('Distance:', order.distance_km);
                            console.log('Service Type:', order.service_type);
                            console.log('Has Pricing Rates:', Object.keys(driverPricingRates).length);
                            console.log('hasCompleteData:', hasCompleteData);
                          }
                          
                          if (hasCompleteData) {
                            const distance = order.distance_km;
                            const serviceType = order.service_type;

                            Object.keys(driverPricingRates).forEach(templateId => {
                              const rates = driverPricingRates[templateId];
                              const template = driverPricingTemplates.find(t => t.id === templateId);

                              if (template && rates && rates.length > 0) {
                                const matchingRate = rates.find(r => {
                                  if (r.max_distance === null) {
                                    return distance >= r.min_distance;
                                  } else {
                                    return distance >= r.min_distance && distance <= r.max_distance;
                                  }
                                });

                                if (matchingRate) {
                                  let price = 0;
                                  if (serviceType === 'delivery') price = matchingRate.delivery_price;
                                  else if (serviceType === 'bc_service') price = matchingRate.bc_service_price;
                                  else if (serviceType === 'tradein') price = matchingRate.tradein_price;
                                  else if (serviceType === 'representacion') price = matchingRate.representacion_price;

                                  suggestedPrices[normalizeDriverTemplateKey(template.name)] = price;
                                  
                                  if (order.id.startsWith('3b1bc016')) {
                                    console.log(`${template.name}: €${price}`);
                                  }
                                }
                              }
                            });
                            
                            if (order.id.startsWith('3b1bc016')) {
                              console.log('Suggested Prices:', suggestedPrices);
                            }
                          }

                          const basicaT = driverPricingTemplates.find((x) => normalizeDriverTemplateKey(x.name) === 'basica');
                          const premiumT = driverPricingTemplates.find((x) => normalizeDriverTemplateKey(x.name) === 'premium');
                          const jluisT = driverPricingTemplates.find((x) => normalizeDriverTemplateKey(x.name) === 'jluis');

                          // ALWAYS render the buttons container
                          return (
                            <div className="p-3 sm:p-4 bg-purple-50/90 rounded-xl border border-purple-200">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-sm">💰</span>
                                <p className="text-xs font-semibold text-gray-700">
                                  {hasCompleteData ? t('admin_rate_suggestions_driver') : t('admin_rate_for_driver_incomplete')}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {/* Básica */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const basicaTemplate = driverPricingTemplates.find(
                                        (t) => normalizeDriverTemplateKey(t.name) === 'basica'
                                      );
                                      const price = basicaTemplate
                                        ? suggestedPrices[normalizeDriverTemplateKey(basicaTemplate.name)]
                                        : undefined;
                                      
                                      if (!basicaTemplate) {
                                        toast.error(t('admin_toast_template_basic_not_found'));
                                        return;
                                      }
                                      
                                      if (!price) {
                                        toast.error(t('admin_toast_complete_distance_and_type'));
                                        return;
                                      }
                                      
                                      const token = localStorage.getItem('admin_token');
                                      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}`, {
                                        method: 'PUT',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          driver_price: price,
                                          driver_pricing_template_id: basicaTemplate.id,
                                          is_driver_price_custom: false
                                        })
                                      });
                                      
                                      if (response.ok) {
                                        toast.success(t('admin_toast_driver_tariff_assigned_named', { label: t('admin_tariff_name_basic'), amount: price.toFixed(2) }));
                                        await loadData();
                                      } else {
                                        toast.error(t('admin_toast_error_assign_rate'));
                                      }
                                    } catch (error) {
                                      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                    }
                                  }}
                                  className={`flex flex-col items-center p-2 sm:p-2.5 bg-white rounded border-2 transition-all cursor-pointer ${
                                    order.driver_pricing_template_id ===
                                      driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'basica')
                                        ?.id && !order.is_driver_price_custom
                                      ? 'border-green-500 bg-green-100 ring-2 ring-green-300'
                                      : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
                                  }`}
                                >
                                  <span className="text-xs font-semibold text-gray-900 leading-tight text-center">
                                    {t('admin_tariff_name_basic')}
                                  </span>
                                  <span className="text-sm font-bold text-green-600">
                                    {basicaT && suggestedPrices[normalizeDriverTemplateKey(basicaT.name)]
                                      ? `€${suggestedPrices[normalizeDriverTemplateKey(basicaT.name)].toFixed(2)}`
                                      : '—'}
                                  </span>
                                  {order.driver_pricing_template_id ===
                                    driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'basica')
                                      ?.id && !order.is_driver_price_custom && (
                                    <span className="text-xs text-green-700 mt-1">✓ {t('admin_active')}</span>
                                  )}
                                </button>
                                
                                {/* Premium */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const premiumTemplate = driverPricingTemplates.find(
                                        (t) => normalizeDriverTemplateKey(t.name) === 'premium'
                                      );
                                      const price = premiumTemplate
                                        ? suggestedPrices[normalizeDriverTemplateKey(premiumTemplate.name)]
                                        : undefined;
                                      
                                      if (!premiumTemplate) {
                                        toast.error(t('admin_toast_template_premium_not_found'));
                                        return;
                                      }
                                      
                                      if (!price) {
                                        toast.error(t('admin_toast_complete_distance_and_type'));
                                        return;
                                      }
                                      
                                      const token = localStorage.getItem('admin_token');
                                      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}`, {
                                        method: 'PUT',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          driver_price: price,
                                          driver_pricing_template_id: premiumTemplate.id,
                                          is_driver_price_custom: false
                                        })
                                      });
                                      
                                      if (response.ok) {
                                        toast.success(t('admin_toast_driver_tariff_assigned_named', { label: t('admin_tariff_name_premium'), amount: price.toFixed(2) }));
                                        await loadData();
                                      } else {
                                        toast.error(t('admin_toast_error_assign_rate'));
                                      }
                                    } catch (error) {
                                      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                    }
                                  }}
                                  className={`flex flex-col items-center p-2 sm:p-2.5 bg-white rounded border-2 transition-all cursor-pointer ${
                                    order.driver_pricing_template_id ===
                                      driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'premium')
                                        ?.id && !order.is_driver_price_custom
                                      ? 'border-green-500 bg-green-100 ring-2 ring-green-300'
                                      : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
                                  }`}
                                >
                                  <span className="text-xs font-semibold text-gray-900 leading-tight text-center">
                                    {t('admin_tariff_name_premium')}
                                  </span>
                                  <span className="text-sm font-bold text-green-600">
                                    {premiumT && suggestedPrices[normalizeDriverTemplateKey(premiumT.name)]
                                      ? `€${suggestedPrices[normalizeDriverTemplateKey(premiumT.name)].toFixed(2)}`
                                      : '—'}
                                  </span>
                                  {order.driver_pricing_template_id ===
                                    driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'premium')
                                      ?.id && !order.is_driver_price_custom && (
                                    <span className="text-xs text-green-700 mt-1">✓ {t('admin_active')}</span>
                                  )}
                                </button>
                                
                                {/* JLUIS */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const jluisTemplate = driverPricingTemplates.find(
                                        (t) => normalizeDriverTemplateKey(t.name) === 'jluis'
                                      );
                                      const price = jluisTemplate
                                        ? suggestedPrices[normalizeDriverTemplateKey(jluisTemplate.name)]
                                        : undefined;
                                      
                                      if (!jluisTemplate) {
                                        toast.error(t('admin_toast_template_jluis_not_found'));
                                        return;
                                      }
                                      
                                      if (!price) {
                                        toast.error(t('admin_toast_complete_distance_and_type'));
                                        return;
                                      }
                                      
                                      const token = localStorage.getItem('admin_token');
                                      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}`, {
                                        method: 'PUT',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          driver_price: price,
                                          driver_pricing_template_id: jluisTemplate.id,
                                          is_driver_price_custom: false
                                        })
                                      });
                                      
                                      if (response.ok) {
                                        toast.success(t('admin_toast_driver_tariff_assigned_named', { label: t('admin_tariff_name_jluis'), amount: price.toFixed(2) }));
                                        await loadData();
                                      } else {
                                        toast.error(t('admin_toast_error_assign_rate'));
                                      }
                                    } catch (error) {
                                      toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                    }
                                  }}
                                  className={`flex flex-col items-center p-2 sm:p-2.5 bg-white rounded border-2 transition-all cursor-pointer ${
                                    order.driver_pricing_template_id ===
                                      driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'jluis')
                                        ?.id && !order.is_driver_price_custom
                                      ? 'border-green-500 bg-green-100 ring-2 ring-green-300'
                                      : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
                                  }`}
                                >
                                  <span className="text-xs font-semibold text-gray-900 leading-tight text-center">
                                    {t('admin_tariff_name_jluis')}
                                  </span>
                                  <span className="text-sm font-bold text-green-600">
                                    {jluisT && suggestedPrices[normalizeDriverTemplateKey(jluisT.name)]
                                      ? `€${suggestedPrices[normalizeDriverTemplateKey(jluisT.name)].toFixed(2)}`
                                      : '—'}
                                  </span>
                                  {order.driver_pricing_template_id ===
                                    driverPricingTemplates.find((t) => normalizeDriverTemplateKey(t.name) === 'jluis')
                                      ?.id && !order.is_driver_price_custom && (
                                    <span className="text-xs text-green-700 mt-1">✓ {t('admin_active')}</span>
                                  )}
                                </button>
                                
                                {/* Personalizado */}
                                <div className={`flex flex-col items-center p-2 sm:p-2.5 bg-white rounded border-2 transition-all ${
                                  order.is_driver_price_custom
                                    ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-300'
                                    : 'border-blue-300'
                                }`}>
                                  <span className="text-xs font-semibold text-gray-900 mb-1 leading-tight text-center">
                                    {t('admin_order_tariff_custom')}
                                  </span>
                                  <div className="flex items-center gap-1 w-full min-w-0">
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder={order.is_driver_price_custom ? `€${order.driver_price?.toFixed(2)}` : "€"}
                                      className="min-w-0 flex-1 max-w-[7rem] text-xs p-1.5 border rounded text-center"
                                      id={`custom-price-${order.id}`}
                                      defaultValue=""
                                      disabled={order.is_driver_price_custom}
                                    />
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          const inputElement = document.getElementById(`custom-price-${order.id}`);
                                          const customPrice = parseFloat(inputElement.value);
                                          
                                          if (!customPrice || customPrice <= 0) {
                                            toast.error(t('admin_toast_enter_valid_price'));
                                            return;
                                          }
                                          
                                          const token = localStorage.getItem('admin_token');
                                          const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}`, {
                                            method: 'PUT',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({
                                              driver_price: customPrice,
                                              driver_pricing_template_id: null,
                                              is_driver_price_custom: true
                                            })
                                          });
                                          
                                          if (response.ok) {
                                            toast.success(t('admin_toast_driver_tariff_assigned_custom', { amount: customPrice.toFixed(2) }));
                                            inputElement.value = '';
                                            await loadData();
                                        } else {
                                          toast.error(t('admin_toast_error_assign_rate'));
                                        }
                                      } catch (error) {
                                        toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                      }
                                    }}
                                      className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                                      disabled={order.is_driver_price_custom}
                                    >
                                      {order.is_driver_price_custom ? '✓' : '✓'}
                                    </button>
                                  </div>
                                  {order.is_driver_price_custom && (
                                    <span className="text-xs text-blue-700 mt-1">✓ {t('admin_active')}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Precio, Distancia y Servicios Adicionales en el header */}
                        <div className="flex flex-wrap items-center gap-4 pt-1">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-xs text-gray-600">{t('price')}</p>
                              <p className="text-base font-bold" style={{color: '#36e961'}}>€{(order.total_price || order.price || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">{t('calculated_distance')}</p>
                              <p className="text-sm font-semibold text-gray-900">{order.distance_km ? `${order.distance_km} km` : t('admin_not_calculated')}</p>
                            </div>
                          </div>
                          {order.additional_services && order.additional_services.length > 0 && (
                            <div className="flex flex-col">
                              <p className="text-xs text-gray-600 mb-1">{t('admin_additional_services')}</p>
                              <div className="flex flex-wrap items-center gap-2">
                                {order.additional_services.map((service, index) => (
                                  <span key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200 text-xs">
                                    <span className="text-base">{getServiceEmoji(service.name)}</span>
                                    <span className="text-gray-700 font-medium">{translateAdditionalServiceName(service.name, t)}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-4 pb-6 px-5 sm:px-7">
                        <div className="space-y-4 sm:space-y-5">
                          {/* Fechas y Horas */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 sm:p-4 bg-blue-50/80 rounded-xl border border-blue-100 text-sm leading-relaxed">
                            <div>
                              <span className="font-semibold">📅 {t('client_pickup_label')}</span>{' '}
                              {adminOrderLegScheduleLine(order, 'pickup', dateLocale, t('gps_live_na'))}
                            </div>
                            <div>
                              <span className="font-semibold">📅 {t('client_delivery_label')}</span>{' '}
                              {adminOrderLegScheduleLine(order, 'delivery', dateLocale, t('gps_live_na'))}
                            </div>
                          </div>

                          {/* Información Principal */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50/80 rounded-xl border border-gray-100 text-sm leading-relaxed">
                            <div>
                              <p className="font-semibold text-gray-700 flex items-center gap-1">
                                <MapPin className="w-3 h-3" style={{color: '#36e961'}} /> {t('client_pickup_label').replace(/:\s*$/, '')}
                              </p>
                              <p className="text-gray-700 truncate">{order.pickup_address}</p>
                              <p className="text-gray-500">{formatAdminContactLine(order.pickup_contact_name, order.pickup_contact_phone, t('gps_live_na'))}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-700 flex items-center gap-1">
                                <MapPin className="w-3 h-3" style={{color: '#8b5cf6'}} /> {t('client_delivery_label').replace(/:\s*$/, '')}
                              </p>
                              <p className="text-gray-700 truncate">{order.delivery_address}</p>
                              <p className="text-gray-500">{formatAdminContactLine(order.delivery_contact_name, order.delivery_contact_phone, t('gps_live_na'))}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-700 flex items-center gap-1">
                                <Car className="w-3 h-3" style={{color: '#36e961'}} /> {t('admin_vehicle_label')}
                              </p>
                              <p className="text-gray-700">{order.car_brand} {order.car_model}</p>
                              <p className="text-gray-500">{order.car_plate} • {t(order.fuel_type)}</p>
                            </div>
                          </div>
                          
                          {/* Second vehicle info for B-C / Trade-In */}
                          {(order.service_type === 'bc_service' || order.service_type === 'tradein') && order.second_vehicle && (
                            <div className="p-4 bg-orange-50/90 border border-orange-200 rounded-xl">
                              <p className="font-semibold text-orange-800 mb-2 flex items-center gap-1">
                                <Car className="w-4 h-4" /> {t('second_vehicle')}
                              </p>
                              <div className="grid md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="font-medium text-orange-700">{t('admin_notes_vehicle2')}</p>
                                  <p className="text-gray-700">{order.second_vehicle.car_brand} {order.second_vehicle.car_model}</p>
                                  <p className="text-gray-500">{order.second_vehicle.car_plate} • {t(order.second_vehicle.fuel_type)}</p>
                                </div>
                                {order.service_type === 'bc_service' && order.point_c_address && (
                                  <div>
                                    <p className="font-medium text-orange-700 flex items-center gap-1">
                                      <MapPin className="w-3 h-3" style={{color: '#f97316'}} /> {t('admin_kanban_delivery_vehicle2_point_c')}
                                    </p>
                                    <p className="text-gray-700 truncate">{order.point_c_address}</p>
                                    <p className="text-gray-500">{order.point_c_contact_name} • {order.point_c_contact_phone}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Notes Section */}
                          {(order.notes || order.first_vehicle_notes || order.second_vehicle_notes) && (
                            <div className="p-4 bg-amber-50/90 border border-amber-200 rounded-xl text-sm leading-relaxed">
                              <p className="font-semibold text-amber-900 mb-1">📝 {t('admin_service_notes')}</p>
                              {order.notes && (
                                <div className="mb-1">
                                  <span className="text-amber-700 font-medium">{t('admin_notes_general')}</span> <span className="text-amber-900">{order.notes}</span>
                                </div>
                              )}
                              {order.first_vehicle_notes && (
                                <div className="mb-1">
                                  <span className="text-amber-700 font-medium">{t('admin_notes_vehicle1')}</span> <span className="text-amber-900">{order.first_vehicle_notes}</span>
                                </div>
                              )}
                              {order.second_vehicle_notes && (
                                <div>
                                  <span className="text-amber-700 font-medium">{t('admin_notes_vehicle2')}</span> <span className="text-amber-900">{order.second_vehicle_notes}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Botones para ver fotos y gastos */}
                          {order.status === 'delivered' && (
                            <div className="flex gap-2 pt-2 border-t border-gray-200">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedOrderForView(order);
                                  setShowAdminPhotosDialog(true);
                                  loadAdminVehiclePhotos(order.id);
                                }}
                                className="flex items-center gap-1 text-xs"
                              >
                                <Image className="w-3 h-3" />
                                Ver Fotos
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedOrderForView(order);
                                  setShowAdminExpensesDialog(true);
                                  loadAdminOrderExpenses(order.id);
                                }}
                                className="flex items-center gap-1 text-xs"
                              >
                                <DollarSign className="w-3 h-3" />
                                Ver Gastos
                              </Button>
                            </div>
                          )}
                          
                          {/* Botones de acción generales - Siempre disponibles */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200 mt-2">
                            {/* Agregar a Google Calendar - disponible para todos */}
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1 text-xs"
                              onClick={() => {
                                const calendarLink = generateGoogleCalendarLink(order);
                                window.open(calendarLink, '_blank');
                                toast.success(t('admin_toast_opening_calendar'));
                              }}
                            >
                              <Calendar className="w-3 h-3" />
                              {t('admin_add_to_calendar')}
                            </Button>
                            
                            {/* Descargar contrato - solo cuando hay conductor */}
                            {order.driver_id && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 text-xs"
                                disabled={downloadingContractOrderId != null}
                                onClick={() => downloadOrderContractPdf(order)}
                              >
                                <FileText className="w-3 h-3" />
                                {t('admin_download_contract')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                      );
                    })()
                  ))
                )}
              </div>

              {/* Pagination */}
              {getFilteredOrders().length > ITEMS_PER_PAGE && (
                <Pagination
                  currentPage={servicesSubTab === 'active' ? servicesPage : historicalPage}
                  totalPages={getTotalPages(getFilteredOrders().length)}
                  totalItems={getFilteredOrders().length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={(page) => {
                    if (servicesSubTab === 'active') {
                      setServicesPage(page);
                    } else {
                      setHistoricalPage(page);
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="mt-6"
                />
              )}
              </>
              )}
            </div>
          )}

          {/* Drivers Tab */}
          {activeTab === 'drivers' && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{t('drivers_management')}</h2>
                  <p className="text-gray-600 mt-1">{t('manage_driver_fleet')}</p>
                </div>
                <Dialog open={showDriverDialog} onOpenChange={setShowDriverDialog}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        setEditingItem(null);
                        setDriverForm({
                          name: '',
                          phone: '',
                          email: '',
                          license_number: '',
                          status: 'available'
                        });
                      }}
                      data-testid="add-driver-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('add_driver')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingItem ? t('admin_edit_driver') : t('admin_create_new_driver')}</DialogTitle>
                      <DialogDescription>{t('admin_enter_driver_info')}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveDriverForm} className="space-y-4" data-testid="driver-form">
                      <div className="space-y-2">
                        <Label>{t('admin_name_required')}</Label>
                        <Input
                          value={driverForm.name}
                          onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                          required
                          data-testid="driver-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('phone_required')}</Label>
                        <Input
                          value={driverForm.phone}
                          onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                          required
                          data-testid="driver-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_email_required')}</Label>
                        <Input
                          type="email"
                          value={driverForm.email}
                          onChange={(e) => setDriverForm({ ...driverForm, email: e.target.value })}
                          required
                          data-testid="driver-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_license_number')} *</Label>
                        <Input
                          value={driverForm.license_number}
                          onChange={(e) => setDriverForm({ ...driverForm, license_number: e.target.value })}
                          required
                          data-testid="driver-license-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_driver_status_required')}</Label>
                        <Select value={driverForm.status} onValueChange={(value) => setDriverForm({ ...driverForm, status: value })}>
                          <SelectTrigger data-testid="driver-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">{t('driver_status_available')}</SelectItem>
                            <SelectItem value="busy">{t('driver_status_busy')}</SelectItem>
                            <SelectItem value="offline">{t('driver_status_disconnected')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowDriverDialog(false)} className="flex-1">
                          {t('admin_cancel')}
                        </Button>
                        <Button type="submit" className="flex-1" data-testid="driver-submit-btn">
                          {editingItem ? t('update') : t('admin_create_driver')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="data-table" data-testid="drivers-table">
                    <thead>
                      <tr>
                        <th>{t('admin_table_name')}</th>
                        <th>{t('admin_table_phone')}</th>
                        <th>{t('admin_table_email')}</th>
                        <th>{t('admin_table_license')}</th>
                        <th>{t('admin_table_status')}</th>
                        <th>{t('admin_table_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems(drivers, driversPage).map((driver) => (
                        <tr key={driver.id}>
                          <td className="font-semibold">{driver.name}</td>
                          <td>{driver.phone}</td>
                          <td>{driver.email}</td>
                          <td className="font-mono text-sm">{driver.license_number}</td>
                          <td>
                            <span className={`status-badge status-${driver.status}`}>
                              {driver.status === 'available'
                                ? t('driver_status_available')
                                : driver.status === 'busy'
                                  ? t('driver_status_busy')
                                  : driver.status === 'offline'
                                    ? t('driver_status_disconnected')
                                    : driver.status === 'pending_approval'
                                      ? t('driver_pending_approval')
                                      : driver.status}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              {/* Approve button for pending drivers or non-validated drivers */}
                              {(driver.status === 'pending_approval' || !driver.documents_validated) && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('admin_token');
                                      const response = await fetch(`${BACKEND_URL}/api/drivers/${driver.id}`, {
                                        method: 'PUT',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                          status: 'available',
                                          documents_validated: true
                                        })
                                      });
                                      
                                      if (response.ok) {
                                        toast.success(t('admin_toast_driver_approved'));
                                        loadData();
                                      } else {
                                        const errorData = await response.json().catch(() => ({}));
                                        toast.error(t('admin_toast_error_approve_driver_detail', { detail: errorData.detail || t('admin_error_unknown') }));
                                      }
                                    } catch (error) {
                                      console.error('Error approving driver:', error);
                                      toast.error(t('admin_toast_error_approve_driver'));
                                    }
                                  }}
                                  title={t('title_approve_driver')}
                                  data-testid={`approve-driver-${driver.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {t('admin_approve_btn')}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedDriver(driver);
                                  setShowDriverProfileDialog(true);
                                }}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                title={t('title_view_profile')}
                                data-testid={`view-driver-${driver.id}`}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditDriver(driver)}
                                data-testid={`edit-driver-${driver.id}`}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteDriver(driver.id)}
                                  data-testid={`delete-driver-${driver.id}`}
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </CardContent>
              </Card>

              {/* Pagination for Drivers */}
              {drivers.length > ITEMS_PER_PAGE && (
                <Pagination
                  currentPage={driversPage}
                  totalPages={getTotalPages(drivers.length)}
                  totalItems={drivers.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={(page) => {
                    setDriversPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="mt-6"
                />
              )}
            </div>
          )}

          {/* Pricing Tab - Only visible if canSeeClientPrices */}
          {activeTab === 'pricing' && canSeeClientPrices && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{t('admin_general_pricing_title')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_general_pricing_description')}</p>
                </div>
                <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        setEditingItem(null);
                        setPricingForm({
                          min_distance: '',
                          max_distance: '',
                          delivery_price: '',
                          bc_service_price: '',
                          tradein_price: '',
                          representacion_price: '',
                          delivery_price_per_km: '',
                          bc_service_price_per_km: '',
                          tradein_price_per_km: ''
                        });
                      }}
                      data-testid="add-pricing-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('admin_add_pricing_rate')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingItem ? t('admin_edit_pricing_rate') : t('admin_add_new_pricing_rate')}</DialogTitle>
                      <DialogDescription>{t('admin_configure_pricing_description')}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreatePricing} className="space-y-4" data-testid="pricing-form">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('admin_min_distance_km')} *</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={pricingForm.min_distance}
                            onChange={(e) => setPricingForm({ ...pricingForm, min_distance: e.target.value })}
                            required
                            data-testid="pricing-min-distance-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('admin_max_distance_km')} *</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={pricingForm.max_distance}
                            onChange={(e) => setPricingForm({ ...pricingForm, max_distance: e.target.value })}
                            required
                            data-testid="pricing-max-distance-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_price_delivery')} *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={pricingForm.delivery_price}
                          onChange={(e) => setPricingForm({ ...pricingForm, delivery_price: e.target.value })}
                          required
                          data-testid="pricing-delivery-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_price_bc')} *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={pricingForm.bc_service_price}
                          onChange={(e) => setPricingForm({ ...pricingForm, bc_service_price: e.target.value })}
                          required
                          data-testid="pricing-bc-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_price_tradein')} *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={pricingForm.tradein_price}
                          onChange={(e) => setPricingForm({ ...pricingForm, tradein_price: e.target.value })}
                          required
                          data-testid="pricing-tradein-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_price_representation')} *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={pricingForm.representacion_price}
                          onChange={(e) => setPricingForm({ ...pricingForm, representacion_price: e.target.value })}
                          required
                          data-testid="pricing-representacion-input"
                        />
                      </div>
                      
                      {/* Campos opcionales para precios por km adicional (1001+ km) */}
                      <div className="border-t pt-4 mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">
                          {t('admin_per_km_section')}
                        </p>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label>{t('admin_per_km_delivery')}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={pricingForm.delivery_price_per_km}
                              onChange={(e) => setPricingForm({ ...pricingForm, delivery_price_per_km: e.target.value })}
                              placeholder={t('admin_placeholder_040')}
                            />
                            <p className="text-xs text-gray-500">
                              {t('admin_per_km_note')}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('admin_per_km_bc')}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={pricingForm.bc_service_price_per_km}
                              onChange={(e) => setPricingForm({ ...pricingForm, bc_service_price_per_km: e.target.value })}
                              placeholder={t('admin_placeholder_032')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('admin_per_km_tradein')}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={pricingForm.tradein_price_per_km}
                              onChange={(e) => setPricingForm({ ...pricingForm, tradein_price_per_km: e.target.value })}
                              placeholder={t('admin_placeholder_028')}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowPricingDialog(false)} className="flex-1">
                          {t('admin_cancel')}
                        </Button>
                        <Button type="submit" className="flex-1" data-testid="pricing-submit-btn">
                          {editingItem ? t('update') : t('create')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_pricing_table_title')}</CardTitle>
                  <CardDescription>{t('admin_pricing_table_description')}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="data-table" data-testid="pricing-table">
                    <thead>
                      <tr>
                        <th>{t('admin_pricing_col_distance')}</th>
                        <th>{t('admin_pricing_col_delivery')}</th>
                        <th>{t('admin_pricing_col_bc')}</th>
                        <th>{t('admin_pricing_col_tradein')}</th>
                        <th>{t('admin_pricing_col_representation')}</th>
                        <th>{t('admin_pricing_col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingRates.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-8 text-gray-500">
                            {t('admin_no_rates_configured')}
                          </td>
                        </tr>
                      ) : (
                        pricingRates.map((rate) => {
                          // Check if this is a per-km rate (for 1001+ km)
                          const isPerKmRate = rate.delivery_price_per_km || rate.bc_service_price_per_km || rate.tradein_price_per_km;
                          
                          return (
                          <tr key={rate.id}>
                            <td className="font-semibold">
                              {rate.min_distance} - {rate.max_distance} km
                            </td>
                            <td className="font-semibold text-green-600">
                              {isPerKmRate && rate.delivery_price_per_km ? 
                                `€${rate.delivery_price.toFixed(2)} + €${rate.delivery_price_per_km.toFixed(2)}/km adicional` : 
                                `€${(rate.delivery_price || 0).toFixed(2)}`}
                            </td>
                            <td className="font-semibold text-blue-600">
                              {isPerKmRate && rate.bc_service_price_per_km ? 
                                `€${rate.bc_service_price.toFixed(2)} + €${rate.bc_service_price_per_km.toFixed(2)}/km adicional` : 
                                `€${(rate.bc_service_price || 0).toFixed(2)}`}
                            </td>
                            <td className="font-semibold text-purple-600">
                              {isPerKmRate && rate.tradein_price_per_km ? 
                                `€${rate.tradein_price.toFixed(2)} + €${rate.tradein_price_per_km.toFixed(2)}/km adicional` : 
                                `€${(rate.tradein_price || 0).toFixed(2)}`}
                            </td>
                            <td className="font-semibold text-orange-600">€{(rate.representacion_price || 0).toFixed(2)}</td>
                            <td>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingItem(rate);
                                    setPricingForm({
                                      min_distance: rate.min_distance.toString(),
                                      max_distance: rate.max_distance.toString(),
                                      delivery_price: rate.delivery_price?.toString() || '',
                                      bc_service_price: rate.bc_service_price?.toString() || '',
                                      tradein_price: rate.tradein_price?.toString() || '',
                                      representacion_price: rate.representacion_price?.toString() || '45',
                                      delivery_price_per_km: rate.delivery_price_per_km?.toString() || '',
                                      bc_service_price_per_km: rate.bc_service_price_per_km?.toString() || '',
                                      tradein_price_per_km: rate.tradein_price_per_km?.toString() || ''
                                    });
                                    setShowPricingDialog(true);
                                  }}
                                  data-testid={`edit-pricing-${rate.id}`}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeletePricing(rate.id)}
                                  data-testid={`delete-pricing-${rate.id}`}
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Services Section */}
              <Card className="mt-8">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>{t('admin_additional_services_title')}</CardTitle>
                      <CardDescription>{t('admin_additional_services_description')}</CardDescription>
                    </div>
                    <Dialog open={showAdditionalServiceDialog} onOpenChange={setShowAdditionalServiceDialog}>
                      <DialogTrigger asChild>
                        <Button
                          onClick={() => {
                            setEditingItem(null);
                            setAdditionalServiceForm({
                              name: '',
                              price: '',
                              unit: 'per_service',
                              description: ''
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t('admin_add_service')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {editingItem ? t('admin_edit_additional_service') : t('admin_new_additional_service')}
                          </DialogTitle>
                          <DialogDescription>
                            {editingItem ? t('admin_edit_additional_service_desc') : t('admin_new_additional_service_desc')}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSaveAdditionalService} className="space-y-4">
                          <div>
                            <Label htmlFor="service_name">{t('admin_service_name_label')}</Label>
                            <Input
                              id="service_name"
                              value={additionalServiceForm.name}
                              onChange={(e) => setAdditionalServiceForm({
                                ...additionalServiceForm,
                                name: e.target.value
                              })}
                              placeholder={t('admin_service_name_placeholder')}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="service_price">{t('admin_service_price_label')}</Label>
                            <Input
                              id="service_price"
                              type="number"
                              step="0.01"
                              value={additionalServiceForm.price}
                              onChange={(e) => setAdditionalServiceForm({
                                ...additionalServiceForm,
                                price: e.target.value
                              })}
                              placeholder="0.00"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="service_unit">{t('admin_billing_unit_label')}</Label>
                            <Select
              value={additionalServiceForm.unit}
              onValueChange={(value) => setAdditionalServiceForm({
                ...additionalServiceForm,
                unit: value
              })}
            >
              <SelectTrigger id="service_unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                                <SelectItem value="per_service">{t('admin_unit_per_service')}</SelectItem>
                                <SelectItem value="per_hour">{t('admin_unit_per_hour')}</SelectItem>
                                <SelectItem value="per_vehicle">{t('admin_unit_per_vehicle')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="service_description">{t('admin_service_description_label')}</Label>
                            <Input
                              id="service_description"
                              value={additionalServiceForm.description}
                              onChange={(e) => setAdditionalServiceForm({
                                ...additionalServiceForm,
                                description: e.target.value
                              })}
                              placeholder={t('admin_service_description_placeholder')}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowAdditionalServiceDialog(false);
                                setEditingItem(null);
                              }}
                            >
                              {t('cancel')}
                            </Button>
                            <Button type="submit">
                              {editingItem ? t('update') : t('create')}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('admin_additional_services_col_service')}</th>
                        <th>{t('admin_additional_services_col_price')}</th>
                        <th>{t('admin_additional_services_col_unit')}</th>
                        <th>{t('admin_additional_services_col_description')}</th>
                        <th>{t('admin_additional_services_col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {additionalServices.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-8 text-gray-500">
                            {t('admin_no_additional_services')}
                          </td>
                        </tr>
                      ) : (
                        additionalServices.map((service) => (
                          <tr key={service.id}>
                            <td className="font-semibold">{translateAdditionalServiceName(service.name, t)}</td>
                            <td className="font-semibold text-green-600">€{service.price.toFixed(2)}</td>
                            <td>
                              <span className="px-2 py-1 text-xs rounded bg-gray-100">
                                {service.unit === 'per_service' ? t('admin_unit_per_service') : 
                                 service.unit === 'per_hour' ? t('admin_unit_per_hour') : 
                                 t('admin_unit_per_vehicle')}
                              </span>
                            </td>
                            <td className="text-gray-600 text-sm">
                              {translateAdditionalServiceDescription(service.name, service.description, t)}
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingItem(service);
                                    setAdditionalServiceForm({
                                      name: service.name,
                                      price: service.price.toString(),
                                      unit: service.unit,
                                      description: service.description
                                    });
                                    setShowAdditionalServiceDialog(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAdditionalService(service.id)}
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Custom Pricing Tab */}
          {/* Custom Pricing Tab - NEW TEMPLATE SYSTEM */}

          {/* Driver Pricing Tab */}
          {activeTab === 'driver-pricing' && (
            <div className="space-y-6 animate-fade-in">
              {/* Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{t('admin_driver_pricing_title')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_driver_pricing_subtitle')}</p>
                </div>
                <Button
                  onClick={() => setShowCreateTemplateDialog(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin_new_template')}
                </Button>
              </div>

              {/* Templates Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {driverPricingTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{template.name}</span>
                        {template.name === 'JLUIS' && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                            {t('admin_badge_premium_plus_10')}
                          </span>
                        )}
                        {template.name === 'Premium' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {t('admin_badge_high_end')}
                          </span>
                        )}
                        {template.name === 'Básica' && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            {t('admin_badge_standard')}
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription>{template.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={async () => {
                          // Load rates for this template
                          const token = localStorage.getItem('admin_token');
                          const response = await fetch(
                            `${BACKEND_URL}/api/driver-pricing-templates/${template.id}/rates`,
                            {
                              headers: { 'Authorization': `Bearer ${token}` }
                            }
                          );
                          
                          if (response.ok) {
                            const rates = await response.json();
                            setSelectedTemplateRates(rates);
                            setSelectedTemplateName(template.name);
                            setSelectedTemplateId(template.id);
                            setShowDriverRatesDialog(true);
                          } else {
                            toast.error(t('admin_error_load_rates'));
                          }
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('admin_view_rates')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Info Card */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">{t('admin_how_tariff_system_works')}</h3>
                      <ul className="space-y-1 text-sm text-blue-800">
                        <li>• {t('admin_basic_tariff_explanation')}</li>
                        <li>• {t('admin_premium_tariff_explanation')}</li>
                        <li>• {t('admin_jluis_tariff_explanation')}</li>
                        <li>• {t('admin_tariff_assignment_explanation')}</li>
                        <li>• {t('admin_custom_tariff_explanation')}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sample Rates Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_tariff_comparison_title')}</CardTitle>
                  <CardDescription>{t('admin_tariff_comparison_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-gray-600 mb-2">{t('admin_tariff_tier_basic_short')}</p>
                      <p className="text-2xl font-bold text-green-700">120€</p>
                      <p className="text-xs text-gray-500 mt-1">{t('admin_standard_delivery')}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-600 mb-2">{t('admin_tariff_name_premium')}</p>
                      <p className="text-2xl font-bold text-blue-700">130€</p>
                      <p className="text-xs text-gray-500 mt-1">{t('admin_superior_delivery')}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-600 mb-2">{t('admin_driver_template_jluis')}</p>
                      <p className="text-2xl font-bold text-purple-700">143€</p>
                      <p className="text-xs text-gray-500 mt-1">{t('admin_premium_plus_10_label')}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    {t('admin_pricing_disclaimer')}
                  </p>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">{t('admin_services_with', { name: t('admin_tariff_tier_basic_short') })}</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {orders.filter(o => o.driver_pricing_template_id === driverPricingTemplates.find(t => t.name === 'Básica')?.id).length}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">{t('admin_services_with', { name: t('admin_tariff_name_premium') })}</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {orders.filter(o => o.driver_pricing_template_id === driverPricingTemplates.find(t => t.name === 'Premium')?.id).length}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">{t('admin_services_with', { name: t('admin_driver_template_jluis') })}</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {orders.filter(o => o.driver_pricing_template_id === driverPricingTemplates.find(t => t.name === 'JLUIS')?.id).length}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'custom-pricing' && canSeeClientPrices && (
            <div className="space-y-6">
              
              {/* Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('admin_custom_pricing_clients_title')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_custom_pricing_subtitle')}</p>
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => {
                    setTemplateForm({ name: '', description: '' });
                    setShowTemplateDialog(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('admin_new_template')}
                  </Button>
                  <Button variant="outline" onClick={() => setShowAssignmentDialog(true)}>
                    <Calculator className="w-4 h-4 mr-2" />
                    {t('admin_assign_rate')}
                  </Button>
                </div>
              </div>

              {/* Client Assignments Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_clients_and_assigned_rates')}</CardTitle>
                  <CardDescription>{t('admin_clients_assigned_rates_description')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {clients.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">{t('admin_no_clients_registered')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-3 px-4 font-semibold text-gray-700">{t('admin_table_client')}</th>
                            <th className="py-3 px-4 font-semibold text-gray-700">{t('admin_email_header')}</th>
                            <th className="py-3 px-4 font-semibold text-gray-700">{t('admin_assigned_rate')}</th>
                            <th className="py-3 px-4 font-semibold text-gray-700">{t('admin_rates_col_actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients.map((client) => {
                            const assignment = clientAssignments.find(a => a.client_id === client.id);
                            const assignedTemplate = assignment 
                              ? pricingTemplates.find(t => t.id === assignment.template_id)
                              : null;
                            const isGeneral = !assignment || assignment.template_id === 'general';
                            
                            return (
                              <tr key={client.id} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <div className="font-medium text-gray-900">
                                    {client.company_name || client.name}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-gray-600 text-sm">
                                  {client.email}
                                </td>
                                <td className="py-3 px-4">
                                  {isGeneral ? (
                                    <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700">
                                      {t('admin_general_rate')}
                                    </span>
                                  ) : (
                                    <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700 font-medium">
                                      {assignedTemplate?.name || t('admin_custom_rate')}
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setAssignmentForm({
                                          client_id: client.id,
                                          template_id: assignment?.template_id || 'general'
                                        });
                                        setShowAssignmentDialog(true);
                                      }}
                                    >
                                      {t('admin_change')}
                                    </Button>
                                    {!isGeneral && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleRemoveAssignment(client.id)}
                                      >
                                        <Trash2 className="w-3 h-3 text-red-600" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pricing Templates */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_available_rate_templates')}</CardTitle>
                  <CardDescription>{t('admin_manage_templates_assign_clients')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* General Pricing (Always visible) */}
                    <Card className="bg-gray-50 border-gray-300">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {t('admin_general_rate')}
                              <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-600">
                                {t('admin_default_badge')}
                              </span>
                            </CardTitle>
                            <CardDescription>{t('admin_standard_rate_default')}</CardDescription>
                          </div>
                          <div className="text-sm text-gray-600">
                            {t('admin_clients_count', { count: clientAssignments.filter(a => !a.template_id || a.template_id === 'general').length })}
                          </div>
                        </div>
                      </CardHeader>
                    </Card>

                    {/* Custom Templates */}
                    {pricingTemplates.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>{t('admin_no_custom_templates')}</p>
                        <p className="text-sm mt-2">{t('admin_click_new_template_to_create')}</p>
                      </div>
                    ) : (
                      pricingTemplates.map((template) => {
                        const clientCount = clientAssignments.filter(a => a.template_id === template.id).length;
                        
                        return (
                          <Card key={template.id} className="border-blue-200 bg-blue-50">
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <CardTitle className="text-lg flex items-center gap-2">
                                    {template.name}
                                    {clientCount > 0 && (
                                      <span className="text-xs px-2 py-1 rounded bg-blue-200 text-blue-700">
                                        {clientCount} cliente{clientCount !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </CardTitle>
                                  {template.description && (
                                    <CardDescription>{template.description}</CardDescription>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      setSelectedTemplate(template);
                                      await loadTemplateRates(template.id);
                                      setTemplateRateForm({
                                        min_distance: '',
                                        max_distance: '',
                                        delivery_price: '',
                                        bc_service_price: '',
                                        tradein_price: '',
                                        representacion_price: '45',
                                        delivery_price_per_km: '',
                                        bc_service_price_per_km: '',
                                        tradein_price_per_km: ''
                                      });
                                    }}
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    {t('admin_view_rates')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteTemplate(template.id)}
                                  >
                                    <Trash2 className="w-3 h-3 text-red-600" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                            
                            {/* Show rates if this template is selected */}
                            {selectedTemplate?.id === template.id && (
                              <CardContent>
                                <div className="space-y-3">
                                  <div className="flex justify-between items-center">
                                    <h4 className="font-semibold text-sm text-gray-700">{t('admin_distance_ranges')}</h4>
                                    <Button
                                      size="sm"
                                      onClick={() => setShowTemplateRateDialog(true)}
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      {t('admin_add_rate')}
                                    </Button>
                                  </div>
                                  
                                  {templateRates.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                      {t('admin_no_rates_for_template')}
                                    </p>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b text-left">
                                            <th className="py-2 px-2 font-medium text-gray-600">{t('admin_template_rates_col_distance_km')}</th>
                                            <th className="py-2 px-2 font-medium text-gray-600">{t('admin_rates_col_delivery')}</th>
                                            <th className="py-2 px-2 font-medium text-gray-600">{t('admin_rates_col_bc')}</th>
                                            <th className="py-2 px-2 font-medium text-gray-600">{t('tradein')}</th>
                                            <th className="py-2 px-2 font-medium text-gray-600">{t('representacion')}</th>
                                            <th className="py-2 px-2"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {templateRates.map((rate) => {
                                            const hasPerKm = rate.delivery_price_per_km || rate.bc_service_price_per_km || rate.tradein_price_per_km;
                                            
                                            return (
                                              <tr key={rate.id} className="border-b">
                                                <td className="py-2 px-2 font-medium">{rate.min_distance} - {rate.max_distance}</td>
                                                <td className="py-2 px-2 text-green-600 font-medium">
                                                  {hasPerKm && rate.delivery_price_per_km 
                                                    ? `€${rate.delivery_price} + €${rate.delivery_price_per_km}/km`
                                                    : `€${rate.delivery_price.toFixed(2)}`}
                                                </td>
                                                <td className="py-2 px-2 text-blue-600 font-medium">
                                                  {hasPerKm && rate.bc_service_price_per_km 
                                                    ? `€${rate.bc_service_price} + €${rate.bc_service_price_per_km}/km`
                                                    : `€${rate.bc_service_price.toFixed(2)}`}
                                                </td>
                                                <td className="py-2 px-2 text-purple-600 font-medium">
                                                  {hasPerKm && rate.tradein_price_per_km 
                                                    ? `€${rate.tradein_price} + €${rate.tradein_price_per_km}/km`
                                                    : `€${rate.tradein_price.toFixed(2)}`}
                                                </td>
                                                <td className="py-2 px-2 text-orange-600 font-medium">
                                                  €{rate.representacion_price?.toFixed(2) || '45.00'}
                                                </td>
                                                <td className="py-2 px-2">
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleDeleteTemplateRate(rate.id)}
                                                  >
                                                    <Trash2 className="w-3 h-3 text-red-600" />
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            )}
                          </Card>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Client Invoices Tab - Only visible if canSeeClientPrices */}
          {/* Client Invoices Tab */}
          {activeTab === 'client-invoices' && canSeeClientPrices && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('admin_client_invoices')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_client_invoices_subtitle')}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={loadBillingData} disabled={loadingBilling} variant="outline">
                    {loadingBilling ? t('loading') : t('update')}
                  </Button>
                  <Button 
                    onClick={() => setShowGenerateClientLiquidationDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('admin_generate_invoices')}
                  </Button>
                </div>
              </div>

              {/* Current Period View - EN CURSO */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    {t('admin_client_invoice_period_card_title')}
                  </CardTitle>
                  <CardDescription>
                    {clientCurrentPeriodData && (
                      <>
                        {t('period_label_period')} {new Date(clientCurrentPeriodData.period_start).toLocaleDateString(dateLocale)} - {new Date(clientCurrentPeriodData.period_end).toLocaleDateString(dateLocale)}
                        {' | '}
                        {t('admin_expected_payment')}: {new Date(clientCurrentPeriodData.payment_start).toLocaleDateString(dateLocale)} - {new Date(clientCurrentPeriodData.payment_end).toLocaleDateString(dateLocale)}
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingClientCurrentPeriod ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">{t('loading')}</p>
                    </div>
                  ) : !clientCurrentPeriodData?.clients_data?.length ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>{t('admin_dash_no_pending_invoice_this_period')}</p>
                    </div>
                  ) : (
                    <>
                      {/* Summary cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="text-sm text-blue-600 font-medium">{t('admin_metric_clients')}</div>
                          <div className="text-2xl font-bold text-blue-700">{clientCurrentPeriodData.clients_data.length}</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="text-sm text-green-600 font-medium">{t('admin_metric_services')}</div>
                          <div className="text-2xl font-bold text-green-700">{clientCurrentPeriodData.total_services}</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="text-sm text-gray-600 font-medium">{t('admin_taxable_base')}</div>
                          <div className="text-2xl font-bold text-gray-700">
                            €{clientCurrentPeriodData.clients_data.reduce((sum, c) => sum + (c.base_imponible || 0), 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <div className="text-sm text-purple-600 font-medium">{t('admin_total_with_vat')}</div>
                          <div className="text-2xl font-bold text-purple-700">€{clientCurrentPeriodData.total_amount?.toFixed(2)}</div>
                        </div>
                      </div>

                      {/* Search */}
                      <div className="mb-4">
                        <Input
                          placeholder={t('admin_search_client_placeholder')}
                          value={clientLiquidationSearchTerm}
                          onChange={(e) => setClientLiquidationSearchTerm(e.target.value)}
                          className="max-w-sm"
                        />
                      </div>

                      {/* Clients table */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('admin_invoices_col_client')}</th>
                              <th className="text-center py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_services')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_base')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_iva')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-orange-600">{t('admin_supplies_col')}</th>
                              <th className="text-right py-2 px-3 font-semibold text-gray-700">{t('admin_invoices_col_total')}</th>
                              <th className="text-center py-2 px-4 font-semibold text-gray-700">{t('admin_invoices_col_actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientCurrentPeriodData.clients_data
                              .filter(c => !clientLiquidationSearchTerm || c.client_name.toLowerCase().includes(clientLiquidationSearchTerm.toLowerCase()))
                              .map((client) => {
                                const isExpanded = expandedClientRows[client.client_id];
                                return (
                                  <React.Fragment key={client.client_id}>
                                    <tr className="border-b hover:bg-gray-50">
                                      <td className="py-2 px-4">
                                        <button
                                          onClick={() => setExpandedClientRows(prev => ({
                                            ...prev,
                                            [client.client_id]: !prev[client.client_id]
                                          }))}
                                          className="flex items-center gap-2 text-left hover:text-blue-600"
                                        >
                                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                          <span className="font-medium">{client.client_name}</span>
                                        </button>
                                        {client.nif && <span className="text-xs text-gray-500 ml-6">{t('admin_nif_label')}: {client.nif}</span>}
                                      </td>
                                      <td className="py-2 px-2 text-center font-medium">{client.total_services}</td>
                                      <td className="py-2 px-2 text-right font-medium">€{client.base_imponible?.toFixed(2)}</td>
                                      <td className="py-2 px-2 text-right text-gray-600">€{client.iva_amount?.toFixed(2)}</td>
                                      <td className="py-2 px-2 text-right text-orange-600 font-medium">
                                        {client.suplidos_total > 0 ? `€${client.suplidos_total?.toFixed(2)}` : '-'}
                                      </td>
                                      <td className="py-2 px-3 text-right">
                                        <span className="font-bold text-base text-blue-700">€{client.total_con_iva?.toFixed(2)}</span>
                                      </td>
                                      <td className="py-2 px-4 text-center">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setExpandedClientRows(prev => ({
                                            ...prev,
                                            [client.client_id]: !prev[client.client_id]
                                          }))}
                                        >
                                          {isExpanded ? t('admin_hide') : t('admin_view_detail')}
                                        </Button>
                                      </td>
                                    </tr>
                                    {/* Expanded row with service details */}
                                    {isExpanded && client.services_details && (
                                      <tr className="bg-gray-50">
                                        <td colSpan={7} className="px-4 py-4">
                                          <div className="bg-white rounded-lg border p-4">
                                            <h4 className="font-semibold mb-3">{t('admin_services_detail')}</h4>
                                            <table className="w-full text-sm">
                                              <thead>
                                                <tr className="bg-gray-100">
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_date')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_plate')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_vehicle')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_type')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_base')}</th>
                                                  <th className="text-center py-2 px-3 font-medium text-purple-600">{t('admin_invoices_col_extras')}</th>
                                                  <th className="text-center py-2 px-3 font-medium text-orange-600">{t('admin_supplies_col')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_total')}</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {client.services_details.map((service, idx) => (
                                                  <React.Fragment key={idx}>
                                                    <tr className="border-b border-gray-100">
                                                      <td className="py-2 px-3">
                                                        {service.pickup_date_from ? new Date(service.pickup_date_from).toLocaleDateString(dateLocale) : '-'}
                                                      </td>
                                                      <td className="py-2 px-3 font-medium">{service.car_plate}</td>
                                                      <td className="py-2 px-3">{service.car_brand} {service.car_model}</td>
                                                      <td className="py-2 px-3">{service.service_type}</td>
                                                      <td className="py-2 px-3 text-right">€{(service.base_price || 0).toFixed(2)}</td>
                                                      <td className="py-2 px-3 text-center">
                                                        {service.additional_services?.length > 0 ? (
                                                          <span className="text-purple-600 font-medium">
                                                            €{(service.additional_services_total || 0).toFixed(2)}
                                                          </span>
                                                        ) : '-'}
                                                      </td>
                                                      <td className="py-2 px-3 text-center">
                                                        {service.suplidos > 0 ? (
                                                          <span className="text-orange-600 font-medium">
                                                            €{(service.suplidos || 0).toFixed(2)}
                                                          </span>
                                                        ) : '-'}
                                                      </td>
                                                      <td className="py-2 px-3 text-right font-medium">€{((service.total_price || service.base_price || 0) + (service.suplidos || 0)).toFixed(2)}</td>
                                                    </tr>
                                                    {/* Show additional services if any */}
                                                    {service.additional_services?.length > 0 && (
                                                      <tr className="bg-purple-50">
                                                        <td colSpan={8} className="py-1 px-6">
                                                          <div className="flex flex-wrap gap-2 text-xs text-purple-700">
                                                            <span className="font-medium">{t('admin_invoices_col_extras')}:</span>
                                                            {service.additional_services.map((extra, i) => (
                                                              <span key={i} className="bg-purple-100 px-2 py-0.5 rounded">
                                                                {translateAdditionalServiceName(extra.name, t)} €{(extra.price || 0).toFixed(2)}
                                                              </span>
                                                            ))}
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    )}
                                                    {/* Show suplidos detail if any */}
                                                    {service.suplidos > 0 && service.suplidos_detail && (
                                                      <tr className="bg-orange-50">
                                                        <td colSpan={8} className="py-1 px-6">
                                                          <div className="flex flex-wrap gap-2 text-xs text-orange-700">
                                                            <span className="font-medium">{t('driver_billing_supplies')}:</span>
                                                            {service.suplidos_detail.fuel > 0 && (
                                                              <span className="bg-orange-100 px-2 py-0.5 rounded">
                                                                {t('inspections_fuel')} €{service.suplidos_detail.fuel.toFixed(2)}
                                                              </span>
                                                            )}
                                                            {service.suplidos_detail.toll > 0 && (
                                                              <span className="bg-orange-100 px-2 py-0.5 rounded">
                                                                {t('admin_expense_toll')} €{service.suplidos_detail.toll.toFixed(2)}
                                                              </span>
                                                            )}
                                                            {service.suplidos_detail.parking > 0 && (
                                                              <span className="bg-orange-100 px-2 py-0.5 rounded">
                                                                {t('admin_expense_parking')} €{service.suplidos_detail.parking.toFixed(2)}
                                                              </span>
                                                            )}
                                                            {service.suplidos_detail.taxi > 0 && (
                                                              <span className="bg-orange-100 px-2 py-0.5 rounded">
                                                                {t('admin_expense_taxi')} €{service.suplidos_detail.taxi.toFixed(2)}
                                                              </span>
                                                            )}
                                                            {service.suplidos_detail.other > 0 && (
                                                              <span className="bg-orange-100 px-2 py-0.5 rounded">
                                                                {t('admin_expense_other')} €{service.suplidos_detail.other.toFixed(2)}
                                                              </span>
                                                            )}
                                                          </div>
                                                        </td>
                                                      </tr>
                                                    )}
                                                  </React.Fragment>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Driver Invoices Tab */}
          {activeTab === 'driver-invoices' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('admin_driver_liquidations_title')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_driver_liquidations_subtitle')}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={loadBillingData} disabled={loadingBilling} variant="outline">
                    {loadingBilling ? t('loading') : t('update')}
                  </Button>
                  <Button 
                    onClick={() => setShowGenerateLiquidationDialog(true)}
                    className="bg-[#36e961] hover:bg-[#2bc950] text-black"
                    data-testid="generate-liquidations-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('admin_generate_liquidations')}
                  </Button>
                </div>
              </div>

              {/* Info Banner */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">{t('admin_payment_periods')}</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>{t('admin_quincena_1_desc')}</li>
                        <li>{t('admin_quincena_2_desc')}</li>
                        <li>{t('admin_only_approved_expenses')}</li>
                        <li>{t('admin_liquidation_calendar_split_hint')}</li>
                      </ul>
                      {pendingApprovalCount > 0 && (
                        <p className="mt-2 text-orange-600 font-medium">
                          ⚠️ {t('admin_pending_expense_approval_count', { count: pendingApprovalCount })}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Expense Reports */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    {t('admin_pending_expense_reports', { count: expenseReports.filter(r => r.status === 'pending').length })}
                  </CardTitle>
                  <CardDescription>{t('admin_approve_reject_expenses_desc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {expenseReports.filter(r => r.status === 'pending').length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>{t('admin_no_pending_reports')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {expenseReports.filter(r => r.status === 'pending').map((report) => (
                        <div key={report.id} className="border rounded-lg p-4">
                          <ExpenseReportServiceInfo report={report} />
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="font-semibold">{report.driver_name || t('admin_expense_driver_unknown')}</div>
                              <div className="text-sm text-gray-600">
                                {t('admin_reported_label')}:{' '}
                                {formatExpenseReportDate(report, dateLocale) || t('admin_expense_other_na')}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">€{report.total_expense?.toFixed(2)}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_driver')}</div>
                              <div className="font-semibold text-sm">€{report.driver_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_fuel')}</div>
                              <div className="font-semibold text-sm">€{report.fuel_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_toll')}</div>
                              <div className="font-semibold text-sm">€{report.toll_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_parking')}</div>
                              <div className="font-semibold text-sm">€{report.parking_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_taxi')}</div>
                              <div className="font-semibold text-sm">€{report.taxi_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600">{t('admin_expense_other')}</div>
                              <div className="font-semibold text-sm">€{report.other_expense?.toFixed(2) || '0.00'}</div>
                            </div>
                          </div>
                          
                          {report.other_expense_description && (
                            <div className="text-sm text-gray-600 mb-3 bg-yellow-50 p-2 rounded">
                              <span className="font-medium">{t('admin_other_expenses_label')}</span> {report.other_expense_description}
                            </div>
                          )}
                          
                          <ExpenseReportReceipts report={report} className="mb-3" />
                          
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => openApproveExpenseDialog(report)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {t('admin_approve_btn')}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                const reason = prompt(t('admin_prompt_rejection_reason'));
                                if (reason) approveExpenseReport(report.id, 'rejected', reason);
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              {t('admin_reject_btn')}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Billing Periods - Tabs by Period */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <CardTitle>{t('admin_billing_periods_title')}</CardTitle>
                      <CardDescription>{t('admin_billing_periods_subtitle')}</CardDescription>
                    </div>
                    {/* Search input */}
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('admin_search_driver_placeholder')}
                        value={liquidationSearchTerm}
                        onChange={(e) => setLiquidationSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="liquidation-search"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingBilling && billingPeriods.length === 0 && !currentPeriodData ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className="text-gray-500">{t('admin_loading_periods')}</p>
                    </div>
                  ) : (
                    <div>
                      {pendingInvoicingData && !pendingInvoicingData.has_pending_period
                        && (pendingInvoicingData.payment_pending_count || 0) > 0 && (
                        <div className="mb-4 p-4 rounded-lg bg-blue-50 border border-blue-300 text-sm text-blue-900" role="status">
                          {t('admin_liquidation_payment_pending_banner', {
                            count: pendingInvoicingData.payment_pending_count,
                            label: (() => {
                              const pe = pendingInvoicingData.period_end;
                              if (!pe) return 'Q1';
                              const d = new Date(pe);
                              const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                              const ql = pendingInvoicingData.period_type === 'quincena_2' ? 'Q2' : 'Q1';
                              return `${ql} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
                            })(),
                          })}
                        </div>
                      )}
                      {/* Period Tabs - Always show, even if no historical periods */}
                      <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b overflow-x-auto">
                        {/* Pending Invoicing Period Tab - Show FIRST if there are pending services */}
                        {pendingInvoicingData && pendingInvoicingData.has_pending_period && (
                          ((pendingInvoicingData.total_services || 0)
                            + (pendingInvoicingData.unapproved_service_count || 0)
                            + (pendingInvoicingData.billing_preview_service_count || 0) > 0)
                        ) && (
                          <button
                            onClick={() => setActiveLiquidationPeriod('pending')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                              activeLiquidationPeriod === 'pending'
                                ? 'bg-amber-600 text-white shadow-md'
                                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-300'
                            }`}
                            data-testid="period-tab-pending"
                          >
                            <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                            {t('admin_tab_pending_to_bill')}
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              activeLiquidationPeriod === 'pending'
                                ? 'bg-amber-500 text-white'
                                : 'bg-amber-100 text-amber-600'
                            }`}>
                              {(pendingInvoicingData.drivers?.length || 0) > 0
                                ? (pendingInvoicingData.drivers?.length || 0)
                                : Math.max(
                                    pendingInvoicingData.unapproved_service_count || 0,
                                    pendingInvoicingData.billing_preview_service_count || 0,
                                  )}
                            </span>
                          </button>
                        )}
                        
                        {/* Current period tab — always visible */}
                        <button
                          onClick={() => setActiveLiquidationPeriod('current')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                            activeLiquidationPeriod === 'current'
                              ? 'bg-green-600 text-white shadow-md'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          }`}
                          data-testid="period-tab-current"
                        >
                          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                          {t('admin_in_progress')}
                          {currentPeriodData && (
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              activeLiquidationPeriod === 'current'
                                ? 'bg-green-500 text-white'
                                : 'bg-green-100 text-green-600'
                            }`}>
                              {currentPeriodData.drivers?.length || 0}
                            </span>
                          )}
                        </button>
                        
                        {/* Historical Period Tabs */}
                        {groupedPeriods.map((group) => (
                          <button
                            key={group.key}
                            onClick={() => setActiveLiquidationPeriod(group.key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                              activeLiquidationPeriod === group.key
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`period-tab-${group.key}`}
                          >
                            {group.label}
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              activeLiquidationPeriod === group.key
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {group.drivers.length}
                            </span>
                          </button>
                        ))}
                      </div>

                      {activeLiquidationPeriod === 'current' && currentPeriodData
                        && (currentPeriodData.billing_preview_service_count || 0) > 0 && (
                        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                          {t('admin_liquidation_banner_preview_line', {
                            count: currentPeriodData.billing_preview_service_count,
                            amount: (currentPeriodData.billing_preview_provisional_total ?? 0).toFixed(2),
                          })}
                        </div>
                      )}
                      
                      {/* Period Info Banner - Pending Invoicing Period */}
                      {activeLiquidationPeriod === 'pending' && pendingInvoicingData && pendingInvoicingData.has_pending_period && (
                        <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300">
                          <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                <Clock className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <div className="font-semibold text-amber-800">
                                  {t('admin_pending_period_banner_title', { label: getPendingPeriodInfo()?.label })}
                                </div>
                                <div className="text-sm text-amber-700">
                                  {t('admin_liquidation_label_services')} {new Date(pendingInvoicingData.period_start).toLocaleDateString(dateLocale)} - {new Date(pendingInvoicingData.period_end).toLocaleDateString(dateLocale)}
                                  <span className="mx-2">|</span>
                                  {t('admin_expected_payment')}: {new Date(pendingInvoicingData.payment_start).toLocaleDateString(dateLocale)} - {new Date(pendingInvoicingData.payment_end).toLocaleDateString(dateLocale)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-amber-700">{pendingInvoicingData.total_services}</div>
                                <div className="text-xs text-amber-600">{t('admin_services_count_label')}</div>
                              </div>
                              <div className="text-center bg-white px-4 py-2 rounded-lg shadow-sm">
                                <div className="text-2xl font-bold text-amber-700">€{pendingInvoicingData.total_amount?.toFixed(2)}</div>
                                <div className="text-xs text-amber-600">{t('admin_total_to_liquidate_label')}</div>
                              </div>
                              <Button 
                                onClick={handleGeneratePendingLiquidations}
                                disabled={generatingLiquidations || !(pendingInvoicingData.total_services > 0)}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="generate-pending-liquidations-btn"
                                title={!(pendingInvoicingData.total_services > 0) ? t('admin_liquidation_generate_disabled_until_approved') : undefined}
                              >
                                {generatingLiquidations ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    {t('admin_generating')}
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    {t('admin_generate_liquidations')}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {t('admin_period_closed_generate_message')}
                          </div>
                          {pendingInvoicingData.message && (
                            <div className="mt-3 p-3 rounded-md bg-white/80 border border-amber-200 text-sm text-amber-950">
                              {pendingInvoicingData.message}
                            </div>
                          )}
                          {(pendingInvoicingData.billing_preview_service_count || 0) > 0 && (
                            <div className="mt-3 p-3 rounded-md bg-amber-100/90 border border-amber-300 text-sm text-amber-950">
                              {t('admin_liquidation_banner_preview_line', {
                                count: pendingInvoicingData.billing_preview_service_count,
                                amount: (pendingInvoicingData.billing_preview_provisional_total ?? 0).toFixed(2),
                              })}
                            </div>
                          )}
                          {(pendingInvoicingData.unapproved_service_count || 0) > 0 && (
                            <div className="mt-3 p-3 rounded-md bg-orange-100 border border-orange-200 text-sm text-orange-900">
                              {t('admin_liquidation_unapproved_banner', {
                                count: pendingInvoicingData.unapproved_service_count || 0
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Drivers Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full" data-testid="liquidations-table">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('admin_driver_col')}</th>
                              <th className="text-center py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_services')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_base')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-amber-700">{t('admin_invoices_col_extras')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_invoices_col_iva')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_supplies_col')}</th>
                              <th className="text-right py-2 px-2 font-semibold text-gray-700">{t('admin_irpf_col')}</th>
                              <th className="text-right py-2 px-3 font-semibold text-gray-700">{t('admin_invoices_col_total')}</th>
                              {activeLiquidationPeriod !== 'current' && activeLiquidationPeriod !== 'pending' && (
                                <th className="text-center py-3 px-2 font-semibold text-gray-700">{t('admin_status_col')}</th>
                              )}
                              <th className="text-right py-2 px-4 font-semibold text-gray-700">{t('admin_invoices_col_actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredDriversForPeriod().length === 0 ? (
                              <tr>
                                <td colSpan={activeLiquidationPeriod === 'current' || activeLiquidationPeriod === 'pending' ? 9 : 10} className="text-center py-8 text-gray-500">
                                  {liquidationSearchTerm 
                                    ? t('admin_liquidation_empty_driver_search') 
                                    : activeLiquidationPeriod === 'current'
                                      ? t('admin_liquidation_empty_current_period')
                                      : activeLiquidationPeriod === 'pending'
                                        ? t('admin_liquidation_empty_pending_tab')
                                        : t('admin_liquidation_empty_period_no_drivers')
                                  }
                                </td>
                              </tr>
                            ) : (
                              getFilteredDriversForPeriod().map((period) => {
                                const isPaid = period.status === 'paid';
                                const rowKey = period.id || period.driver_id;
                                const isExpanded = expandedLiquidationRows[rowKey];
                                const hasServices = period.services_details && period.services_details.length > 0;
                                const hasPreview = Array.isArray(period.preview_services_details) && period.preview_services_details.length > 0;
                                const hasExpandable = hasServices || hasPreview;
                                const showPreviewDriverCol =
                                  (period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0)
                                  || (hasPreview && new Set(period.preview_services_details.map((s) => s.driver_id).filter(Boolean)).size > 1);
                                const isPendingTab = activeLiquidationPeriod === 'pending';
                                const isCurrentTab = activeLiquidationPeriod === 'current';
                                
                                return (
                                  <React.Fragment key={rowKey}>
                                    <tr className={`border-b hover:bg-gray-50 transition-colors ${isCurrentTab ? 'bg-green-50/30' : isPendingTab ? 'bg-amber-50/30' : ''} ${isExpanded ? 'bg-blue-50/50' : ''}`} data-testid={`driver-row-${rowKey}`}>
                                      <td className="py-2 px-4">
                                        <div className="flex items-center gap-3">
                                          {/* Expand/Collapse Button */}
                                          {hasExpandable && (
                                            <button
                                              onClick={() => setExpandedLiquidationRows(prev => ({
                                                ...prev,
                                                [rowKey]: !prev[rowKey]
                                              }))}
                                              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                                              title={isExpanded ? t('admin_hide_services') : t('admin_view_services')}
                                            >
                                              {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-gray-600" />
                                              ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-600" />
                                              )}
                                            </button>
                                          )}
                                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCurrentTab ? 'bg-green-100' : isPendingTab ? 'bg-amber-100' : 'bg-blue-100'}`}>
                                            <User className={`w-4 h-4 ${isCurrentTab ? 'text-green-600' : isPendingTab ? 'text-amber-600' : 'text-blue-600'}`} />
                                          </div>
                                          <div>
                                            <div className="font-semibold text-gray-900">{period.driver_name}</div>
                                            <div className="text-xs text-gray-500">
                                              #{(period.id || period.driver_id)?.slice(0, 8)}
                                              {period.settlement_preview_only && (
                                                <span className="ml-2 bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded text-xs font-medium">
                                                  {t('admin_liquidation_row_preview_only_badge')}
                                                </span>
                                              )}
                                              {period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 && (
                                                <span className="ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">
                                                  {t('admin_liquidation_fleet_owner_badge', { count: Object.keys(period.fleet_breakdown).length })}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        <span
                                          className="inline-flex items-center justify-center gap-0.5 min-w-[1.75rem] h-7 px-1 bg-blue-50 text-blue-700 rounded-full font-semibold text-xs"
                                          title={hasPreview ? t('admin_liquidation_preview_section_title') : undefined}
                                        >
                                          {!period.total_trips && hasPreview ? (
                                            <span className="text-amber-800">{period.preview_services_details.length}</span>
                                          ) : (
                                            <>
                                              {period.total_trips}
                                              {hasPreview && period.total_trips > 0 ? (
                                                <span className="text-[10px] text-amber-700 font-normal">+{period.preview_services_details.length}</span>
                                              ) : null}
                                            </>
                                          )}
                                        </span>
                                      </td>
                                      <td className="py-2 px-2 text-right font-medium text-gray-700">
                                        €{(Number(period.base_imponible ?? period.total_revenue) || 0).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-2 text-right font-medium text-amber-600">
                                        €{(Number(period.extras_base) || 0).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-2 text-right text-gray-600">
                                        €{(Number(period.iva_amount ?? ((Number(period.base_imponible ?? period.total_revenue) || 0) * 0.21)) || 0).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-2 text-right text-orange-600">
                                        €{(Number(period.gastos_suplidos ?? period.total_expenses) || 0).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-2 text-right text-red-600">
                                        €{(Number(period.irpf_amount) || 0).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-3 text-right">
                                        <span className="font-bold text-base text-purple-700">
                                          €{(Number(period.total ?? period.net_amount) || 0).toFixed(2)}
                                        </span>
                                      </td>
                                      {activeLiquidationPeriod !== 'current' && activeLiquidationPeriod !== 'pending' && (
                                        <td className="py-2 px-2 text-center">
                                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                            isPaid 
                                              ? 'bg-green-100 text-green-700' 
                                              : 'bg-yellow-100 text-yellow-700'
                                          }`}>
                                            {isPaid ? t('admin_liquidation_status_paid') : t('admin_liquidation_status_pending')}
                                          </span>
                                        </td>
                                      )}
                                      <td className="py-2 px-4">
                                        <div className="flex items-center justify-end gap-1">
                                          {activeLiquidationPeriod === 'current' ? (
                                            /* Current period - show expand button and info badge */
                                            <div className="flex items-center gap-2">
                                              {hasExpandable && (
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => setExpandedLiquidationRows(prev => ({
                                                    ...prev,
                                                    [rowKey]: !prev[rowKey]
                                                  }))}
                                                  className="text-blue-600 hover:bg-blue-50 h-8 px-2"
                                                  title={isExpanded ? t('admin_hide_services') : t('admin_view_services')}
                                                >
                                                  <Eye className="w-4 h-4" />
                                                </Button>
                                              )}
                                              {hasServices && currentPeriodData && (
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => downloadProvisionalDriverLiquidationExcel(
                                                    period,
                                                    currentPeriodData.period_start,
                                                    currentPeriodData.period_end
                                                  )}
                                                  className="text-green-600 hover:bg-green-50 h-8 px-2"
                                                  title={t('title_download_excel')}
                                                  data-testid={`download-excel-current-${period.driver_id}`}
                                                >
                                                  <Download className="w-4 h-4" />
                                                </Button>
                                              )}
                                              {/* Edit billing info button for current/pending periods */}
                                              <Button 
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openDriverBillingEdit({
                                                  id: period.driver_id,
                                                  name: period.driver_name,
                                                  full_name: period.full_name,
                                                  dni_nie: period.dni_nie,
                                                  fiscal_address: period.fiscal_address,
                                                  bank_name: period.bank_name,
                                                  bank_account: period.bank_account,
                                                  irpf_retention: period.irpf_percentage || 0
                                                })}
                                                className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 h-8 px-2"
                                                title={t('admin_title_edit_billing')}
                                              >
                                                <Settings className="w-4 h-4" />
                                              </Button>
                                              <span className={`px-3 py-1 ${isPendingTab ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'} text-xs font-medium rounded-full flex items-center gap-1`}>
                                                <span className={`w-1.5 h-1.5 ${isPendingTab ? 'bg-amber-500' : 'bg-green-500'} rounded-full ${isCurrentTab ? 'animate-pulse' : ''}`}></span>
                                                {isPendingTab ? t('payment_status_pending') : t('admin_accumulating_status')}
                                              </span>
                                            </div>
                                          ) : (
                                          /* Historical periods - show all action buttons */
                                          <>
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={() => {
                                                if (isPendingTab) {
                                                  setExpandedLiquidationRows((prev) => ({
                                                    ...prev,
                                                    [rowKey]: !prev[rowKey],
                                                  }));
                                                } else {
                                                  loadPeriodTrips(period);
                                                }
                                              }}
                                              className="text-blue-600 hover:bg-blue-50 h-8 px-2"
                                              title={isPendingTab ? (isExpanded ? t('admin_hide_services') : t('admin_view_services')) : t('view_details')}
                                            >
                                              <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={() => {
                                                if (isPendingTab && pendingInvoicingData) {
                                                  downloadProvisionalDriverLiquidationExcel(
                                                    period,
                                                    pendingInvoicingData.period_start,
                                                    pendingInvoicingData.period_end
                                                  );
                                                } else {
                                                  downloadDriverLiquidationExcel(period.id);
                                                }
                                              }}
                                              className="text-green-600 hover:bg-green-50 h-8 px-2"
                                              title={t('title_download_excel')}
                                              data-testid={isPendingTab ? `download-excel-pending-${period.driver_id}` : `download-excel-${period.id}`}
                                            >
                                              <Download className="w-4 h-4" />
                                            </Button>
                                            {/* Generate invoice button - always show */}
                                            {!isPendingTab && period.id && (
                                              <Button 
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                  if (period.invoice_pdf_url) {
                                                    if (window.confirm(t('admin_confirm_invoice_regenerate'))) {
                                                      generateInvoiceForPeriod(period.id, true);
                                                    }
                                                  } else {
                                                    generateInvoiceForPeriod(period.id);
                                                  }
                                                }}
                                                className={`${period.invoice_pdf_url ? 'text-orange-500 hover:bg-orange-50' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'} h-8 px-2`}
                                                title={period.invoice_pdf_url ? t('title_regenerate_invoice_pdf') : t('title_generate_invoice_pdf')}
                                              >
                                                {period.invoice_pdf_url ? <RefreshCw className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                              </Button>
                                            )}
                                            {/* Download existing PDF if available */}
                                            {!isPendingTab && period.invoice_pdf_url && (
                                              <Button 
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => window.open(`${BACKEND_URL}${period.invoice_pdf_url}`, '_blank')}
                                                className="text-purple-600 hover:bg-purple-50 h-8 px-2"
                                                title={t('title_download_pdf_existing')}
                                              >
                                                <FileText className="w-4 h-4" />
                                              </Button>
                                            )}
                                            {!isPendingTab && !isPaid && period.id && (
                                              <Button 
                                                size="sm"
                                                onClick={() => markPeriodAsPaid(period.id)}
                                                className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 ml-1"
                                              >
                                                {t('driver_billing_paid')}
                                              </Button>
                                            )}
                                            {/* Edit billing info button */}
                                            <Button 
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => openDriverBillingEdit({
                                                id: period.driver_id,
                                                name: period.driver_name,
                                                full_name: period.driver_full_name || period.full_name,
                                                dni_nie: period.driver_dni || period.dni_nie,
                                                fiscal_address: period.driver_fiscal_address || period.fiscal_address,
                                                bank_name: period.driver_bank_name || period.bank_name,
                                                bank_account: period.driver_bank_account || period.bank_account,
                                                irpf_retention: period.irpf_percentage
                                              })}
                                              className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 h-8 px-2"
                                              title={t('admin_title_edit_billing')}
                                            >
                                              <Settings className="w-4 h-4" />
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  
                                  {/* Expanded row with service details */}
                                  {isExpanded && hasExpandable && (
                                    <tr className="bg-gray-50">
                                      <td colSpan={activeLiquidationPeriod === 'current' || activeLiquidationPeriod === 'pending' ? 9 : 10} className="px-4 py-4 space-y-4">
                                        {hasServices && (
                                        <div className="bg-white rounded-lg border shadow-sm p-4">
                                          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                            <Truck className="w-5 h-5 text-blue-600" />
                                            <span className="font-semibold text-gray-800">{t('admin_services_detail_count', { count: period.services_details.length })}</span>
                                          </div>
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                              <thead>
                                                <tr className="bg-gray-100">
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_date')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_plate')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_invoices_col_vehicle')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_route_col')}</th>
                                                  {period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 && (
                                                    <th className="text-left py-2 px-3 font-medium text-gray-600">{t('admin_driver_col')}</th>
                                                  )}
                                                  <th className="text-right py-2 px-3 font-medium text-gray-600">{t('admin_rate_col')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-amber-600">{t('admin_invoices_col_extras')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-gray-600">{t('admin_supplies_col')}</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {period.services_details.map((service, idx) => (
                                                  <tr key={service.id || idx} className="border-b hover:bg-gray-50">
                                                    <td className="py-2 px-3">
                                                      <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-gray-400" />
                                                        <span>{service.pickup_date_from ? new Date(service.pickup_date_from).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' }) : '-'}</span>
                                                      </div>
                                                    </td>
                                                    <td className="py-2 px-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                                                          {service.car_plate || '-'}
                                                        </span>
                                                        {service.debug_in_other_period && (
                                                          <span
                                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300"
                                                            title={
                                                              (service.debug_other_period_refs || [])
                                                                .map((ref) => `${ref.period_type || 'period'} ${ref.period_start} -> ${ref.period_end}`)
                                                                .join(' | ') || 'Service also exists in another billing period'
                                                            }
                                                          >
                                                            cross-period
                                                          </span>
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-600">
                                                      {service.car_brand} {service.car_model}
                                                    </td>
                                                    <td className="py-2 px-3">
                                                      <div className="max-w-xs">
                                                        <div className="flex items-center gap-1 text-xs">
                                                          <span className="text-green-600">●</span>
                                                          <span className="truncate" title={service.pickup_address}>
                                                            {service.pickup_address?.split(',')[0] || '-'}
                                                          </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 text-xs mt-0.5">
                                                          <span className="text-red-600">●</span>
                                                          <span className="truncate" title={service.delivery_address}>
                                                            {service.delivery_address?.split(',')[0] || '-'}
                                                          </span>
                                                        </div>
                                                      </div>
                                                    </td>
                                                    {period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 && (
                                                      <td className="py-2 px-3">
                                                        <span className="text-gray-700">{service.driver_name || '-'}</span>
                                                      </td>
                                                    )}
                                                    <td className="py-2 px-3 text-right font-medium text-gray-800">
                                                      €{(service.driver_price || 0).toFixed(2)}
                                                    </td>
                                                    <td className="py-2 px-3 text-right font-medium text-amber-600">
                                                      {service.driver_extras_total > 0 ? (
                                                        <span title={service.driver_extras?.map(e => `${e.category_name}: €${e.total}`).join(', ')}>
                                                          €{(service.driver_extras_total || 0).toFixed(2)}
                                                        </span>
                                                      ) : '-'}
                                                    </td>
                                                    <td className="py-2 px-3 text-right text-orange-600">
                                                      €{(service.expenses || 0).toFixed(2)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                              <tfoot>
                                                <tr className="bg-gray-100 font-semibold">
                                                  <td colSpan={period.fleet_breakdown && Object.keys(period.fleet_breakdown).length > 0 ? 5 : 4} className="py-2 px-3 text-right text-gray-700">
                                                    {t('admin_totals_label')}
                                                  </td>
                                                  <td className="py-2 px-3 text-right text-gray-800">
                                                    €{period.services_details.reduce((sum, s) => sum + (s.driver_price || 0), 0).toFixed(2)}
                                                  </td>
                                                  <td className="py-2 px-3 text-right text-amber-600">
                                                    €{period.services_details.reduce((sum, s) => sum + (s.driver_extras_total || 0), 0).toFixed(2)}
                                                  </td>
                                                  <td className="py-2 px-3 text-right text-orange-600">
                                                    €{period.services_details.reduce((sum, s) => sum + (s.expenses || 0), 0).toFixed(2)}
                                                  </td>
                                                </tr>
                                              </tfoot>
                                            </table>
                                          </div>
                                        </div>
                                        )}
                                        {hasPreview && (
                                        <div className="bg-amber-50/90 rounded-lg border border-amber-300 shadow-sm p-4">
                                          <div className="flex items-start gap-2 mb-3 pb-2 border-b border-amber-200">
                                            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                                            <div>
                                              <div className="font-semibold text-amber-950">{t('admin_liquidation_preview_section_title')}</div>
                                              <p className="text-xs text-amber-900 mt-1 leading-relaxed">{t('admin_liquidation_preview_section_note')}</p>
                                            </div>
                                          </div>
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                              <thead>
                                                <tr className="bg-amber-100">
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_invoices_col_date')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_invoices_col_plate')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_service_id_col')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_invoices_col_vehicle')}</th>
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_route_col')}</th>
                                                  {showPreviewDriverCol && (
                                                    <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_driver_col')}</th>
                                                  )}
                                                  <th className="text-left py-2 px-3 font-medium text-gray-700">{t('admin_liquidation_preview_col_settlement')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-gray-700">{t('admin_rate_col')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-amber-700">{t('admin_invoices_col_extras')}</th>
                                                  <th className="text-right py-2 px-3 font-medium text-gray-700">{t('admin_supplies_col')}</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {period.preview_services_details.map((service, pidx) => (
                                                  <tr key={service.id || pidx} className="border-b border-amber-100/80 hover:bg-amber-50/50">
                                                    <td className="py-2 px-3">
                                                      <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-amber-700/70" />
                                                        <span>{service.pickup_date_from ? new Date(service.pickup_date_from).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' }) : '-'}</span>
                                                      </div>
                                                    </td>
                                                    <td className="py-2 px-3 font-mono bg-amber-100/50 px-2 py-0.5 rounded text-gray-800">
                                                      {service.car_plate || '-'}
                                                    </td>
                                                    <td className="py-2 px-3">
                                                      <button
                                                        type="button"
                                                        className="text-blue-700 hover:underline font-mono text-xs"
                                                        onClick={() => navigate(`/admin/servicio/${service.id}`)}
                                                      >
                                                        {(service.id || '').slice(0, 10)}…
                                                      </button>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-700">
                                                      {service.car_brand} {service.car_model}
                                                    </td>
                                                    <td className="py-2 px-3">
                                                      <div className="max-w-xs text-xs">
                                                        <div className="flex items-center gap-1">
                                                          <span className="text-green-700">●</span>
                                                          <span className="truncate" title={service.pickup_address}>{service.pickup_address?.split(',')[0] || '-'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                          <span className="text-red-700">●</span>
                                                          <span className="truncate" title={service.delivery_address}>{service.delivery_address?.split(',')[0] || '-'}</span>
                                                        </div>
                                                      </div>
                                                    </td>
                                                    {showPreviewDriverCol && (
                                                      <td className="py-2 px-3 text-gray-700">{service.driver_name || '-'}</td>
                                                    )}
                                                    <td className="py-2 px-3">
                                                      <SettlementApprovalChip
                                                        order={{
                                                          driver_id: service.driver_id,
                                                          approval_status: service.approval_status,
                                                          include_in_driver_invoice: false,
                                                        }}
                                                        t={t}
                                                      />
                                                    </td>
                                                    <td className="py-2 px-3 text-right font-medium text-gray-900">
                                                      €{(service.driver_price || 0).toFixed(2)}
                                                    </td>
                                                    <td className="py-2 px-3 text-right text-amber-800">
                                                      {service.driver_extras_total > 0 ? (
                                                        <span title={service.driver_extras?.map((e) => `${e.category_name}: €${e.total}`).join(', ')}>
                                                          €{(service.driver_extras_total || 0).toFixed(2)}
                                                        </span>
                                                      ) : '-'}
                                                    </td>
                                                    <td className="py-2 px-3 text-right text-orange-700">
                                                      €{(service.expenses || 0).toFixed(2)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                              <tfoot>
                                                <tr className="bg-amber-100 font-semibold text-amber-950">
                                                  <td colSpan={showPreviewDriverCol ? 7 : 6} className="py-2 px-3 text-right"
                                                  >
                                                    {t('admin_totals_label')} ({t('admin_liquidation_preview_section_title')})
                                                  </td>
                                                  <td className="py-2 px-3 text-right">
                                                    €{period.preview_services_details.reduce((sum, s) => sum + (s.driver_price || 0), 0).toFixed(2)}
                                                  </td>
                                                  <td className="py-2 px-3 text-right text-amber-900">
                                                    €{period.preview_services_details.reduce((sum, s) => sum + (s.driver_extras_total || 0), 0).toFixed(2)}
                                                  </td>
                                                  <td className="py-2 px-3 text-right text-orange-800">
                                                    €{period.preview_services_details.reduce((sum, s) => sum + (s.expenses || 0), 0).toFixed(2)}
                                                  </td>
                                                </tr>
                                              </tfoot>
                                            </table>
                                          </div>
                                        </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Summary Footer */}
                      {getFilteredDriversForPeriod().length > 0 && (
                        <div className="mt-4 pt-4 border-t bg-gray-50 rounded-lg p-4">
                          <div className="flex flex-wrap justify-between items-center gap-4">
                            <div className="text-sm text-gray-600">
                              {t('admin_liquidation_footer_drivers', { count: getFilteredDriversForPeriod().length })}
                            </div>
                            <div className="flex gap-6 text-sm">
                              <div>
                                <span className="text-gray-500">{t('admin_liquidation_footer_total_base')}</span>
                                <span className="ml-2 font-semibold text-gray-700">
                                  €{getFilteredDriversForPeriod().reduce((sum, p) => sum + (p.base_imponible || p.total_revenue || 0), 0).toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">{t('admin_liquidation_footer_total_supplies')}</span>
                                <span className="ml-2 font-semibold text-orange-600">
                                  €{getFilteredDriversForPeriod().reduce((sum, p) => sum + (p.gastos_suplidos || p.total_expenses || 0), 0).toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">{t('admin_liquidation_footer_total_to_pay')}</span>
                                <span className="ml-2 font-bold text-lg text-purple-700">
                                  €{getFilteredDriversForPeriod().reduce((sum, p) => sum + (p.total || p.net_amount || 0), 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Profiles Tab - Visible for all admins, but sub_admins only see their assigned drivers */}
          {activeTab === 'profiles' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('admin_profiles_title')}</h2>
                <p className="text-gray-600 mt-1">
                  {canSeeClientPrices 
                    ? t('admin_profiles_subtitle')
                    : t('admin_profiles_subtitle_drivers_only')
                  }
                </p>
              </div>

              <Tabs defaultValue={canSeeClientPrices ? "clients" : "drivers"} className="w-full">
                <TabsList className={`grid w-full ${canSeeClientPrices ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {canSeeClientPrices && <TabsTrigger value="clients">{t('admin_profiles_clients_tab')}</TabsTrigger>}
                  <TabsTrigger value="drivers">{t('admin_profiles_drivers_tab')}</TabsTrigger>
                  {canSeeClientPrices && <TabsTrigger value="fleets" onClick={loadFleetData}>{t('admin_profiles_fleets_tab')}</TabsTrigger>}
                </TabsList>
                
                {canSeeClientPrices && (
                <TabsContent value="clients" className="mt-6">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>{t('admin_clients_registered')}</CardTitle>
                          <CardDescription>{t('admin_clients_detail')}</CardDescription>
                        </div>
                        <Button onClick={() => setShowClientCreateDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]">
                          <Plus className="w-4 h-4 mr-2" />
                          {t('admin_create_client')}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Buscador de Clientes */}
                      <div className="mb-4 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type="text"
                          placeholder={t('admin_search_client_placeholder')}
                          value={searchClient}
                          onChange={(e) => setSearchClient(e.target.value)}
                          className="pl-10 w-full"
                        />
                      </div>

                      {filteredClients.length === 0 ? (
                        <p className="text-center py-8 text-gray-500">
                          {searchClient ? t('admin_no_clients_search') : t('admin_no_clients_registered')}
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {getPaginatedItems(filteredClients, clientsPage).map((client) => (
                            <Card key={client.id} className="border-l-4 border-blue-500">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      {client.company_logo ? (
                                        <img 
                                          src={client.company_logo} 
                                          alt="Logo" 
                                          className="w-10 h-10 rounded-full object-cover border-2 border-blue-500"
                                        />
                                      ) : (
                                        <UserCircle className="w-10 h-10 text-blue-500" />
                                      )}
                                      <div>
                                        <h3 className="text-lg font-semibold">{client.company_name || client.name}</h3>
                                        <p className="text-sm text-gray-600">{client.email}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">{t('admin_cif_dni_label')}</span>
                                        <p className="font-medium">{client.cif_dni || t('admin_not_specified')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_phone_label')}</span>
                                        <p className="font-medium">{client.phone || t('admin_not_specified')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_address_label')}</span>
                                        <p className="font-medium">{client.address || t('admin_not_specified_f')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_country_label')}</span>
                                        <p className="font-medium">{client.country || t('admin_not_specified')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_registered_label')}</span>
                                        <p className="font-medium">{new Date(client.created_at).toLocaleDateString(dateLocale)}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_tariff_label_colon')}</span>
                                        <p className="font-medium">
                                          {client.has_custom_pricing ? (
                                            <span className="text-blue-600">{t('admin_custom_rate')}</span>
                                          ) : (
                                            <span className="text-gray-600">{t('admin_general_rate')}</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleEditClient(client)}
                                  >
                                    {t('admin_view_details')}
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}

                      {/* Pagination for Clients */}
                      {filteredClients.length > ITEMS_PER_PAGE && (
                        <Pagination
                          currentPage={clientsPage}
                          totalPages={getTotalPages(filteredClients.length)}
                          totalItems={filteredClients.length}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={(page) => {
                            setClientsPage(page);
                          }}
                          className="mt-6"
                        />
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                )}
                
                <TabsContent value="drivers" className="mt-6">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>{canSeeClientPrices ? t('admin_drivers_registered_title') : t('admin_drivers_assigned_title')}</CardTitle>
                          <CardDescription>
                            {canSeeClientPrices 
                              ? t('admin_drivers_detail')
                              : t('admin_drivers_assigned_desc')
                            }
                          </CardDescription>
                        </div>
                        <Button onClick={() => setShowDriverCreateDialog(true)} className="bg-[#36e961] hover:bg-[#2dd156]">
                          <Plus className="w-4 h-4 mr-2" />
                          {t('admin_create_driver')}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Buscador de Conductores */}
                      <div className="mb-4 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                          type="text"
                          placeholder={t('admin_search_driver_profiles_placeholder')}
                          value={searchDriver}
                          onChange={(e) => setSearchDriver(e.target.value)}
                          className="pl-10 w-full"
                        />
                      </div>

                      {filteredDrivers.length === 0 ? (
                        <p className="text-center py-8 text-gray-500">
                          {searchDriver ? t('admin_no_drivers_search') : t('admin_no_drivers_registered')}
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {filteredDrivers.map((driver) => (
                            <Card key={driver.id} className="border-l-4 border-green-500">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <UserCircle className="w-8 h-8 text-green-500" />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <h3 className="text-lg font-semibold">{driver.name}</h3>
                                          <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getDriverStatusBadge(driver).color}`}>
                                            {getDriverStatusBadge(driver).text}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-600">{driver.email}</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">{t('admin_driver_dni_label')}</span>
                                        <p className="font-medium">{driver.license_number || t('admin_not_specified')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_phone_label')}</span>
                                        <p className="font-medium">{driver.phone}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_vehicle_label')}:</span>
                                        <p className="font-medium">{driver.vehicle_info || t('admin_not_specified')}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_status_label')}</span>
                                        <p className="font-medium">
                                          {driver.is_available ? (
                                            <span className="text-green-600">{t('driver_status_available')}</span>
                                          ) : (
                                            <span className="text-red-600">{t('driver_status_not_available')}</span>
                                          )}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">{t('admin_registered_label')}</span>
                                        <p className="font-medium">{new Date(driver.created_at).toLocaleDateString(dateLocale)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleEditDriver(driver)}
                                    >
                                      {t('admin_edit')}
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => {
                                        setSelectedDriver(driver);
                                        setShowDriverProfileDialog(true);
                                      }}
                                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      {t('admin_view_more')}
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
              
              {/* Generate Liquidation Dialog */}
              <Dialog open={showGenerateLiquidationDialog} onOpenChange={setShowGenerateLiquidationDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#36e961]" />
                      {t('admin_liquidation_dialog_title')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('admin_liquidation_dialog_description')}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>{t('admin_liquidation_period_type')}</Label>
                      <Select
                        value={liquidationForm.period_type}
                        onValueChange={(value) => setLiquidationForm(prev => ({ ...prev, period_type: value }))}
                      >
                        <SelectTrigger data-testid="liquidation-period-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quincena_1">{t('admin_liquidation_quincena_1')}</SelectItem>
                          <SelectItem value="quincena_2">{t('admin_liquidation_quincena_2')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('admin_liquidation_year_label')}</Label>
                        <Select
                          value={liquidationForm.year.toString()}
                          onValueChange={(value) => setLiquidationForm(prev => ({ ...prev, year: parseInt(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2024">2024</SelectItem>
                            <SelectItem value="2025">2025</SelectItem>
                            <SelectItem value="2026">2026</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t('admin_liquidation_payment_month_label')}</Label>
                        <Select
                          value={liquidationForm.month.toString()}
                          onValueChange={(value) => setLiquidationForm(prev => ({ ...prev, month: parseInt(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                              <SelectItem key={m} value={String(m)}>
                                {new Date(2000, m - 1, 1).toLocaleDateString(dateLocale, { month: 'long' })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <p className="font-medium text-gray-700 mb-1">{t('admin_liquidation_period_summary')}</p>
                      {liquidationForm.period_type === 'quincena_1' ? (
                        <p className="text-gray-600">
                          {t('admin_liquidation_label_services')}{' '}
                          {liquidationForm.month === 1
                            ? new Date(liquidationForm.year, 11, 1).toLocaleDateString(dateLocale, { month: 'short' })
                            : new Date(liquidationForm.year, liquidationForm.month - 2, 1).toLocaleDateString(dateLocale, { month: 'short' })
                          } 25 - {new Date(liquidationForm.year, liquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 9
                          <br />
                          {t('admin_liquidation_label_payment')}{' '}
                          {new Date(liquidationForm.year, liquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 10-15
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          {t('admin_liquidation_label_services')}{' '}
                          {new Date(liquidationForm.year, liquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 10-24
                          <br />
                          {t('admin_liquidation_label_payment')}{' '}
                          {new Date(liquidationForm.year, liquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} {t('admin_liquidation_payment_suffix_last')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowGenerateLiquidationDialog(false)}>
                      {t('cancel')}
                    </Button>
                    <Button 
                      onClick={generateDriverLiquidations}
                      disabled={generatingLiquidations}
                      className="bg-[#36e961] hover:bg-[#2bc950] text-black"
                      data-testid="confirm-generate-liquidations"
                    >
                      {generatingLiquidations ? t('admin_generating') : t('admin_liquidation_generate_btn')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Client Invoice Generation Dialog */}
          <Dialog open={showGenerateClientLiquidationDialog} onOpenChange={setShowGenerateClientLiquidationDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {t('admin_client_invoice_dialog_title')}
                </DialogTitle>
                <DialogDescription>
                  {t('admin_client_invoice_dialog_description')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>{t('admin_liquidation_period_type')}</Label>
                  <Select
                    value={clientLiquidationForm.period_type}
                    onValueChange={(value) => setClientLiquidationForm(prev => ({ ...prev, period_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quincena_1">{t('admin_client_invoice_q1')}</SelectItem>
                      <SelectItem value="quincena_2">{t('admin_client_invoice_q2')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('admin_liquidation_year_label')}</Label>
                    <Select
                      value={clientLiquidationForm.year.toString()}
                      onValueChange={(value) => setClientLiquidationForm(prev => ({ ...prev, year: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('admin_liquidation_payment_month_label')}</Label>
                    <Select
                      value={clientLiquidationForm.month.toString()}
                      onValueChange={(value) => setClientLiquidationForm(prev => ({ ...prev, month: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {new Date(2000, m - 1, 1).toLocaleDateString(dateLocale, { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-700 mb-1">{t('admin_liquidation_period_summary')}</p>
                  {clientLiquidationForm.period_type === 'quincena_1' ? (
                    <p className="text-blue-600">
                      {t('admin_liquidation_label_services')}{' '}
                      {clientLiquidationForm.month === 1
                        ? new Date(clientLiquidationForm.year, 11, 1).toLocaleDateString(dateLocale, { month: 'short' })
                        : new Date(clientLiquidationForm.year, clientLiquidationForm.month - 2, 1).toLocaleDateString(dateLocale, { month: 'short' })
                      } 25 - {new Date(clientLiquidationForm.year, clientLiquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 9
                      <br />
                      {t('admin_client_invoice_label_billing')}{' '}
                      {new Date(clientLiquidationForm.year, clientLiquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 10-15
                    </p>
                  ) : (
                    <p className="text-blue-600">
                      {t('admin_liquidation_label_services')}{' '}
                      {new Date(clientLiquidationForm.year, clientLiquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} 10-24
                      <br />
                      {t('admin_client_invoice_label_billing')}{' '}
                      {new Date(clientLiquidationForm.year, clientLiquidationForm.month - 1, 1).toLocaleDateString(dateLocale, { month: 'short' })} {t('admin_liquidation_payment_suffix_last')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowGenerateClientLiquidationDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={generateClientLiquidations}
                  disabled={generatingClientLiquidations}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {generatingClientLiquidations ? t('admin_generating') : t('admin_client_invoice_generate_btn')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('admin_documents_drivers_title')}</h2>
                <p className="text-gray-600 mt-1">{t('admin_documents_subtitle')}</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_documents_pending_expired_title')}</CardTitle>
                  <CardDescription>
                    {t('admin_documents_require_attention', { count: pendingDocuments.length })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingDocuments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>{t('admin_no_documents_pending')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingDocuments.map((doc) => {
                        return (
                          <Card key={doc.id} className="border-l-4 border-yellow-500">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <UserCircle className="w-8 h-8 text-yellow-500" />
                                    <div>
                                      <h3 className="font-semibold">{doc.driver_name}</h3>
                                      <p className="text-sm text-gray-600">{doc.driver_email}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-600">{t('admin_document_label')}</span>
                                      <p className="font-medium">{getDriverDocumentTypeLabel(t, doc.document_type)}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">{t('admin_upload_date_label')}</span>
                                      <p className="font-medium">{new Date(doc.upload_date).toLocaleDateString(dateLocale)}</p>
                                    </div>
                                    {doc.expiry_date && (
                                      <div>
                                        <span className="text-gray-600">{t('admin_expiry_date_label')}</span>
                                        <p className="font-medium">{new Date(doc.expiry_date).toLocaleDateString(dateLocale)}</p>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-gray-600">{t('admin_service_status_label')}</span>
                                      <p className="font-medium">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                          doc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          doc.status === 'expired' ? 'bg-orange-100 text-orange-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {doc.status === 'pending' ? `⏳ ${t('driver_doc_pending')}` :
                                           doc.status === 'expired' ? t('admin_status_expired') : doc.status}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex gap-2 ml-4">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewDocument(doc)}
                                    className="mr-2"
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    {t('driver_view')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleApproveDocument(doc.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    {t('admin_approve_btn')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRejectDocument(doc.id)}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    {t('admin_reject_btn')}
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('admin_approvals_page_title')}</h2>
                  <p className="text-gray-600 mt-1">{t('admin_approvals_page_subtitle')}</p>
                  {lastApprovalReconcile && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t('admin_approvals_last_reconcile', {
                        defaultValue: 'Última sincronización con la app: {{time}}',
                        time: lastApprovalReconcile.toLocaleTimeString(),
                      })}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reconcileApprovals({ silent: false })}
                  disabled={reconcilingApprovals}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${reconcilingApprovals ? 'animate-spin' : ''}`} />
                  {t('admin_approvals_refresh', { defaultValue: 'Sincronizar con la app' })}
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('admin_approvals_card_title')}</CardTitle>
                  <CardDescription>
                    {t('admin_approvals_card_description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {orders.filter(o => o.approval_status === 'pending_approval').length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>{t('admin_approvals_empty')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.filter(o => o.approval_status === 'pending_approval').map((order) => (
                        <Card key={order.id} className="border-l-4 border-yellow-500">
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="font-semibold text-lg">#{order.id.substring(0, 8).toUpperCase()}</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  <strong>{t('admin_incident_vehicle_label')}</strong> {order.car_brand} {order.car_model} ({order.car_plate})
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>{t('admin_driver_label')}</strong> {order.driver_name}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>{t('admin_client_label')}</strong> {order.client_name}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                  ⏳ {t('admin_pending_approval_badge')}
                                </span>
                                <SettlementApprovalChip order={order} t={t} />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                              <div>
                                <span className="text-gray-600">{t('calendar_origin')}</span>
                                <p className="font-medium">{order.pickup_address}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">{t('calendar_destination')}</span>
                                <p className="font-medium">{order.delivery_address}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">{t('admin_total_price_label')}</span>
                                <p className="font-medium">€{order.total_price}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">{t('admin_distance')}</span>
                                <p className="font-medium">{order.distance_km || 0} km</p>
                              </div>
                              <div>
                                <span className="text-gray-600">{t('admin_service_status_label')}</span>
                                <p className="font-medium">{order.status}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">{t('admin_service_type_label')}</span>
                                <p className="font-medium">
                                  {order.service_type === 'delivery' ? t('delivery') : 
                                   order.service_type === 'bc_service' ? t('bc_service') : 
                                   order.service_type === 'tradein' ? t('tradein') : 
                                   order.service_type}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3 mb-4 text-sm">
                              <div className="flex items-center gap-1">
                                {order.has_pickup_photos ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span>{t('admin_photos_pickup_label')}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {order.has_delivery_photos ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span>{t('admin_photos_delivery_label')}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {order.has_expense_report ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span>{t('admin_expenses_label')}</span>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedOrderForView(order);
                                  setShowAdminPhotosDialog(true);
                                  loadAdminVehiclePhotos(order.id);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                {t('admin_view_photos_btn')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedOrderForView(order);
                                  setShowAdminExpensesDialog(true);
                                  loadAdminOrderExpenses(order.id);
                                }}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                {t('admin_view_expenses')}
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('admin_token');
                                    const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/approve`, {
                                      method: 'PUT',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({ approval_status: 'approved' })
                                    });
                                    
                                    if (response.ok) {
                                      toast.success(t('admin_toast_service_approved'));
                                      await loadData();
                                    } else {
                                      toast.error(t('admin_toast_error_approve_service'));
                                    }
                                  } catch (error) {
                                    toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                  }
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {t('admin_approve_btn')}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  const reason = prompt(t('admin_prompt_rejection_reason'));
                                  if (!reason) return;
                                  
                                  try {
                                    const token = localStorage.getItem('admin_token');
                                    const response = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/approve`, {
                                      method: 'PUT',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({ 
                                        approval_status: 'rejected',
                                        rejection_reason: reason
                                      })
                                    });
                                    
                                    if (response.ok) {
                                      toast.success(t('admin_toast_service_rejected'));
                                      await loadData();
                                    } else {
                                      toast.error(t('admin_toast_error_reject_service'));
                                    }
                                  } catch (error) {
                                    toast.error(t('admin_toast_error_colon_message', { message: error.message }));
                                  }
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                {t('admin_reject_btn')}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sub-Admins Management Tab */}
          {activeTab === 'sub-admins' && adminType === 'super_admin' && (
            <SubAdminManagement drivers={drivers} orders={orders} />
          )}

          {/* Incidents Tab */}
          {activeTab === 'incidents' && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                    {t('admin_incidents_reported_title')}
                  </h2>
                  <p className="text-gray-600 mt-1">{t('admin_incidents_page_subtitle')}</p>
                </div>
                <Button variant="outline" onClick={loadIncidents}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('gps_live_refresh')}
                </Button>
              </div>

              {/* Tabs: Activas / Histórico */}
              <div className="flex gap-4 border-b">
                <button
                  className={`pb-3 px-1 font-medium transition-colors ${
                    incidentTab === 'active' 
                      ? 'border-b-2 border-red-500 text-red-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setIncidentTab('active')}
                >
                  {t('admin_incidents_tab_active')}
                  {incidents.filter(i => i.status !== 'resolved').length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {incidents.filter(i => i.status !== 'resolved').length}
                    </span>
                  )}
                </button>
                <button
                  className={`pb-3 px-1 font-medium transition-colors ${
                    incidentTab === 'history' 
                      ? 'border-b-2 border-gray-500 text-gray-700' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setIncidentTab('history')}
                >
                  {t('admin_historical')}
                  <span className="ml-2 bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {incidents.filter(i => i.status === 'resolved').length}
                  </span>
                </button>
              </div>

              {loadingIncidents ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (() => {
                const filteredIncidents = incidentTab === 'active' 
                  ? incidents.filter(i => i.status !== 'resolved')
                  : incidents.filter(i => i.status === 'resolved');
                
                return filteredIncidents.length === 0 ? (
                  <Card className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700">
                      {incidentTab === 'active' ? t('admin_incidents_empty_active_title') : t('admin_incidents_empty_history_title')}
                    </h3>
                    <p className="text-gray-500 mt-2">
                      {incidentTab === 'active' 
                        ? t('admin_incidents_empty_active_desc') 
                        : t('admin_incidents_empty_history_desc')}
                    </p>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredIncidents.map(incident => {
                      const order = orders.find(o => o.id === incident.order_id);
                      const driver = drivers.find(d => d.id === incident.driver_id);
                      
                      const incidentTypeLabels = {
                        'pickup_unavailable': { label: t('admin_incident_type_pickup_unavailable'), icon: '🚗', color: 'bg-yellow-100 text-yellow-800' },
                        'vehicle_breakdown': { label: t('admin_incident_type_vehicle_breakdown'), icon: '🔧', color: 'bg-red-100 text-red-800' },
                        'accident': { label: t('admin_incident_type_accident'), icon: '💥', color: 'bg-red-100 text-red-800' },
                        'minor_damage': { label: t('admin_incident_type_minor_damage'), icon: '⚠️', color: 'bg-orange-100 text-orange-800' },
                        'delay': { label: t('admin_incident_type_delay'), icon: '⏰', color: 'bg-blue-100 text-blue-800' },
                        'other': { label: t('admin_incident_type_other'), icon: '📝', color: 'bg-gray-100 text-gray-800' }
                      };
                      
                      const statusColors = {
                        'reported': 'bg-red-500',
                        'in_progress': 'bg-yellow-500',
                        'resolved': 'bg-green-500'
                      };
                      
                      const typeInfo = incidentTypeLabels[incident.incident_type] || incidentTypeLabels.other;
                      
                      return (
                        <Card 
                          key={incident.id} 
                          className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowIncidentDialog(true);
                          }}
                        >
                          <div className="flex items-start gap-4">
                            {/* Status indicator */}
                            <div className={`w-3 h-3 rounded-full ${statusColors[incident.status]} mt-2 flex-shrink-0`} />
                            
                            {/* Main content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                                  {typeInfo.icon} {typeInfo.label}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {new Date(incident.created_at).toLocaleString(dateLocale, { 
                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                                  })}
                                </span>
                                {incident.severity === 'high' && (
                                  <span className="px-2 py-1 bg-red-600 text-white rounded-full text-xs font-bold animate-pulse">
                                    {t('admin_incident_severity_urgent')}
                                  </span>
                                )}
                              </div>
                              
                              <h4 className="font-semibold text-gray-900 mt-2">
                                {order?.car_plate || 'N/A'} - {order?.car_brand} {order?.car_model}
                              </h4>
                              
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {incident.description}
                              </p>
                              
                              {incident.cancellation_reason && (
                                <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded mt-2">
                                  <strong>{t('admin_incident_card_cancellation_prefix')}</strong> {incident.cancellation_reason}
                                </p>
                              )}
                              
                              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {driver?.name || t('admin_unknown_driver')}
                                </span>
                                {order && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {order.pickup_address?.split(',')[0]}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Photos preview */}
                            <div className="flex gap-2 flex-shrink-0">
                              {incident.contract_photo && (
                                <div className="relative">
                                  <img 
                                    src={incident.contract_photo} 
                                    alt={t('admin_incident_alt_contract')} 
                                    className="w-16 h-16 object-cover rounded border"
                                  />
                                  <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-[10px] px-1 rounded">
                                    {t('admin_incident_alt_contract')}
                                  </span>
                                </div>
                              )}
                              {incident.vehicle_photo && (
                                <div className="relative">
                                  <img 
                                    src={incident.vehicle_photo} 
                                    alt={t('admin_incident_alt_vehicle')} 
                                    className="w-16 h-16 object-cover rounded border"
                                  />
                                  <span className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-[10px] px-1 rounded">
                                    {t('admin_incident_alt_vehicle')}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Action button */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedIncident(incident);
                                setShowIncidentDialog(true);
                              }}
                            >
                              {t('admin_view_details')}
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}
              
              {/* Incident Detail Dialog */}
              <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  {selectedIncident && (() => {
                    const order = orders.find(o => o.id === selectedIncident.order_id);
                    const driver = drivers.find(d => d.id === selectedIncident.driver_id);
                    const typeLabels = {
                      'pickup_unavailable': t('admin_incident_type_pickup_unavailable'),
                      'vehicle_breakdown': t('admin_incident_type_vehicle_breakdown'),
                      'accident': t('admin_incident_type_accident'),
                      'minor_damage': t('admin_incident_type_minor_damage'),
                      'delay': t('admin_incident_type_delay'),
                      'other': t('admin_incident_type_other')
                    };
                    
                    return (
                      <>
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            {typeLabels[selectedIncident.incident_type]}
                          </DialogTitle>
                          <DialogDescription>
                            {t('admin_incident_reported_on', {
                              datetime: new Date(selectedIncident.created_at).toLocaleString(dateLocale)
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                          {/* Service Info */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold mb-2">{t('admin_incident_service_info_heading')}</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div><strong>{t('admin_incident_vehicle_label')}</strong> {order?.car_plate} - {order?.car_brand} {order?.car_model}</div>
                              <div><strong>{t('admin_incident_driver_label')}</strong> {driver?.name}</div>
                              <div><strong>{t('calendar_origin')}</strong> {order?.pickup_address}</div>
                              <div><strong>{t('calendar_destination')}</strong> {order?.delivery_address}</div>
                            </div>
                          </div>
                          
                          {/* Incident Details */}
                          <div>
                            <h4 className="font-semibold mb-2">{t('admin_incident_description_heading')}</h4>
                            <p className="text-gray-700 bg-white p-3 border rounded">{selectedIncident.description}</p>
                          </div>
                          
                          {selectedIncident.cancellation_reason && (
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                              <h4 className="font-semibold text-yellow-800 mb-2">{t('admin_incident_cancellation_heading')}</h4>
                              <p className="text-yellow-900">{selectedIncident.cancellation_reason}</p>
                            </div>
                          )}
                          
                          {/* Photos */}
                          {(selectedIncident.contract_photo || selectedIncident.vehicle_photo) && (
                            <div>
                              <h4 className="font-semibold mb-2">{t('admin_incident_photos_heading')}</h4>
                              <div className="flex gap-4 flex-wrap">
                                {selectedIncident.contract_photo && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">{t('admin_incident_contract_signed_caption')}</p>
                                    <img 
                                      src={selectedIncident.contract_photo} 
                                      alt={t('admin_incident_alt_contract')} 
                                      className="w-48 h-48 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => window.open(selectedIncident.contract_photo, '_blank')}
                                    />
                                  </div>
                                )}
                                {selectedIncident.vehicle_photo && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1">{t('admin_incident_vehicle_photo_caption')}</p>
                                    <img 
                                      src={selectedIncident.vehicle_photo} 
                                      alt={t('admin_incident_alt_vehicle')} 
                                      className="w-48 h-48 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => window.open(selectedIncident.vehicle_photo, '_blank')}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Admin Notes */}
                          {selectedIncident.admin_notes && (
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <h4 className="font-semibold text-blue-800 mb-2">{t('admin_incident_admin_notes_heading')}</h4>
                              <p className="text-blue-900">{selectedIncident.admin_notes}</p>
                            </div>
                          )}
                          
                          {/* Status */}
                          <div className="flex items-center justify-between pt-4 border-t">
                            <div>
                              <span className="text-sm text-gray-500">{t('admin_incident_status_current_label')}</span>
                              <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${
                                selectedIncident.status === 'reported' ? 'bg-red-100 text-red-800' :
                                selectedIncident.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {selectedIncident.status === 'reported' ? t('admin_incident_status_reported') :
                                 selectedIncident.status === 'in_progress' ? t('admin_incident_status_in_progress_short') : t('admin_incident_status_resolved')}
                              </span>
                            </div>
                            {/* Service status */}
                            {order && (
                              <div>
                                <span className="text-sm text-gray-500">{t('admin_incident_order_status_label')}</span>
                                <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${
                                  order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                  order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {t(order.status)}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Sent to client indicator */}
                          {selectedIncident.admin_notes?.includes('[Enviado al cliente:') && (
                            <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-sm text-green-800">
                              ✅ {t('admin_incident_report_sent_to_client')}
                            </div>
                          )}
                          
                          {/* Info about "En Progreso" status */}
                          {selectedIncident.status === 'in_progress' && (
                            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 text-sm text-yellow-800">
                              ⏳ {t('admin_incident_in_progress_notice')}
                            </div>
                          )}
                        </div>
                        
                        {/* Finalize Service Form - Shows when clicking "Finalizar Servicio" */}
                        {showFinalizeFromIncidentForm && order && !['delivered', 'cancelled', 'cancelled_at_pickup'].includes(order.status) && (
                          <div className="border-t pt-4 mt-4 space-y-4 bg-purple-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              {t('admin_incident_finalize_title')}
                            </h4>
                            
                            {/* Final Status Selection */}
                            <div className="bg-white p-3 rounded-lg border">
                              <Label className="text-sm font-medium mb-2 block">{t('admin_incident_finalize_how_label')}</Label>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                                    finalizeIncidentForm.final_status === 'delivered'
                                      ? 'border-green-500 bg-green-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => setFinalizeIncidentForm({...finalizeIncidentForm, final_status: 'delivered'})}
                                >
                                  <div className="font-medium text-green-700">✅ {t('admin_incident_finalize_delivered_title')}</div>
                                  <div className="text-xs text-gray-500 mt-1">{t('admin_incident_finalize_delivered_help')}</div>
                                </button>
                                <button
                                  type="button"
                                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                                    finalizeIncidentForm.final_status === 'cancelled_at_pickup'
                                      ? 'border-orange-500 bg-orange-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => setFinalizeIncidentForm({...finalizeIncidentForm, final_status: 'cancelled_at_pickup'})}
                                >
                                  <div className="font-medium text-orange-700">🚗 {t('admin_incident_finalize_cancel_pickup_title')}</div>
                                  <div className="text-xs text-gray-500 mt-1">{t('admin_incident_finalize_cancel_pickup_help')}</div>
                                </button>
                              </div>
                            </div>
                            
                            {/* Info based on selection */}
                            {finalizeIncidentForm.final_status === 'delivered' ? (
                              <div className="text-sm text-green-700 bg-green-50 p-3 rounded border border-green-200">
                                <p className="font-medium">✅ {t('admin_incident_finalize_box_delivered_title')}</p>
                                <p className="mt-1">{t('admin_incident_finalize_box_delivered_body')}</p>
                              </div>
                            ) : (
                              <div className="text-sm text-orange-700 bg-orange-50 p-3 rounded border border-orange-200">
                                <p className="font-medium">🚗 {t('admin_incident_finalize_box_cancel_title')}</p>
                                <p className="mt-1">{t('admin_incident_finalize_box_cancel_body')}</p>
                                <p className="mt-1 text-xs">
                                  {t('admin_incident_finalize_box_cancel_footer')}
                                </p>
                              </div>
                            )}
                            
                            {/* Admin Notes */}
                            <div>
                              <Label className="text-sm font-medium">{t('admin_incident_finalize_notes_label')}</Label>
                              <textarea
                                className="mt-1 w-full p-2 border rounded-lg text-sm resize-none"
                                rows={2}
                                placeholder={t('admin_incident_finalize_notes_placeholder')}
                                value={finalizeIncidentForm.admin_notes}
                                onChange={(e) => setFinalizeIncidentForm({...finalizeIncidentForm, admin_notes: e.target.value})}
                              />
                            </div>
                            
                            {/* Expenses - Show for both since driver showed up */}
                            <div>
                              <Label className="text-sm font-medium mb-2 block">
                                {t('admin_incident_finalize_expenses_label')}
                                {finalizeIncidentForm.final_status === 'cancelled_at_pickup' && (
                                  <span className="text-xs text-gray-500 ml-2">- {t('admin_incident_finalize_expenses_hint_cancel')}</span>
                                )}
                              </Label>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">{t('admin_complete_expense_tolls')}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={finalizeIncidentForm.toll_expenses}
                                    onChange={(e) => setFinalizeIncidentForm({...finalizeIncidentForm, toll_expenses: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">{t('admin_complete_expense_fuel')}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={finalizeIncidentForm.fuel_expenses}
                                    onChange={(e) => setFinalizeIncidentForm({...finalizeIncidentForm, fuel_expenses: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">{t('admin_complete_expense_other')}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={finalizeIncidentForm.other_expenses}
                                    onChange={(e) => setFinalizeIncidentForm({...finalizeIncidentForm, other_expenses: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex gap-2 justify-end pt-2">
                              <Button 
                                variant="outline" 
                                onClick={() => setShowFinalizeFromIncidentForm(false)}
                              >
                                {t('cancel')}
                              </Button>
                              <Button 
                                className={finalizeIncidentForm.final_status === 'delivered' 
                                  ? "bg-green-600 hover:bg-green-700" 
                                  : "bg-orange-600 hover:bg-orange-700"}
                                onClick={() => finalizeServiceFromIncident(selectedIncident)}
                                disabled={finalizingServiceFromIncident}
                              >
                                {finalizingServiceFromIncident ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                {finalizeIncidentForm.final_status === 'delivered' 
                                  ? t('admin_incident_finalize_confirm_delivered') 
                                  : t('admin_incident_finalize_confirm_compensation')}
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Actions - Only show if form is not open */}
                        {!showFinalizeFromIncidentForm && (
                          <div className="flex flex-wrap gap-2 justify-between pt-4 border-t">
                            {/* Left side - Service actions */}
                            <div className="flex gap-2">
                              {/* Finalize Service Button - Only if service is not already delivered/cancelled */}
                              {order && !['delivered', 'cancelled'].includes(order.status) && (
                                <Button 
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={() => setShowFinalizeFromIncidentForm(true)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  {t('admin_incident_finalize_service_btn')}
                                </Button>
                              )}
                              
                              {/* Send to Client Button */}
                              <Button 
                                variant="outline"
                                onClick={() => sendIncidentToClient(selectedIncident)}
                                disabled={sendingIncidentToClient}
                              >
                                {sendingIncidentToClient ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-2" />
                                )}
                                {t('admin_incident_send_client_btn')}
                              </Button>
                            </div>
                            
                            {/* Right side - Status actions */}
                            <div className="flex gap-2">
                              {selectedIncident.status !== 'resolved' && (
                                <>
                                  {selectedIncident.status === 'reported' && (
                                    <Button 
                                      variant="outline"
                                      className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                                      onClick={() => updateIncidentStatus(selectedIncident.id, 'in_progress')}
                                    >
                                      <Clock className="w-4 h-4 mr-2" />
                                      {t('admin_incident_btn_in_review')}
                                    </Button>
                                  )}
                                  <Button 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => updateIncidentStatus(selectedIncident.id, 'resolved')}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {t('admin_incident_btn_move_history')}
                                  </Button>
                                </>
                              )}
                              <Button variant="outline" onClick={() => {
                                setShowIncidentDialog(false);
                                setShowFinalizeFromIncidentForm(false);
                              }}>
                                {t('admin_close')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* GPS Live Tab */}
          {activeTab === 'gps-live' && (
            <GPSLiveTab />
          )}

          {/* Inspections Tab */}
          {activeTab === 'inspections' && (
            <InspectionsTab />
          )}

          {/* Transport Services Tab */}
          {activeTab === 'transport-services' && (
            <TransportServicesTab />
          )}
        </div>
      </main>
      </div>

      {/* Order Update Dialog */}
      <Dialog open={showOrderUpdateDialog} onOpenChange={(open) => {
        // Don't close main dialog if pricing dialog is open
        if (!open && showDriverPricingDialog) {
          return;
        }
        setShowOrderUpdateDialog(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('edit_order_details')}</DialogTitle>
            <DialogDescription>{t('modify_order_information')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateOrder} className="space-y-6" data-testid="order-update-form">
            
            {/* Service Type Selector - Visual Cards */}
            <div className="space-y-4 pb-4 border-b">
              <Label className="text-lg font-semibold">{t('admin_order_edit_service_type_heading')}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Entrega */}
                <button
                  type="button"
                  onClick={() => setOrderUpdateForm({ ...orderUpdateForm, service_type: 'delivery' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    orderUpdateForm.service_type === 'delivery' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{t('admin_order_card_delivery_title')}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {t('admin_order_card_delivery_desc')}
                  </div>
                </button>

                {/* Servicio B-C */}
                <button
                  type="button"
                  onClick={() => setOrderUpdateForm({ ...orderUpdateForm, service_type: 'bc_service' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    orderUpdateForm.service_type === 'bc_service' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{t('admin_order_card_bc_title')}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {t('admin_order_card_bc_desc')}
                  </div>
                </button>

                {/* Trade-in */}
                <button
                  type="button"
                  onClick={() => setOrderUpdateForm({ ...orderUpdateForm, service_type: 'tradein' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    orderUpdateForm.service_type === 'tradein' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{t('admin_order_card_tradein_title')}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {t('admin_order_card_tradein_desc')}
                  </div>
                </button>

                {/* Representación */}
                <button
                  type="button"
                  onClick={() => setOrderUpdateForm({ ...orderUpdateForm, service_type: 'representacion' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    orderUpdateForm.service_type === 'representacion' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{t('admin_order_card_repr_title')}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {t('admin_order_card_repr_desc')}
                  </div>
                </button>
              </div>
            </div>

            {/* Status, Payment Status and Driver Section */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('status')} *</Label>
                <Select value={orderUpdateForm.status || "pending"} onValueChange={(value) => setOrderUpdateForm({ ...orderUpdateForm, status: value })}>
                  <SelectTrigger data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="confirmed">{t('confirmed')}</SelectItem>
                    <SelectItem value="assigned">{t('assigned')}</SelectItem>
                    <SelectItem value="in_transit">{t('in_transit')}</SelectItem>
                    <SelectItem value="delivered">{t('delivered')}</SelectItem>
                    <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                    <SelectItem value="Cancelado en Garantía">{t('Cancelado en Garantía')}</SelectItem>
                    <SelectItem value="Cancelado con -24h antelación">{t('Cancelado con -24h antelación')}</SelectItem>
                    <SelectItem value="Cancelado con +24h de antelación">{t('Cancelado con +24h de antelación')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('admin_payment_status_label')}</Label>
                <Select value={orderUpdateForm.payment_status || "pending"} onValueChange={(value) => setOrderUpdateForm({ ...orderUpdateForm, payment_status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('payment_status_pending')}</SelectItem>
                    <SelectItem value="paid">{t('paid')}</SelectItem>
                    <SelectItem value="failed">{t('failed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{t('assign_driver')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('search_driver')}
                    value={driverSearchTerm}
                    onChange={(e) => {
                      setDriverSearchTerm(e.target.value);
                      setShowDriverOptions(true);
                    }}
                    onFocus={() => setShowDriverOptions(true)}
                    className="w-full"
                  />
                  
                  {/* Display selected driver */}
                  {orderUpdateForm.driver_id && orderUpdateForm.driver_id !== '' && !showDriverOptions && (
                    <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded-md flex items-center justify-between">
                      <span className="text-sm font-medium text-indigo-900">
                        {drivers.find(d => d.id === orderUpdateForm.driver_id)?.name || t('no_driver')}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setOrderUpdateForm({ ...orderUpdateForm, driver_id: '' });
                          setDriverSearchTerm('');
                        }}
                        className="h-6 px-2"
                      >
                        ✕
                      </Button>
                    </div>
                  )}

                  {/* Dropdown Options */}
                  {showDriverOptions && (
                    <>
                      {/* Backdrop for mobile - click outside to close dropdown only */}
                      <div 
                        className="fixed inset-0 z-[9998] sm:hidden"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDriverOptions(false);
                        }}
                      />
                      <div className="fixed sm:absolute z-[9999] left-4 right-4 sm:left-auto sm:right-auto sm:w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {/* No Driver Option */}
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2"
                        onClick={() => {
                          setOrderUpdateForm({ ...orderUpdateForm, driver_id: '' });
                          setDriverSearchTerm('');
                          setShowDriverOptions(false);
                        }}
                      >
                        <span className="text-gray-500">⊘</span>
                        <span>{t('no_driver')}</span>
                      </button>

                      {/* Filtered Drivers */}
                      {getFilteredDrivers().length > 0 ? (
                        getFilteredDrivers().map((driver) => (
                          <button
                            key={driver.id}
                            type="button"
                            className={`w-full text-left px-4 py-2 hover:bg-gray-100 active:bg-gray-200 flex items-center justify-between ${
                              !driver.documents_validated ? 'opacity-50' : ''
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // First update the form with the selected driver
                              setOrderUpdateForm({ ...orderUpdateForm, driver_id: driver.id });
                              setDriverSearchTerm(driver.name);
                              setShowDriverOptions(false);
                              
                              // Then trigger pricing dialog
                              handleDriverSelection(driver);
                            }}
                          >
                            <span className="flex items-center gap-2">
                              {driver.name}
                              {driver.is_fleet_driver && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                  {t('admin_fleet_driver_badge')}
                                </span>
                              )}
                              {!driver.documents_validated && !driver.is_fleet_driver && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                  {t('admin_driver_not_validated_badge')}
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500">{driver.phone}</span>
                          </button>
                        ))
                      ) : driverSearchTerm && (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {t('no_results')}
                        </div>
                      )}

                      {/* Create New Driver Option */}
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 border-t border-gray-200 hover:bg-indigo-50 active:bg-indigo-100 text-indigo-600 font-medium flex items-center gap-2"
                        onClick={handleCreateNewDriver}
                      >
                        <span>➕</span>
                        <span>{t('create_new_driver')}</span>
                      </button>
                    </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Client and Notes Section */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>{t('admin_order_assigned_client')} *</Label>
                <Select 
                  value={orderUpdateForm.client_id || ""} 
                  onValueChange={(value) => setOrderUpdateForm({ ...orderUpdateForm, client_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin_order_select_client_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">{t('admin_order_change_client_hint')}</p>
              </div>
              
              <div className="space-y-2">
                <Label>{t('admin_order_notes_label')}</Label>
                <Textarea
                  value={orderUpdateForm.notes || ''}
                  onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, notes: e.target.value })}
                  placeholder={t('admin_order_notes_placeholder')}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">{t('admin_order_notes_visible_driver')}</p>
              </div>
            </div>

            {/* Service Details - IMPORTANT FOR PRICING */}
            <div className="space-y-4 pt-4 border-t bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <InfoIcon className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">{t('admin_order_service_details_editable')}</h4>
              </div>
              
              {/* Alert for missing client price */}
              {(!orderUpdateForm.total_price || orderUpdateForm.total_price === 0) && orderUpdateForm.distance_km > 0 && (
                <div className="flex items-center gap-3 p-3 bg-orange-100 border border-orange-300 rounded-lg mb-4">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-800">{t('admin_order_client_price_undefined_title')}</p>
                    <p className="text-xs text-orange-700">{t('admin_order_client_price_undefined_hint')}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      try {
                        const priceResponse = await fetch(
                          `${BACKEND_URL}/api/pricing/calculate?distance_km=${orderUpdateForm.distance_km}&service_type=${orderUpdateForm.service_type || 'delivery'}`,
                          { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } }
                        );
                        if (priceResponse.ok) {
                          const priceData = await priceResponse.json();
                          const serviceType = orderUpdateForm.service_type || 'delivery';
                          let newBasePrice = 0;
                          if (serviceType === 'delivery') newBasePrice = priceData.delivery_price || 0;
                          else if (serviceType === 'bc_service') newBasePrice = priceData.bc_service_price || 0;
                          else if (serviceType === 'tradein') newBasePrice = priceData.tradein_price || 0;
                          else if (serviceType === 'representacion') newBasePrice = priceData.representacion_price || 0;
                          else newBasePrice = priceData.delivery_price || 0;
                          
                          const additionalServicesTotal = (selectedOrder?.additional_services || [])
                            .reduce((sum, svc) => sum + (svc.price || 0), 0);
                          const newTotalPrice = newBasePrice + additionalServicesTotal;
                          
                          setOrderUpdateForm(prev => ({ 
                            ...prev, 
                            base_price: newBasePrice, 
                            total_price: newTotalPrice 
                          }));
                          toast.success(t('admin_toast_client_price_calculated', { amount: newTotalPrice.toFixed(2) }));
                        }
                      } catch (err) {
                        console.error('Error calculating price:', err);
                        toast.error(t('admin_toast_price_calculate_error'));
                      }
                    }}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    💰 {t('admin_order_recalc_client_price')}
                  </Button>
                </div>
              )}
              
              {/* Show current client price */}
              {orderUpdateForm.total_price > 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-100 border border-green-300 rounded-lg mb-4">
                  <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">{t('admin_order_client_price_prefix')} €{orderUpdateForm.total_price.toFixed(2)}</p>
                    <p className="text-xs text-green-700">{t('admin_order_client_price_company_hint')}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin_order_distance_km_label')}</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      step="0.01"
                      value={orderUpdateForm.distance_km || 0} 
                      onChange={async (e) => {
                        const newDistance = parseFloat(e.target.value) || 0;
                        setOrderUpdateForm({ ...orderUpdateForm, distance_km: newDistance });
                        
                        // Recalculate base price when distance changes
                        if (newDistance > 0) {
                          try {
                            const priceResponse = await fetch(
                              `${BACKEND_URL}/api/pricing/calculate?distance_km=${newDistance}&service_type=${orderUpdateForm.service_type || 'delivery'}`,
                              { headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } }
                            );
                            if (priceResponse.ok) {
                              const priceData = await priceResponse.json();
                              const newBasePrice = priceData.delivery_price || priceData.bc_service_price || 0;
                              
                              // Calculate total including additional services
                              const additionalServicesTotal = (selectedOrder?.additional_services || [])
                                .reduce((sum, svc) => sum + (svc.price || 0), 0);
                              const newTotalPrice = newBasePrice + additionalServicesTotal;
                              
                              setOrderUpdateForm(prev => ({ 
                                ...prev, 
                                base_price: newBasePrice, 
                                total_price: newTotalPrice 
                              }));
                            }
                          } catch (err) {
                            console.error('Error recalculating price:', err);
                          }
                        }
                      }}
                      placeholder={t('admin_order_distance_placeholder_example')}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => recalculateOrderUpdateDistanceFromGeocode()}
                      disabled={!orderUpdateForm.pickup_address || !orderUpdateForm.delivery_address}
                      className="whitespace-nowrap"
                    >
                      🧮 {t('admin_order_calc_distance_btn')}
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600">{t('admin_order_distance_auto_hint')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin_order_service_type_field_label')}</Label>
                  <select
                    value={orderUpdateForm.service_type || 'delivery'}
                    onChange={(e) => {
                      refreshOrderUpdateDrivingDistance({ service_type: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="delivery">{t('delivery')}</option>
                    <option value="bc_service">{t('bc_service')}</option>
                    <option value="tradein">{t('tradein')}</option>
                    <option value="representacion">{t('representacion')}</option>
                  </select>
                  <p className="text-xs text-blue-600">{t('admin_order_service_type_edit_hint')}</p>
                </div>
              </div>
            </div>

            {/* Driver Pricing Selection - Show if distance and service type are set */}
            {orderUpdateForm.distance_km > 0 && orderUpdateForm.service_type && (
              <div className={`space-y-4 pt-4 border-t p-4 rounded-lg ${
                orderUpdateForm.driver_id && !orderUpdateForm.driver_price && !selectedOrder?.driver_price
                  ? 'bg-red-50 border-2 border-red-400 animate-pulse'
                  : 'bg-purple-50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">💰</span>
                  <h4 className="font-semibold text-gray-900">{t('admin_order_driver_rate_title')}</h4>
                  {orderUpdateForm.driver_id && !orderUpdateForm.driver_price && !selectedOrder?.driver_price && (
                    <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded animate-bounce">
                      {t('admin_order_rate_required_badge')}
                    </span>
                  )}
                </div>
                {orderUpdateForm.driver_id && !orderUpdateForm.driver_price && !selectedOrder?.driver_price && (
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg mb-3">
                    <p className="text-sm font-semibold text-red-700">
                      {t('admin_order_driver_rate_required_warning')}
                    </p>
                  </div>
                )}
                <p className="text-sm text-gray-600 mb-3">
                  {t('admin_order_select_driver_rate_hint', { km: orderUpdateForm.distance_km })}
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Básica */}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const price = calculatedPrices['básica'];
                        if (!price) {
                          toast.error(t('admin_toast_tariff_calculate_failed'));
                          return;
                        }
                        setOrderUpdateForm({ ...orderUpdateForm, driver_pricing_template_id: 'basica', driver_price: price, is_driver_price_custom: false });
                        toast.success(t('admin_toast_driver_tariff_selected', { label: t('admin_tariff_name_basic'), price }));
                      } catch(e) { toast.error(t('admin_toast_error_colon_message', { message: e.message })); }
                    }}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      orderUpdateForm.driver_pricing_template_id === 'basica' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{t('admin_order_tariff_basic')}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {calculatedPrices['básica'] ? `${calculatedPrices['básica'].toFixed(2)}€` : t('calculating')}
                    </div>
                  </button>

                  {/* Premium */}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const price = calculatedPrices['premium'];
                        if (!price) {
                          toast.error(t('admin_toast_tariff_calculate_failed'));
                          return;
                        }
                        setOrderUpdateForm({ ...orderUpdateForm, driver_pricing_template_id: 'premium', driver_price: price, is_driver_price_custom: false });
                        toast.success(t('admin_toast_driver_tariff_selected', { label: t('admin_tariff_name_premium'), price }));
                      } catch(e) { toast.error(t('admin_toast_error_colon_message', { message: e.message })); }
                    }}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      orderUpdateForm.driver_pricing_template_id === 'premium' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{t('admin_order_tariff_premium')}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {calculatedPrices['premium'] ? `${calculatedPrices['premium'].toFixed(2)}€` : t('calculating')}
                    </div>
                  </button>

                  {/* JLUIS */}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const price = calculatedPrices['jluis'];
                        if (!price) {
                          toast.error(t('admin_toast_tariff_calculate_failed'));
                          return;
                        }
                        setOrderUpdateForm({ ...orderUpdateForm, driver_pricing_template_id: 'jluis', driver_price: price, is_driver_price_custom: false });
                        toast.success(t('admin_toast_driver_tariff_selected', { label: t('admin_tariff_name_jluis'), price }));
                      } catch(e) { toast.error(t('admin_toast_error_colon_message', { message: e.message })); }
                    }}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      orderUpdateForm.driver_pricing_template_id === 'jluis' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{t('admin_order_tariff_jluis')}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {calculatedPrices['jluis'] ? `${calculatedPrices['jluis'].toFixed(2)}€` : t('calculating')}
                    </div>
                  </button>

                  {/* Personalizada */}
                  <div className={`p-3 border-2 rounded-lg ${
                    orderUpdateForm.is_driver_price_custom 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300'
                  }`}>
                    <div className="font-semibold text-gray-900 mb-2">{t('admin_order_tariff_custom')}</div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="150.00"
                        value={orderUpdateForm.is_driver_price_custom ? orderUpdateForm.driver_price : ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setOrderUpdateForm({
                            ...orderUpdateForm,
                            driver_pricing_template_id: null,
                            driver_price: value,
                            is_driver_price_custom: true
                          });
                        }}
                        className="flex-1"
                      />
                      <span className="self-center text-gray-600">€</span>
                    </div>
                  </div>
                </div>

                {orderUpdateForm.driver_price > 0 && (
                  <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg">
                    <p className="text-sm font-semibold text-green-800">
                      {t('admin_order_rate_selected_line', {
                        price: orderUpdateForm.driver_price.toFixed(2),
                        suffix: orderUpdateForm.is_driver_price_custom
                          ? t('admin_order_rate_suffix_custom')
                          : ` (${orderUpdateForm.driver_pricing_template_id})`,
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Warning if driver assigned but no distance to calculate pricing */}
            {orderUpdateForm.driver_id && orderUpdateForm.driver_id !== 'no-driver' && 
             (!orderUpdateForm.distance_km || orderUpdateForm.distance_km <= 0) && 
             !selectedOrder?.driver_price && (
              <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800">
                  {t('admin_order_assign_driver_distance_warning')}
                </p>
              </div>
            )}

            {/* Vehicle Information */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">{t('vehicle')} {t('information')}</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('car_plate')}</Label>
                  <Input 
                    value={orderUpdateForm.car_plate} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, car_plate: e.target.value })}
                    placeholder="ABC-1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('car_brand')}</Label>
                  <Input 
                    value={orderUpdateForm.car_brand} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, car_brand: e.target.value })}
                    placeholder="Toyota"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('car_model')}</Label>
                  <Input 
                    value={orderUpdateForm.car_model} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, car_model: e.target.value })}
                    placeholder="Corolla"
                  />
                </div>
              </div>
            </div>

            {/* Pickup Information */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">{t('pickup')} {t('information')}</h4>
              <div className="space-y-2">
                <Label>{t('pickup_address')}</Label>
                <AddressAutocomplete
                  value={orderUpdateForm.pickup_address}
                  onChange={(data) => {
                    refreshOrderUpdateDrivingDistance({
                      pickup_address: data.address,
                      pickup_lat: data.lat,
                      pickup_lng: data.lng,
                    });
                  }}
                  placeholder={t('ph_address_maps')}
                  inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                />
                <p className="text-xs text-blue-600">{t('admin_order_address_distance_hint')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('contact_name')}</Label>
                  <Input 
                    value={orderUpdateForm.pickup_contact_name} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_contact_name: e.target.value })}
                    placeholder={t('contact_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('contact_phone')}</Label>
                  <Input 
                    type="tel"
                    value={orderUpdateForm.pickup_contact_phone} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_contact_phone: e.target.value })}
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">{t('delivery')} {t('information')}</h4>
              <div className="space-y-2">
                <Label>{t('delivery_address')}</Label>
                <AddressAutocomplete
                  value={orderUpdateForm.delivery_address}
                  onChange={(data) => {
                    refreshOrderUpdateDrivingDistance({
                      delivery_address: data.address,
                      delivery_lat: data.lat,
                      delivery_lng: data.lng,
                    });
                  }}
                  placeholder={t('ph_address_maps')}
                  inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                />
                <p className="text-xs text-blue-600">{t('admin_order_address_distance_hint')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('contact_name')}</Label>
                  <Input 
                    value={orderUpdateForm.delivery_contact_name} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_contact_name: e.target.value })}
                    placeholder={t('contact_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('contact_phone')}</Label>
                  <Input 
                    type="tel"
                    value={orderUpdateForm.delivery_contact_phone} 
                    onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_contact_phone: e.target.value })}
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>
            </div>

            {orderUpdateForm.service_type === 'bc_service' && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-900">{t('point_c_information')}</h4>
                <div className="space-y-2">
                  <Label>{t('point_c_address')}</Label>
                  <AddressAutocomplete
                    value={orderUpdateForm.point_c_address || ''}
                    onChange={(data) => {
                      refreshOrderUpdateDrivingDistance({
                        point_c_address: data.address,
                        point_c_lat: data.lat,
                        point_c_lng: data.lng,
                      });
                    }}
                    placeholder={t('ph_address_maps')}
                    inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('contact_name')}</Label>
                    <Input
                      value={orderUpdateForm.point_c_contact_name || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, point_c_contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('contact_phone')}</Label>
                    <Input
                      type="tel"
                      value={orderUpdateForm.point_c_contact_phone || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, point_c_contact_phone: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {(orderUpdateForm.service_type === 'bc_service' || orderUpdateForm.service_type === 'tradein') && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-900">{t('second_vehicle')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('vehicle_type')}</Label>
                    <Input
                      value={orderUpdateForm.second_car_type || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, second_car_type: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('car_brand')}</Label>
                    <Input
                      value={orderUpdateForm.second_car_brand || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, second_car_brand: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('car_model')}</Label>
                    <Input
                      value={orderUpdateForm.second_car_model || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, second_car_model: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('car_plate')}</Label>
                    <Input
                      value={orderUpdateForm.second_car_plate || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, second_car_plate: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('fuel_type')}</Label>
                    <Select
                      value={orderUpdateForm.second_fuel_type || 'gasoline'}
                      onValueChange={(value) => setOrderUpdateForm({ ...orderUpdateForm, second_fuel_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                        <SelectItem value="diesel">{t('diesel')}</SelectItem>
                        <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                        <SelectItem value="electric">{t('electric')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('admin_notes_vehicle1')}</Label>
                    <textarea
                      value={orderUpdateForm.first_vehicle_notes || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, first_vehicle_notes: e.target.value })}
                      className="w-full p-3 border rounded-lg min-h-[90px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_notes_vehicle2')}</Label>
                    <textarea
                      value={orderUpdateForm.second_vehicle_notes || ''}
                      onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, second_vehicle_notes: e.target.value })}
                      className="w-full p-3 border rounded-lg min-h-[90px]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Dates and Times */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">{t('admin_order_dates_times_title')}</h4>
              
              {/* Pickup Dates */}
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700">{t('admin_order_pickup_dates')}</Label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orderUpdateForm.pickup_fixed_date}
                      onChange={(e) => setOrderUpdateForm({ 
                        ...orderUpdateForm, 
                        pickup_fixed_date: e.target.checked,
                        pickup_date_to: e.target.checked ? orderUpdateForm.pickup_date_from : orderUpdateForm.pickup_date_to
                      })}
                      className="w-4 h-4"
                    />
                    <span className="font-medium text-blue-700">{t('admin_order_fixed_datetime')}</span>
                  </label>
                </div>
                
                {orderUpdateForm.pickup_fixed_date ? (
                  // Fixed date and time
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">{t('admin_order_date_label')}</Label>
                      <Input 
                        type="date"
                        value={orderUpdateForm.pickup_date_from || ''} 
                        onChange={(e) => setOrderUpdateForm({ 
                          ...orderUpdateForm, 
                          pickup_date_from: e.target.value,
                          pickup_date_to: e.target.value,
                          service_date: e.target.value // Auto-set service date
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">{t('admin_order_time_label')}</Label>
                      <Input 
                        type="time"
                        value={orderUpdateForm.pickup_time_start || ''} 
                        onChange={(e) => setOrderUpdateForm({ 
                          ...orderUpdateForm, 
                          pickup_time_start: e.target.value,
                          pickup_time_end: e.target.value
                        })}
                      />
                    </div>
                  </div>
                ) : (
                  // Range of dates and times
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_date_from')}</Label>
                        <Input 
                          type="date"
                          value={orderUpdateForm.pickup_date_from || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_date_from: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_date_to')}</Label>
                        <Input 
                          type="date"
                          value={orderUpdateForm.pickup_date_to || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_date_to: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_time_from')}</Label>
                        <Input 
                          type="time"
                          value={orderUpdateForm.pickup_time_start || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_time_start: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_time_to')}</Label>
                        <Input 
                          type="time"
                          value={orderUpdateForm.pickup_time_end || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, pickup_time_end: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Delivery Dates */}
              <div className="space-y-3 p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700">{t('admin_order_delivery_dates')}</Label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orderUpdateForm.delivery_fixed_date}
                      onChange={(e) => setOrderUpdateForm({ 
                        ...orderUpdateForm, 
                        delivery_fixed_date: e.target.checked,
                        delivery_date_to: e.target.checked ? orderUpdateForm.delivery_date_from : orderUpdateForm.delivery_date_to
                      })}
                      className="w-4 h-4"
                    />
                    <span className="font-medium text-purple-700">{t('admin_order_fixed_datetime')}</span>
                  </label>
                </div>
                
                {orderUpdateForm.delivery_fixed_date ? (
                  // Fixed date and time
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">{t('admin_order_date_label')}</Label>
                      <Input 
                        type="date"
                        value={orderUpdateForm.delivery_date_from || ''} 
                        onChange={(e) => setOrderUpdateForm({ 
                          ...orderUpdateForm, 
                          delivery_date_from: e.target.value,
                          delivery_date_to: e.target.value
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">{t('admin_order_time_label')}</Label>
                      <Input 
                        type="time"
                        value={orderUpdateForm.delivery_time_start || ''} 
                        onChange={(e) => setOrderUpdateForm({ 
                          ...orderUpdateForm, 
                          delivery_time_start: e.target.value,
                          delivery_time_end: e.target.value
                        })}
                      />
                    </div>
                  </div>
                ) : (
                  // Range of dates and times
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_date_from')}</Label>
                        <Input 
                          type="date"
                          value={orderUpdateForm.delivery_date_from || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_date_from: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_date_to')}</Label>
                        <Input 
                          type="date"
                          value={orderUpdateForm.delivery_date_to || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_date_to: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_time_from')}</Label>
                        <Input 
                          type="time"
                          value={orderUpdateForm.delivery_time_start || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_time_start: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{t('admin_order_time_to')}</Label>
                        <Input 
                          type="time"
                          value={orderUpdateForm.delivery_time_end || ''} 
                          onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, delivery_time_end: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Service Date - Auto-populated */}
              <div className="space-y-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Label className="text-sm font-medium text-green-800">{t('admin_order_service_date_calendar')}</Label>
                <Input 
                  type="date"
                  value={orderUpdateForm.service_date || orderUpdateForm.pickup_date_from || ''} 
                  onChange={(e) => setOrderUpdateForm({ ...orderUpdateForm, service_date: e.target.value })}
                  className="bg-white"
                />
                <p className="text-xs text-green-700">
                  {t('admin_order_service_date_calendar_hint')}
                </p>
              </div>
            </div>

            {/* Additional Services Section - Enhanced */}
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2">
                <span className="text-2xl">✨</span>
                <h4 className="font-semibold text-gray-900 text-lg">{t('admin_additional_services_title')}</h4>
              </div>
              <p className="text-sm text-gray-600">{t('admin_order_additional_services_blurb')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {additionalServices.map((service) => {
                  const extraDesc = translateAdditionalServiceDescription(service.name, service.description, t);
                  return (
                  <label
                    key={service.id}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      orderUpdateForm.selected_additional_services?.includes(service.id)
                        ? 'border-green-500 bg-green-50 shadow-sm'
                        : 'border-gray-300 hover:border-green-300 bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={orderUpdateForm.selected_additional_services?.includes(service.id)}
                      onChange={(e) => {
                        const currentServices = orderUpdateForm.selected_additional_services || [];
                        if (e.target.checked) {
                          setOrderUpdateForm({
                            ...orderUpdateForm,
                            selected_additional_services: [...currentServices, service.id]
                          });
                        } else {
                          setOrderUpdateForm({
                            ...orderUpdateForm,
                            selected_additional_services: currentServices.filter(id => id !== service.id)
                          });
                        }
                      }}
                      className="w-5 h-5 mt-1 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">{translateAdditionalServiceName(service.name, t)}</p>
                        <p className="text-base font-bold text-green-600">€{service.price.toFixed(2)}</p>
                      </div>
                      {extraDesc ? (
                        <p className="text-xs text-gray-600 mt-1">{extraDesc}</p>
                      ) : null}
                    </div>
                  </label>
                  );
                })}
              </div>
            </div>

            {/* Driver Extras Section - Additional payments for driver (Pit Stop, Espera, ITV, etc.) */}
            {orderUpdateForm.driver_id && orderUpdateForm.driver_id !== 'no-driver' && (
              <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💰</span>
                  <h4 className="font-semibold text-gray-900 text-lg">{t('admin_order_driver_extras_title')}</h4>
                </div>
                <p className="text-sm text-gray-600">{t('admin_order_driver_extras_intro')}</p>
                
                {/* Current extras list */}
                {driverExtras.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('admin_order_extras_added')}</Label>
                    <div className="space-y-2">
                      {driverExtras.map((extra, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded border border-amber-200">
                          <div className="flex-1">
                            <span className="font-medium">{extra.category_name}</span>
                            {extra.description && <span className="text-gray-500 ml-2 text-sm">({extra.description})</span>}
                            <div className="text-sm text-gray-600">
                              {extra.quantity} x €{Number(extra.unit_price).toFixed(2)} = <span className="font-bold text-amber-700">€{Number(extra.total).toFixed(2)}</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDriverExtras(driverExtras.filter((_, i) => i !== index));
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="p-2 bg-amber-100 rounded text-right">
                        <span className="font-bold text-amber-800">
                          {t('admin_order_extras_total')} €{driverExtras.reduce((sum, e) => sum + (Number(e.total) || 0), 0).toFixed(2)}
                        </span>
                        <span className="text-xs text-amber-600 ml-2">{t('admin_order_extras_vat_note')}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Add new extra */}
                <div className="space-y-3 p-3 bg-white rounded border border-amber-200">
                  <Label className="text-sm font-medium">{t('admin_order_add_extra_label')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <select
                        value={newDriverExtra.category_id}
                        onChange={(e) => {
                          const category = driverExtraCategories.find(c => c.id === e.target.value);
                          if (category) {
                            setNewDriverExtra({
                              ...newDriverExtra,
                              category_id: category.id,
                              category_name: category.name,
                              unit_price: category.default_price,
                              total: category.default_price * newDriverExtra.quantity,
                              description: category.is_custom ? newDriverExtra.description : ''
                            });
                          }
                        }}
                        className="w-full p-2 border rounded text-sm"
                      >
                        <option value="">{t('admin_order_select_extra_type_placeholder')}</option>
                        {driverExtraCategories.filter(c => c.is_active).map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name} (€{cat.default_price.toFixed(2)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="1"
                        placeholder={t('admin_order_extra_qty_short')}
                        value={newDriverExtra.quantity}
                        onChange={(e) => {
                          const qty = parseInt(e.target.value) || 1;
                          setNewDriverExtra({
                            ...newDriverExtra,
                            quantity: qty,
                            total: newDriverExtra.unit_price * qty
                          });
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={t('admin_order_extra_price_placeholder')}
                        value={newDriverExtra.unit_price}
                        onChange={(e) => {
                          const price = parseFloat(e.target.value) || 0;
                          setNewDriverExtra({
                            ...newDriverExtra,
                            unit_price: price,
                            total: price * newDriverExtra.quantity
                          });
                        }}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Description for custom category */}
                  {driverExtraCategories.find(c => c.id === newDriverExtra.category_id)?.is_custom && (
                    <div>
                      <Input
                        type="text"
                        placeholder={t('admin_order_extra_custom_description_ph')}
                        value={newDriverExtra.description}
                        onChange={(e) => setNewDriverExtra({ ...newDriverExtra, description: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-amber-700 font-medium">
                      {t('admin_order_extra_subtotal')} €{(newDriverExtra.total || 0).toFixed(2)}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        if (!newDriverExtra.category_id) {
                          toast.error(t('admin_toast_select_extra_type'));
                          return;
                        }
                        if (newDriverExtra.unit_price <= 0) {
                          toast.error(t('admin_toast_extra_price_gt_zero'));
                          return;
                        }
                        setDriverExtras([...driverExtras, { ...newDriverExtra }]);
                        setNewDriverExtra({ category_id: '', category_name: '', unit_price: 0, quantity: 1, total: 0, description: '' });
                        toast.success(t('admin_toast_extra_added_ok'));
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" /> {t('admin_order_extra_add_button')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <OrderServiceDocumentsField
              files={orderUpdateAttachmentFiles}
              onFilesChange={setOrderUpdateAttachmentFiles}
              disabled={false}
              onValidationError={(msg) => toast.error(msg)}
              labels={{
                fieldId: 'admin-order-update-docs',
                title: t('service_docs_title'),
                hint: t('service_docs_hint_edit'),
                browse: t('service_docs_browse'),
                previewTitle: t('service_docs_preview'),
                remove: t('service_docs_remove'),
                maxFiles: t('service_docs_max_files'),
                fileTooLarge: t('service_docs_file_too_large'),
                invalidType: t('service_docs_invalid_type'),
              }}
            />

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowOrderUpdateDialog(false)} className="flex-1">
                {t('cancel')}
              </Button>
              <Button type="submit" className="flex-1" data-testid="order-update-submit-btn">
                {t('save_changes')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Pricing Dialog */}
      <Dialog open={showCustomPricingDialog} onOpenChange={setShowCustomPricingDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? t('admin_custom_pricing_dialog_edit') : t('admin_custom_pricing_dialog_assign')}
            </DialogTitle>
            <DialogDescription>
              {selectedClient 
                ? t('admin_custom_pricing_desc_for_client', { name: selectedClient.company_name || selectedClient.name })
                : t('admin_custom_pricing_desc_select_client')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const token = localStorage.getItem('admin_token');
              const client = selectedClient || clients.find(c => c.id === customPricingForm.client_id);
              
              const data = {
                id: editingItem?.id || `custom-${Date.now()}`,
                client_id: client.id,
                client_name: client.company_name || client.name,
                min_distance: parseFloat(customPricingForm.min_distance),
                max_distance: parseFloat(customPricingForm.max_distance),
                delivery_price: parseFloat(customPricingForm.delivery_price),
                bc_service_price: parseFloat(customPricingForm.bc_service_price),
                tradein_price: parseFloat(customPricingForm.tradein_price)
              };

              const method = editingItem ? 'PUT' : 'POST';
              const url = editingItem 
                ? `${BACKEND_URL}/api/custom-pricing/${editingItem.id}`
                : `${BACKEND_URL}/api/custom-pricing`;

              await fetch(url, {
                method,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
              });

              setShowCustomPricingDialog(false);
              setEditingItem(null);
              setSelectedClient(null);
              setCustomPricingForm({
                min_distance: '',
                max_distance: '',
                delivery_price: '',
                bc_service_price: '',
                tradein_price: ''
              });
              loadData();
            } catch (error) {
              console.error('Error saving custom pricing:', error);
            }
          }}>
            <div className="space-y-4">
              {/* Client Selector - Only show if no client selected */}
              {!selectedClient && (
                <div>
                  <Label>{t('transport_tab_client_required')}</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={customPricingForm.client_id || ''}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, client_id: e.target.value })}
                    required
                  >
                    <option value="">{t('admin_order_select_client_placeholder')}…</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.company_name || client.name} ({client.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Copy from General Pricing Button */}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pricingRates.length > 0) {
                      const generalRate = pricingRates[0];
                      setCustomPricingForm({
                        ...customPricingForm,
                        min_distance: generalRate.min_distance?.toString() || '',
                        max_distance: generalRate.max_distance?.toString() || '',
                        delivery_price: generalRate.delivery_price?.toString() || '',
                        bc_service_price: generalRate.bc_service_price?.toString() || '',
                        tradein_price: generalRate.tradein_price?.toString() || ''
                      });
                    }
                  }}
                >
                  {t('admin_copy_from_general_pricing')}
                </Button>
              </div>

              {/* Distance Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('min_distance')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.min_distance}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, min_distance: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('max_distance')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.max_distance}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, max_distance: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Prices */}
              <div className="space-y-3">
                <div>
                  <Label>{t('admin_price_delivery')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.delivery_price}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, delivery_price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('admin_price_bc')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.bc_service_price}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, bc_service_price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('admin_price_tradein')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.tradein_price}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, tradein_price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('admin_price_representation')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={customPricingForm.representacion_price}
                    onChange={(e) => setCustomPricingForm({ ...customPricingForm, representacion_price: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button type="button" variant="outline" onClick={() => {
                setShowCustomPricingDialog(false);
                setEditingItem(null);
                setSelectedClient(null);
              }}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {editingItem ? t('update') : t('create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_create_new_template_singular')}</DialogTitle>
            <DialogDescription>
              {t('admin_define_template_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div>
              <Label htmlFor="template_name">{t('admin_template_name_required')}</Label>
              <Input
                id="template_name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder={t('admin_template_name_placeholder')}
                required
              />
            </div>
            <div>
              <Label htmlFor="template_description">{t('admin_template_description_optional')}</Label>
              <Input
                id="template_description"
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder={t('admin_template_description_placeholder')}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowTemplateDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {t('admin_create_template')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Template Rate Dialog */}
      <Dialog open={showTemplateRateDialog} onOpenChange={setShowTemplateRateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('admin_add_rate_to_template', { name: selectedTemplate?.name || '' })}</DialogTitle>
            <DialogDescription>
              {t('admin_template_rate_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTemplateRate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('min_distance')} *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={templateRateForm.min_distance}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, min_distance: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('max_distance')} *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={templateRateForm.max_distance}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, max_distance: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('admin_pricing_col_delivery')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={templateRateForm.delivery_price}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, delivery_price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('admin_pricing_col_bc')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={templateRateForm.bc_service_price}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, bc_service_price: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('admin_pricing_col_tradein')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={templateRateForm.tradein_price}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, tradein_price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('admin_pricing_col_representation')} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={templateRateForm.representacion_price}
                  onChange={(e) => setTemplateRateForm({ ...templateRateForm, representacion_price: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Optional per-km rates */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                {t('admin_template_rate_per_km_section')}
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{t('admin_per_km_delivery')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={templateRateForm.delivery_price_per_km}
                    onChange={(e) => setTemplateRateForm({ ...templateRateForm, delivery_price_per_km: e.target.value })}
                    placeholder="0.40"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('admin_per_km_bc')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={templateRateForm.bc_service_price_per_km}
                    onChange={(e) => setTemplateRateForm({ ...templateRateForm, bc_service_price_per_km: e.target.value })}
                    placeholder="0.32"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('admin_per_km_tradein')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={templateRateForm.tradein_price_per_km}
                    onChange={(e) => setTemplateRateForm({ ...templateRateForm, tradein_price_per_km: e.target.value })}
                    placeholder="0.28"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowTemplateRateDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {t('admin_add_pricing_rate')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Pricing Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_assign_rate_dialog_title')}</DialogTitle>
            <DialogDescription>
              {t('admin_assign_rate_dialog_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignPricing} className="space-y-4">
            <div>
              <Label htmlFor="client_select">{t('admin_assign_rate_client_field')}</Label>
              <Select
                value={assignmentForm.client_id}
                onValueChange={(value) => setAssignmentForm({ ...assignmentForm, client_id: value })}
              >
                <SelectTrigger id="client_select">
                  <SelectValue placeholder={t('admin_order_select_client_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name || client.name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="template_select">{t('admin_assign_rate_template_field')}</Label>
              <Select
                value={assignmentForm.template_id}
                onValueChange={(value) => setAssignmentForm({ ...assignmentForm, template_id: value })}
              >
                <SelectTrigger id="template_select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t('admin_assign_rate_option_general')}</SelectItem>
                  {pricingTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                {t('admin_assign_rate_replace_warning')}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowAssignmentDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {t('admin_assign_rate')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Edit Dialog */}
      <Dialog open={showClientEditDialog} onOpenChange={setShowClientEditDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('admin_edit_client')}</DialogTitle>
            <DialogDescription>
              {t('admin_edit_client_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveClient} className="space-y-4">
            {/* Logo Upload Section */}
            <div className="border-b pb-4">
              <Label>{t('admin_company_logo')}</Label>
              <div className="flex items-center gap-4 mt-2">
                {clients.find(c => c.id === clientEditForm.id)?.company_logo ? (
                  <img 
                    src={clients.find(c => c.id === clientEditForm.id)?.company_logo} 
                    alt={t('company_logo')} 
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo_upload"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAdminLogoUpload(clientEditForm.id, file);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo_upload').click()}
                    disabled={uploadingLogo}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLogo ? t('admin_uploading') : t('admin_upload_logo')}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">{t('admin_logo_hint')}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client_name">{t('admin_name_required')}</Label>
                <Input
                  id="client_name"
                  value={clientEditForm.name}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="client_email">{t('admin_email_required')}</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={clientEditForm.email}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, email: e.target.value })}
                  required
                />
                <p className="text-xs text-amber-600 mt-1">⚠️ {t('admin_email_change_warning')}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client_company">{t('admin_company_name')}</Label>
                <Input
                  id="client_company"
                  value={clientEditForm.company_name}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="client_cif">{t('admin_cif_dni')}</Label>
                <Input
                  id="client_cif"
                  value={clientEditForm.cif_dni}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, cif_dni: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client_phone">{t('admin_phone')}</Label>
                <Input
                  id="client_phone"
                  value={clientEditForm.phone}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="client_country">{t('admin_country')}</Label>
                <Input
                  id="client_country"
                  value={clientEditForm.country}
                  onChange={(e) => setClientEditForm({ ...clientEditForm, country: e.target.value })}
                  placeholder={t('admin_country_placeholder')}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="client_address">{t('admin_address')}</Label>
              <Input
                id="client_address"
                value={clientEditForm.address}
                onChange={(e) => setClientEditForm({ ...clientEditForm, address: e.target.value })}
              />
            </div>

            {/* Payment Type Selector */}
            <div className="border-t pt-4">
              <Label htmlFor="client_payment_type">{t('admin_payment_type')}</Label>
              <select
                id="client_payment_type"
                value={clientEditForm.payment_type || 'biweekly'}
                onChange={(e) => setClientEditForm({ ...clientEditForm, payment_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#36e961]"
              >
                <option value="biweekly">{t('admin_payment_biweekly')}</option>
                <option value="instant">{t('admin_payment_instant')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {clientEditForm.payment_type === 'biweekly' 
                  ? `💳 ${t('admin_payment_biweekly_desc')}`
                  : `⚡ ${t('admin_payment_instant_desc')}`}
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowClientEditDialog(false)}>
                {t('admin_cancel')}
              </Button>
              <Button type="submit">
                {t('admin_save_changes')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Driver Edit Dialog */}
      <Dialog open={showDriverEditDialog} onOpenChange={setShowDriverEditDialog}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin_edit_driver')}</DialogTitle>
            <DialogDescription>
              {t('admin_edit_driver_description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveDriver} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="driver_name">{t('admin_name_required')}</Label>
                <Input
                  id="driver_name"
                  value={driverEditForm.name}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="driver_email">{t('admin_email_required')}</Label>
                <Input
                  id="driver_email"
                  type="email"
                  value={driverEditForm.email}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="driver_phone">{t('phone_required')}</Label>
                <Input
                  id="driver_phone"
                  value={driverEditForm.phone}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="driver_dni">{t('admin_label_dni_nif')}</Label>
                <Input
                  id="driver_dni"
                  value={driverEditForm.license_number}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, license_number: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="driver_vehicle">{t('admin_vehicle_info_label')}</Label>
              <Input
                id="driver_vehicle"
                value={driverEditForm.vehicle_info}
                onChange={(e) => setDriverEditForm({ ...driverEditForm, vehicle_info: e.target.value })}
                placeholder={t('admin_vehicle_info_placeholder')}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="driver_available"
                  checked={driverEditForm.is_available}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, is_available: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="driver_available" className="cursor-pointer">
                  {t('admin_driver_available')}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="driver_validated"
                  checked={driverEditForm.documents_validated}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, documents_validated: e.target.checked })}
                  className="w-4 h-4 accent-green-600"
                />
                <Label htmlFor="driver_validated" className="cursor-pointer">
                  {t('admin_documents_validated')}
                </Label>
              </div>
            </div>

            {/* Billing Information Section */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 text-gray-900">{t('admin_billing_info')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-1 md:col-span-2">
                  <Label htmlFor="driver_full_name">{t('admin_billing_full_name')}</Label>
                  <Input
                    id="driver_full_name"
                    value={driverEditForm.full_name || ''}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, full_name: e.target.value })}
                    placeholder={t('admin_billing_full_name_placeholder')}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('admin_billing_legal_name_hint')}</p>
                </div>

                <div>
                  <Label htmlFor="driver_dni_nie">{t('admin_billing_dni_nie')}</Label>
                  <Input
                    id="driver_dni_nie"
                    value={driverEditForm.dni_nie || ''}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, dni_nie: e.target.value })}
                    placeholder="12345678A"
                  />
                </div>

                <div>
                  <Label htmlFor="driver_irpf">{t('admin_billing_irpf')}</Label>
                  <Input
                    id="driver_irpf"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={driverEditForm.irpf_retention || 0}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, irpf_retention: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('admin_billing_irpf_hint')}</p>
                </div>

                <div className="col-span-1 md:col-span-2">
                  <Label htmlFor="driver_fiscal_address">{t('admin_billing_fiscal_address')}</Label>
                  <Input
                    id="driver_fiscal_address"
                    value={driverEditForm.fiscal_address || ''}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, fiscal_address: e.target.value })}
                    placeholder={t('admin_billing_fiscal_placeholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="driver_bank_name">{t('admin_billing_bank_name')}</Label>
                  <Input
                    id="driver_bank_name"
                    value={driverEditForm.bank_name || ''}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, bank_name: e.target.value })}
                    placeholder={t('admin_billing_bank_placeholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="driver_bank_account">{t('admin_billing_iban')}</Label>
                  <Input
                    id="driver_bank_account"
                    value={driverEditForm.bank_account || ''}
                    onChange={(e) => setDriverEditForm({ ...driverEditForm, bank_account: e.target.value })}
                    placeholder={t('admin_billing_iban_placeholder')}
                  />
                </div>
              </div>
            </div>

            {/* Fleet Driver Type */}
            <div className="border-t pt-4">
              <Label htmlFor="edit_fleet_driver_checkbox">{t('admin_driver_type')}</Label>
              <div className="flex items-center space-x-2 mt-2">
                <input
                  type="checkbox"
                  id="edit_fleet_driver_checkbox"
                  checked={driverEditForm.is_fleet_driver || false}
                  onChange={(e) => setDriverEditForm({ 
                    ...driverEditForm, 
                    is_fleet_driver: e.target.checked,
                    fleet_driver_id: e.target.checked ? '' : driverEditForm.fleet_driver_id
                  })}
                  className="h-4 w-4 text-[#36e961] border-gray-300 rounded"
                />
                <Label htmlFor="edit_fleet_driver_checkbox" className="text-sm cursor-pointer">
                  {t('admin_fleet_driver')}
                </Label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('admin_fleet_driver_hint')}
              </p>
            </div>

            {/* Fleet Assignment - only show if not a fleet driver */}
            {!driverEditForm.is_fleet_driver && filteredFleetDrivers.length > 0 && (
              <div>
                <Label htmlFor="edit_fleet_selection">{t('admin_assign_fleet')}</Label>
                <select
                  id="edit_fleet_selection"
                  value={driverEditForm.fleet_driver_id || ''}
                  onChange={(e) => setDriverEditForm({ ...driverEditForm, fleet_driver_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">{t('admin_independent_driver')}</option>
                  {filteredFleetDrivers.map((fleet) => (
                    <option key={fleet.id} value={fleet.id}>
                      {fleet.name} - {fleet.email}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin_fleet_assign_hint')}
                </p>
              </div>
            )}

            {/* Document Upload Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg text-gray-900">{t('admin_driver_docs_title')}</h3>
                {driverEditForm.documents_validated ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {t('driver_validated')}
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {t('admin_validation_pending')}
                  </span>
                )}
              </div>
              
              {/* Warning when validated but no documents */}
              {driverEditForm.documents_validated && (!existingDocuments || existingDocuments.length === 0) && (
                <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">⚠️ {t('admin_docs_warning_title')}</p>
                      <p className="text-sm text-red-700 mt-1">
                        {t('admin_docs_warning_body')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info message based on driver type */}
              {driverEditForm.fleet_driver_id && !driverEditForm.is_fleet_driver ? (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    ℹ️ {t('admin_fleet_driver_info')}
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm text-amber-700">
                    ℹ️ {t('admin_independent_driver_info')}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-4">
                {t('admin_docs_upload_note')}
              </p>

              {/* Existing Documents Section */}
              {existingDocuments && existingDocuments.length > 0 && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-md mb-3 text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('admin_docs_uploaded_count', { count: existingDocuments.length })}
                  </h4>
                  <div className="space-y-2">
                    {existingDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {getDriverEditDocumentLineLabel(t, doc.document_type)}
                          </p>
                          <div className="flex gap-4 text-xs text-gray-600 mt-1">
                            <span>📅 {t('admin_uploaded')}: {new Date(doc.upload_date).toLocaleDateString(dateLocale)}</span>
                            {doc.expiry_date && (
                              <span className={
                                new Date(doc.expiry_date) < new Date() 
                                  ? 'text-red-600 font-semibold' 
                                  : new Date(doc.expiry_date) < new Date(Date.now() + 7*24*60*60*1000)
                                  ? 'text-orange-600 font-semibold'
                                  : ''
                              }>
                                ⏰ {t('admin_expires')}: {new Date(doc.expiry_date).toLocaleDateString(dateLocale)}
                                {new Date(doc.expiry_date) < new Date() && ` (${t('admin_expired')})`}
                                {new Date(doc.expiry_date) < new Date(Date.now() + 7*24*60*60*1000) && 
                                 new Date(doc.expiry_date) >= new Date() && ` (${t('admin_expires_soon')})`}
                              </span>
                            )}
                            <span className={`font-semibold ${
                              doc.status === 'approved' ? 'text-green-600' : 
                              doc.status === 'pending' ? 'text-yellow-600' : 
                              'text-red-600'
                            }`}>
                              {doc.status === 'approved' && `✅ ${t('driver_doc_approved')}`}
                              {doc.status === 'pending' && `⏳ ${t('driver_doc_pending')}`}
                              {doc.status === 'rejected' && `❌ ${t('driver_doc_rejected')}`}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleViewDocument(doc.id, doc.document_type)}
                          className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          {t('driver_view')}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    💡 {t('admin_docs_no_delete')}
                  </p>
                </div>
              )}
              
              <div className="space-y-6">
                {/* DOCUMENTOS BÁSICOS - Todos los conductores */}
                <div>
                  <h4 className="font-semibold text-md mb-3 text-gray-800">📄 {t('admin_docs_basic')}</h4>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Licencia Frontal */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label htmlFor="license_front_upload" className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4" />
                        {`${t('admin_doc_license_front_full')} *`}
                      </Label>
                      <Input
                        id="license_front_upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDriverDocumentUpload(e, 'license_front')}
                        className="mb-2"
                      />
                      <Label htmlFor="license_front_expiry" className="text-xs text-gray-600 mb-1">
                        {t('driver_expiry_date_label')}
                      </Label>
                      <Input
                        id="license_front_expiry"
                        type="date"
                        value={licenseExpiryDate}
                        onChange={(e) => setLicenseExpiryDate(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin_format_image_pdf')}</p>
                    </div>

                    {/* Licencia Trasera */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label htmlFor="license_back_upload" className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4" />
                        {`${t('admin_doc_license_back_full')} *`}
                      </Label>
                      <Input
                        id="license_back_upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDriverDocumentUpload(e, 'license_back')}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin_format_image_pdf')}</p>
                    </div>

                    {/* DNI Frontal */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label htmlFor="dni_front_upload" className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4" />
                        {`${t('admin_doc_dni_front_full')} *`}
                      </Label>
                      <Input
                        id="dni_front_upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDriverDocumentUpload(e, 'dni_front')}
                        className="mb-2"
                      />
                      <Label htmlFor="dni_expiry" className="text-xs text-gray-600 mb-1">
                        {t('driver_expiry_date_label')}
                      </Label>
                      <Input
                        id="dni_expiry"
                        type="date"
                        value={dniExpiryDate}
                        onChange={(e) => setDniExpiryDate(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin_format_image_pdf')}</p>
                    </div>

                    {/* DNI Trasero */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label htmlFor="dni_back_upload" className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4" />
                        {`${t('admin_doc_dni_back_full')} *`}
                      </Label>
                      <Input
                        id="dni_back_upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDriverDocumentUpload(e, 'dni_back')}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('admin_format_image_pdf')}</p>
                    </div>
                  </div>
                </div>

                {/* CERTIFICADOS - Solo para independientes y flotistas */}
                {(!driverEditForm.fleet_driver_id || driverEditForm.is_fleet_driver) && (
                  <div>
                    <h4 className="font-semibold text-md mb-3 text-gray-800">📋 {t('admin_docs_certificates')}</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {/* Alta de Autónomo */}
                      <div className="border rounded-lg p-4 bg-amber-50">
                        <Label htmlFor="autonomo_upload" className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4" />
                          {`${t('admin_doc_autonomo_full')} *`}
                        </Label>
                        <Input
                          id="autonomo_upload"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleDriverDocumentUpload(e, 'autonomo_certificate')}
                          className="mb-2"
                        />
                        <Label htmlFor="autonomo_expiry" className="text-xs text-gray-600 mb-1">
                          {t('admin_expiry_date')}
                        </Label>
                        <Input
                          id="autonomo_expiry"
                          type="date"
                          value={autonomoExpiryDate}
                          onChange={(e) => setAutonomoExpiryDate(e.target.value)}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin_format_pdf')}</p>
                      </div>

                      {/* Certificado Seguridad Social */}
                      <div className="border rounded-lg p-4 bg-amber-50">
                        <Label htmlFor="ss_upload" className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4" />
                          {`${t('admin_doc_ss_full')} *`}
                        </Label>
                        <Input
                          id="ss_upload"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleDriverDocumentUpload(e, 'ss_certificate')}
                          className="mb-2"
                        />
                        <Label htmlFor="ss_expiry" className="text-xs text-gray-600 mb-1">
                          {t('admin_expiry_date')}
                        </Label>
                        <Input
                          id="ss_expiry"
                          type="date"
                          value={ssExpiryDate}
                          onChange={(e) => setSsExpiryDate(e.target.value)}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin_format_pdf')}</p>
                      </div>

                      {/* Certificado Hacienda */}
                      <div className="border rounded-lg p-4 bg-amber-50">
                        <Label htmlFor="hacienda_upload" className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4" />
                          {`${t('admin_doc_hacienda_full')} *`}
                        </Label>
                        <Input
                          id="hacienda_upload"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleDriverDocumentUpload(e, 'hacienda_certificate')}
                          className="mb-2"
                        />
                        <Label htmlFor="hacienda_expiry" className="text-xs text-gray-600 mb-1">
                          {t('admin_expiry_date')}
                        </Label>
                        <Input
                          id="hacienda_expiry"
                          type="date"
                          value={haciendaExpiryDate}
                          onChange={(e) => setHaciendaExpiryDate(e.target.value)}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('admin_format_pdf')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {uploadingDriverDoc && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">📤 {t('admin_uploading_doc')}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDriverEditDialog(false)}>
                {t('admin_cancel')}
              </Button>
              <Button type="submit">
                {t('admin_save_changes')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Create Dialog */}
      <Dialog open={showClientCreateDialog} onOpenChange={setShowClientCreateDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('admin_create_new_client')}</DialogTitle>
            <DialogDescription>
              {t('admin_create_new_client_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_client_name">{t('admin_name_required')}</Label>
                <Input
                  id="new_client_name"
                  value={clientCreateForm.name}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_client_email">{t('admin_email_required')}</Label>
                <Input
                  id="new_client_email"
                  type="email"
                  value={clientCreateForm.email}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_client_company">{t('admin_company_name')}</Label>
                <Input
                  id="new_client_company"
                  value={clientCreateForm.company_name}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="new_client_cif">{t('admin_cif_dni')}</Label>
                <Input
                  id="new_client_cif"
                  value={clientCreateForm.cif_dni}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, cif_dni: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_client_phone">{t('admin_phone')}</Label>
                <Input
                  id="new_client_phone"
                  value={clientCreateForm.phone}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="new_client_country">{t('admin_country')}</Label>
                <Input
                  id="new_client_country"
                  value={clientCreateForm.country}
                  onChange={(e) => setClientCreateForm({ ...clientCreateForm, country: e.target.value })}
                  placeholder={t('admin_country_placeholder')}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="new_client_address">{t('admin_address')}</Label>
              <Input
                id="new_client_address"
                value={clientCreateForm.address}
                onChange={(e) => setClientCreateForm({ ...clientCreateForm, address: e.target.value })}
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <p className="text-sm text-blue-800">
                ℹ️ {t('admin_create_client_password_info')}
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowClientCreateDialog(false)}>
                {t('admin_cancel')}
              </Button>
              <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                {t('admin_create_client')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Driver Create Dialog */}
      <Dialog open={showDriverCreateDialog} onOpenChange={setShowDriverCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('admin_create_new_driver')}</DialogTitle>
            <DialogDescription>
              {t('admin_create_new_driver_desc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDriver} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_driver_name">{t('admin_name_required')}</Label>
                <Input
                  id="new_driver_name"
                  value={driverCreateForm.name}
                  onChange={(e) => setDriverCreateForm({ ...driverCreateForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_driver_email">{t('admin_email_required')}</Label>
                <Input
                  id="new_driver_email"
                  type="email"
                  value={driverCreateForm.email}
                  onChange={(e) => setDriverCreateForm({ ...driverCreateForm, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_driver_phone">{t('phone_required')}</Label>
                <Input
                  id="new_driver_phone"
                  value={driverCreateForm.phone}
                  onChange={(e) => setDriverCreateForm({ ...driverCreateForm, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_driver_dni">{t('admin_driver_dni_required')}</Label>
                <Input
                  id="new_driver_dni"
                  value={driverCreateForm.license_number}
                  onChange={(e) => setDriverCreateForm({ ...driverCreateForm, license_number: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fleet_driver_checkbox">{t('admin_driver_type')}</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="fleet_driver_checkbox"
                    checked={driverCreateForm.is_fleet_driver}
                    onChange={(e) => setDriverCreateForm({ 
                      ...driverCreateForm, 
                      is_fleet_driver: e.target.checked,
                      fleet_driver_id: e.target.checked ? '' : driverCreateForm.fleet_driver_id
                    })}
                    className="h-4 w-4 text-[#36e961] border-gray-300 rounded"
                  />
                  <Label htmlFor="fleet_driver_checkbox" className="text-sm">
                    {t('admin_fleet_driver')}
                  </Label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin_fleet_driver_hint')}
                </p>
              </div>
            </div>

            {/* Fleet Driver Selection - only show if not creating fleet driver */}
            {!driverCreateForm.is_fleet_driver && filteredFleetDrivers.length > 0 && (
              <div>
                <Label htmlFor="fleet_selection">{t('admin_assign_fleet')}</Label>
                <select
                  id="fleet_selection"
                  value={driverCreateForm.fleet_driver_id}
                  onChange={(e) => setDriverCreateForm({ ...driverCreateForm, fleet_driver_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">{t('admin_independent_driver')}</option>
                  {filteredFleetDrivers.map((fleet) => (
                    <option key={fleet.id} value={fleet.id}>
                      {fleet.name} - {fleet.email}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin_fleet_assign_hint')}
                </p>
              </div>
            )}

            {/* Documents Section - Optional */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('driver_dialog_docs_optional')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('driver_dialog_docs_later')}
              </p>
              
              <div className="space-y-3">
                {/* License Documents */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('admin_doc_license_front_full')}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, license_front: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('admin_doc_license_back_full')}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, license_back: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* DNI Documents */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('admin_doc_dni_front_full')}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, dni_front: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('admin_doc_dni_back_full')}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, dni_back: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Certificates */}
                <div>
                  <Label className="text-sm">{t('admin_doc_label_pdf', { name: t('admin_doc_autonomo_full') })}</Label>
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setDriverDocuments({ ...driverDocuments, autonomo_certificate: e.target.files[0] })}
                    className="text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('admin_doc_label_pdf', { name: t('admin_doc_hacienda_full') })}</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, hacienda_certificate: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('admin_certificate_expiry_12m')}</Label>
                    <Input
                      type="date"
                      value={driverDocuments.hacienda_expiry}
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, hacienda_expiry: e.target.value })}
                      className="text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('admin_doc_label_pdf', { name: t('admin_doc_ss_full') })}</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, ss_certificate: e.target.files[0] })}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('admin_certificate_expiry_6m')}</Label>
                    <Input
                      type="date"
                      value={driverDocuments.ss_expiry}
                      onChange={(e) => setDriverDocuments({ ...driverDocuments, ss_expiry: e.target.value })}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <p className="text-sm text-blue-800">
                ℹ️ {t('admin_create_driver_password_info')}
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDriverCreateDialog(false)}>
                {t('admin_cancel')}
              </Button>
              <Button type="submit" className="bg-[#36e961] hover:bg-[#2dd156]">
                {t('admin_create_driver')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document View Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>{t('admin_review_document')}</DialogTitle>
            <DialogDescription>
              {currentDocument && (
                <>
                  <strong>{currentDocument.display_name}</strong> - {currentDocument.driver_name}
                  <br />
                  {t('admin_uploaded')}: {currentDocument && new Date(currentDocument.upload_date).toLocaleDateString(dateLocale)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center p-4">
            {currentDocument && <DocumentImage document={currentDocument} />}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowDocumentModal(false)}
            >
              {t('admin_close')}
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                handleApproveDocument(currentDocument.id);
                setShowDocumentModal(false);
              }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('admin_approve_btn')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleRejectDocument(currentDocument.id);
                setShowDocumentModal(false);
              }}
            >
              <XCircle className="w-4 h-4 mr-2" />
              {t('admin_reject_btn')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog para ver fotos del vehículo (Admin) */}
      {showAdminPhotosDialog && (
        <Dialog open={showAdminPhotosDialog} onOpenChange={setShowAdminPhotosDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_complete_service_photos')}</DialogTitle>
              {selectedOrderForView && (
                <DialogDescription>
                  {t('admin_dialog_order_vehicle_line', {
                    id: selectedOrderForView.id.substring(0, 8),
                    brand: selectedOrderForView.car_brand,
                    model: selectedOrderForView.car_model,
                    plate: selectedOrderForView.car_plate,
                  })}
                </DialogDescription>
              )}
            </DialogHeader>

            {loadingAdminPhotos ? (
              <div className="text-center py-8">{t('service_detail_loading_photos')}</div>
            ) : adminVehiclePhotos && adminVehiclePhotos.has_photos ? (
              <div className="space-y-8">
                {/* Info sobre origen de las fotos */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    📷 {t('admin_photos_dialog_caption_driver_admin')}
                  </p>
                </div>
                
                {adminVehiclePhotos.driver_selfies && adminVehiclePhotos.driver_selfies.length > 0 && (
                  <div className="border-2 border-sky-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-sky-700">
                      👤 {t('admin_driver_selfie_section')} ({adminVehiclePhotos.driver_selfies.length})
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.driver_selfies.map((photo, idx) => (
                        <div key={photo.id || idx} className="group relative">
                          <img
                            src={photo.photo_data}
                            alt={t('inspections_driver_selfie_alt')}
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => window.open(photo.photo_data, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">
                            {photo.inspection_type === 'delivery'
                              ? t('admin_driver_selfie_delivery')
                              : t('admin_driver_selfie_pickup')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fotos de Recogida */}
                {adminVehiclePhotos.pickup_photos && adminVehiclePhotos.pickup_photos.length > 0 && (
                  <div className="border-2 border-blue-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-blue-600">📸 {t('admin_complete_pickup_photos')} ({adminVehiclePhotos.pickup_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.pickup_photos.map((photo, idx) => (
                        <div key={photo.id || idx} className="group relative">
                          <img 
                            src={photo.photo_data || photo.photo_url || photo.front_photo || photo} 
                            alt={t('admin_photo_pickup_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photo.photo_data || photo.photo_url || photo.front_photo || photo, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">
                            {t('admin_photo_numbered', { n: idx + 1 })}{' '}
                            {photo.source === 'admin' ? t('admin_photo_source_admin') : photo.source === 'driver' ? t('admin_photo_source_driver') : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Entrega */}
                {adminVehiclePhotos.delivery_photos && adminVehiclePhotos.delivery_photos.length > 0 && (
                  <div className="border-2 border-purple-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-purple-600">📸 {t('admin_complete_delivery_photos')} ({adminVehiclePhotos.delivery_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.delivery_photos.map((photo, idx) => (
                        <div key={photo.id || idx} className="group relative">
                          <img 
                            src={photo.photo_data || photo.photo_url || photo.front_photo || photo} 
                            alt={t('admin_photo_delivery_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photo.photo_data || photo.photo_url || photo.front_photo || photo, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">
                            {t('admin_photo_numbered', { n: idx + 1 })}{' '}
                            {photo.source === 'admin' ? t('admin_photo_source_admin') : photo.source === 'driver' ? t('admin_photo_source_driver') : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fotos del Contrato */}
                {adminVehiclePhotos.contract_photos && adminVehiclePhotos.contract_photos.length > 0 && (
                  <div className="border-2 border-green-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-green-600">📄 {t('admin_history_contract_photos')} ({adminVehiclePhotos.contract_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.contract_photos.map((photo, idx) => (
                        <div key={photo.id || idx} className="group relative">
                          <img 
                            src={photo.photo_data || photo.photo_url || photo} 
                            alt={t('admin_photo_contract_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photo.photo_data || photo.photo_url || photo, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">
                            {t('admin_photo_contract_label', { n: idx + 1 })}{' '}
                            {photo.source === 'admin' ? t('admin_photo_source_admin') : photo.source === 'driver' ? t('admin_photo_source_driver') : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Gastos */}
                {adminVehiclePhotos.expense_photos && adminVehiclePhotos.expense_photos.length > 0 && (
                  <div className="border-2 border-orange-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-orange-600">🧾 {t('admin_history_expense_photos')} ({adminVehiclePhotos.expense_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.expense_photos.map((photo, idx) => (
                        <div key={photo.id || idx} className="group relative">
                          <img 
                            src={photo.photo_data || photo.photo_url || photo} 
                            alt={t('admin_photo_expense_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photo.photo_data || photo.photo_url || photo, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">
                            {t('admin_photo_ticket_label', { n: idx + 1 })}{' '}
                            {photo.source === 'admin' ? t('admin_photo_source_admin') : photo.source === 'driver' ? t('admin_photo_source_driver') : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : adminVehiclePhotos && (adminVehiclePhotos.pickup_photos || adminVehiclePhotos.delivery_photos || adminVehiclePhotos.expense_photos) ? (
              <div className="space-y-8">
                {/* Info sobre origen de las fotos */}
                {adminVehiclePhotos.source === 'admin_completion' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      📷 {t('admin_photos_completion_notice')}
                      {adminVehiclePhotos.uploaded_by_name && <> {t('admin_expenses_by', { name: adminVehiclePhotos.uploaded_by_name })}</>}
                      {adminVehiclePhotos.uploaded_at && <> {t('admin_expenses_on', { date: new Date(adminVehiclePhotos.uploaded_at).toLocaleDateString(dateLocale) })}</>}
                    </p>
                  </div>
                )}
                
                {/* Fotos de Recogida (Pickup) - Nuevo formato (array de URLs) */}
                {adminVehiclePhotos.source === 'admin_completion' && adminVehiclePhotos.pickup_photos && adminVehiclePhotos.pickup_photos.length > 0 && (
                  <div className="border-2 border-blue-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-blue-600">📸 {t('admin_complete_pickup_photos')} ({adminVehiclePhotos.pickup_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.pickup_photos.map((photoUrl, idx) => (
                        <div key={idx} className="group relative">
                          <img 
                            src={photoUrl} 
                            alt={t('admin_photo_pickup_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">{t('admin_photo_numbered', { n: idx + 1 })}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Entrega (Delivery) - Nuevo formato (array de URLs) */}
                {adminVehiclePhotos.source === 'admin_completion' && adminVehiclePhotos.delivery_photos && adminVehiclePhotos.delivery_photos.length > 0 && (
                  <div className="border-2 border-purple-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-purple-600">📸 {t('admin_complete_delivery_photos')} ({adminVehiclePhotos.delivery_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.delivery_photos.map((photoUrl, idx) => (
                        <div key={idx} className="group relative">
                          <img 
                            src={photoUrl} 
                            alt={t('admin_photo_delivery_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">{t('admin_photo_numbered', { n: idx + 1 })}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Gastos - Nuevo formato (array de URLs) */}
                {adminVehiclePhotos.source === 'admin_completion' && adminVehiclePhotos.expense_photos && adminVehiclePhotos.expense_photos.length > 0 && (
                  <div className="border-2 border-orange-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-orange-600">🧾 {t('admin_history_expense_photos')} ({adminVehiclePhotos.expense_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.expense_photos.map((photoUrl, idx) => (
                        <div key={idx} className="group relative">
                          <img 
                            src={photoUrl} 
                            alt={t('admin_photo_expense_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">{t('admin_photo_ticket_label', { n: idx + 1 })}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fotos del Contrato Firmado - Nuevo formato (array de URLs) */}
                {adminVehiclePhotos.source === 'admin_completion' && adminVehiclePhotos.contract_photos && adminVehiclePhotos.contract_photos.length > 0 && (
                  <div className="border-2 border-purple-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-purple-600">📄 {t('admin_complete_contract_photos')} ({adminVehiclePhotos.contract_photos.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {adminVehiclePhotos.contract_photos.map((photoUrl, idx) => (
                        <div key={idx} className="group relative">
                          <img 
                            src={photoUrl} 
                            alt={t('admin_photo_contract_alt', { n: idx + 1 })} 
                            className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">{t('admin_photo_contract_label', { n: idx + 1 })}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fotos de Recogida (Pickup) - Formato antiguo (objeto con propiedades) */}
                {adminVehiclePhotos.source === 'driver' && adminVehiclePhotos.pickup_photos && (
                  <div className="border-2 border-blue-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-blue-600">📸 {t('admin_pickup_photos_driver_heading')}</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_exterior_section')}</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {adminVehiclePhotos.pickup_photos.front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_front')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.front_photo} alt={t('admin_vehicle_photo_angle_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_rear')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.rear_photo} alt={t('admin_vehicle_photo_angle_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.left_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_left_front')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.left_side_front_photo} alt={t('admin_vehicle_photo_angle_left_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.left_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_left_rear')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.left_side_rear_photo} alt={t('admin_vehicle_photo_angle_left_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.right_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_right_front')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.right_side_front_photo} alt={t('admin_vehicle_photo_angle_right_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.right_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_right_rear')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.right_side_rear_photo} alt={t('admin_vehicle_photo_angle_right_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_interior_section')}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {adminVehiclePhotos.pickup_photos.front_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_front_seats')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.front_seats_photo} alt={t('admin_vehicle_photo_front_seats')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.rear_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_rear_seats')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.rear_seats_photo} alt={t('admin_vehicle_photo_rear_seats')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.dashboard_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_dashboard')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.dashboard_photo} alt={t('admin_vehicle_photo_dashboard')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.pickup_photos.trunk_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_trunk')}</p>
                              <img src={adminVehiclePhotos.pickup_photos.trunk_photo} alt={t('admin_vehicle_photo_trunk')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      {adminVehiclePhotos.pickup_photos.delivery_certificate_photo && (
                        <div>
                          <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_acta_pickup')}</h3>
                          <img src={adminVehiclePhotos.pickup_photos.delivery_certificate_photo} alt={t('admin_vehicle_photo_acta_pickup')} className="w-full max-w-2xl mx-auto rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Fotos de Entrega (Delivery) - Formato antiguo (objeto con propiedades) */}
                {adminVehiclePhotos.source === 'driver' && adminVehiclePhotos.delivery_photos && (
                  <div className="border-2 border-purple-200 rounded-lg p-4">
                    <h2 className="text-xl font-bold mb-4 text-purple-600">📸 {t('admin_delivery_photos_driver_heading')}</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_exterior_section')}</h3>
                        <div className="grid grid-cols-3 gap-4">
                          {adminVehiclePhotos.delivery_photos.front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_front')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.front_photo} alt={t('admin_vehicle_photo_angle_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_rear')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.rear_photo} alt={t('admin_vehicle_photo_angle_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.left_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_left_front')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.left_side_front_photo} alt={t('admin_vehicle_photo_angle_left_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.left_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_left_rear')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.left_side_rear_photo} alt={t('admin_vehicle_photo_angle_left_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.right_side_front_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_right_front')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.right_side_front_photo} alt={t('admin_vehicle_photo_angle_right_front')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.right_side_rear_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_angle_right_rear')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.right_side_rear_photo} alt={t('admin_vehicle_photo_angle_right_rear')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_interior_section')}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {adminVehiclePhotos.delivery_photos.front_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_front_seats')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.front_seats_photo} alt={t('admin_vehicle_photo_front_seats')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.rear_seats_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_rear_seats')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.rear_seats_photo} alt={t('admin_vehicle_photo_rear_seats')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.dashboard_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_dashboard')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.dashboard_photo} alt={t('admin_vehicle_photo_dashboard')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                          {adminVehiclePhotos.delivery_photos.trunk_photo && (
                            <div>
                              <p className="text-sm font-medium mb-2">{t('admin_vehicle_photo_trunk')}</p>
                              <img src={adminVehiclePhotos.delivery_photos.trunk_photo} alt={t('admin_vehicle_photo_trunk')} className="w-full h-48 object-cover rounded-lg border" />
                            </div>
                          )}
                        </div>
                      </div>

                      {adminVehiclePhotos.delivery_photos.delivery_certificate_photo && (
                        <div>
                          <h3 className="font-semibold text-lg mb-4">{t('admin_vehicle_photo_acta_delivery')}</h3>
                          <img src={adminVehiclePhotos.delivery_photos.delivery_certificate_photo} alt={t('admin_vehicle_photo_acta_delivery')} className="w-full max-w-2xl mx-auto rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                {t('admin_no_photos_for_service')}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAdminPhotosDialog(false)}>
                {t('service_detail_close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para ver gastos (Admin) */}
      {showAdminExpensesDialog && (
        <Dialog open={showAdminExpensesDialog} onOpenChange={setShowAdminExpensesDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_service_expenses')}</DialogTitle>
              {selectedOrderForView && (
                <DialogDescription>
                  {t('admin_service_expenses_subtitle', {
                    id: selectedOrderForView.id.substring(0, 8),
                    brand: selectedOrderForView.car_brand,
                    model: selectedOrderForView.car_model,
                  })}
                </DialogDescription>
              )}
            </DialogHeader>

            {loadingAdminExpenses ? (
              <div className="text-center py-8">{t('admin_loading_order_expenses')}</div>
            ) : adminOrderExpenses && adminOrderExpenses.total_expense !== undefined ? (
              <div className="space-y-4">
                {adminOrderExpenses.source !== 'admin_completion' && (
                  <ExpenseReportServiceInfo report={adminOrderExpenses} />
                )}
                {/* Info sobre origen de los gastos */}
                {adminOrderExpenses.source === 'admin_completion' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      💰 {t('admin_expenses_registered_completion')}
                      {adminOrderExpenses.submitted_by_name && <> {t('admin_expenses_by', { name: adminOrderExpenses.submitted_by_name })}</>}
                      {adminOrderExpenses.submitted_at && <> {t('admin_expenses_on', { date: new Date(adminOrderExpenses.submitted_at).toLocaleDateString(dateLocale) })}</>}
                    </p>
                  </div>
                )}

                <div className={`border rounded-lg p-4 ${
                  adminOrderExpenses.source === 'admin_completion' ? 'bg-green-50 border-green-200' :
                  adminOrderExpenses.status === 'approved' ? 'bg-green-50 border-green-200' :
                  adminOrderExpenses.status === 'rejected' ? 'bg-red-50 border-red-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}>
                  {adminOrderExpenses.source !== 'admin_completion' && (
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm font-medium">{t('admin_expense_status_label')}{' '}
                        {adminOrderExpenses.status === 'approved' ? t('admin_expense_status_approved') :
                        adminOrderExpenses.status === 'rejected' ? t('admin_expense_status_rejected') :
                        t('admin_expense_status_pending')}
                      </p>
                      <p className="text-xs text-gray-600">
                        {t('admin_expense_reported')}{' '}
                        {formatExpenseReportDate(adminOrderExpenses, dateLocale) || t('admin_expense_other_na')}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">{t('admin_expense_gas_label')}</span>
                      <span className="font-semibold">€{adminOrderExpenses.fuel_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">{t('admin_expense_tolls_label')}</span>
                      <span className="font-semibold">€{adminOrderExpenses.toll_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">{t('admin_expense_parking_label')}</span>
                      <span className="font-semibold">€{adminOrderExpenses.parking_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-700">{t('admin_expense_taxi_label')}</span>
                      <span className="font-semibold">€{adminOrderExpenses.taxi_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                    {adminOrderExpenses.other_expense > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-700">{t('admin_expense_other_label_short', { desc: adminOrderExpenses.other_expense_description || t('admin_expense_other_na') })}</span>
                        <span className="font-semibold">€{adminOrderExpenses.other_expense?.toFixed(2) || '0.00'}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t">
                      <span className="font-semibold text-gray-900">{t('admin_expense_total_expenses')}</span>
                      <span className="font-bold text-lg">€{adminOrderExpenses.total_expense?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  {adminOrderExpenses.rejection_reason && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded">
                      <p className="text-sm font-medium text-red-800">{t('admin_expense_rejection_reason')}</p>
                      <p className="text-sm text-red-700">{adminOrderExpenses.rejection_reason}</p>
                    </div>
                  )}
                </div>

                {/* Fotos de gastos del nuevo sistema (admin_completion) */}
                {adminOrderExpenses.expense_photos && adminOrderExpenses.expense_photos.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      {t('admin_expense_tickets_attached', { count: adminOrderExpenses.expense_photos.length })}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {adminOrderExpenses.expense_photos.map((photoUrl, idx) => (
                        <div key={idx} className="group relative border rounded-lg p-2 bg-gray-50">
                          <img 
                            src={photoUrl} 
                            alt={t('admin_photo_ticket_label', { n: idx + 1 })} 
                            className="w-full h-64 object-contain rounded-lg cursor-pointer hover:opacity-80 transition-opacity" 
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                          <p className="text-xs text-center mt-1 text-gray-600">{t('admin_photo_ticket_label', { n: idx + 1 })}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">💡 {t('admin_expense_click_enlarge')}</p>
                  </div>
                )}

                <ExpenseReportReceipts report={adminOrderExpenses} className="border rounded-lg" />

                {(!adminOrderExpenses.receipt_urls?.length && !adminOrderExpenses.receipts?.length) &&
                 (!adminOrderExpenses.expense_photos || adminOrderExpenses.expense_photos.length === 0) && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-600 text-center">📄 {t('admin_expense_no_receipts')}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                {t('admin_no_expenses_for_service')}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAdminExpensesDialog(false)}>
                {t('service_detail_close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para ver documentos de conductor */}
      {showDriverDocumentsDialog && selectedDriver && (
        <Dialog open={showDriverDocumentsDialog} onOpenChange={setShowDriverDocumentsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_driver_documents_modal_title')}</DialogTitle>
              <DialogDescription>
                {selectedDriver.name} - {selectedDriver.email}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {selectedDriver.documents && Object.keys(selectedDriver.documents).length > 0 ? (
                <>
                  <div>
                    <h3 className="font-semibold text-lg mb-3">{t('admin_doc_section_license')}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedDriver.documents.license_front && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('admin_doc_side_front')}</p>
                          <img src={selectedDriver.documents.license_front} alt={t('driver_doc_license_front')} className="w-full h-48 object-cover rounded-lg border" />
                        </div>
                      )}
                      {selectedDriver.documents.license_back && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('admin_doc_side_back')}</p>
                          <img src={selectedDriver.documents.license_back} alt={t('driver_doc_license_back')} className="w-full h-48 object-cover rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">{t('admin_doc_section_dni')}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedDriver.documents.dni_front && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('admin_doc_side_front')}</p>
                          <img src={selectedDriver.documents.dni_front} alt={t('driver_doc_dni_front')} className="w-full h-48 object-cover rounded-lg border" />
                        </div>
                      )}
                      {selectedDriver.documents.dni_back && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('admin_doc_side_back')}</p>
                          <img src={selectedDriver.documents.dni_back} alt={t('driver_doc_dni_back')} className="w-full h-48 object-cover rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">{t('admin_doc_section_certificates')}</h3>
                    <div className="space-y-4">
                      {selectedDriver.documents.alta_autonomo && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('driver_doc_autonomo')}</p>
                          <img src={selectedDriver.documents.alta_autonomo} alt={t('driver_doc_autonomo')} className="w-full max-w-md rounded-lg border" />
                        </div>
                      )}
                      {selectedDriver.documents.hacienda && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('driver_doc_hacienda')}</p>
                          <img src={selectedDriver.documents.hacienda} alt={t('driver_doc_hacienda')} className="w-full max-w-md rounded-lg border" />
                        </div>
                      )}
                      {selectedDriver.documents.seguridad_social && (
                        <div>
                          <p className="text-sm font-medium mb-2">{t('driver_doc_ss')}</p>
                          <img src={selectedDriver.documents.seguridad_social} alt={t('driver_doc_ss')} className="w-full max-w-md rounded-lg border" />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-600">
                  {t('admin_no_documents_for_driver')}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDriverDocumentsDialog(false)}>
                {t('admin_close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para ver viajes del periodo */}
      {showPeriodTripsDialog && selectedPeriod && (
        <Dialog open={showPeriodTripsDialog} onOpenChange={setShowPeriodTripsDialog}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_trip_details_dialog_title', { name: selectedPeriod.driver_name })}</DialogTitle>
              <DialogDescription>
                {t('period_trips_period_intro', {
                  start: new Date(selectedPeriod.period_start).toLocaleDateString(dateLocale),
                  end: new Date(selectedPeriod.period_end).toLocaleDateString(dateLocale)
                })}
              </DialogDescription>
            </DialogHeader>

            {loadingPeriodTrips ? (
              <div className="text-center py-8">{t('period_trips_loading')}</div>
            ) : periodTrips.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('period_trips_total_trips')}</p>
                    <p className="text-2xl font-bold text-blue-600">{periodTrips.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('period_trips_income')}</p>
                    <p className="text-2xl font-bold text-green-600">€{selectedPeriod.total_revenue?.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('period_trips_expenses')}</p>
                    <p className="text-2xl font-bold text-orange-600">€{selectedPeriod.total_expenses?.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('period_trips_total_vat')}</p>
                    <p className="text-2xl font-bold text-purple-600">€{((selectedPeriod.total_revenue * 1.21) + selectedPeriod.total_expenses)?.toFixed(2)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {periodTrips.map((trip, idx) => {
                    // Calculate expenses from expense_report if available
                    const expenses = trip.expense_report || {};
                    const totalExpenses = (expenses.fuel_expense || 0) + 
                                         (expenses.toll_expense || 0) + 
                                         (expenses.parking_expense || 0) + 
                                         (expenses.taxi_expense || 0) + 
                                         (expenses.other_expense || 0);
                    const driverPrice = trip.driver_price || 0;
                    const totalWithExpenses = driverPrice + totalExpenses;
                    
                    return (
                    <div key={trip.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-lg">#{idx + 1}</span>
                            <span className="text-sm text-gray-600">{t('period_trips_id_label')} {trip.id.substring(0, 12)}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              trip.status === 'completed' ? 'bg-green-100 text-green-700' :
                              trip.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {trip.status === 'completed' ? t('transport_tab_status_completed') : trip.status}
                            </span>
                            {trip.approval_status === 'approved' && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                                {t('period_trips_expenses_approved')}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <p className="text-gray-600">{t('period_trips_vehicle')}</p>
                              <p className="font-medium">{trip.car_brand} {trip.car_model}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">{t('period_trips_plate')}</p>
                              <p className="font-medium">{trip.car_plate || t('gps_live_na')}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">{t('period_trips_client')}</p>
                              <p className="font-medium">{trip.client_name || t('gps_live_na')}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">{t('period_trips_pickup_date')}</p>
                              <p className="font-medium">
                                {trip.pickup_date_from ? (
                                  <>
                                    {new Date(trip.pickup_date_from).toLocaleDateString(dateLocale)}
                                    {trip.pickup_time_start && ` ${trip.pickup_time_start}`}
                                  </>
                                ) : trip.service_date ? (
                                  new Date(trip.service_date).toLocaleDateString(dateLocale)
                                ) : t('gps_live_na')}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">{t('period_trips_delivery_date')}</p>
                              <p className="font-medium">
                                {trip.delivery_date_from ? (
                                  <>
                                    {new Date(trip.delivery_date_from).toLocaleDateString(dateLocale)}
                                    {trip.delivery_time_start && ` ${trip.delivery_time_start}`}
                                  </>
                                ) : trip.actual_delivery_date ? (
                                  new Date(trip.actual_delivery_date).toLocaleDateString(dateLocale)
                                ) : t('gps_live_na')}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">{t('period_trips_service')}</p>
                              <p className="font-medium">{trip.service_type ? t(trip.service_type) : t('gps_live_na')}</p>
                            </div>
                            <div className="col-span-3">
                              <p className="text-gray-600">{t('period_trips_route')}</p>
                              <p className="font-medium text-xs">{trip.pickup_address || t('gps_live_na')} → {trip.delivery_address || t('gps_live_na')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right min-w-[140px]">
                          <div className="space-y-1">
                            <div>
                              <p className="text-xs text-gray-500">{t('period_trips_driver_rate')}</p>
                              <p className="text-lg font-bold text-green-600">€{driverPrice.toFixed(2)}</p>
                            </div>
                            {totalExpenses > 0 && (
                              <div className="border-t pt-1">
                                <p className="text-xs text-gray-500">{t('period_trips_supplied_expenses')}</p>
                                <p className="text-sm font-semibold text-orange-600">+€{totalExpenses.toFixed(2)}</p>
                                <div className="text-xs text-gray-400 mt-1">
                                  {expenses.fuel_expense > 0 && <span className="block">⛽ {t('period_trips_fuel')} €{expenses.fuel_expense.toFixed(2)}</span>}
                                  {expenses.toll_expense > 0 && <span className="block">🛣️ {t('period_trips_tolls')} €{expenses.toll_expense.toFixed(2)}</span>}
                                  {expenses.parking_expense > 0 && <span className="block">🅿️ {t('period_trips_parking')} €{expenses.parking_expense.toFixed(2)}</span>}
                                  {expenses.taxi_expense > 0 && <span className="block">🚕 {t('period_trips_taxi')} €{expenses.taxi_expense.toFixed(2)}</span>}
                                  {expenses.other_expense > 0 && <span className="block">📦 {t('period_trips_other')} €{expenses.other_expense.toFixed(2)}</span>}
                                </div>
                              </div>
                            )}
                            <div className="border-t pt-1 mt-1">
                              <p className="text-xs text-gray-500">{t('period_trips_total_service')}</p>
                              <p className="text-lg font-bold text-purple-700">€{totalWithExpenses.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                {t('period_trips_empty')}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPeriodTripsDialog(false)}>
                {t('admin_close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Driver Pricing Selection Dialog */}
      {showDriverPricingDialog && selectedOrder && (
        <Dialog open={showDriverPricingDialog} onOpenChange={setShowDriverPricingDialog}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('admin_assign_driver_rate_dialog_title')}</DialogTitle>
              <p className="text-sm text-gray-500 mt-2">
                {t('admin_driver_label')} <span className="font-semibold">{selectedDriverForPricing?.name}</span>
              </p>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Client Price Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-purple-900">💰 {t('admin_assign_driver_client_charge_title')}</h3>
                    <p className="text-sm text-purple-700 mt-1">{t('admin_assign_driver_client_charge_hint')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-purple-600">{selectedOrder.total_price?.toFixed(2)}€</p>
                    <p className="text-xs text-purple-700 mt-1">{t('admin_price_vat_included_short')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-purple-700">{t('admin_penalty_service_label')}</p>
                    <p className="font-semibold text-purple-900">
                      {selectedOrder.service_type === 'delivery'
                        ? t('delivery')
                        : selectedOrder.service_type === 'bc_service'
                          ? t('bc_service')
                          : t('tradein')}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-700">{t('admin_distance')}</p>
                    <p className="font-semibold text-purple-900">{selectedOrder.distance_km} km</p>
                  </div>
                </div>
              </div>

              {/* Driver Price Selection */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('admin_select_tariff_for_driver_title')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('admin_select_tariff_for_driver_subtitle')}</p>
                
                {/* Show calculated prices for each template */}
                <div className="space-y-3">
                  {Object.entries(calculatedPrices).map(([templateId, data]) => {
                    const margin = selectedOrder.total_price - data.price;
                    const marginPercent = ((margin / selectedOrder.total_price) * 100).toFixed(1);
                    
                    return (
                      <div 
                        key={templateId}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 cursor-pointer transition-all"
                        onClick={() => confirmDriverAssignment(templateId, false)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-lg">{data.name}</h4>
                              {data.name === 'Básica' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{t('admin_badge_standard')}</span>}
                              {data.name === 'Premium' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{t('admin_badge_high_end')}</span>}
                              {data.name === 'JLUIS' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{t('admin_badge_premium_plus_10')}</span>}
                            </div>
                            <p className="text-sm text-gray-600">
                              {t('admin_margin_label_plain')} <span className="font-semibold text-green-600">{margin.toFixed(2)}€</span> ({marginPercent}%)
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">{data.price.toFixed(2)}€</p>
                            <p className="text-xs text-gray-500">{t('admin_payment_for_driver_caption')}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom price option */}
              {!showCustomPriceInput ? (
                <div 
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 cursor-pointer transition-all"
                  onClick={() => setShowCustomPriceInput(true)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-lg">✏️ {t('admin_custom_rate')}</h4>
                      <p className="text-sm text-gray-600">{t('admin_custom_driver_price_manual_hint')}</p>
                    </div>
                    <div className="text-indigo-600 font-semibold">
                      {t('admin_edit_go_arrow')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-2 border-indigo-500 rounded-lg bg-indigo-50">
                  <h4 className="font-semibold text-lg mb-3">✏️ {t('admin_custom_rate')}</h4>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('ph_price_eur')}
                        value={customDriverPrice}
                        onChange={(e) => setCustomDriverPrice(e.target.value)}
                      />
                      {customDriverPrice && (
                        <p className="text-xs text-gray-600 mt-1">
                          {t('admin_margin_label_plain')} {(selectedOrder.total_price - parseFloat(customDriverPrice)).toFixed(2)}€ 
                          ({(((selectedOrder.total_price - parseFloat(customDriverPrice)) / selectedOrder.total_price) * 100).toFixed(1)}%)
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        if (!customDriverPrice || parseFloat(customDriverPrice) <= 0) {
                          toast.error(t('admin_toast_please_enter_valid_price'));
                          return;
                        }
                        confirmDriverAssignment(null, true, customDriverPrice);
                      }}
                      className="bg-indigo-600"
                    >
                      {t('admin_confirm')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCustomPriceInput(false);
                        setCustomDriverPrice('');
                      }}
                    >
                      {t('admin_cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDriverPricingDialog(false);
                  setShowCustomPriceInput(false);
                  setCustomDriverPrice('');
                }}
              >
                {t('admin_remove_assignment_btn')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Driver Rates Detail Dialog */}
      {showDriverRatesDialog && (
        <Dialog open={showDriverRatesDialog} onOpenChange={setShowDriverRatesDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_rates_for_template_title', { name: selectedTemplateName })}</DialogTitle>
              <p className="text-sm text-gray-500 mt-2">
                {t('admin_all_rates_configured_for_template')}
              </p>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedTemplateRates.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">{t('admin_range_km')}</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">{t('admin_rates_col_delivery')}</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">{t('admin_rates_col_bc')}</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">{t('admin_rates_col_tradein')}</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">{t('admin_rates_col_representation')}</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">{t('admin_rates_col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTemplateRates
                        .sort((a, b) => a.min_distance - b.min_distance)
                        .map((rate, idx) => (
                        <tr key={rate.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 border-b">
                            {rate.max_distance 
                              ? `${rate.min_distance} - ${rate.max_distance} km`
                              : `${rate.min_distance}+ km`
                            }
                            {!rate.max_distance && (
                              <div className="text-xs text-gray-500 mt-1">
                                {t('admin_base_plus_per_km')}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right border-b">
                            <span className="font-semibold text-green-700">
                              {rate.delivery_price.toFixed(2)}€
                            </span>
                            {rate.delivery_price_per_km && (
                              <div className="text-xs text-gray-500">
                                + {rate.delivery_price_per_km.toFixed(2)}€/km
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right border-b">
                            <span className="font-semibold text-blue-700">
                              {rate.bc_service_price.toFixed(2)}€
                            </span>
                            {rate.bc_service_price_per_km && (
                              <div className="text-xs text-gray-500">
                                + {rate.bc_service_price_per_km.toFixed(2)}€/km
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right border-b">
                            <span className="font-semibold text-purple-700">
                              {rate.tradein_price.toFixed(2)}€
                            </span>
                            {rate.tradein_price_per_km && (
                              <div className="text-xs text-gray-500">
                                + {rate.tradein_price_per_km.toFixed(2)}€/km
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right border-b">
                            <span className="font-semibold text-orange-700">
                              {rate.representacion_price?.toFixed(2) || '45.00'}€
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center border-b">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditRate(rate)}
                              className="h-8 px-2"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('admin_no_rates_for_template')}
                </div>
              )}

              {/* Info Footer */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>{t('admin_note')}</strong> {t('admin_rates_per_km_long_note')}
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDriverRatesDialog(false)}>
                {t('admin_close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Rate Dialog */}
      {showEditRateDialog && editingRate && (
        <Dialog open={showEditRateDialog} onOpenChange={setShowEditRateDialog}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_edit_rate_title')}</DialogTitle>
              <p className="text-sm text-gray-500 mt-2">
                {t('admin_edit_rate_range_caption', {
                  range: editingRate.max_distance ? `${editingRate.min_distance} - ${editingRate.max_distance} km` : `${editingRate.min_distance}+ km`
                })}
              </p>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('admin_price_delivery_eur')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRate.delivery_price}
                    onChange={(e) => setEditingRate({...editingRate, delivery_price: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{t('admin_price_bc_eur')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRate.bc_service_price}
                    onChange={(e) => setEditingRate({...editingRate, bc_service_price: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{t('admin_price_tradein_eur')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRate.tradein_price}
                    onChange={(e) => setEditingRate({...editingRate, tradein_price: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{t('admin_price_representation_eur')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRate.representacion_price}
                    onChange={(e) => setEditingRate({...editingRate, representacion_price: e.target.value})}
                  />
                </div>
              </div>

              {!editingRate.max_distance && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">{t('admin_edit_rate_per_km_heading')}</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>{t('admin_rate_delivery_per_km')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingRate.delivery_price_per_km || ''}
                        onChange={(e) => setEditingRate({...editingRate, delivery_price_per_km: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{t('admin_rate_bc_per_km')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingRate.bc_service_price_per_km || ''}
                        onChange={(e) => setEditingRate({...editingRate, bc_service_price_per_km: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>{t('admin_rate_tradein_per_km')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingRate.tradein_price_per_km || ''}
                        onChange={(e) => setEditingRate({...editingRate, tradein_price_per_km: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditRateDialog(false)}>
                {t('admin_cancel')}
              </Button>
              <Button onClick={handleSaveEditedRate} className="bg-indigo-600">
                {t('admin_save_changes')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create New Template Dialog */}
      {showCreateTemplateDialog && (
        <Dialog open={showCreateTemplateDialog} onOpenChange={setShowCreateTemplateDialog}>
          <DialogContent className="max-w-xl w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>{t('admin_create_new_pricing_template')}</DialogTitle>
              <p className="text-sm text-gray-500 mt-2">
                {t('admin_new_template_intro')}
              </p>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>{t('admin_template_name_required')}</Label>
                <Input
                  placeholder={t('admin_template_name_placeholder')}
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('admin_additional_services_col_description')}</Label>
                <Input
                  placeholder={t('admin_template_description_placeholder')}
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>{t('admin_note')}</strong> {t('admin_new_template_note_body')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateTemplateDialog(false);
                  setNewTemplateName('');
                  setNewTemplateDescription('');
                }}
                disabled={creatingTemplate}
              >
                {t('admin_cancel')}
              </Button>
              <Button 
                onClick={handleCreateDriverPricingTemplate} 
                className="bg-indigo-600"
                disabled={creatingTemplate || !newTemplateName.trim()}
              >
                {creatingTemplate ? t('admin_creating') : t('admin_create_template')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Service Dialog */}
      {showCreateServiceDialog && (
        <Dialog open={showCreateServiceDialog} onOpenChange={setShowCreateServiceDialog}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">{t('transport_tab_modal_create_title')}</DialogTitle>
              <p className="text-gray-600">{t('admin_modal_create_intro')}</p>
            </DialogHeader>

            <form onSubmit={handleCreateAdminService} className="space-y-6">
              {/* Cliente y Tipo de Servicio */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('admin_modal_client_optional_label')}</Label>
                  <Select 
                    value={createServiceForm.client_id || "__none__"} 
                    onValueChange={(value) => setCreateServiceForm({...createServiceForm, client_id: value === "__none__" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin_modal_no_client_admin')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('admin_modal_no_client_admin')}</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} - {client.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin_modal_no_client_hint')}
                  </p>
                </div>

                <div>
                  <Label>{t('service_type_required')}</Label>
                  <Select 
                    value={createServiceForm.service_type} 
                    onValueChange={handleServiceTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="delivery">{t('delivery')}</SelectItem>
                      <SelectItem value="bc_service">{t('bc_service')}</SelectItem>
                      <SelectItem value="tradein">{t('tradein')}</SelectItem>
                      <SelectItem value="representacion">{t('representacion')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dirección de Recogida */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#36e961]" />
                  {t('transport_tab_pickup_address_required')}
                </h3>
                
                <div>
                  <Label>{t('pickup_address')}</Label>
                  <AddressAutocomplete
                    value={createServiceForm.pickup_address}
                    onChange={(data) => handlePickupAddressSelect(data.address, data.lat, data.lng)}
                    placeholder={t('start_typing_address')}
                    inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('contact_name')}</Label>
                    <Input
                      value={createServiceForm.pickup_contact_name}
                      onChange={(e) => updateFormField('pickup_contact_name', e.target.value)}
                      placeholder={t('admin_placeholder_contact_first_name')}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('contact_phone')}</Label>
                    <Input
                      value={createServiceForm.pickup_contact_phone}
                      onChange={(e) => updateFormField('pickup_contact_phone', e.target.value)}
                      placeholder={t('transport_tab_ph_phone')}
                      required
                    />
                  </div>
                </div>
              </div>


              {/* Fechas de Recogida */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center p-3 bg-white rounded">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <Label className="font-semibold text-base">{t('admin_modal_datetime_range_title')}</Label>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setUseFixedPickupDate(!useFixedPickupDate)}
                    className="text-sm font-medium"
                    style={{backgroundColor: '#36e961', color: 'white', border: 'none'}}
                  >
                    {useFixedPickupDate ? t('admin_modal_switch_to_range') : t('admin_modal_switch_to_fixed')}
                  </Button>
                </div>

                {useFixedPickupDate ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('admin_modal_pickup_date_label')}</Label>
                      <DatePicker
                        selected={createServiceForm.pickup_date_start}
                        onChange={(date) => {
                          setCreateServiceForm(prev => ({ 
                            ...prev, 
                            pickup_date_start: date,
                            pickup_date_end: date,
                            pickup_fixed_date: true
                          }));
                        }}
                        className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholderText={t('select_date')}
                        dateFormat="dd/MM/yyyy"
                        {...DATE_PICKER_DIALOG_PROPS}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin_modal_pickup_time_label')}</Label>
                      <Input
                        type="time"
                        value={createServiceForm.pickup_time_start}
                        onChange={(e) => setCreateServiceForm(prev => ({ 
                          ...prev, 
                          pickup_time_start: e.target.value,
                          pickup_time_end: e.target.value
                        }))}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>{t('pickup_date_range')}</Label>
                      <DatePicker
                        selectsRange
                        startDate={createServiceForm.pickup_date_start}
                        endDate={createServiceForm.pickup_date_end}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          setCreateServiceForm(prev => ({ 
                            ...prev, 
                            pickup_date_start: start, 
                            pickup_date_end: end,
                            pickup_fixed_date: false
                          }));
                        }}
                        className="w-full min-w-[18rem] max-w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholderText={t('select_dates')}
                        dateFormat="dd/MM/yyyy"
                        {...DATE_PICKER_DIALOG_PROPS}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('admin_modal_pickup_time_from')}</Label>
                        <Input
                          type="time"
                          value={createServiceForm.pickup_time_start}
                          onChange={(e) => setCreateServiceForm(prev => ({ ...prev, pickup_time_start: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_modal_pickup_time_to')}</Label>
                        <Input
                          type="time"
                          value={createServiceForm.pickup_time_end}
                          onChange={(e) => setCreateServiceForm(prev => ({ ...prev, pickup_time_end: e.target.value }))}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>


              {/* Dirección de Entrega */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#36e961]" />
                  {t('transport_tab_delivery_address_required')}
                </h3>
                
                <div>
                  <Label>{t('delivery_address')}</Label>
                  <AddressAutocomplete
                    value={createServiceForm.delivery_address}
                    onChange={(data) => handleDeliveryAddressSelect(data.address, data.lat, data.lng)}
                    placeholder={t('start_typing_address')}
                    inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('contact_name')}</Label>
                    <Input
                      value={createServiceForm.delivery_contact_name}
                      onChange={(e) => updateFormField('delivery_contact_name', e.target.value)}
                      placeholder={t('admin_placeholder_contact_first_name')}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('contact_phone')}</Label>
                    <Input
                      value={createServiceForm.delivery_contact_phone}
                      onChange={(e) => updateFormField('delivery_contact_phone', e.target.value)}
                      placeholder={t('transport_tab_ph_phone')}
                      required
                    />
                  </div>
                </div>

                {/* Distancia y Precio Calculado (incl. 0 km mismo punto → tarifa corta) */}
                {hasCoordinateValue(createServiceForm.pickup_lat) &&
                  hasCoordinateValue(createServiceForm.delivery_lat) &&
                  createServiceForm.calculated_price > 0 && (
                  <div className="flex gap-4 p-3 bg-white rounded border border-[#36e961]">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{t('distance')}</p>
                      <p className="text-lg font-bold text-gray-900">{createServiceForm.distance_km} km</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{t('base_price')}</p>
                      <p className="text-lg font-bold text-[#36e961]">{createServiceForm.calculated_price.toFixed(2)} €</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Point C (only for B-C service) */}
              {createServiceForm.service_type === 'bc_service' && (
                <div className="space-y-4 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-yellow-600" />
                    {t('point_c_information')}
                  </h3>
                  <div>
                    <Label>{t('point_c_address')}</Label>
                    <AddressAutocomplete
                      value={createServiceForm.point_c_address}
                      onChange={async (data) => {
                        await updateCreateServiceDistanceAndPrice({
                          point_c_address: data.address,
                          point_c_lat: data.lat,
                          point_c_lng: data.lng
                        });
                      }}
                      placeholder={t('start_typing_address')}
                      inputClassName="text-[15px] leading-snug text-gray-900 font-sans min-h-[42px]"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('contact_name')}</Label>
                      <Input
                        value={createServiceForm.point_c_contact_name}
                        onChange={(e) => updateFormField('point_c_contact_name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('contact_phone')}</Label>
                      <Input
                        value={createServiceForm.point_c_contact_phone}
                        onChange={(e) => updateFormField('point_c_contact_phone', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-3 p-3 bg-white/80 rounded border border-yellow-200">
                    <div className="flex justify-between items-center">
                      <Label className="font-semibold">
                        {useFixedPointCDate ? t('admin_modal_delivery_date_label') : t('delivery_date_range')}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseFixedPointCDate(!useFixedPointCDate)}
                        className="text-xs"
                      >
                        {useFixedPointCDate ? t('admin_modal_switch_to_range') : t('admin_modal_switch_to_fixed')}
                      </Button>
                    </div>

                    {useFixedPointCDate ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('admin_modal_delivery_date_label')}</Label>
                          <DatePicker
                            selected={createServiceForm.point_c_date_start}
                            onChange={(date) => setCreateServiceForm(prev => ({
                              ...prev,
                              point_c_date_start: date,
                              point_c_date_end: date
                            }))}
                            className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholderText={t('select_date')}
                            dateFormat="dd/MM/yyyy"
                            minDate={createServiceForm.delivery_date_end || new Date()}
                            {...DATE_PICKER_DIALOG_PROPS}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('admin_modal_delivery_time_label')}</Label>
                          <Input
                            type="time"
                            value={createServiceForm.point_c_time_start}
                            onChange={(e) => setCreateServiceForm(prev => ({
                              ...prev,
                              point_c_time_start: e.target.value,
                              point_c_time_end: e.target.value
                            }))}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>{t('delivery_date_range')}</Label>
                          <DatePicker
                            selectsRange
                            startDate={createServiceForm.point_c_date_start}
                            endDate={createServiceForm.point_c_date_end}
                            onChange={(dates) => {
                              const [start, end] = dates;
                              setCreateServiceForm(prev => ({
                                ...prev,
                                point_c_date_start: start,
                                point_c_date_end: end
                              }));
                            }}
                            className="w-full min-w-[18rem] max-w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholderText={t('select_dates')}
                            dateFormat="dd/MM/yyyy"
                            minDate={createServiceForm.delivery_date_end || new Date()}
                            {...DATE_PICKER_DIALOG_PROPS}
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{t('admin_modal_delivery_time_from')}</Label>
                            <Input
                              type="time"
                              value={createServiceForm.point_c_time_start}
                              onChange={(e) => setCreateServiceForm(prev => ({
                                ...prev,
                                point_c_time_start: e.target.value
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('admin_modal_delivery_time_to')}</Label>
                            <Input
                              type="time"
                              value={createServiceForm.point_c_time_end}
                              onChange={(e) => setCreateServiceForm(prev => ({
                                ...prev,
                                point_c_time_end: e.target.value
                              }))}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}


              {/* Fechas de Entrega */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center p-3 bg-white rounded">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <Label className="font-semibold text-base">{t('delivery_information')}</Label>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setUseFixedDeliveryDate(!useFixedDeliveryDate)}
                    className="text-sm font-medium"
                    style={{backgroundColor: '#36e961', color: 'white', border: 'none'}}
                  >
                    {useFixedDeliveryDate ? t('admin_modal_switch_to_range') : t('admin_modal_switch_to_fixed')}
                  </Button>
                </div>

                {useFixedDeliveryDate ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('admin_modal_delivery_date_label')}</Label>
                      <DatePicker
                        selected={createServiceForm.delivery_date_start}
                        onChange={(date) => {
                          setCreateServiceForm(prev => ({ 
                            ...prev, 
                            delivery_date_start: date,
                            delivery_date_end: date,
                            delivery_fixed_date: true
                          }));
                        }}
                        className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholderText={t('select_date')}
                        dateFormat="dd/MM/yyyy"
                        {...DATE_PICKER_DIALOG_PROPS}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin_modal_delivery_time_label')}</Label>
                      <Input
                        type="time"
                        value={createServiceForm.delivery_time_start}
                        onChange={(e) => setCreateServiceForm(prev => ({ 
                          ...prev, 
                          delivery_time_start: e.target.value,
                          delivery_time_end: e.target.value
                        }))}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>{t('delivery_date_range')}</Label>
                      <DatePicker
                        selectsRange
                        startDate={createServiceForm.delivery_date_start}
                        endDate={createServiceForm.delivery_date_end}
                        onChange={(dates) => {
                          const [start, end] = dates;
                          setCreateServiceForm(prev => ({ 
                            ...prev, 
                            delivery_date_start: start, 
                            delivery_date_end: end,
                            delivery_fixed_date: false
                          }));
                        }}
                        className="w-full min-w-[18rem] max-w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholderText={t('select_dates')}
                        dateFormat="dd/MM/yyyy"
                        {...DATE_PICKER_DIALOG_PROPS}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('admin_modal_delivery_time_from')}</Label>
                        <Input
                          type="time"
                          value={createServiceForm.delivery_time_start}
                          onChange={(e) => setCreateServiceForm(prev => ({ ...prev, delivery_time_start: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin_modal_delivery_time_to')}</Label>
                        <Input
                          type="time"
                          value={createServiceForm.delivery_time_end}
                          onChange={(e) => setCreateServiceForm(prev => ({ ...prev, delivery_time_end: e.target.value }))}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Second vehicle for B-C and Trade-In */}
              {(createServiceForm.service_type === 'bc_service' || createServiceForm.service_type === 'tradein') && (
                <div className="space-y-4 p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Car className="w-5 h-5 text-orange-600" />
                    {t('second_vehicle')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>{t('vehicle_type')}</Label>
                      <Input
                        value={createServiceForm.second_car_type}
                        onChange={(e) => updateFormField('second_car_type', e.target.value)}
                        placeholder="Turismo"
                      />
                    </div>
                    <div>
                      <Label>{t('car_brand')} *</Label>
                      <Input
                        value={createServiceForm.second_car_brand}
                        onChange={(e) => updateFormField('second_car_brand', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('car_model')}</Label>
                      <Input
                        value={createServiceForm.second_car_model}
                        onChange={(e) => updateFormField('second_car_model', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>{t('car_plate')} *</Label>
                      <Input
                        value={createServiceForm.second_car_plate}
                        onChange={(e) => updateFormField('second_car_plate', e.target.value.toUpperCase())}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('fuel_type')}</Label>
                      <Select
                        value={createServiceForm.second_fuel_type}
                        onValueChange={(value) => setCreateServiceForm({ ...createServiceForm, second_fuel_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                          <SelectItem value="diesel">{t('diesel')}</SelectItem>
                          <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                          <SelectItem value="electric">{t('electric')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}


              {/* Datos del Vehículo */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#36e961]" />
                  {t('vehicle_information')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>{t('car_brand')} *</Label>
                    <Input
                      value={createServiceForm.car_brand}
                      onChange={(e) => updateFormField('car_brand', e.target.value)}
                      placeholder={t('admin_placeholder_brand_example')}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('car_model')} *</Label>
                    <Input
                      value={createServiceForm.car_model}
                      onChange={(e) => updateFormField('car_model', e.target.value)}
                      placeholder={t('admin_placeholder_model_example')}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('car_plate')} *</Label>
                    <Input
                      value={createServiceForm.car_plate}
                      onChange={(e) => updateFormField('car_plate', e.target.value.toUpperCase())}
                      placeholder={t('transport_tab_ph_plate')}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>{t('fuel_type')}</Label>
                    <Select 
                      value={createServiceForm.fuel_type} 
                      onValueChange={(value) => setCreateServiceForm({...createServiceForm, fuel_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">{t('gasoline')}</SelectItem>
                        <SelectItem value="diesel">{t('diesel')}</SelectItem>
                        <SelectItem value="hybrid">{t('hybrid')}</SelectItem>
                        <SelectItem value="electric">{t('electric')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('transport_tab_color')}</Label>
                    <Input
                      value={createServiceForm.car_color}
                      onChange={(e) => updateFormField('car_color', e.target.value)}
                      placeholder={t('transport_tab_ph_color')}
                    />
                  </div>
                  <div>
                    <Label>{t('admin_year')}</Label>
                    <Input
                      type="number"
                      value={createServiceForm.car_year}
                      onChange={(e) => updateFormField('car_year', e.target.value)}
                      placeholder="2023"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                </div>
              </div>

              {/* Servicios Adicionales */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#36e961]" />
                  {t('additional_services')} {t('admin_optional_paren')}
                </h3>

                {additionalServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {additionalServices.map((service) => (
                      <div key={service.id} className="flex items-center gap-3 p-3 bg-white rounded border">
                        <input
                          type="checkbox"
                          id={`service-${service.id}`}
                          checked={createServiceForm.additional_services.some(s => s.id === service.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Add service
                              setCreateServiceForm({
                                ...createServiceForm,
                                additional_services: [
                                  ...createServiceForm.additional_services,
                                  { id: service.id, name: service.name, price: service.price, quantity: 1 }
                                ]
                              });
                            } else {
                              // Remove service
                              setCreateServiceForm({
                                ...createServiceForm,
                                additional_services: createServiceForm.additional_services.filter(s => s.id !== service.id)
                              });
                            }
                          }}
                          className="w-4 h-4 text-[#36e961] rounded"
                        />
                        <div className="flex-1">
                          <label htmlFor={`service-${service.id}`} className="text-sm font-medium cursor-pointer">
                            {translateAdditionalServiceName(service.name, t)}
                          </label>
                          <p className="text-xs text-gray-500">{service.price.toFixed(2)} €</p>
                        </div>
                        {createServiceForm.additional_services.some(s => s.id === service.id) && (
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">{t('admin_order_extra_qty_short')}</Label>
                            <Input
                              type="number"
                              min="1"
                              value={createServiceForm.additional_services.find(s => s.id === service.id)?.quantity || 1}
                              onChange={(e) => {
                                setCreateServiceForm({
                                  ...createServiceForm,
                                  additional_services: createServiceForm.additional_services.map(s =>
                                    s.id === service.id ? { ...s, quantity: parseInt(e.target.value) || 1 } : s
                                  )
                                });
                              }}
                              className="w-16 h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('admin_modal_no_additional_services')}</p>
                )}

                {/* Total de servicios adicionales */}
                {createServiceForm.additional_services.length > 0 && (
                  <div className="p-3 bg-white rounded border border-[#36e961]">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">{t('admin_modal_additional_selected_label')}</span>
                      <span className="text-lg font-bold text-[#36e961]">
                        {createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0).toFixed(2)} €
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {createServiceForm.additional_services.map((service) => (
                        <div key={service.id} className="text-xs text-gray-600 flex justify-between">
                          <span>{translateAdditionalServiceName(service.name, t)} x{service.quantity}</span>
                          <span>{(service.price * service.quantity).toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Precio total (base + servicios) */}
                {(createServiceForm.calculated_price > 0 || createServiceForm.additional_services.length > 0) && (
                  <div className="p-4 bg-[#36e961] bg-opacity-10 rounded border-2 border-[#36e961]">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-semibold text-gray-800">{t('admin_modal_total_price_label')}</span>
                      <span className="text-2xl font-bold text-[#36e961]">
                        {(
                          createServiceForm.calculated_price + 
                          createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0)
                        ).toFixed(2)} €
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      {createServiceForm.calculated_price > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_modal_price_base_service')}</span>
                          <span>{createServiceForm.calculated_price.toFixed(2)} €</span>
                        </div>
                      )}
                      {createServiceForm.additional_services.length > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_modal_additional_services_sum')}</span>
                          <span>{createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0).toFixed(2)} €</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>


              {/* Asignación de Conductor */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-[#36e961]" />
                  {t('admin_modal_assign_driver_heading')}
                </h3>

                <div>
                  <Label>{t('transport_tab_detail_driver')}</Label>
                  <Select 
                    value={createServiceForm.driver_id || "__none__"} 
                    onValueChange={(value) => setCreateServiceForm({...createServiceForm, driver_id: value === "__none__" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin_modal_driver_unassigned_placeholder')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="__none__">{t('admin_unassigned')}</SelectItem>
                      {getFilteredDrivers().map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name} - {driver.phone}
                          {driver.is_fleet_driver ? ' 🚗' : ''}
                          {!driver.documents_validated && !driver.is_fleet_driver ? ' ⚠️' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {createServiceForm.driver_id 
                      ? t('admin_modal_driver_status_assigned') 
                      : t('admin_modal_driver_status_pending')}
                  </p>
                </div>


              {/* Tarifa del Conductor */}
              {createServiceForm.driver_id && createServiceForm.driver_id !== "__none__" && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    {t('admin_modal_driver_rate_block_title')}
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <Label>{t('admin_modal_driver_price_label')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={createServiceForm.driver_price}
                        onChange={(e) => setCreateServiceForm({...createServiceForm, driver_price: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        required={createServiceForm.driver_id && createServiceForm.driver_id !== "__none__"}
                        className="text-lg font-medium"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        {t('admin_modal_driver_price_hint')}
                      </p>
                    </div>

                    {/* Comparación de precios */}
                    {(createServiceForm.calculated_price > 0 || createServiceForm.driver_price > 0) && (
                      <div className="grid grid-cols-2 gap-3">
                        {/* Only show client price if admin has permission */}
                        {canSeeClientPrices && (
                          <div className="p-3 bg-green-100 rounded border border-green-300">
                            <p className="text-xs text-gray-600 mb-1">{t('admin_price_client')}</p>
                            <p className="text-xl font-bold text-green-700">
                              {(
                                createServiceForm.calculated_price + 
                                createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0)
                              ).toFixed(2)} €
                            </p>
                          </div>
                        )}
                        <div className={`p-3 bg-blue-100 rounded border border-blue-300 ${!canSeeClientPrices ? 'col-span-2' : ''}`}>
                          <p className="text-xs text-gray-600 mb-1">{t('admin_modal_pay_driver_short')}</p>
                          <p className="text-xl font-bold text-blue-700">{createServiceForm.driver_price.toFixed(2)} €</p>
                        </div>
                      </div>
                    )}

                    {/* Margen de ganancia - Only show if admin can see client prices */}
                    {canSeeClientPrices && createServiceForm.driver_price > 0 && createServiceForm.calculated_price > 0 && (
                      <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{t('admin_modal_your_margin')}</span>
                          <span className="text-lg font-bold text-yellow-700">
                            {(
                              (createServiceForm.calculated_price + 
                              createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0)) -
                              createServiceForm.driver_price
                            ).toFixed(2)} €
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {t('admin_modal_margin_percent', {
                            pct: (
                            ((createServiceForm.calculated_price + 
                            createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0) -
                            createServiceForm.driver_price) / 
                            (createServiceForm.calculated_price + 
                            createServiceForm.additional_services.reduce((sum, s) => sum + (s.price * s.quantity), 0))) * 100
                          ).toFixed(1),
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              </div>

              {/* Estado de Pago */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#36e961]" />
                  {t('admin_modal_payment_status_title')}
                </h3>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_paid"
                    checked={createServiceForm.is_paid}
                    onChange={(e) => setCreateServiceForm({...createServiceForm, is_paid: e.target.checked})}
                    className="w-4 h-4 text-[#36e961] rounded"
                  />
                  <label htmlFor="is_paid" className="text-sm font-medium">
                    {t('admin_modal_mark_paid_label')}
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  {createServiceForm.is_paid 
                    ? t('admin_modal_payment_paid_hint') 
                    : t('admin_modal_payment_pending_hint')}
                </p>
              </div>

              {/* Notas */}
              <div>
                <Label>{t('admin_modal_notes_additional')}</Label>
                <textarea
                  value={createServiceForm.notes}
                  onChange={(e) => setCreateServiceForm(prev => ({...prev, notes: e.target.value}))}
                  placeholder={t('transport_tab_notes_placeholder')}
                  className="w-full p-3 border rounded-lg min-h-[100px]"
                />
              </div>

              {(createServiceForm.service_type === 'bc_service' || createServiceForm.service_type === 'tradein') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('admin_notes_vehicle1')}</Label>
                    <textarea
                      value={createServiceForm.first_vehicle_notes}
                      onChange={(e) => setCreateServiceForm(prev => ({ ...prev, first_vehicle_notes: e.target.value }))}
                      className="w-full p-3 border rounded-lg min-h-[90px]"
                    />
                  </div>
                  <div>
                    <Label>{t('admin_notes_vehicle2')}</Label>
                    <textarea
                      value={createServiceForm.second_vehicle_notes}
                      onChange={(e) => setCreateServiceForm(prev => ({ ...prev, second_vehicle_notes: e.target.value }))}
                      className="w-full p-3 border rounded-lg min-h-[90px]"
                    />
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateServiceDialog(false)}
                  disabled={loadingCreateService}
                >
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#36e961] hover:bg-[#2dd351]"
                  disabled={loadingCreateService}
                >
                  {loadingCreateService ? t('admin_creating') : t('create_service')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Client Invoice Dialog */}
      {showUploadInvoiceDialog && (
        <Dialog open={showUploadInvoiceDialog} onOpenChange={setShowUploadInvoiceDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('admin_upload_client_invoice_title')}</DialogTitle>
              <DialogDescription>
                {t('admin_upload_client_invoice_desc')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Period Info */}
              {selectedPeriod && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <p><strong>{t('admin_client_label')}</strong> {selectedPeriod.client_name}</p>
                  <p><strong>{t('driver_billing_period_label')}</strong> {new Date(selectedPeriod.period_start).toLocaleDateString(dateLocale)} - {new Date(selectedPeriod.period_end).toLocaleDateString(dateLocale)}</p>
                  <p><strong>{t('transport_tab_stat_total')}:</strong> {selectedPeriod.total_with_iva?.toFixed(2)}€</p>
                </div>
              )}

              {/* Invoice Number */}
              <div>
                <Label htmlFor="invoice_number">{t('admin_upload_client_invoice_number_label')}</Label>
                <Input
                  id="invoice_number"
                  value={uploadInvoiceNumber}
                  onChange={(e) => setUploadInvoiceNumber(e.target.value)}
                  placeholder={t('ph_invoice_ref')}
                />
              </div>

              {/* Invoice Date */}
              <div>
                <Label htmlFor="invoice_date">{t('admin_upload_client_invoice_date_label')}</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={uploadInvoiceDate}
                  onChange={(e) => setUploadInvoiceDate(e.target.value)}
                />
              </div>

              {/* File Upload */}
              <div>
                <Label htmlFor="invoice_file">{t('admin_upload_client_invoice_pdf_label')}</Label>
                <Input
                  id="invoice_file"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadInvoiceFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                {uploadInvoiceFile && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ {uploadInvoiceFile.name}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadInvoiceDialog(false);
                    setUploadInvoiceFile(null);
                    setUploadInvoiceNumber('');
                    setUploadInvoiceDate('');
                  }}
                  disabled={uploadingInvoice}
                >
                  {t('admin_cancel')}
                </Button>
                <Button
                  onClick={handleUploadClientInvoice}
                  disabled={uploadingInvoice || !uploadInvoiceFile || !uploadInvoiceNumber || !uploadInvoiceDate}
                >
                  {uploadingInvoice ? t('admin_uploading') : t('admin_upload_client_invoice_submit')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Period Details Dialog */}
      <Dialog open={showPeriodDetailsDialog} onOpenChange={setShowPeriodDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('driver_billing_period_details_title')}</DialogTitle>
            <DialogDescription>
              {t('admin_client_period_services_included')}
            </DialogDescription>
          </DialogHeader>
          
          {periodDetails && (
            <div className="space-y-6">
              {/* Period Summary */}
              <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">{t('admin_filter_client')}</p>
                  <p className="font-semibold">{periodDetails.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('driver_billing_period_label').replace(/:\s*$/, '')}</p>
                  <p className="font-semibold">
                    {new Date(periodDetails.period_start).toLocaleDateString(dateLocale)} - {new Date(periodDetails.period_end).toLocaleDateString(dateLocale)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('admin_metric_services')}</p>
                  <p className="font-semibold">{periodDetails.services?.length || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('admin_total_with_vat')}</p>
                  <p className="font-semibold text-lg text-green-600">
                    {periodDetails.total_with_iva?.toFixed(2)}€
                  </p>
                </div>
              </div>

              {/* Services Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('period_trips_id_label').replace(/:\s*$/, '')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('admin_invoices_col_date')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('admin_origin_label')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('admin_destination_label')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('admin_driver_col')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">{t('admin_table_status')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">{t('client_base_price_no_vat')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">{t('admin_additional_services')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">{t('admin_invoices_col_total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {periodDetails.services && periodDetails.services.length > 0 ? (
                      periodDetails.services.map((service) => {
                        const additionalServicesTotal = service.additional_services?.reduce((sum, s) => sum + (s.price || 0), 0) || 0;
                        const serviceTotal = (service.total_price || 0);
                        
                        return (
                          <tr key={service.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <span className="font-mono text-xs">
                                {service.id?.substring(0, 8).toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {service.service_date ? new Date(service.service_date).toLocaleDateString(dateLocale) : t('gps_live_na')}
                            </td>
                            <td className="px-4 py-3 text-sm max-w-[150px] truncate" title={service.pickup_address}>
                              {service.pickup_address || t('gps_live_na')}
                            </td>
                            <td className="px-4 py-3 text-sm max-w-[150px] truncate" title={service.delivery_address}>
                              {service.delivery_address || t('gps_live_na')}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {service.driver_name || t('no_driver_assigned')}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`status-badge status-${service.status} text-xs`}>
                                {service.status === 'completed' ? t('transport_tab_status_completed')
                                  : service.status === 'in_transit' ? t('in_transit')
                                  : service.status === 'pending' ? t('pending')
                                  : service.status === 'assigned' ? t('assigned')
                                  : service.status === 'delivery' ? t('delivery')
                                  : service.status === 'cancelled' ? t('cancelled')
                                  : t(service.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {((service.total_price || 0) - additionalServicesTotal).toFixed(2)}€
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {additionalServicesTotal > 0 ? (
                                <div>
                                  <span className="font-medium text-blue-600">
                                    +{additionalServicesTotal.toFixed(2)}€
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {service.additional_services?.map(s => translateAdditionalServiceName(s.name, t)).join(', ')}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                              {serviceTotal.toFixed(2)}€
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                          {t('admin_billing_period_no_services')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="8" className="px-4 py-3 text-right font-semibold">
                        {t('client_subtotal_no_vat')}
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {periodDetails.total_without_iva?.toFixed(2)}€
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="8" className="px-4 py-3 text-right font-semibold">
                        {t('admin_invoices_col_iva')}:
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {periodDetails.total_iva?.toFixed(2)}€
                      </td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td colSpan="8" className="px-4 py-3 text-right font-bold text-lg">
                        {t('admin_total_with_vat')}:
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-lg text-green-600">
                        {periodDetails.total_with_iva?.toFixed(2)}€
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Additional Services Summary */}
              {periodDetails.services?.some(s => s.additional_services && s.additional_services.length > 0) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">
                    📋 {t('admin_additional_services')}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    {(() => {
                      const additionalServicesCounts = {};
                      periodDetails.services.forEach(service => {
                        service.additional_services?.forEach(addService => {
                          const key = addService.name;
                          if (!additionalServicesCounts[key]) {
                            additionalServicesCounts[key] = { count: 0, total: 0 };
                          }
                          additionalServicesCounts[key].count++;
                          additionalServicesCounts[key].total += addService.price || 0;
                        });
                      });
                      
                      return Object.entries(additionalServicesCounts).map(([name, data]) => (
                        <div key={name} className="bg-white p-2 rounded border border-blue-200">
                          <span className="font-medium">{translateAdditionalServiceName(name, t)}</span>
                          <span className="text-gray-600"> x{data.count}</span>
                          <div className="text-blue-600 font-semibold">
                            {data.total.toFixed(2)}€
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4">
                <Button onClick={() => setShowPeriodDetailsDialog(false)}>
                  {t('admin_close')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Service Dialog */}
      <Dialog open={showCompleteServiceDialog} onOpenChange={setShowCompleteServiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t('driver_complete_service')}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder && (
                <Trans
                  i18nKey="admin_complete_dialog_subtitle"
                  values={{
                    client: selectedOrder.client_name,
                    brand: selectedOrder.car_brand,
                    model: selectedOrder.car_model,
                    plate: selectedOrder.car_plate,
                  }}
                  components={{ clientHighlight: <strong /> }}
                />
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Expenses Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">💰 {t('admin_complete_journey_expenses')}</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expense_fuel">{t('admin_complete_expense_fuel')}</Label>
                  <Input
                    id="expense_fuel"
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeServiceForm.fuel}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, fuel: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="expense_tolls">{t('admin_complete_expense_tolls')}</Label>
                  <Input
                    id="expense_tolls"
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeServiceForm.tolls}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, tolls: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="expense_parking">{t('admin_complete_expense_parking')}</Label>
                  <Input
                    id="expense_parking"
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeServiceForm.parking}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, parking: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="expense_taxi">{t('admin_complete_expense_taxi')}</Label>
                  <Input
                    id="expense_taxi"
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeServiceForm.taxi}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, taxi: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expense_other">{t('admin_complete_expense_other')}</Label>
                  <Input
                    id="expense_other"
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeServiceForm.other}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, other: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="expense_other_desc">{t('admin_complete_expense_other_desc')}</Label>
                  <Input
                    id="expense_other_desc"
                    value={completeServiceForm.other_description}
                    onChange={(e) => setCompleteServiceForm({...completeServiceForm, other_description: e.target.value})}
                    placeholder={t('admin_complete_expense_other_ph')}
                  />
                </div>
              </div>

              {/* Total */}
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-lg font-semibold">
                  {t('admin_complete_total_expenses')} €{(
                    (parseFloat(completeServiceForm.fuel) || 0) +
                    (parseFloat(completeServiceForm.tolls) || 0) +
                    (parseFloat(completeServiceForm.parking) || 0) +
                    (parseFloat(completeServiceForm.taxi) || 0) +
                    (parseFloat(completeServiceForm.other) || 0)
                  ).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Photos Section - Organized by Category */}
            <div className="space-y-6">
              <h3 className="font-semibold text-lg border-b pb-2">📷 {t('admin_complete_service_photos')}</h3>
              
              {/* Pickup Photos */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <span>📍</span> {t('admin_complete_pickup_photos')}
                  </h4>
                  <span className="text-sm text-blue-600">{t('admin_complete_photo_count', { count: pickupPhotos.length })}</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadCompletionPhoto(e, 'pickup')}
                    className="hidden"
                    id="pickup-photo-upload"
                    disabled={uploadingCompletionPhoto}
                  />
                  <label 
                    htmlFor="pickup-photo-upload"
                    className={`cursor-pointer flex items-center gap-2 px-3 py-2 bg-white border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-100 ${uploadingCompletionPhoto ? 'opacity-50' : ''}`}
                  >
                    {uploadingCompletionPhoto ? <span className="animate-spin">⏳</span> : <Plus className="w-4 h-4" />}
                    <span className="text-sm">{t('admin_complete_add_photos')}</span>
                  </label>
                </div>
                {pickupPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {pickupPhotos.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_pickup', { num: index + 1 })} className="w-full h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setPickupPhotos(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Delivery Photos */}
              <div className="bg-green-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-green-800 flex items-center gap-2">
                    <span>🏁</span> {t('admin_complete_delivery_photos')}
                  </h4>
                  <span className="text-sm text-green-600">{t('admin_complete_photo_count', { count: deliveryPhotos.length })}</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadCompletionPhoto(e, 'delivery')}
                    className="hidden"
                    id="delivery-photo-upload"
                    disabled={uploadingCompletionPhoto}
                  />
                  <label 
                    htmlFor="delivery-photo-upload"
                    className={`cursor-pointer flex items-center gap-2 px-3 py-2 bg-white border-2 border-dashed border-green-300 rounded-lg hover:bg-green-100 ${uploadingCompletionPhoto ? 'opacity-50' : ''}`}
                  >
                    {uploadingCompletionPhoto ? <span className="animate-spin">⏳</span> : <Plus className="w-4 h-4" />}
                    <span className="text-sm">{t('admin_complete_add_photos')}</span>
                  </label>
                </div>
                {deliveryPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {deliveryPhotos.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_delivery', { num: index + 1 })} className="w-full h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setDeliveryPhotos(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Expense Photos (Receipts/Tickets) */}
              <div className="bg-yellow-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-yellow-800 flex items-center gap-2">
                    <span>🧾</span> {t('admin_complete_expense_photos')}
                  </h4>
                  <span className="text-sm text-yellow-600">{t('admin_complete_photo_count', { count: expensePhotos.length })}</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadCompletionPhoto(e, 'expense')}
                    className="hidden"
                    id="expense-photo-upload"
                    disabled={uploadingCompletionPhoto}
                  />
                  <label 
                    htmlFor="expense-photo-upload"
                    className={`cursor-pointer flex items-center gap-2 px-3 py-2 bg-white border-2 border-dashed border-yellow-300 rounded-lg hover:bg-yellow-100 ${uploadingCompletionPhoto ? 'opacity-50' : ''}`}
                  >
                    {uploadingCompletionPhoto ? <span className="animate-spin">⏳</span> : <Plus className="w-4 h-4" />}
                    <span className="text-sm">{t('admin_complete_add_tickets')}</span>
                  </label>
                </div>
                {expensePhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {expensePhotos.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_expense', { num: index + 1 })} className="w-full h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setExpensePhotos(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contract Photos (Signed Transport Contract) */}
              <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-purple-800 flex items-center gap-2">
                    <span>📄</span> {t('admin_complete_contract_photos')}
                  </h4>
                  <span className="text-sm text-purple-600">{t('admin_complete_photo_count', { count: contractPhotos.length })}</span>
                </div>
                <p className="text-xs text-purple-600">{t('admin_complete_contract_hint')}</p>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleUploadCompletionPhoto(e, 'contract')}
                    className="hidden"
                    id="contract-photo-upload"
                    disabled={uploadingCompletionPhoto}
                  />
                  <label 
                    htmlFor="contract-photo-upload"
                    className={`cursor-pointer flex items-center gap-2 px-3 py-2 bg-white border-2 border-dashed border-purple-300 rounded-lg hover:bg-purple-100 ${uploadingCompletionPhoto ? 'opacity-50' : ''}`}
                  >
                    {uploadingCompletionPhoto ? <span className="animate-spin">⏳</span> : <Plus className="w-4 h-4" />}
                    <span className="text-sm">{t('admin_complete_add_contract')}</span>
                  </label>
                </div>
                {contractPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {contractPhotos.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_contract', { num: index + 1 })} className="w-full h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setContractPhotos(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Admin Additional Charges Section - Only for client, not visible to driver */}
            <div className="space-y-3 border rounded-lg p-4 bg-amber-50">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-600" />
                {t('admin_complete_client_charges_title')}
                <span className="text-xs font-normal text-amber-700 bg-amber-200 px-2 py-0.5 rounded">
                  {t('admin_complete_client_charges_badge')}
                </span>
              </h3>
              <p className="text-sm text-gray-600">
                {t('admin_complete_client_charges_intro')}
              </p>
              
              {/* Add new charge form */}
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-gray-500">{t('admin_complete_charge_type')}</Label>
                  <select
                    className="w-full p-2 border rounded text-sm"
                    value={newCharge.type}
                    onChange={(e) => setNewCharge({...newCharge, type: e.target.value})}
                  >
                    <option value="">{t('admin_complete_select_placeholder')}</option>
                    {CHARGE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-gray-500">{t('admin_complete_charge_desc_optional')}</Label>
                  <Input
                    type="text"
                    placeholder={t('admin_complete_charge_desc_ph')}
                    value={newCharge.description}
                    onChange={(e) => setNewCharge({...newCharge, description: e.target.value})}
                    className="text-sm"
                  />
                </div>
                <div className="w-24">
                  <Label className="text-xs text-gray-500">{t('admin_complete_charge_amount')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={newCharge.amount}
                    onChange={(e) => setNewCharge({...newCharge, amount: e.target.value})}
                    className="text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCharge}
                  className="bg-amber-100 hover:bg-amber-200 border-amber-300"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('admin_order_extra_add_button')}
                </Button>
              </div>
              
              {/* List of added charges */}
              {adminCharges.length > 0 && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs text-gray-500">{t('admin_complete_charges_added')}</Label>
                  {adminCharges.map((charge, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{charge.label}</span>
                        {charge.description && (
                          <span className="text-xs text-gray-500">({charge.description})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-amber-700">{charge.amount.toFixed(2)}€</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCharge(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t">
                    <span className="font-bold text-amber-800">
                      {t('admin_complete_charges_total')} {adminCharges.reduce((sum, c) => sum + c.amount, 0).toFixed(2)}€
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className="space-y-2">
              <h3 className="font-semibold text-lg border-b pb-2">📝 {t('admin_complete_notes_title')}</h3>
              <textarea
                className="w-full p-3 border rounded-lg resize-none"
                rows={3}
                value={completeServiceForm.completion_notes}
                onChange={(e) => setCompleteServiceForm({...completeServiceForm, completion_notes: e.target.value})}
                placeholder={t('admin_complete_notes_ph')}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCompleteServiceDialog(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleCompleteService}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('driver_complete_service')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service History Dialog */}
      <Dialog open={showServiceHistoryDialog} onOpenChange={setShowServiceHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {t('admin_service_history_title')}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder && (
                <Trans
                  i18nKey="admin_complete_dialog_subtitle"
                  values={{
                    client: selectedOrder.client_name,
                    brand: selectedOrder.car_brand,
                    model: selectedOrder.car_model,
                    plate: selectedOrder.car_plate,
                  }}
                  components={{ clientHighlight: <strong /> }}
                />
              )}
            </DialogDescription>
          </DialogHeader>

          {serviceHistoryDetails && (
            <div className="space-y-6 py-4">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  serviceHistoryDetails.status === 'delivered' ? 'bg-green-100 text-green-800' :
                  serviceHistoryDetails.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {serviceHistoryDetails.status === 'delivered' ? `✅ ${t('admin_history_delivered')}` : serviceHistoryDetails.status}
                </span>
                {serviceHistoryDetails.completed_at && (
                  <span className="text-sm text-gray-500">
                    {t('admin_history_completed_on')}{' '}
                    {new Date(serviceHistoryDetails.completed_at).toLocaleString(dateLocale)}
                  </span>
                )}
              </div>

              {/* Expenses */}
              {serviceHistoryDetails.expenses && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">💰 {t('admin_history_expenses_registered')}</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {serviceHistoryDetails.expenses.fuel_expense > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_expense_gas_label')}</span>
                          <span className="font-medium">€{serviceHistoryDetails.expenses.fuel_expense.toFixed(2)}</span>
                        </div>
                      )}
                      {serviceHistoryDetails.expenses.toll_expense > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_expense_tolls_label')}</span>
                          <span className="font-medium">€{serviceHistoryDetails.expenses.toll_expense.toFixed(2)}</span>
                        </div>
                      )}
                      {serviceHistoryDetails.expenses.parking_expense > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_expense_parking_label')}</span>
                          <span className="font-medium">€{serviceHistoryDetails.expenses.parking_expense.toFixed(2)}</span>
                        </div>
                      )}
                      {serviceHistoryDetails.expenses.taxi_expense > 0 && (
                        <div className="flex justify-between">
                          <span>{t('admin_expense_taxi_label')}</span>
                          <span className="font-medium">€{serviceHistoryDetails.expenses.taxi_expense.toFixed(2)}</span>
                        </div>
                      )}
                      {serviceHistoryDetails.expenses.other_expense > 0 && (
                        <div className="flex justify-between col-span-2">
                          <span>{t('admin_expense_other_label_short', { desc: serviceHistoryDetails.expenses.other_expense_description || t('admin_expense_other_na') })}</span>
                          <span className="font-medium">€{serviceHistoryDetails.expenses.other_expense.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="border-t mt-3 pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>{t('admin_history_total_label')}</span>
                        <span>€{serviceHistoryDetails.expenses.total_expense?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {t('admin_history_submitted_by')} {serviceHistoryDetails.expenses.submitted_by_name || t('admin_expense_other_na')}
                    </div>
                  </div>
                </div>
              )}

              {/* Photos - Organized by Category */}
              {(serviceHistoryDetails.completion_photos?.pickup_photos?.length > 0 ||
                serviceHistoryDetails.completion_photos?.delivery_photos?.length > 0 ||
                serviceHistoryDetails.completion_photos?.expense_photos?.length > 0) && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">📷 {t('admin_complete_service_photos')}</h3>
                  
                  {/* Pickup Photos */}
                  {serviceHistoryDetails.completion_photos?.pickup_photos?.length > 0 && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <span>📍</span> {t('admin_complete_pickup_photos')} ({serviceHistoryDetails.completion_photos.pickup_photos.length})
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {serviceHistoryDetails.completion_photos.pickup_photos.map((url, index) => (
                          <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                            <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_pickup', { num: index + 1 })} className="w-full h-20 object-cover rounded border hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Delivery Photos */}
                  {serviceHistoryDetails.completion_photos?.delivery_photos?.length > 0 && (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                        <span>🏁</span> {t('admin_complete_delivery_photos')} ({serviceHistoryDetails.completion_photos.delivery_photos.length})
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {serviceHistoryDetails.completion_photos.delivery_photos.map((url, index) => (
                          <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                            <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_delivery', { num: index + 1 })} className="w-full h-20 object-cover rounded border hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expense Photos */}
                  {serviceHistoryDetails.completion_photos?.expense_photos?.length > 0 && (
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                        <span>🧾</span> {t('admin_complete_expense_photos')} ({serviceHistoryDetails.completion_photos.expense_photos.length})
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {serviceHistoryDetails.completion_photos.expense_photos.map((url, index) => (
                          <a key={index} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                            <img src={`${BACKEND_URL}${url}`} alt={t('admin_complete_photo_alt_expense', { num: index + 1 })} className="w-full h-20 object-cover rounded border hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Completion Notes */}
              {serviceHistoryDetails.completion_notes && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg border-b pb-2">📝 {t('admin_history_completion_notes')}</h3>
                  <p className="bg-gray-50 p-4 rounded-lg text-gray-700">
                    {serviceHistoryDetails.completion_notes}
                  </p>
                </div>
              )}

              {/* Completed By */}
              {serviceHistoryDetails.completed_by && (
                <div className="text-sm text-gray-500 border-t pt-3">
                  {t('admin_history_completed_by')} {serviceHistoryDetails.completed_by}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowServiceHistoryDialog(false)}>
              {t('service_detail_close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Expense Report Dialog with Additional Charges */}
      <Dialog open={showApproveExpenseDialog} onOpenChange={setShowApproveExpenseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t('admin_expense_approve_dialog_title')}
            </DialogTitle>
            <DialogDescription>
              {selectedExpenseReport && (
                <span>
                  {t('admin_driver_label')} <strong>{selectedExpenseReport.driver_name}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedExpenseReport && (
            <div className="space-y-4">
              <ExpenseReportServiceInfo report={selectedExpenseReport} />
              <ExpenseReportReceipts report={selectedExpenseReport} />
              {/* Summary of driver expenses */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">{t('admin_expense_approve_reported_heading')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>{t('admin_expense_gas_label')} <span className="font-semibold">€{selectedExpenseReport.fuel_expense?.toFixed(2) || '0.00'}</span></div>
                  <div>{t('admin_expense_tolls_label')} <span className="font-semibold">€{selectedExpenseReport.toll_expense?.toFixed(2) || '0.00'}</span></div>
                  <div>{t('admin_expense_parking_label')} <span className="font-semibold">€{selectedExpenseReport.parking_expense?.toFixed(2) || '0.00'}</span></div>
                  <div>{t('admin_expense_taxi_label')} <span className="font-semibold">€{selectedExpenseReport.taxi_expense?.toFixed(2) || '0.00'}</span></div>
                  <div className="col-span-2">{t('admin_expense_other')}: <span className="font-semibold">€{selectedExpenseReport.other_expense?.toFixed(2) || '0.00'}</span></div>
                </div>
                <div className="mt-2 pt-2 border-t font-bold">
                  {t('admin_expense_approve_driver_total')} €{selectedExpenseReport.total_expense?.toFixed(2) || '0.00'}
                </div>
              </div>
              
              {/* Admin Additional Charges Section */}
              <div className="border rounded-lg p-4 bg-amber-50">
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  {t('admin_expense_approve_client_charges_title')}
                  <span className="text-xs font-normal text-amber-700 bg-amber-200 px-2 py-0.5 rounded">
                    {t('admin_optional_paren')}
                  </span>
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  {t('admin_expense_approve_client_charges_hint')}
                </p>
                
                {/* Add charge form */}
                <div className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[120px]">
                    <Label className="text-xs text-gray-500">{t('admin_label_type_short')}</Label>
                    <select
                      className="w-full p-2 border rounded text-sm"
                      value={newApprovalCharge.type}
                      onChange={(e) => setNewApprovalCharge({...newApprovalCharge, type: e.target.value})}
                    >
                      <option value="">{t('admin_complete_select_placeholder')}</option>
                      {CHARGE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <Label className="text-xs text-gray-500">{t('admin_service_description_label')}</Label>
                    <Input
                      type="text"
                      placeholder={t('ph_optional')}
                      value={newApprovalCharge.description}
                      onChange={(e) => setNewApprovalCharge({...newApprovalCharge, description: e.target.value})}
                      className="text-sm"
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs text-gray-500">€</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={newApprovalCharge.amount}
                      onChange={(e) => setNewApprovalCharge({...newApprovalCharge, amount: e.target.value})}
                      className="text-sm"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddApprovalCharge}
                    className="bg-amber-100 hover:bg-amber-200 border-amber-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* List of added charges */}
                {approvalCharges.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {approvalCharges.map((charge, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                        <div>
                          <span className="font-medium">{charge.label}</span>
                          {charge.description && <span className="text-gray-500 ml-1">({charge.description})</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-amber-700">{charge.amount.toFixed(2)}€</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveApprovalCharge(index)}
                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right pt-1 font-bold text-amber-800 text-sm">
                      {t('admin_expense_approve_total_charges', { total: approvalCharges.reduce((sum, c) => sum + c.amount, 0).toFixed(2) })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowApproveExpenseDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={() => approveExpenseReport(selectedExpenseReport.id, 'approved', null, approvalCharges)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {approvalCharges.length > 0 ? t('admin_approve_with_charges') : t('admin_approve_btn')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Driver Billing Info Edit Dialog */}
      <Dialog open={showDriverBillingDialog} onOpenChange={setShowDriverBillingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {t('admin_driver_billing_dialog_title', { name: editingDriverBilling?.name || '' })}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin_billing_full_name')}
              </label>
              <Input
                value={driverBillingForm.full_name}
                onChange={(e) => setDriverBillingForm({...driverBillingForm, full_name: e.target.value})}
                placeholder={t('admin_billing_full_name_placeholder')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin_billing_dni_nie')}
              </label>
              <Input
                value={driverBillingForm.dni_nie}
                onChange={(e) => setDriverBillingForm({...driverBillingForm, dni_nie: e.target.value})}
                placeholder="12345678A"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin_billing_fiscal_address')}
              </label>
              <Input
                value={driverBillingForm.fiscal_address}
                onChange={(e) => setDriverBillingForm({...driverBillingForm, fiscal_address: e.target.value})}
                placeholder={t('admin_billing_fiscal_placeholder')}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin_billing_bank_name')}
                </label>
                <Input
                  value={driverBillingForm.bank_name}
                  onChange={(e) => setDriverBillingForm({...driverBillingForm, bank_name: e.target.value})}
                  placeholder={t('admin_billing_bank_placeholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin_billing_irpf')}
                </label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={driverBillingForm.irpf_retention}
                  onChange={(e) => setDriverBillingForm({...driverBillingForm, irpf_retention: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin_billing_iban')}
              </label>
              <Input
                value={driverBillingForm.bank_account}
                onChange={(e) => setDriverBillingForm({...driverBillingForm, bank_account: e.target.value})}
                placeholder={t('admin_billing_iban_placeholder')}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDriverBillingDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={saveDriverBillingInfo} className="bg-blue-600 hover:bg-blue-700">
              {t('admin_save_billing_data')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Driver Profile Dialog */}
      <DriverProfileDialog
        open={showDriverProfileDialog}
        onOpenChange={setShowDriverProfileDialog}
        driver={selectedDriver}
      />

      {/* Penalty Dialog - shown when removing/changing driver from order */}
      <Dialog open={showPenaltyDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPenaltyDialog(false);
          setPendingOrderUpdate(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-orange-600 flex items-center gap-2">
              <span>⚠️</span> {t('admin_penalty_dialog_title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p className="font-medium">{t('admin_driver_label')} <span className="text-blue-600">{penaltyData.driver_name}</span></p>
              {penaltyData.order_info && (
                <>
                  <p className="text-sm text-gray-600">{t('admin_penalty_service_label')} {penaltyData.order_info.car_plate}</p>
                  <p className="text-sm text-gray-600">{t('admin_penalty_route_label')} {penaltyData.order_info.route}</p>
                </>
              )}
              <p className="text-sm">
                <span className={penaltyData.hours_until_pickup < 48 ? 'text-red-600 font-medium' : 'text-green-600'}>
                  {t('admin_penalty_hours_until_pickup', { hours: penaltyData.hours_until_pickup.toFixed(1) })}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <input
                type="checkbox"
                id="apply-penalty"
                checked={penaltyData.apply_penalty}
                onChange={(e) => setPenaltyData({...penaltyData, apply_penalty: e.target.checked})}
                className="h-5 w-5 text-orange-600 rounded"
              />
              <label htmlFor="apply-penalty" className="font-medium cursor-pointer">
                {t('admin_penalty_apply_label')}
              </label>
            </div>

            {penaltyData.apply_penalty && (
              <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-red-700">{t('admin_penalty_calculated_label')}</span>
                  <span className="text-lg font-bold text-red-600">{penaltyData.suggested_amount}€</span>
                </div>
                <p className="text-sm text-red-600">
                  {penaltyData.hours_until_pickup < 48 
                    ? t('admin_penalty_rule_under_48')
                    : t('admin_penalty_rule_over_48')}
                </p>
                
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin_penalty_amount_label')}
                    </label>
                    <Input
                      type="number"
                      value={penaltyData.amount}
                      onChange={(e) => setPenaltyData({...penaltyData, amount: parseFloat(e.target.value) || 0})}
                      className="text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin_penalty_reason_label')}
                    </label>
                    <select
                      value={penaltyData.reason}
                      onChange={(e) => setPenaltyData({...penaltyData, reason: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="Cancelación tardía">{t('admin_penalty_reason_late')}</option>
                      <option value="No presentado">{t('admin_penalty_reason_no_show')}</option>
                      <option value="Incumplimiento">{t('admin_penalty_reason_breach')}</option>
                      <option value="Otro">{t('admin_penalty_reason_other')}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPenaltyDialog(false);
                setPendingOrderUpdate(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setPenaltyData({...penaltyData, apply_penalty: false});
                applyDriverPenalty();
              }}
            >
              {t('admin_penalty_save_without')}
            </Button>
            {penaltyData.apply_penalty && (
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={applyDriverPenalty}
              >
                {t('admin_penalty_save_with', { amount: penaltyData.amount })}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
